<template>
  <div class="workspace-detail-container" v-loading="pageLoading">
    <!-- Top Workspace Summary Card -->
    <div v-if="workspace" class="workspace-summary-card">
      <div class="summary-left">
        <el-button link @click="goBack" class="back-link">
          <el-icon>
            <ArrowLeft />
          </el-icon> 返回列表
        </el-button>
        <div class="ws-info">
          <h2>{{ workspace.name }}</h2>
          <div class="ws-meta">
            <el-tag :type="getStatusType(workspace.status)" size="small">
              {{ getStatusText(workspace.status) }}
            </el-tag>
            <span class="meta-item">每周天数：{{ workspace.config?.days_per_week || 5 }}天</span>
            <span class="meta-item">最大日节数：{{ totalDailyPeriods }}节</span>
            <span class="meta-item">任课安排：{{ tasks.length }}条</span>
            <span class="meta-item">排课要求：{{ constraints.length }}条</span>
          </div>
        </div>
      </div>
      <div class="summary-right" style="display: flex; align-items: center; gap: 15px; flex-wrap: wrap;">
        <!-- Sandbox toggle switch -->
        <div class="sandbox-toggle-wrapper" style="display: flex; align-items: center; gap: 8px; background: #fffbeb; border: 1px solid #fef08a; padding: 6px 12px; border-radius: 6px; box-shadow: 0 1px 3px rgba(0,0,0,0.02); flex-shrink: 0;">
          <span style="font-size: 13px; font-weight: 600; color: #854d0e; display: flex; align-items: center; gap: 4px; white-space: nowrap;">
            <span class="sandbox-status-dot" :style="{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: isSandboxActive ? '#ca8a04' : '#94a3b8' }"></span>
            沙盒推演模式
          </span>
          <el-switch v-model="isSandboxActive" :before-change="handleSandboxChange" active-color="#ca8a04" inactive-color="#d1d5db" />
        </div>

        <div style="display: flex; align-items: center; gap: 10px; flex-wrap: wrap;">
          <el-button type="success" :disabled="tasks.length === 0" @click="activeTab = 'solve'" style="white-space: nowrap;">
            <el-icon>
              <Cpu />
            </el-icon> 生成课表
          </el-button>
          <el-button type="primary" :disabled="results.length === 0" @click="activeTab = 'timetable'" style="white-space: nowrap;">
            <el-icon>
              <Grid />
            </el-icon> 查看课表
          </el-button>
          <el-button type="warning" :disabled="results.length === 0" @click="exportTimetable" style="white-space: nowrap;">
            <el-icon><Download /></el-icon> 导出 Excel 课表
          </el-button>
          <el-button type="success" :disabled="results.length === 0" @click="exportTimetableWord" style="white-space: nowrap;">
            <el-icon><Download /></el-icon> 导出 Word 课表
          </el-button>
        </div>
      </div>
    </div>

    <!-- Global Sandbox Warning Banner -->
    <div v-if="isSandboxActive" class="global-sandbox-banner"
      style="margin: 10px 0 20px 0; padding: 16px 24px; border-radius: 8px; display: flex; align-items: center; justify-content: space-between; background: #fffbeb; border: 1px solid #eab308; box-shadow: 0 4px 12px rgba(234, 179, 8, 0.12); transition: all 0.3s ease; flex-wrap: wrap; gap: 15px;">
      <div style="display: flex; align-items: center; gap: 10px; color: #854d0e; min-width: 300px; flex: 1;">
        <el-icon size="18" class="tuning-pulse" style="color: #ca8a04;"><Warning /></el-icon>
        <div>
          <span style="font-weight: 700; font-size: 14px;">您当前正处于「沙盒推演模式」中</span>
          <p style="font-size: 12px; margin-top: 2px; color: #713f12; line-height: 1.4;">所有对课表的更改、排课操作都处于临时草稿状态。您可以放心尝试各种排课调整方案。确认满意后可“合并至正式版”生效，或点击“放弃修改”彻底还原。</p>
        </div>
      </div>
      <div style="display: flex; gap: 12px; flex-shrink: 0; align-items: center; flex-wrap: nowrap;">
        <el-button type="success" size="small" @click="publishSandbox" style="font-weight: 600; white-space: nowrap;">发布合并至正式版</el-button>
        <el-button type="danger" size="small" plain @click="rollbackSandbox" style="font-weight: 600; white-space: nowrap;">放弃修改彻底回滚</el-button>
      </div>
    </div>

    <!-- Active Tab Menu -->
    <el-tabs v-model="activeTab" class="custom-tabs" type="card" @tab-change="handleTabChange">
      <!-- Tab 1: Combined & Walk Class Wizard -->
      <el-tab-pane v-if="enableSportsCombined || enableWalkClass" name="combined" label="1. 走班与体育合班设置" lazy>
        <div style="padding: 10px 20px; display: flex; flex-direction: column; gap: 10px;">
          <!-- Walk-class and P.E. Wizard Banner -->
          <div v-if="enableSportsCombined || enableWalkClass"
            style="margin: 10px 0 20px 0; padding: 24px; background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%); border-radius: 12px; border: 1px solid #c7d2fe; box-shadow: 0 4px 12px rgba(79, 70, 229, 0.05); display: flex; justify-content: space-between; align-items: center; gap: 20px; flex-wrap: wrap;">
            <div>
              <h4 style="margin: 0 0 8px 0; color: #312e81; font-size: 16px; font-weight: 700; display: flex; align-items: center; gap: 8px;">
                <el-icon color="#4f46e5"><Cpu /></el-icon>
                走班与体育专项“向导式”规则配置
              </h4>
              <p style="margin: 0; color: #4338ca; font-size: 13px; line-height: 1.5;">
                <span style="font-weight: bold; color: #ef4444;">提示：</span>请优先在此处配置{{ enableWalkClass ? '走班课' : '' }}{{ enableWalkClass && enableSportsCombined ? '和' : '' }}{{ enableSportsCombined ? '体育专项合班' : '' }}，它们会自动录入至下一步的课时安排中，防止后续常规课时设置出现冲突或重叠。
              </p>
            </div>
            <div style="display: flex; gap: 12px;">
              <el-button v-if="enableSportsCombined" type="primary" size="large" :icon="Collection" @click="openSportsWizard">
                体育合班拼盘向导
              </el-button>
              <el-button v-if="enableWalkClass" type="warning" size="large" :icon="Grid" @click="openWalkClassWizard">
                走班课设置向导
              </el-button>
            </div>
          </div>

          <!-- Configured Sports Groups Section -->
          <div v-if="enableSportsCombined && sportsCombinedGroups.length > 0" class="configured-sports-section" style="margin: 10px 0 15px 0; padding: 20px; background-color: #fafbfd; border-radius: 12px; border: 1px solid #e2e8f0; box-shadow: 0 2px 8px rgba(0,0,0,0.02);">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
              <h4 style="margin: 0; color: #1e293b; font-size: 14px; font-weight: 700; display: flex; align-items: center; gap: 6px;">
                <span style="display: inline-block; width: 4px; height: 14px; background-color: #4f46e5; border-radius: 2px;"></span>
                已配置的体育合班计划
              </h4>
            </div>
            <el-table :data="sportsCombinedGroups" size="small" border style="width: 100%; border-radius: 8px; overflow: hidden;">
              <el-table-column label="年级" prop="grade" width="100" />
              <el-table-column label="科目" prop="subjectName" width="120" />
              <el-table-column label="参与班级">
                <template #default="{ row }">
                  <el-tag v-for="c in row.classes" :key="c" size="small" style="margin-right: 5px;">{{ c }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="体育教师">
                <template #default="{ row }">
                  <span v-if="row.teachers.length > 0">
                    {{ row.teachers.map(t => t.name).join('、') }}
                  </span>
                  <span v-else style="color: #94a3b8; font-style: italic;">未分配老师</span>
                </template>
              </el-table-column>
              <el-table-column label="周课时" width="80">
                <template #default="{ row }">
                  {{ row.weeklyPeriods }} 节
                </template>
              </el-table-column>
              <el-table-column label="操作" width="120" align="center">
                <template #default="{ row }">
                  <el-button type="primary" link size="small" @click="editSportsGroup(row)">修改</el-button>
                  <el-button type="danger" link size="small" @click="deleteSportsGroup(row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- Configured Walk-Class Groups Section -->
          <div v-if="enableWalkClass && walkClassCombinedGroups.length > 0" class="configured-sports-section" style="margin: 10px 0 25px 0; padding: 20px; background-color: #fafbfd; border-radius: 12px; border: 1px solid #e2e8f0; box-shadow: 0 2px 8px rgba(0,0,0,0.02);">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
              <h4 style="margin: 0; color: #1e293b; font-size: 14px; font-weight: 700; display: flex; align-items: center; gap: 6px;">
                <span style="display: inline-block; width: 4px; height: 14px; background-color: #f59e0b; border-radius: 2px;"></span>
                已配置的走班合班计划
              </h4>
            </div>
            <el-table :data="walkClassCombinedGroups" size="small" border style="width: 100%; border-radius: 8px; overflow: hidden;">
              <el-table-column label="年级" prop="grade" width="100" />
              <el-table-column label="走班虚拟班级" prop="className" width="130" />
              <el-table-column label="走班科目" width="200">
                <template #default="{ row }">
                  <el-tag v-for="sub in row.subjects" :key="sub" type="success" size="small" style="margin-right: 5px;">{{ sub }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="参与行政班级">
                <template #default="{ row }">
                  <el-tag v-for="c in row.classes" :key="c" type="info" size="small" style="margin-right: 5px;">{{ c }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="任课教师">
                <template #default="{ row }">
                  <span v-if="row.teachers.length > 0">
                    {{ row.teachers.map(t => t.name).join('、') }}
                  </span>
                  <span v-else style="color: #94a3b8; font-style: italic;">未分配老师</span>
                </template>
              </el-table-column>
              <el-table-column label="周课时" width="80">
                <template #default="{ row }">
                  {{ row.weeklyPeriods }} 节
                </template>
              </el-table-column>
              <el-table-column label="操作" width="120" align="center">
                <template #default="{ row }">
                  <el-button type="danger" link size="small" @click="deleteWalkClassGroup(row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- Both disabled placeholder -->
          <div v-if="!enableSportsCombined && !enableWalkClass"
            style="margin: 40px auto; padding: 40px; text-align: center; max-width: 600px; background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 12px;">
            <el-icon size="48" color="#94a3b8" style="margin-bottom: 16px;"><CircleCheck /></el-icon>
            <h3 style="margin: 0 0 8px 0; color: #334155; font-size: 18px; font-weight: 600;">无需配置走班与体育合班</h3>
            <p style="margin: 0 0 24px 0; color: #64748b; font-size: 14px; line-height: 1.5;">
              当前排课项目的配置已禁用了“走班计划”和“体育合班计划”。您无需在此步骤进行任何操作，请直接点击下方按钮进入下一步。
            </p>
            <el-button type="primary" size="large" @click="activeTab = 'tasks'">
              进入下一步：基础信息与任课安排
            </el-button>
          </div>

          <div v-if="enableSportsCombined || enableWalkClass" class="step-navigation-buttons" style="margin-top: 30px;">
            <el-button disabled>上一步</el-button>
            <el-button type="primary" @click="activeTab = 'tasks'">下一步：基础信息与任课安排</el-button>
          </div>
        </div>
      </el-tab-pane>

      <!-- Tab 2: Tasks List (Teaching plan) -->
      <el-tab-pane name="tasks" :label="(enableSportsCombined || enableWalkClass ? '2. ' : '1. ') + '基础信息与任课安排'" lazy>
        <!-- View Mode Selector -->
        <div
          style="margin-bottom: 15px; display: flex; justify-content: space-between; align-items: center; background: #ffffff; padding: 12px 20px; border-radius: 8px; border: 1px solid #e4e7ed; box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);">
          <el-radio-group v-model="entryViewMode" size="default">
            <el-radio-button label="wizard">
              <el-icon style="vertical-align: middle; margin-right: 4px;">
                <Grid />
              </el-icon> 分步填写
            </el-radio-button>
            <el-radio-button label="list">
              <el-icon style="vertical-align: middle; margin-right: 4px;">
                <Notebook />
              </el-icon> 直接看列表
            </el-radio-button>
          </el-radio-group>
          <div v-if="entryViewMode === 'wizard'" style="font-size: 13px; color: #909399;">
            按顺序填写年级、班级、课时和任课教师，更适合新建排课
          </div>
          <div v-else style="font-size: 13px; color: #909399;">
            适合已经有数据时集中查看、导入和修改
          </div>
        </div>

        <!-- Wizard View -->
        <div v-if="entryViewMode === 'wizard'" class="wizard-container"
          style="background: #ffffff; padding: 24px; border-radius: 12px; border: 1px solid #e2e8f0; box-shadow: 0 4px 20px rgba(0,0,0,0.03); margin-top: 10px;">
          <!-- Custom Premium Tabs -->
          <div class="custom-wizard-tabs"
            style="display: flex; gap: 12px; margin-bottom: 24px; border-bottom: 2px solid #f1f5f9; padding-bottom: 12px;">
            <div v-for="(step, idx) in ['科目设置', '教师设置', '年级班级', '上课时间', '每周课时', '任课教师']" :key="idx"
              class="wizard-tab-item" :class="{ active: wizardStep === idx }"
              style="padding: 10px 20px; border-radius: 8px; font-weight: 600; font-size: 14px; cursor: pointer; transition: all 0.3s; display: flex; align-items: center; gap: 8px;"
              :style="wizardStep === idx ? { background: '#eff6ff', color: '#1d4ed8' } : { color: '#64748b' }"
              @click="goToStep(idx)">
              <span class="tab-badge"
                style="display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; border-radius: 50%; font-size: 11px; font-weight: 700;"
                :style="wizardStep === idx ? { background: '#3b82f6', color: '#ffffff' } : { background: '#cbd5e1', color: '#64748b' }">
                {{ idx + 1 }}
              </span>
              {{ step }}
            </div>
          </div>

          <!-- Step 0: Subject Settings Tab -->
          <div v-if="wizardStep === 0" class="wizard-step-content animate-fade-in">
            <div style="border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; background: #ffffff;">
              <div
                style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; border-bottom: 1px solid #f1f5f9; padding-bottom: 16px; flex-wrap: wrap; gap: 15px;">
                <div>
                  <h4 style="margin: 0; color: #1e293b; font-size: 16px; font-weight: 700;">科目/学科库管理 (独立排课科目)</h4>
                  <span style="font-size: 13px; color: #64748b;">在此添加和配置本次排课所用的学科。点击“编辑”配置细节规则。</span>
                </div>
                <div
                  style="background: #f8fafc; padding: 8px 12px; border-radius: 8px; border: 1px solid #f1f5f9; display: flex; align-items: center; gap: 10px;">
                  <el-form :inline="true" :model="newSubjectForm" @submit.prevent="createSubject"
                    style="margin-bottom: 0; display: flex; align-items: center; gap: 8px;">
                    <el-form-item label="快捷新增" required style="margin-bottom: 0; margin-right: 0;">
                      <el-input v-model="newSubjectForm.name" placeholder="如：心理健康" style="width: 160px;" size="small" />
                    </el-form-item>
                    <el-form-item style="margin-bottom: 0; margin-right: 0;">
                      <el-button type="primary" size="small" @click="createSubject">添加</el-button>
                    </el-form-item>
                  </el-form>
                </div>
              </div>

              <el-table :data="subjectList" border size="small" style="width: 100%" max-height="500">
                <el-table-column prop="name" label="科目名称">
                  <template #default="{ row }">
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <span v-if="row.color"
                        :style="{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: row.color, border: '1px solid #cbd5e1' }"></span>
                      <span v-else
                        style="display: inline-block; width: 12px; height: 12px; borderRadius: '50%', backgroundColor: '#e2e8f0', border: '1px solid #cbd5e1'"></span>
                      <span style="font-weight: 600; color: #334155;">{{ row.name }}</span>
                      <el-tag v-if="row.short_name" size="small" type="info" style="font-family: monospace;">{{
                        row.short_name }}</el-tag>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column prop="subject_type" label="所属类型" width="120" align="center">
                  <template #default="{ row }">
                    <el-tag size="small" :type="row.subject_type === '文化课' ? 'primary' : 'success'">{{ row.subject_type
                      || '文化课' }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="基础规则" min-width="200">
                  <template #default="{ row }">
                    <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                      <el-tag v-if="row.requires_teacher !== false" size="small" type="info" effect="plain">需教师</el-tag>
                      <el-tag v-if="row.requires_room" size="small" type="warning" effect="plain">需场地</el-tag>
                      <el-tag v-if="row.allows_consecutive !== false" size="small" type="success"
                        effect="plain">连堂</el-tag>
                      <el-tag v-if="row.allows_multiple_daily" size="small" type="danger" effect="plain">一天多次</el-tag>
                      <el-tag v-if="row.allows_morning === false" size="small" type="danger" effect="plain">禁上午</el-tag>
                      <el-tag v-if="row.allows_afternoon === false" size="small" type="danger"
                        effect="plain">禁下午</el-tag>
                      <el-tag v-if="row.allows_last_period === false" size="small" type="danger"
                        effect="plain">禁末节</el-tag>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="140" align="center">
                  <template #default="{ row }">
                    <el-button-group>
                      <el-button size="small" type="primary" plain :icon="Edit"
                        @click="openSubjectDetailDialog(row)">编辑</el-button>
                      <el-button size="small" type="danger" plain :icon="Delete"
                        @click="deleteSubject(row.id)">删除</el-button>
                    </el-button-group>
                  </template>
                </el-table-column>
              </el-table>

              <div style="display: flex; justify-content: flex-end; margin-top: 20px;">
                <el-button type="primary" class="action-btn-gradient-primary" @click="nextStep">下一步：教师设置</el-button>
              </div>
            </div>
          </div>

          <!-- Step 1: Teacher Settings Tab -->
          <div v-if="wizardStep === 1" class="wizard-step-content animate-fade-in">
            <div style="border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; background: #ffffff;">
              <div
                style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; border-bottom: 1px solid #f1f5f9; padding-bottom: 16px; flex-wrap: wrap; gap: 15px;">
                <div>
                  <h4 style="margin: 0; color: #1e293b; font-size: 16px; font-weight: 700;">教师管理 (排课可用教师)</h4>
                  <span style="font-size: 13px; color: #64748b;">在此添加和配置本次排课所用的教师。你可以指定他们任教的科目与年级。</span>
                </div>
                <div style="background: #f8fafc; padding: 8px 12px; border-radius: 8px; border: 1px solid #f1f5f9;">
                  <el-form :inline="true" :model="newTeacherForm" @submit.prevent="createScheduleTeacher"
                    style="margin-bottom: 0; display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                    <el-form-item label="姓名" required style="margin-bottom: 0; margin-right: 0;">
                      <el-input v-model="newTeacherForm.name" placeholder="如：张三" style="width: 110px;" size="small" />
                    </el-form-item>
                    <el-form-item label="工号" style="margin-bottom: 0; margin-right: 0;">
                      <el-input v-model="newTeacherForm.employee_id" placeholder="可选" style="width: 90px;"
                        size="small" />
                    </el-form-item>
                    <el-form-item label="学科" style="margin-bottom: 0; margin-right: 0;">
                      <el-select v-model="newTeacherForm.subject_ids" multiple collapse-tags placeholder="选择学科"
                        style="width: 130px;" size="small">
                        <el-option v-for="sub in subjectList" :key="sub.id" :label="sub.name" :value="sub.id" />
                      </el-select>
                    </el-form-item>
                    <el-form-item label="年级" style="margin-bottom: 0; margin-right: 0;">
                      <el-select v-model="newTeacherForm.grades" multiple collapse-tags placeholder="选择年级"
                        style="width: 130px;" size="small">
                        <el-option v-for="grade in gradeOptions" :key="grade" :label="grade" :value="grade" />
                      </el-select>
                    </el-form-item>
                    <el-form-item style="margin-bottom: 0; margin-right: 0;">
                      <el-button-group>
                        <el-button type="primary" size="small" @click="createScheduleTeacher">添加教师</el-button>
                        <el-button type="success" size="small" plain :icon="Upload"
                          @click="showTeacherBatchImport">批量导入</el-button>
                      </el-button-group>
                    </el-form-item>
                  </el-form>
                </div>
              </div>

              <el-table :data="teacherList" border size="small" style="width: 100%" max-height="500">
                <el-table-column prop="name" label="教师姓名" min-width="120" />
                <el-table-column prop="employee_id" label="工号" width="120">
                  <template #default="{ row }">
                    <span>{{ row.employee_id || '-' }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="任教学科" min-width="180">
                  <template #default="{ row }">
                    <span>{{(row.subjects || []).map(item => item.name).join('、') || '-'}}</span>
                  </template>
                </el-table-column>
                <el-table-column label="任教年级" min-width="160">
                  <template #default="{ row }">
                    <span>{{ (row.grades || []).join('、') || '-' }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="140" align="center">
                  <template #default="{ row }">
                    <el-button-group>
                      <el-button size="small" type="primary" plain :icon="Edit"
                        @click="openTeacherDetailDialog(row)">编辑</el-button>
                      <el-button size="small" type="danger" plain :icon="Delete"
                        @click="deleteTeacher(row.id)">删除</el-button>
                    </el-button-group>
                  </template>
                </el-table-column>
              </el-table>

              <div class="step-navigation-buttons"
                style="margin-top: 20px; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #f1f5f9; padding-top: 20px;">
                <el-button @click="prevStep">上一步：科目设置</el-button>
                <el-button type="primary" class="action-btn-gradient-primary" @click="nextStep">下一步：年级班级</el-button>
              </div>
            </div>
          </div>

          <!-- Step 2: Grades & Classes Configuration -->
          <div v-if="wizardStep === 2" class="wizard-step-content animate-fade-in">
            <div class="wizard-base-panels">
              <div class="wizard-base-panel">
                <div class="wizard-base-title">排课学科</div>
                <div class="wizard-base-desc">先把这次排课会用到的学科准备好，后面填写课时和教师时会直接带出来。</div>
                <div class="wizard-base-actions">
                  <el-tag size="small" type="info">{{ subjectList.length }} 个学科</el-tag>
                  <el-button type="primary" plain @click="goToStep(0)">
                    <el-icon>
                      <Collection />
                    </el-icon> 学科设置
                  </el-button>
                </div>
              </div>
              <div class="wizard-base-panel">
                <div class="wizard-base-title">任课教师</div>
                <div class="wizard-base-desc">先把老师补齐，后面第四步可以直接给每个班每门课选老师。</div>
                <div class="wizard-base-actions">
                  <el-tag size="small" type="success">{{ teacherList.length }} 位教师</el-tag>
                  <el-button type="success" plain @click="openTeacherManager">
                    <el-icon>
                      <Plus />
                    </el-icon> 教师设置
                  </el-button>
                </div>
              </div>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
              <h3
                style="margin: 0; display: flex; align-items: center; gap: 8px; color: #1e293b; font-size: 16px; font-weight: 700;">
                <el-icon color="#4f46e5">
                  <Plus />
                </el-icon> 年级和班级
              </h3>
              <el-button type="primary" class="action-btn-gradient-primary" @click="addGrade" size="default">
                <el-icon>
                  <Plus />
                </el-icon> 添加年级
              </el-button>
            </div>

            <div v-if="gradesConfig.length === 0"
              style="text-align: center; padding: 50px 20px; color: #94a3b8; background: #f8fafc; border-radius: 12px; border: 1.5px dashed #cbd5e1;">
              <el-icon style="font-size: 40px; color: #cbd5e1; margin-bottom: 12px;">
                <Grid />
              </el-icon>
              <div style="font-size: 14px; font-weight: 500;">还没有年级和班级，先添加一个年级开始填写</div>
            </div>

            <div v-else class="grade-list" style="display: flex; flex-direction: column; gap: 24px;">
              <div v-for="(grade, gIdx) in gradesConfig" :key="gIdx" class="grade-card"
                style="border-radius: 12px; box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02); border: 1px solid #e2e8f0; padding: 20px;">
                <div class="grade-card-header"
                  style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #f1f5f9; padding-bottom: 16px; margin-bottom: 16px; flex-wrap: wrap; gap: 15px;">
                  <div style="display: flex; align-items: center; gap: 12px; flex-grow: 1; flex-wrap: wrap;">
                    <span
                      style="font-weight: 700; color: #475569; font-size: 14px; display: inline-flex; align-items: center; gap: 6px;">
                      <span style="width: 6px; height: 14px; background: #4f46e5; border-radius: 2px;"></span>
                      年级名称:
                    </span>
                    <el-input v-model="grade.name" placeholder="例如：高一" style="width: 120px;" size="default" />

                    <span style="font-weight: 700; color: #475569; font-size: 14px; margin-left: 10px;">学段:</span>
                    <el-select v-model="grade.phase" placeholder="学段" style="width: 100px;" size="default">
                      <el-option label="小学" value="小学" />
                      <el-option label="初中" value="初中" />
                      <el-option label="高中" value="高中" />
                    </el-select>

                    <span style="font-weight: 700; color: #475569; font-size: 14px; margin-left: 10px;">校区:</span>
                    <el-input v-model="grade.campus" placeholder="主校区" style="width: 120px;" size="default" />

                    <!-- Quick Class Generation -->
                    <el-button-group style="margin-left: 15px;">
                      <el-button size="small" plain type="info" @click="autoGenerateClasses(gIdx, 5)">生成 1-5
                        班</el-button>
                      <el-button size="small" plain type="info" @click="autoGenerateClasses(gIdx, 10)">生成 1-10
                        班</el-button>
                      <el-button size="small" plain type="info" @click="autoGenerateClasses(gIdx, 15)">生成 1-15
                        班</el-button>
                    </el-button-group>
                  </div>
                  <div style="display: flex; gap: 10px; align-items: center;">
                    <el-button type="primary" link size="default" @click="openGradeScheduleConfig(gIdx)"
                      style="font-weight: 600;">
                      <el-icon style="margin-right: 4px;">
                        <Notebook />
                      </el-icon> 设置课表与不排课时
                    </el-button>
                    <el-button type="danger" link size="default" @click="removeGrade(gIdx)" style="font-weight: 600;">
                      <el-icon style="margin-right: 4px;">
                        <Delete />
                      </el-icon> 删除该年级
                    </el-button>
                  </div>
                </div>

                <!-- Grade Classes Configuration Table -->
                <div class="grade-classes-section" style="margin-bottom: 20px;">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                    <span
                      style="font-weight: 700; color: #475569; font-size: 14px; display: inline-flex; align-items: center; gap: 6px;">
                      <el-icon color="#64748b">
                        <Grid />
                      </el-icon>
                      班级列表与类型设置
                    </span>
                    <div style="display: flex; gap: 10px; align-items: center;">
                      <el-input v-model="newClassInputs[gIdx]" placeholder="回车快速添加班级" size="small" style="width: 180px;"
                        @keyup.enter="addClass(gIdx)">
                        <template #append>
                          <el-button :icon="Plus" @click="addClass(gIdx)" />
                        </template>
                      </el-input>
                      <el-button type="primary" plain size="small" :icon="Plus"
                        @click="addNewClassEntry(gIdx)">添加空白行</el-button>
                    </div>
                  </div>

                  <el-table :data="grade.classes" border size="small" style="width: 100%; border-radius: 8px;">
                    <el-table-column label="序号" type="index" width="60" align="center" />
                    <el-table-column label="班级名称" min-width="150">
                      <template #default="scope">
                        <el-input v-model="scope.row.name" placeholder="例如: 1班" size="small" />
                      </template>
                    </el-table-column>
                    <el-table-column label="班级类型" width="180">
                      <template #default="scope">
                        <el-select v-model="scope.row.type" placeholder="选择类型" size="small" style="width: 100%;">
                          <el-option label="普通班" value="普通班" />
                          <el-option label="实验班" value="实验班" />
                          <el-option label="艺体班" value="艺体班" />
                          <el-option label="走班/选课班" value="选课走班" />
                          <el-option label="合班" value="合班" />
                          <el-option label="行政班" value="行政班" />
                        </el-select>
                      </template>
                    </el-table-column>
                    <el-table-column label="参与本次排课" width="120" align="center">
                      <template #default="scope">
                        <el-switch v-model="scope.row.participate" active-text="参与" inactive-text="不参与" inline-prompt
                          size="small" />
                      </template>
                    </el-table-column>
                    <el-table-column label="操作" width="80" align="center">
                      <template #default="scope">
                        <el-button type="danger" link size="small" @click="removeClassEntry(gIdx, scope.$index)">
                          <el-icon>
                            <Delete />
                          </el-icon> 删除
                        </el-button>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>

                <div class="grade-subject-chooser"
                  style="margin-top: 15px; background: #f8fafc; padding: 12px; border-radius: 8px; border: 1px solid #f1f5f9;">
                  <span class="grade-subject-label"
                    style="font-weight: 700; color: #475569; font-size: 13px; margin-bottom: 8px; display: block;">该年级开设学科</span>
                  <el-select v-model="grade.subject_ids" multiple collapse-tags collapse-tags-tooltip
                    placeholder="选择这个年级要排的学科" style="width: 100%;">
                    <el-option v-for="sub in subjectList" :key="sub.id" :label="sub.name" :value="sub.id" />
                  </el-select>
                </div>
              </div>
            </div>

            <div class="step-navigation-buttons"
              style="margin-top: 30px; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #f1f5f9; padding-top: 20px;">
              <el-button @click="prevStep">上一步：教师设置</el-button>
              <div style="display: flex; gap: 12px;">
                <el-button type="success" class="action-btn-gradient-success" :loading="savingGrades"
                  @click="saveGradesOnly">
                  <el-icon style="margin-right: 4px;">
                    <CircleCheck />
                  </el-icon>保存这一步
                </el-button>
                <el-button type="primary" class="action-btn-gradient-primary" @click="nextStep">下一步：设置上课时间</el-button>
              </div>
            </div>
          </div>

          <!-- Step 2: Grade Schedule Config -->
          <div v-if="wizardStep === 3" class="wizard-step-content animate-fade-in">
            <h3
              style="margin-top: 0; margin-bottom: 8px; color: #1e293b; display: flex; align-items: center; gap: 8px; font-size: 16px; font-weight: 700;">
              <el-icon color="#4f46e5">
                <Notebook />
              </el-icon> 设置上课时间
            </h3>
            <p style="font-size: 13px; color: #64748b; margin-bottom: 20px; margin-top: 0; line-height: 1.5;">
              这一步设置每个年级每天上几节课、早自习和午自习、每节课时间，以及整年级统一不排课的时段。
            </p>

            <div v-if="gradesConfig.length === 0"
              style="text-align: center; padding: 50px 20px; color: #94a3b8; background: #f8fafc; border-radius: 12px; border: 1.5px dashed #cbd5e1;">
              <el-icon style="font-size: 40px; color: #cbd5e1; margin-bottom: 12px;">
                <Notebook />
              </el-icon>
              <div style="font-size: 14px; font-weight: 500; margin-bottom: 15px;">还没有年级，请先回到第一步填写。</div>
              <el-button type="primary" @click="goToStep(2)">去设置年级班级</el-button>
            </div>
            <div v-else style="display: flex; flex-direction: column; gap: 20px;">
              <!-- Public Periods Setting Card -->
              <div class="public-periods-card"
                style="background: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0; padding: 20px; box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);">
                <div
                  style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #f1f5f9; padding-bottom: 12px; margin-bottom: 16px;">
                  <span
                    style="font-weight: 700; color: #1e293b; font-size: 15px; display: inline-flex; align-items: center; gap: 8px;">
                    <el-icon color="#4f46e5">
                      <Bell />
                    </el-icon>
                    公共时段设置 (早读、课间操、眼保健操、午休等)
                  </span>
                  <el-button type="primary" size="small" :icon="Plus" @click="openAddPublicPeriodDialog">
                    新增公共时段
                  </el-button>
                </div>

                <div v-if="publicPeriods.length === 0"
                  style="text-align: center; padding: 20px; color: #94a3b8; font-size: 13px;">
                  暂无公共时段配置，点击右上角“新增公共时段”开始配置。
                </div>
                <div v-else>
                  <el-table :data="publicPeriods" border size="small" style="width: 100%; border-radius: 8px;">
                    <el-table-column prop="name" label="时段名称" width="120" />
                    <el-table-column label="类型" width="100">
                      <template #default="scope">
                        <el-tag size="small"
                          :type="scope.row.type === 'forbidden' ? 'danger' : scope.row.type === 'activity' ? 'warning' : 'info'">
                          {{ getPublicPeriodTypeLabel(scope.row.type) }}
                        </el-tag>
                      </template>
                    </el-table-column>
                    <el-table-column label="位置" width="160">
                      <template #default="scope">
                        {{ getPublicPeriodPositionLabel(scope.row) }}
                      </template>
                    </el-table-column>
                    <el-table-column label="适用范围" width="140">
                      <template #default="scope">
                        {{ getPublicPeriodScopeLabel(scope.row) }}
                      </template>
                    </el-table-column>
                    <el-table-column label="时间范围" width="120">
                      <template #default="scope">
                        {{ scope.row.start || '--' }} - {{ scope.row.end || '--' }}
                      </template>
                    </el-table-column>
                    <el-table-column label="显示在课表" width="100" align="center">
                      <template #default="scope">
                        <el-tag :type="scope.row.show_in_schedule ? 'success' : 'info'" size="small">
                          {{ scope.row.show_in_schedule ? '显示' : '隐藏' }}
                        </el-tag>
                      </template>
                    </el-table-column>
                    <el-table-column label="教师/场地/排课影响" min-width="200">
                      <template #default="scope">
                        <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                          <el-tag size="small" :type="scope.row.occupies_teacher ? 'danger' : 'info'">
                            {{ scope.row.occupies_teacher ? '占用教师' : '不占教师' }}
                          </el-tag>
                          <el-tag size="small" :type="scope.row.occupies_room ? 'danger' : 'info'">
                            {{ scope.row.occupies_room ? '占用场地' : '不占场地' }}
                          </el-tag>
                          <el-tag size="small" :type="scope.row.affects_scheduling ? 'danger' : 'info'">
                            {{ scope.row.affects_scheduling ? '影响排课' : '不影响排课' }}
                          </el-tag>
                        </div>
                      </template>
                    </el-table-column>
                    <el-table-column label="操作" width="120" align="center">
                      <template #default="scope">
                        <el-button type="primary" link size="small" @click="openEditPublicPeriodDialog(scope.row)">
                          编辑
                        </el-button>
                        <el-button type="danger" link size="small" @click="deletePublicPeriod(scope.row)">
                          删除
                        </el-button>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </div>

              <div class="grade-list" style="display: flex; flex-direction: column; gap: 20px;">
                <div v-for="(grade, gIdx) in gradesConfig" :key="`schedule-${gIdx}`" class="grade-card">
                  <div class="grade-card-header">
                    <div style="display: flex; align-items: center; gap: 12px; flex-grow: 1; flex-wrap: wrap;">
                      <span style="font-weight: 700; color: #475569; font-size: 15px;">{{ grade.name || `年级 ${gIdx + 1}`
                        }}</span>
                      <el-tag size="small" type="info">{{ (grade.classes || []).length }} 个班</el-tag>
                      <el-tag size="small" type="success">{{ getGradeSubjects(grade).length }} 门课</el-tag>
                    </div>
                  </div>
                  <div class="grade-schedule-summary">
                    <div class="grade-schedule-summary__label">当前设置</div>
                    <div class="grade-schedule-summary__content">
                      <el-tag size="small" type="success">{{ getGradeScheduleSummary(grade) }}</el-tag>
                      <span class="grade-schedule-summary__hint">包括每天课节数、作息时间和整年级统一不排课时段。</span>
                    </div>
                  </div>

                  <div class="schedule-settings-shell" style="margin-top: 16px;">
                    <div class="schedule-settings-panel">
                      <div class="schedule-settings-panel__title">课节设置</div>
                      <div class="schedule-settings-header schedule-settings-header--cards">
                        <div class="schedule-count-card">
                          <div class="schedule-count-card__label">默认上午课节</div>
                          <el-input-number v-model="grade.schedule_cfg.morning_count" :min="1" :max="6" size="default"
                            style="width: 100%;" @change="handleGradePeriodCountChange(grade)" />
                        </div>
                        <div class="schedule-count-card">
                          <div class="schedule-count-card__label">默认下午课节</div>
                          <el-input-number v-model="grade.schedule_cfg.afternoon_count" :min="0" :max="6" size="default"
                            style="width: 100%;" @change="handleGradePeriodCountChange(grade)" />
                        </div>
                        <div class="schedule-count-card">
                          <div class="schedule-count-card__label">默认晚上课节</div>
                          <el-input-number v-model="grade.schedule_cfg.evening_count" :min="0" :max="4" size="default"
                            style="width: 100%;" @change="handleGradePeriodCountChange(grade)" />
                        </div>
                        <div class="schedule-count-card schedule-count-card--action">
                          <div class="schedule-count-card__label">快速应用</div>
                          <el-button type="primary" plain class="schedule-apply-btn"
                            @click="applyDefaultCountsToAllDaysForGrade(grade)">应用到所有上课日</el-button>
                        </div>
                      </div>
                      <div class="schedule-settings-checkboxes">
                        <el-checkbox v-model="grade.schedule_cfg.has_early_study"
                          @change="handleGradePeriodCountChange(grade)">开启早自习</el-checkbox>
                        <el-checkbox v-model="grade.schedule_cfg.has_noon_study"
                          @change="handleGradePeriodCountChange(grade)">开启午自习</el-checkbox>
                        <el-button size="small" type="success" plain :loading="scheduleAutoSaving[grade.name || gIdx]"
                          @click="saveGradeScheduleSettings(grade, true)">
                          保存这部分
                        </el-button>
                      </div>
                    </div>

                    <div class="schedule-settings-panel">
                      <div class="schedule-settings-panel__title">每天课节数微调</div>
                      <div class="day-count-grid">
                        <div class="day-count-grid__header">日期</div>
                        <div class="day-count-grid__header">上午</div>
                        <div class="day-count-grid__header">下午</div>
                        <div class="day-count-grid__header">晚上</div>
                        <template v-for="d in grade.schedule_cfg.days_per_week"
                          :key="`${grade.name || gIdx}-day-count-${d}`">
                          <div class="day-count-grid__day">{{ getDayChineseName(d) }}</div>
                          <el-input-number v-model="grade.schedule_cfg.day_period_counts[String(d)].morning" :min="1"
                            :max="6" size="small" @change="handleGradePeriodCountChange(grade)" />
                          <el-input-number v-model="grade.schedule_cfg.day_period_counts[String(d)].afternoon" :min="0"
                            :max="6" size="small" @change="handleGradePeriodCountChange(grade)" />
                          <el-input-number v-model="grade.schedule_cfg.day_period_counts[String(d)].evening" :min="0"
                            :max="4" size="small" @change="handleGradePeriodCountChange(grade)" />
                        </template>
                      </div>
                    </div>
                  </div>

                  <div style="font-size: 13px; color: #64748b; line-height: 1.6; margin-top: 14px;">
                    <span style="font-weight: 700; color: #1e293b;">操作说明：</span>
                    直接在下面设置这个年级的作息时间。点课表格子可以标记“免任课”，可按整个年级设置，也可以切换成某个班单独设置。
                  </div>

                  <div class="grade-schedule-scope-bar">
                    <el-radio-group v-model="gradeScheduleScopes[gIdx]" size="small">
                      <el-radio-button label="grade">整个年级</el-radio-button>
                      <el-radio-button label="class">某个班级</el-radio-button>
                    </el-radio-group>
                    <el-select v-if="gradeScheduleScopes[gIdx] === 'class'" v-model="gradeScheduleClassTargets[gIdx]"
                      placeholder="选择班级" size="small" style="width: 160px;">
                      <el-option v-for="cls in grade.classes"
                        :key="`${grade.name}-${typeof cls === 'string' ? cls : cls.name}`"
                        :label="typeof cls === 'string' ? cls : cls.name"
                        :value="typeof cls === 'string' ? cls : cls.name" />
                    </el-select>
                    <el-select v-model="gradeFreePresetTexts[gIdx]" placeholder="选择免任课说明" size="small"
                      style="width: 170px;" @change="handleFreePresetChange(gIdx)">
                      <el-option v-for="item in freePeriodPresetOptions" :key="item" :label="item" :value="item" />
                      <el-option label="自定义" value="__custom__" />
                    </el-select>
                    <el-input v-model="gradeFreeCustomTexts[gIdx]" size="small" style="width: 180px;"
                      placeholder="输入免任课说明" />
                    <el-tag size="small" type="warning" v-if="scheduleAutoSaving[grade.name || gIdx]">正在保存</el-tag>
                    <el-tag size="small" type="success" v-else>点击格子后自动保存</el-tag>
                  </div>

                  <div class="excel-table-wrapper" style="margin-top: 0;">
                    <table class="excel-table">
                      <thead>
                        <tr>
                          <th style="width: 120px;" class="sticky-col">课节</th>
                          <th style="width: 150px;">作息时间</th>
                          <th v-for="d in grade.schedule_cfg.days_per_week" :key="`${grade.name || gIdx}-day-${d}`">
                            {{ getDayChineseName(d) }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-for="row in getScheduleRowsWithPublicPeriods(grade)"
                          :key="`${grade.name || gIdx}-${row.key}`">
                          <!-- Public Period Row -->
                          <tr v-if="row.is_public" class="public-period-row" style="background-color: #f8fafc;">
                            <td class="sticky-col" style="font-weight: 700; color: #4f46e5; background-color: #f8fafc;">
                              <el-icon style="margin-right: 4px; vertical-align: middle;">
                                <Notification />
                              </el-icon>
                              <span style="vertical-align: middle;">{{ row.name }}</span>
                            </td>
                            <td>
                              <div style="display: flex; align-items: center; gap: 4px; justify-content: center;">
                                <el-input v-model="row.start" placeholder="开始" size="small" style="width: 65px;"
                                  @change="handlePublicPeriodTimeChange(row)" />
                                <span style="color: #94a3b8;">-</span>
                                <el-input v-model="row.end" placeholder="结束" size="small" style="width: 65px;"
                                  @change="handlePublicPeriodTimeChange(row)" />
                              </div>
                            </td>
                            <td :colspan="grade.schedule_cfg.days_per_week"
                              style="text-align: center; color: #64748b; font-size: 12px; font-weight: 500; background: repeating-linear-gradient(45deg, #f8fafc, #f8fafc 10px, #f1f5f9 10px, #f1f5f9 20px);">
                              <span>[ {{ row.name }} ] - {{ getPublicPeriodTypeLabel(row.type) }}</span>
                            </td>
                          </tr>
                          <!-- Academic Period Row -->
                          <tr v-else>
                            <td class="sticky-col" style="font-weight: 700;">
                              <span :style="{ color: row.is_academic ? '#1e293b' : '#64748b' }">{{ row.name }}</span>
                            </td>
                            <td>
                              <div style="display: flex; align-items: center; gap: 4px; justify-content: center;">
                                <el-input v-model="grade.schedule_cfg.period_times[row.key].start" placeholder="开始"
                                  size="small" style="width: 65px;" @change="saveGradeScheduleSettings(grade)" />
                                <span style="color: #94a3b8;">-</span>
                                <el-input v-model="grade.schedule_cfg.period_times[row.key].end" placeholder="结束"
                                  size="small" style="width: 65px;" @change="saveGradeScheduleSettings(grade)" />
                              </div>
                            </td>
                            <td v-for="d in grade.schedule_cfg.days_per_week"
                              :key="`${grade.name || gIdx}-${row.key}-${d}`" :class="{
                                'forbidden-slot-cell': isGradeCellForbidden(grade, d, row),
                                'self-study-cell': !row.is_academic,
                                'inactive-slot-cell': !isRowActiveForDayWithGrade(grade, row, d),
                                'diff-forbidden-slot-cell': isGradeScopeDifferent(grade, gIdx, d, row),
                                'class-forbidden-slot-cell': (gradeScheduleScopes[gIdx] === 'class' && isClassCellForbidden(grade, d, row)) || (gradeScheduleScopes[gIdx] === 'grade' && isGradeScopeUniformClassForbidden(grade, gIdx, d, row))
                              }" :style="{
                              cursor: row.is_academic && isRowActiveForDayWithGrade(grade, row, d) ? 'pointer' : 'default',
                              userSelect: 'none'
                            }" @click="toggleGradeCellForbidden(grade, gIdx, d, row)"
                              @contextmenu.prevent="openFreeLabelEditor(grade, gIdx, d, row)">
                              <template v-if="!row.is_academic">
                                <span style="color: #94a3b8; font-size: 12px; font-weight: 500;">自习 / 非排课节次</span>
                              </template>
                              <template v-else-if="!isRowActiveForDayWithGrade(grade, row, d)">
                                <span style="color: #94a3b8; font-size: 12px; font-weight: 500;">本日无此节</span>
                              </template>
                              <template v-else-if="isGradeCellForbidden(grade, d, row)">
                                <el-tooltip :content="getGradeFreeLabel(grade, d, row) || '全年级免任课'" placement="top"
                                  :show-after="200">
                                  <div class="free-slot-content free-slot-content--grade">
                                    <el-icon style="font-size: 16px;">
                                      <Close />
                                    </el-icon>
                                    <span class="free-slot-badge">{{ getShortFreeLabel(getGradeFreeLabel(grade, d, row),
                                      '全年级免任课') }}</span>
                                  </div>
                                </el-tooltip>
                              </template>
                              <!-- Non-uniform across classes in Grade View -->
                              <template
                                v-else-if="gradeScheduleScopes[gIdx] === 'grade' && isGradeScopeDifferent(grade, gIdx, d, row)">
                                <el-tooltip :content="getClassesDifferentTooltip(grade, d, row)" placement="top"
                                  :show-after="200">
                                  <div class="free-slot-content free-slot-content--diff">
                                    <el-icon style="font-size: 16px;">
                                      <Warning />
                                    </el-icon>
                                    <span class="free-slot-badge">各班不同</span>
                                  </div>
                                </el-tooltip>
                              </template>
                              <!-- Uniform Class forbidden in Grade View -->
                              <template
                                v-else-if="gradeScheduleScopes[gIdx] === 'grade' && isGradeScopeUniformClassForbidden(grade, gIdx, d, row)">
                                <el-tooltip :content="getGradeScopeUniformLabel(grade, d, row) || '各班免任课'"
                                  placement="top" :show-after="200">
                                  <div class="free-slot-content free-slot-content--class">
                                    <el-icon style="font-size: 16px;">
                                      <Close />
                                    </el-icon>
                                    <span class="free-slot-badge">{{ getShortFreeLabel(getGradeScopeUniformLabel(grade,
                                      d, row), '各班免任课') }}</span>
                                  </div>
                                </el-tooltip>
                              </template>
                              <!-- Class View specific cell forbidden -->
                              <template
                                v-else-if="gradeScheduleScopes[gIdx] === 'class' && isClassCellForbidden(grade, d, row)">
                                <el-tooltip
                                  :content="getClassFreeLabel(grade, gIdx, d, row) || ((gradeScheduleClassTargets[gIdx] || '本班') + '免任课')"
                                  placement="top" :show-after="200">
                                  <div class="free-slot-content free-slot-content--class">
                                    <el-icon style="font-size: 16px;">
                                      <Close />
                                    </el-icon>
                                    <span class="free-slot-badge">{{ getShortFreeLabel(getClassFreeLabel(grade, gIdx, d,
                                      row), (gradeScheduleClassTargets[gIdx] || '本班') + '免任课') }}</span>
                                  </div>
                                </el-tooltip>
                              </template>
                              <template v-else>
                                <div
                                  style="display: flex; flex-direction: column; align-items: center; gap: 4px; color: #10b981;">
                                  <el-icon style="font-size: 16px;">
                                    <CircleCheck />
                                  </el-icon>
                                  <span style="font-size: 11px; font-weight: 500; color: #64748b;">正常上课</span>
                                </div>
                              </template>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div class="step-navigation-buttons"
              style="margin-top: 30px; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #f1f5f9; padding-top: 20px;">
              <el-button @click="prevStep">上一步：年级班级</el-button>
              <div style="display: flex; gap: 10px;">
                <el-button type="success" plain :loading="savingProgress" @click="saveWizardProgress">保存课时修改</el-button>
                <el-button type="primary" class="action-btn-gradient-primary" @click="nextStep">下一步：每周课时</el-button>
              </div>
            </div>
          </div>

          <!-- Step 3: Subject Weekly Periods Grid -->
          <div v-if="wizardStep === 4" class="wizard-step-content animate-fade-in">
            <div
              style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 10px;">
              <div>
                <h3
                  style="margin: 0 0 4px 0; color: #1e293b; display: flex; align-items: center; gap: 8px; font-size: 16px; font-weight: 700;">
                  <el-icon color="#10b981">
                    <Grid />
                  </el-icon> 年级统一周课时模板
                </h3>
                <p style="font-size: 13px; color: #64748b; margin: 0; line-height: 1.5;">
                  同一年级各班的学科周课时保持一致，填写后会自动同步到该年级下所有班级。
                </p>
              </div>
              <div v-if="flatClasses.length > 0" style="display: flex; gap: 10px;">
                <el-dropdown split-button type="primary" size="small" @click="downloadPeriodsTemplate">
                  <el-icon style="margin-right: 4px;">
                    <Download />
                  </el-icon> 下载Excel课时模板
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item>
                        <el-upload action="#" :auto-upload="false" :show-file-list="false"
                          :on-change="handlePeriodsExcelUpload" accept=".xlsx, .xls"
                          style="display: inline-block; width: 100%;">
                          <span style="display: flex; align-items: center;"><el-icon>
                              <Upload />
                            </el-icon> 导入Excel课时表</span>
                        </el-upload>
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </div>

            <div v-if="flatClasses.length === 0"
              style="text-align: center; padding: 50px 20px; color: #94a3b8; background: #f8fafc; border-radius: 12px; border: 1.5px dashed #cbd5e1;">
              <el-icon style="font-size: 40px; color: #cbd5e1; margin-bottom: 12px;">
                <Grid />
              </el-icon>
              <div style="font-size: 14px; font-weight: 500; margin-bottom: 15px;">还没有年级和班级，请先回到上一步填写。</div>
              <el-button type="primary" @click="goToStep(1)">去设置年级班级</el-button>
            </div>

            <div v-else class="wizard-grade-grids">
              <div v-for="grade in groupedGradeTables" :key="grade.gradeName" class="grade-grid-card">
                <div class="grade-grid-header"
                  style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px;">
                  <div>
                    <div class="grade-grid-title">{{ grade.gradeName }}</div>
                    <div class="grade-grid-subtitle">
                      {{ gradePeriodEditMode[grade.gradeName] === 'individual' ? '按班级独立设置各学科周课时。' :
                      '按年级统一设置周课时，自动应用到所有班级。' }}
                    </div>
                  </div>
                  <div style="display: flex; align-items: center; gap: 12px;">
                    <el-radio-group v-model="gradePeriodEditMode[grade.gradeName]" size="small">
                      <el-radio-button label="unified">年级统一</el-radio-button>
                      <el-radio-button label="individual">按班级设置</el-radio-button>
                    </el-radio-group>
                    <el-tag size="small" type="info">{{ grade.classEntries.length }} 个班级 / {{ grade.subjects.length }}
                      个学科</el-tag>
                  </div>
                </div>

                <!-- Unified periods table -->
                <div v-if="gradePeriodEditMode[grade.gradeName] !== 'individual'" class="excel-table-wrapper">
                  <table class="excel-table">
                    <thead>
                      <tr>
                        <th class="sticky-col">科目</th>
                        <th>统一周课时</th>
                        <th>应用范围</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="sub in grade.subjects" :key="sub.id">
                        <td class="sticky-col">
                          <div style="display: flex; align-items: center; gap: 8px;">
                            <span v-if="sub.color"
                              :style="{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: sub.color, border: '1px solid #cbd5e1' }"></span>
                            <span v-else
                              style="display: inline-block; width: 10px; height: 10px; borderRadius: '50%', backgroundColor: '#cbd5e1', border: '1px solid #cbd5e1'"></span>
                            <span>{{ sub.name }}</span>
                            <span v-if="sub.short_name"
                              style="font-size: 11px; color: #94a3b8; font-family: monospace;">({{
                              sub.short_name }})</span>
                          </div>
                        </td>
                        <td>
                          <input type="number" :value="getGradePeriodValue(sub.id, grade.classEntries)"
                            @input="handlePeriodChange(sub.id, grade.classEntries, $event.target.value)"
                            class="excel-grid-input" min="0" step="0.5" />
                        </td>
                        <td class="grade-period-apply-cell">
                          {{grade.classEntries.map(item => item.class_name).join('、')}}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Individual periods table -->
                <div v-else class="excel-table-wrapper">
                  <table class="excel-table">
                    <thead>
                      <tr>
                        <th class="sticky-col">科目</th>
                        <th v-for="cls in grade.classEntries" :key="cls.key">{{ cls.class_name }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="sub in grade.subjects" :key="sub.id">
                        <td class="sticky-col">
                          <div style="display: flex; align-items: center; gap: 8px;">
                            <span v-if="sub.color"
                              :style="{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: sub.color, border: '1px solid #cbd5e1' }"></span>
                            <span v-else
                              style="display: inline-block; width: 10px; height: 10px; borderRadius: '50%', backgroundColor: '#cbd5e1', border: '1px solid #cbd5e1'"></span>
                            <span>{{ sub.name }}</span>
                            <span v-if="sub.short_name"
                              style="font-size: 11px; color: #94a3b8; font-family: monospace;">({{
                              sub.short_name }})</span>
                          </div>
                        </td>
                        <td v-for="cls in grade.classEntries" :key="cls.key">
                          <input type="number" :value="isSubjectDisabledForClass(sub, cls) ? 0 : (periodsGrid?.[sub.id]?.[cls.key] ?? 0)"
                            @input="handleClassPeriodChange(sub.id, cls.key, $event.target.value)"
                            :disabled="isSubjectDisabledForClass(sub, cls)"
                            :title="getSubjectDisabledTooltip(sub, cls)"
                            :style="{
                              backgroundColor: isSubjectDisabledForClass(sub, cls) ? '#f8fafc' : '',
                              cursor: isSubjectDisabledForClass(sub, cls) ? 'not-allowed' : '',
                              color: isSubjectDisabledForClass(sub, cls) ? '#94a3b8' : '',
                              border: isSubjectDisabledForClass(sub, cls) ? '1px dashed #cbd5e1' : ''
                            }"
                            class="excel-grid-input" min="0" step="0.5" />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Combined/Walk-class periods section -->
                <div v-if="getGradeCombinedTasks(grade.gradeName).length > 0" 
                  style="margin-top: 20px; border-top: 1px dashed #cbd5e1; padding-top: 15px;">
                  <div style="font-size: 13px; font-weight: bold; color: #1e293b; margin-bottom: 10px; display: flex; align-items: center; gap: 6px;">
                    <el-icon color="#f59e0b"><Grid /></el-icon>
                    走班与合班课时设置
                  </div>
                  <el-table :data="getGradeCombinedTasks(grade.gradeName)" border size="small" style="width: 100%">
                    <el-table-column label="班级类型" width="120">
                      <template #default="{ row }">
                        <el-tag v-if="row.combined_group_id && row.combined_group_id.startsWith('WALK_COMBINED_')" type="warning" size="small">走班拼盘</el-tag>
                        <el-tag v-else-if="row.combined_group_id && row.combined_group_id.startsWith('PE_COMBINED_')" type="primary" size="small">体育合班</el-tag>
                        <el-tag v-else type="success" size="small">通用合班</el-tag>
                      </template>
                    </el-table-column>
                    <el-table-column label="班级名称/涉及行政班" min-width="200">
                      <template #default="{ row }">
                        <span v-if="row.combined_group_id && row.combined_group_id.startsWith('WALK_COMBINED_')" style="font-weight: bold;">
                          {{ row.class_name }} <span style="font-weight: normal; color: #64748b; font-size: 11px;">({{ row.linked_classes?.map(c => c.class_name || c.name).join(',') }})</span>
                        </span>
                        <span v-else-if="row.combined_group_id && row.combined_group_id.startsWith('PE_COMBINED_')">
                          {{ row.subject_name }}{{ row.subject_name.endsWith('合班') ? '' : '合班' }} <span style="color: #64748b; font-size: 11px;">({{ row.linked_classes?.map(c => c.class_name || c.name).join(',') }})</span>
                        </span>
                        <span v-else>
                          {{ row.class_name }} <span style="color: #64748b; font-size: 11px;">({{ row.linked_classes?.map(c => c.class_name || c.name).join(',') || '-' }})</span>
                        </span>
                      </template>
                    </el-table-column>
                    <el-table-column label="科目" width="180">
                      <template #default="{ row }">
                        <span>
                          {{ row.subject_name }}
                        </span>
                      </template>
                    </el-table-column>
                    <el-table-column label="教师" min-width="150">
                      <template #default="{ row }">
                        <span>{{ row.linked_classes?.[0]?.teacher_names || row.teacher_name }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column label="周课时" width="120" align="center">
                      <template #default="{ row }">
                        <el-input-number v-model="row.weekly_periods" :min="0.5" :max="15" :step="0.5" size="small" 
                          @change="handleCombinedTaskPeriodChange(row)" />
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </div>
            </div>

            <!-- Real-time Capacity Validation Card -->
            <div style="margin-top: 20px; margin-bottom: 20px;">
              <el-card shadow="hover" :body-style="{ padding: '15px' }"
                style="border-radius: 8px; border: 1px solid #e2e8f0;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                  <span
                    style="font-weight: 700; font-size: 14px; color: #1e293b; display: flex; align-items: center; gap: 6px;">
                    <el-icon :color="hasCapacityError ? '#ef4444' : '#10b981'">
                      <Warning v-if="hasCapacityError" />
                      <CircleCheck v-else />
                    </el-icon>
                    班级课时与容量校验 (实时)
                  </span>
                  <el-tag :type="hasCapacityError ? 'danger' : 'success'" size="small">
                    {{ hasCapacityError ? '存在超限班级，自动排课将受阻' : '校验通过' }}
                  </el-tag>
                </div>

                <div style="margin-top: 8px; font-size: 12px; color: #64748b; margin-bottom: 12px; border-top: 1px solid #f1f5f9; padding-top: 10px;">
                  各班级容量明细 (总课时 / 可排容量 / 剩余可排容量 / 超出课时)
                </div>

                <div style="max-height: 250px; overflow-y: auto; padding: 5px;">
                  <div v-for="gGroup in groupedCapacityStats" :key="gGroup.grade" style="margin-bottom: 12px;">
                    <div
                      style="font-weight: 600; font-size: 12px; color: #475569; margin-bottom: 6px; border-left: 3px solid #3b82f6; padding-left: 6px;">
                      {{ gGroup.grade }}
                    </div>
                    <el-row :gutter="10">
                      <el-col v-for="stat in gGroup.classes" :key="stat.key" :xs="24" :sm="12" :md="8"
                        style="margin-bottom: 8px;">
                        <div :style="{
                          padding: '8px 12px',
                          borderRadius: '6px',
                          background: stat.hasError ? '#fef2f2' : '#f8fafc',
                          border: stat.hasError ? '1px solid #fca5a5' : '1px solid #e2e8f0',
                          fontSize: '12px'
                        }">
                          <div
                            style="display: flex; justify-content: space-between; font-weight: 600; margin-bottom: 4px;">
                            <span :style="{ color: stat.hasError ? '#dc2626' : '#1e293b' }">{{ stat.label }}</span>
                            <el-tag v-if="stat.hasError" type="danger" size="small" effect="dark">超出 {{ stat.excess
                              }}
                              节</el-tag>
                            <el-tag v-else-if="stat.missing > 0" type="warning" size="small">缺 {{ stat.missing }}
                              节</el-tag>
                            <el-tag v-else type="success" size="small">满额</el-tag>
                          </div>
                          <div style="color: #64748b; font-size: 11px;">
                            <div>总课时: <strong style="color: #334155;">{{ stat.totalPeriods }}</strong> / 可排容量:
                              <strong style="color: #334155;">{{ stat.capacity }}</strong></div>
                            <div>已占用固定课: {{ stat.occupiedFixed }} | 剩余可排: {{ stat.remaining }}</div>
                          </div>
                        </div>
                      </el-col>
                    </el-row>
                  </div>
                </div>
              </el-card>
            </div>

            <div class="step-navigation-buttons"
              style="margin-top: 30px; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #f1f5f9; padding-top: 20px;">
              <el-button @click="prevStep">上一步：设置上课时间</el-button>
              <div style="display: flex; gap: 10px;">
                <el-button type="success" plain :loading="savingProgress" @click="saveWizardProgress">保存课时修改</el-button>
                <el-button type="primary" class="action-btn-gradient-primary" @click="nextStep">下一步：任课教师</el-button>
              </div>
            </div>
          </div>

          <!-- Step 4: Teacher Assignment Grid -->
          <div v-if="wizardStep === 5" class="wizard-step-content animate-fade-in">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
              <div>
                <h3
                  style="margin-top: 0; margin-bottom: 8px; color: #1e293b; display: flex; align-items: center; gap: 8px; font-size: 16px; font-weight: 700;">
                  <el-icon color="#f59e0b">
                    <Collection />
                  </el-icon> 任课教师安排
                </h3>
                <p style="font-size: 13px; color: #64748b; margin-bottom: 0; margin-top: 0; line-height: 1.5;">
                  给每个班每门课选任课教师。可以直接输入老师姓名，输入拼音首字母也能快速找到。
                  下载模板时第一列为班级、第一行为学科，请在对应单元格填写教师。
                </p>
              </div>
              <div style="display: flex; gap: 15px; align-items: center;">
                <el-radio-group v-model="teacherAssignViewMode" size="small">
                  <el-radio-button label="grid">班级视角</el-radio-button>
                  <el-radio-button label="teacher">教师视角</el-radio-button>
                </el-radio-group>
                <el-dropdown split-button type="primary" size="small" @click="downloadTeacherGridTemplate">
                  <el-icon style="margin-right: 4px;">
                    <Download />
                  </el-icon> 下载Excel模板
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item>
                        <el-upload action="#" :auto-upload="false" :show-file-list="false"
                          :on-change="handleTeacherGridExcelUpload" accept=".xlsx, .xls"
                          style="display: inline-block; width: 100%;">
                          <span style="display: flex; align-items: center;"><el-icon>
                              <Upload />
                            </el-icon> 导入Excel任课表</span>
                        </el-upload>
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </div>

            <div v-if="flatClasses.length === 0"
              style="text-align: center; padding: 50px 20px; color: #94a3b8; background: #f8fafc; border-radius: 12px; border: 1.5px dashed #cbd5e1;">
              <el-icon style="font-size: 40px; color: #cbd5e1; margin-bottom: 12px;">
                <Grid />
              </el-icon>
              <div style="font-size: 14px; font-weight: 500; margin-bottom: 15px;">还没有年级和班级，请先回到上一步填写。</div>
              <el-button type="primary" @click="goToStep(1)">去设置年级班级</el-button>
            </div>

            <div v-else-if="teacherAssignViewMode === 'grid'" class="wizard-grade-grids">
              <div v-for="grade in groupedGradeTables" :key="grade.gradeName" class="grade-grid-card">
                <div class="grade-grid-header">
                  <div>
                    <div class="grade-grid-title">{{ grade.gradeName }}</div>
                    <div class="grade-grid-subtitle">给这个年级每个班的每门课安排老师。</div>
                  </div>
                  <el-tag size="small" type="success">{{ grade.classEntries.length }} 个班级 / {{ grade.subjects.length }}
                    个学科</el-tag>
                </div>
                <div class="excel-table-wrapper">
                  <table class="excel-table">
                    <thead>
                      <tr>
                        <th class="sticky-col">科目</th>
                        <th v-for="cls in grade.classEntries" :key="cls.key">{{ cls.class_name }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="sub in grade.subjects" :key="sub.id">
                        <td class="sticky-col">{{ sub.name }}</td>
                        <td v-for="cls in grade.classEntries" :key="cls.key">
                          <div v-if="isClassPeriodZero(sub.id, cls.key)"
                            style="text-align: center; color: #94a3b8; font-size: 13px; padding: 6px 0;">
                            <span style="background: #f1f5f9; padding: 2px 8px; border-radius: 4px;">无课</span>
                          </div>
                          <div v-else>
                            <el-popover placement="bottom" width="300" trigger="click"
                              @show="onTeacherCellClick(sub.id, cls.key)">
                              <template #reference>
                                <div class="teacher-grid-cell"
                                  :class="{ 'is-empty': !teachersGrid?.[sub.id]?.[cls.key] || teachersGrid?.[sub.id]?.[cls.key]?.length === 0 }"
                                  style="min-height: 32px; padding: 4px; border: 1px solid #e2e8f0; border-radius: 4px; cursor: pointer; display: flex; flex-wrap: wrap; gap: 4px; align-items: center;">
                                  <div
                                    v-if="!teachersGrid?.[sub.id]?.[cls.key] || teachersGrid?.[sub.id]?.[cls.key]?.length === 0"
                                    style="color: #94a3b8; font-size: 13px; padding-left: 8px;">点击设置</div>
                                  <el-tag v-else v-for="(t, idx) in (teachersGrid?.[sub.id]?.[cls.key] || [])"
                                    :key="idx" size="small" closable
                                    @close.stop="removeTeacherFromCell(sub.id, cls.key, idx)">
                                    {{ getTeacherName(t.teacher_id) }}({{ t.periods }}节)
                                  </el-tag>
                                </div>
                              </template>
                              <div class="teacher-cell-editor">
                                <div
                                  style="font-size: 13px; font-weight: bold; margin-bottom: 8px; display: flex; justify-content: space-between;">
                                  <span>分配教师 (共 {{ periodsGrid?.[sub.id]?.[cls.key] || 0 }} 节)</span>
                                  <span
                                    :style="{ color: getRemainingPeriods(sub.id, cls.key) < 0 ? '#ef4444' : '#10b981' }">
                                    剩余: {{ getRemainingPeriods(sub.id, cls.key) }}节
                                  </span>
                                </div>
                                <div style="margin-bottom: 12px;">
                                  <el-select v-model="tempTeacherId" filterable placeholder="添加教师..."
                                    style="width: 100%;" size="small" @change="addTeacherToCell(sub.id, cls.key)">
                                    <el-option v-for="teacher in teacherList" :key="teacher.id" :label="teacher.name"
                                      :value="teacher.id" />
                                  </el-select>
                                </div>
                                <div v-for="(t, idx) in (teachersGrid?.[sub.id]?.[cls.key] || [])" :key="idx"
                                  style="display: flex; gap: 8px; margin-bottom: 8px; align-items: center;">
                                  <span
                                    style="flex: 1; font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{
                                    getTeacherName(t.teacher_id) }}</span>
                                  <el-input-number v-model="t.periods" :min="0"
                                    :max="periodsGrid?.[sub.id]?.[cls.key] || 99" size="small" style="width: 100px;" />
                                  <el-button type="danger" link :icon="Delete"
                                    @click="removeTeacherFromCell(sub.id, cls.key, idx)" />
                                </div>
                                <div
                                  style="display: flex; gap: 10px; margin-top: 15px; border-top: 1px solid #f1f5f9; padding-top: 10px;">
                                  <el-button size="small" style="flex: 1;"
                                    @click="copyTeacherCell(sub.id, cls.key)">复制设置</el-button>
                                  <el-button size="small" type="primary" style="flex: 1;"
                                    @click="pasteTeacherCell(sub.id, cls.key)"
                                    :disabled="!copiedTeacherCell">粘贴设置</el-button>
                                </div>
                              </div>
                            </el-popover>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Combined/Walk-class teachers section -->
                <div v-if="getGradeCombinedTasks(grade.gradeName).length > 0" 
                  style="margin-top: 20px; border-top: 1px dashed #cbd5e1; padding-top: 15px;">
                  <div style="font-size: 13px; font-weight: bold; color: #1e293b; margin-bottom: 10px; display: flex; align-items: center; gap: 6px;">
                    <el-icon color="#f59e0b"><Grid /></el-icon>
                    走班与合班教师安排
                  </div>
                  <el-table :data="getGradeCombinedTasks(grade.gradeName)" border size="small" style="width: 100%">
                    <el-table-column label="班级类型" width="120">
                      <template #default="{ row }">
                        <el-tag v-if="row.combined_group_id && row.combined_group_id.startsWith('WALK_COMBINED_')" type="warning" size="small">走班拼盘</el-tag>
                        <el-tag v-else-if="row.combined_group_id && row.combined_group_id.startsWith('PE_COMBINED_')" type="primary" size="small">体育合班</el-tag>
                        <el-tag v-else type="success" size="small">通用合班</el-tag>
                      </template>
                    </el-table-column>
                    <el-table-column label="班级名称/涉及行政班" min-width="200">
                      <template #default="{ row }">
                        <span v-if="row.combined_group_id && row.combined_group_id.startsWith('WALK_COMBINED_')" style="font-weight: bold;">
                          {{ row.class_name }} <span style="font-weight: normal; color: #64748b; font-size: 11px;">({{ row.linked_classes?.map(c => c.class_name || c.name).join(',') }})</span>
                        </span>
                        <span v-else-if="row.combined_group_id && row.combined_group_id.startsWith('PE_COMBINED_')">
                          {{ row.subject_name }}{{ row.subject_name.endsWith('合班') ? '' : '合班' }} <span style="color: #64748b; font-size: 11px;">({{ row.linked_classes?.map(c => c.class_name || c.name).join(',') }})</span>
                        </span>
                        <span v-else>
                          {{ row.class_name }} <span style="color: #64748b; font-size: 11px;">({{ row.linked_classes?.map(c => c.class_name || c.name).join(',') || '-' }})</span>
                        </span>
                      </template>
                    </el-table-column>
                    <el-table-column label="科目" width="180">
                      <template #default="{ row }">
                        <span>
                          {{ row.subject_name }}
                        </span>
                      </template>
                    </el-table-column>
                    <el-table-column label="任课教师" min-width="200">
                      <template #default="{ row }">
                        <span style="font-weight: 600; color: #475569;">{{ row.linked_classes?.[0]?.teacher_names || row.teacher_name }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column label="周课时" width="90" align="center">
                      <template #default="{ row }">
                        <span>{{ row.weekly_periods }} 节</span>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </div>
            </div>

            <!-- Teacher View Layout -->
            <div v-else-if="teacherAssignViewMode === 'teacher'" class="teacher-view-container"
              style="display: flex; gap: 20px; height: 600px; margin-top: 10px;">
              <!-- Left: Teachers List -->
              <div
                style="flex: 2; border: 1px solid #e2e8f0; border-radius: 8px; display: flex; flex-direction: column; background: #fff;">
                <div
                  style="padding: 10px 15px; border-bottom: 1px solid #e2e8f0; background: #f8fafc; font-weight: 600;">
                  教师列表
                </div>
                <div style="flex: 1; overflow-y: auto; padding: 15px;">
                  <div v-for="teacher in teacherList" :key="teacher.id" class="teacher-drop-zone" @dragover.prevent
                    @drop="onDropToTeacher($event, teacher.id)"
                    style="border: 1px solid #cbd5e1; border-radius: 6px; padding: 10px; margin-bottom: 15px; background: #f8fafc;">
                    <div
                      style="font-weight: bold; margin-bottom: 10px; font-size: 14px; display: flex; justify-content: space-between;">
                      <span>{{ teacher.name }} <span
                          style="color: #94a3b8; font-size: 12px; font-weight: normal; margin-left: 4px;">({{
                          getTeacherShortName(teacher.name) }})</span></span>
                      <span style="font-size: 12px; font-weight: normal; color: #64748b;">已分配: {{
                        getTeacherTotalPeriods(teacher.id) }} 节</span>
                    </div>
                    <div style="display: flex; flex-wrap: wrap; gap: 8px; min-height: 40px;">
                      <el-tag v-for="(block, idx) in getTeacherAssignedBlocks(teacher.id)" :key="idx" closable
                        @close="removeTeacherFromCell(block.subId, block.classKey, block.assignIdx)"
                        style="cursor: pointer;" @click="editAssignedBlock(block)">
                        {{ block.className }} - {{ block.subjectName }} ({{ block.periods }}节)
                      </el-tag>
                      <div v-if="getTeacherAssignedBlocks(teacher.id).length === 0"
                        style="color: #94a3b8; font-size: 12px; line-height: 24px;">拖拽课程到这里分配</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right: Unassigned Blocks -->
              <div
                style="flex: 1; border: 1px solid #e2e8f0; border-radius: 8px; display: flex; flex-direction: column; background: #fff;">
                <div
                  style="padding: 10px 15px; border-bottom: 1px solid #e2e8f0; background: #f8fafc; font-weight: 600; display: flex; justify-content: space-between; align-items: center;">
                  <span>未分配课程池</span>
                  <el-input v-model="unassignedFilter" size="small" placeholder="搜索..." style="width: 120px;" />
                </div>
                <div style="flex: 1; overflow-y: auto; padding: 15px;">
                  <div v-for="(block, idx) in filteredUnassignedBlocks" :key="idx" draggable="true"
                    @dragstart="onDragStart($event, block)"
                    style="padding: 10px; border: 1px dashed #94a3b8; border-radius: 4px; margin-bottom: 10px; background: #fff; cursor: grab; font-size: 13px;">
                    <div style="font-weight: 600;">{{ block.className }}</div>
                    <div style="display: flex; justify-content: space-between; margin-top: 5px; color: #64748b;">
                      <span>{{ block.subjectName }}</span>
                      <span style="color: #ef4444;">待分: {{ block.remainingPeriods }}节</span>
                    </div>
                  </div>
                  <div v-if="filteredUnassignedBlocks.length === 0"
                    style="text-align: center; color: #94a3b8; padding: 20px 0; font-size: 13px;">
                    全部课程已分配完毕
                  </div>
                </div>
              </div>
            </div>

            <div class="step-navigation-buttons"
              style="margin-top: 30px; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #f1f5f9; padding-top: 20px;">
              <el-button @click="prevStep">上一步：每周课时</el-button>
              <el-button type="success" class="action-btn-gradient-success" :loading="savingWizard"
                @click="saveWizardData">
                <el-icon>
                  <CircleCheck />
                </el-icon> 保存任课安排
              </el-button>
            </div>
          </div>
        </div>

        <!-- Task List View (Existing List View) -->
        <div v-else>
          <div class="tab-action-bar">
            <div class="bar-left">
              <el-input v-model="searchTaskQuery" placeholder="搜索年级、班级、学科、教师" clearable style="width: 240px" />
            </div>
            <div class="bar-right">
              <el-button type="warning" plain @click="downloadTemplate">
                <el-icon>
                  <Download />
                </el-icon> 下载模板
              </el-button>
              <el-button type="info" plain @click="openSubjectManager">
                <el-icon>
                  <Collection />
                </el-icon> 学科设置
              </el-button>
              <el-button type="success" plain @click="openTeacherManager">
                <el-icon>
                  <Plus />
                </el-icon> 教师设置
              </el-button>
              <el-button type="primary" @click="importDialogVisible = true">
                <el-icon>
                  <Upload />
                </el-icon> Excel 导入
              </el-button>
              <el-button type="success" plain @click="pasteImportDialogVisible = true">
                <el-icon>
                  <Notebook />
                </el-icon> 粘贴导入
              </el-button>
              <el-button type="success" @click="openGridEditDialog">
                <el-icon>
                  <Grid />
                </el-icon> 表格填写
              </el-button>
              <el-button-group style="margin-left: 5px; margin-right: 5px;">
                <el-button type="primary" :icon="Collection" @click="openSportsWizard">
                  体育合班向导
                </el-button>
                <el-button type="warning" :icon="Grid" @click="openWalkClassWizard">
                  走班课向导
                </el-button>
              </el-button-group>
              <el-button type="success" plain @click="openTaskDialog(null)">
                <el-icon>
                  <Plus />
                </el-icon> 添加一条
              </el-button>
              <el-button type="danger" plain @click="clearAllTasks">
                <el-icon>
                  <Delete />
                </el-icon> 清空任课安排
              </el-button>
            </div>
          </div>

          <el-table :data="filteredTasks" border stripe style="width: 100%; margin-top: 15px;">
            <el-table-column prop="grade" label="年级" width="100" />
            <el-table-column label="班级" min-width="180">
              <template #default="{ row }">
                <div v-if="row.is_combined && row.combined_group_id && row.combined_group_id.startsWith('WALK_COMBINED_')">
                  <div style="font-weight: bold; color: #b45309; display: flex; align-items: center; gap: 4px;">
                    <el-icon color="#b45309"><Grid /></el-icon>
                    {{ row.class_name }} (走班)
                  </div>
                  <div style="font-size: 11px; color: #64748b; margin-top: 2px;">
                    关联班级: {{ row.linked_classes?.map(c => c.class_name || c.name).join(',') }}
                  </div>
                </div>
                <div v-else-if="row.is_combined && row.combined_group_id && row.combined_group_id.startsWith('PE_COMBINED_')">
                  <div style="font-weight: bold; color: #4f46e5; display: flex; align-items: center; gap: 4px;">
                    <el-icon color="#4f46e5"><Collection /></el-icon>
                    体育合班
                  </div>
                  <div style="font-size: 11px; color: #64748b; margin-top: 2px;">
                    关联班级: {{ row.linked_classes?.map(c => c.class_name || c.name).join(',') }}
                  </div>
                </div>
                <div v-else-if="row.is_combined">
                  <div style="font-weight: bold; color: #0d9488;">
                    合班 ({{ row.combined_group_id }})
                  </div>
                  <div style="font-size: 11px; color: #64748b; margin-top: 2px;">
                    关联班级: {{ row.linked_classes?.map(c => c.class_name || c.name).join(',') || row.class_name }}
                  </div>
                </div>
                <div v-else>
                  {{ row.class_name }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="subject_name" label="科目" min-width="120">
              <template #default="{ row }">
                <span v-if="row.is_combined && row.combined_group_id && row.combined_group_id.startsWith('WALK_COMBINED_')" style="font-weight: 600; color: #b45309;">
                  {{ row.subject_name }}
                </span>
                <span v-else-if="row.is_combined && row.combined_group_id && row.combined_group_id.startsWith('PE_COMBINED_')" style="font-weight: 600; color: #4f46e5;">
                  {{ row.subject_name }}
                </span>
                <span v-else>{{ row.subject_name }}</span>
              </template>
            </el-table-column>
            <el-table-column label="任课教师" min-width="160">
              <template #default="{ row }">
                <div v-if="row.is_combined && row.linked_classes && row.linked_classes.length > 0">
                  <span style="font-weight: 500;">{{ row.linked_classes[0].teacher_names || row.teacher_name }}</span>
                </div>
                <div v-else>
                  <span>{{ row.teacher_name }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="weekly_periods" label="周课时" width="90" align="center" />
            <el-table-column prop="consecutive_periods" label="连堂课时" width="90" align="center">
              <template #default="{ row }">
                {{ row.consecutive_periods > 1 ? `${row.consecutive_periods}连堂` : '不连堂' }}
              </template>
            </el-table-column>
            <el-table-column prop="room_name" label="固定/场室教室" min-width="130">
              <template #default="{ row }">
                <span>{{ row.room_name || '默认班级教室' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="合班性质" width="150">
              <template #default="{ row }">
                <el-tag v-if="row.is_combined && row.combined_group_id && row.combined_group_id.startsWith('WALK_COMBINED_')" type="warning" size="small">
                  走班拼盘
                </el-tag>
                <el-tag v-else-if="row.is_combined && row.combined_group_id && row.combined_group_id.startsWith('PE_COMBINED_')" type="primary" size="small">
                  体育专项合班
                </el-tag>
                <el-tag v-else-if="row.is_combined" type="success" size="small">
                  通用合班
                </el-tag>
                <span v-else class="muted-text">-</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="150" align="center">
              <template #default="{ row }">
                <el-button-group>
                  <el-button size="small" :icon="Edit" @click="openTaskDialog(row)" />
                  <el-button size="small" type="danger" :icon="Delete" @click="deleteTask(row.id)" />
                </el-button-group>
              </template>
            </el-table-column>
          </el-table>

          <!-- Real-time Capacity and Consecutive Validation Alerts -->
          <div style="margin-top: 20px; margin-bottom: 20px;">
            <el-row :gutter="15">
              <el-col :xs="24" :md="12" style="margin-bottom: 12px;">
                <el-card shadow="hover" :body-style="{ padding: '15px' }"
                  style="border-radius: 8px; border: 1px solid #e2e8f0; height: 100%;">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                    <span
                      style="font-weight: 700; font-size: 14px; color: #1e293b; display: flex; align-items: center; gap: 6px;">
                      <el-icon :color="hasCapacityError ? '#ef4444' : '#10b981'">
                        <Warning v-if="hasCapacityError" />
                        <CircleCheck v-else />
                      </el-icon>
                      班级课时与容量校验 (实时)
                    </span>
                    <el-tag :type="hasCapacityError ? 'danger' : 'success'" size="small">
                      {{ hasCapacityError ? '存在超限' : '校验通过' }}
                    </el-tag>
                  </div>

                  <el-collapse>
                    <el-collapse-item name="capacity-details-step4">
                      <template #title>
                        <span style="font-size: 12px; color: #64748b;">查看班级容量明细</span>
                      </template>
                      <div style="max-height: 200px; overflow-y: auto; font-size: 12px;">
                        <div v-for="stat in classCapacityStats" :key="stat.key"
                          style="padding: 6px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center;">
                          <span>{{ stat.label }} (课时/容量: <strong>{{ stat.totalPeriods }} / {{ stat.capacity
                              }}</strong>)</span>
                          <el-tag v-if="stat.hasError" type="danger" size="small">超额 {{ stat.excess }} 节</el-tag>
                          <el-tag v-else-if="stat.missing > 0" type="warning" size="small">缺 {{ stat.missing }}
                            节</el-tag>
                          <el-tag v-else type="success" size="small">正常</el-tag>
                        </div>
                      </div>
                    </el-collapse-item>
                  </el-collapse>
                </el-card>
              </el-col>

              <el-col :xs="24" :md="12" style="margin-bottom: 12px;">
                <el-card shadow="hover" :body-style="{ padding: '15px' }"
                  style="border-radius: 8px; border: 1px solid #e2e8f0; height: 100%;">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                    <span
                      style="font-weight: 700; font-size: 14px; color: #1e293b; display: flex; align-items: center; gap: 6px;">
                      <el-icon :color="hasConsecutiveError ? '#ef4444' : '#10b981'">
                        <Warning v-if="hasConsecutiveError" />
                        <CircleCheck v-else />
                      </el-icon>
                      高级连堂规则诊断 (实时)
                    </span>
                    <el-tag :type="hasConsecutiveError ? 'danger' : 'success'" size="small">
                      {{ hasConsecutiveError ? '存在冲突' : '校验通过' }}
                    </el-tag>
                  </div>

                  <div style="max-height: 200px; overflow-y: auto; font-size: 12px;">
                    <div v-if="consecutiveValidationAlerts.length === 0"
                      style="color: #94a3b8; text-align: center; padding: 15px;">
                      暂未配置高级连堂规则
                    </div>
                    <div v-else>
                      <div v-for="(alert, aIdx) in consecutiveValidationAlerts" :key="aIdx" :style="{
                        padding: '8px 12px',
                        borderRadius: '4px',
                        marginBottom: '6px',
                        background: alert.type === 'error' ? '#fef2f2' : '#f0fdf4',
                        color: alert.type === 'error' ? '#991b1b' : '#166534',
                        border: alert.type === 'error' ? '1px solid #fca5a5' : '1px solid #bbf7d0'
                      }">
                        {{ alert.message }}
                      </div>
                    </div>
                  </div>
                </el-card>
              </el-col>
            </el-row>
          </div>

          <div class="step-navigation-buttons">
            <el-button v-if="enableSportsCombined || enableWalkClass" @click="activeTab = 'combined'">上一步：走班与体育合班设置</el-button>
            <el-button type="primary" @click="activeTab = 'rules'">下一步：配置其它规则</el-button>
          </div>
        </div>
      </el-tab-pane>

      <!-- Tab 3: Constraints Config -->
      <el-tab-pane name="rules" :label="(enableSportsCombined || enableWalkClass ? '3. ' : '2. ') + '其它规则与要求'" lazy>
        <div class="rules-container">
          <div class="rules-left">

            <div class="card-title-row">
              <h3>补充要求</h3>
              <div>
                <el-button type="success" @click="addPresetConstraints" plain>
                  <el-icon><MagicStick /></el-icon> 智能添加常用预设约束
                </el-button>
                <el-button type="warning" @click="openBulkForbiddenDialog" plain>
                  <el-icon><Calendar /></el-icon> 批量设置禁排时段
                </el-button>
              </div>
            </div>

            <!-- 全局偏好设置 -->
            <div style="margin: 15px 0; padding: 15px 20px; background-color: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div style="margin-bottom: 12px; font-weight: 600; color: #334155; display: flex; align-items: center; gap: 8px;">
                <el-icon><Setting /></el-icon> 全局排课偏好 (自动保存)：
              </div>
              <el-form :model="globalParams" label-position="top" size="default">
                <el-row :gutter="20">
                  <el-col :span="12">
                    <el-form-item label="同日同班同科连堂偏好" style="margin-bottom: 15px;">
                      <el-select v-model="globalParams.consecutive_preference" @change="saveGlobalParameters"
                        style="width: 100%" :disabled="workspace?.status === 'running'">
                        <el-option label="无所谓" value="none" />
                        <el-option label="尽量连堂" value="consecutive" />
                        <el-option label="尽量不连堂 (分散上课)" value="dispersed" />
                      </el-select>
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item label="教师排程集中度偏好" style="margin-bottom: 15px;">
                      <el-select v-model="globalParams.teacher_half_day_preference" @change="saveGlobalParameters"
                        style="width: 100%" :disabled="workspace?.status === 'running'">
                        <el-option label="无所谓" value="none" />
                        <el-option label="尽量半天排完课 (上午/下午集中)" value="consolidate" />
                        <el-option label="尽量分散在上下午" value="disperse" />
                      </el-select>
                    </el-form-item>
                  </el-col>
                </el-row>
                <el-form-item label="教师午休时间保护 (避免跨中午连堂)" style="margin-bottom: 0;">
                  <div style="display: flex; align-items: center; gap: 10px;">
                    <el-switch v-model="globalParams.avoid_lunch_consecutive" active-text="开启" inactive-text="关闭"
                      @change="saveGlobalParameters" :disabled="workspace?.status === 'running'" />
                    <span style="font-size: 12px; color: #64748b;">
                      启用后，算法会尽量避免让同一位老师连续上【上午最后一节】和【下午第一节】课。
                    </span>
                  </div>
                </el-form-item>
              </el-form>
            </div>

            <div
              style="margin: 15px 0; padding: 20px; background-color: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div
                style="margin-bottom: 12px; font-weight: 600; color: #334155; display: flex; align-items: center; gap: 8px;">
                <el-icon>
                  <Plus />
                </el-icon> 点击快速添加补充要求：
              </div>
              <div style="display: flex; flex-wrap: wrap; gap: 10px;">
                <el-button v-for="t in constraintTypesList" :key="t.value" size="small"
                  @click="openConstraintDialog(t.value)" :type="getConstraintTagType(t.value)" plain>
                  {{ t.label }}
                </el-button>
              </div>
            </div>

            <el-table :data="constraints" border style="width: 100%; margin-top: 15px;">
              <el-table-column prop="constraint_type" label="要求类型" width="180">
                <template #default="{ row }">
                  <el-tag :type="getConstraintTagType(row.constraint_type)">
                    {{ getConstraintTypeText(row.constraint_type) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="内容">
                <template #default="{ row }">
                  <span>{{ formatConstraintDesc(row) }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="is_hard" label="是否必须满足" width="130" align="center">
                <template #default="{ row }">
                  <el-tag :type="row.is_hard ? 'danger' : 'info'" effect="dark">
                    {{ row.is_hard ? '必须满足' : '尽量满足' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="100" align="center">
                <template #default="{ row }">
                  <el-button size="small" type="danger" :icon="Delete" @click="deleteConstraint(row.id)" />
                </template>
              </el-table-column>
            </el-table>

            <!-- Venue Resources Management Card -->
            <div style="margin-top: 25px; border-top: 1px solid #e2e8f0; padding-top: 20px;">
              <div class="card-title-row" style="display: flex; justify-content: space-between; align-items: center;">
                <h3
                  style="margin: 0; font-size: 16px; font-weight: 700; color: #1e293b; display: flex; align-items: center; gap: 8px;">
                  <el-icon color="#3b82f6">
                    <OfficeBuilding />
                  </el-icon> 专用场室资源管理
                </h3>
                <el-button type="primary" size="small" @click="openVenueDialog(null)">
                  <el-icon>
                    <Plus />
                  </el-icon> 添加场室
                </el-button>
              </div>
              <p style="font-size: 12px; color: #64748b; margin-top: 4px; margin-bottom: 15px;">
                录入如“机房”、“体育馆”、“实验室”等有限公共资源，系统会自动在排课时限制这些场地的班级并发使用数。
              </p>

              <el-table :data="venues" border style="width: 100%;">
                <el-table-column prop="name" label="场室名称" min-width="150" />
                <el-table-column prop="type" label="场室类型 / 标签" width="150">
                  <template #default="{ row }">
                    <el-tag size="small" type="success">{{ row.type }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="capacity" label="容纳班级上限" width="120" align="center">
                  <template #default="{ row }">
                    <strong>{{ row.capacity }}</strong> 个班级
                  </template>
                </el-table-column>
                <el-table-column label="不可用时间/禁排" width="180" align="center">
                  <template #default="{ row }">
                    <el-button size="small" type="warning" plain @click="openVenueForbiddenDialog(row)">
                      设置禁排 ({{ (row.forbidden_slots || []).length }} 节)
                    </el-button>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="120" align="center">
                  <template #default="{ row }">
                    <el-button-group>
                      <el-button size="small" :icon="Edit" @click="openVenueDialog(row)" />
                      <el-button size="small" type="danger" :icon="Delete" @click="deleteVenue(row)" />
                    </el-button-group>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>

          <div class="rules-right">
            <h3>常见设置</h3>
            <div class="rule-hint-box">
              <h4>可以在这里补充这些要求：</h4>
              <ul>
                <li><strong>固定课</strong>：例如周一第一节一定上语文。</li>
                <li><strong>不排课</strong>：例如某位老师某节不能上课，或某门课不放在下午。</li>
                <li><strong>同一天错开</strong>：例如两门课不要排在同一天。</li>
                <li><strong>老师一天别太满</strong>：控制老师单日课时数。</li>
                <li><strong>同年级尽量一致</strong>：同一年级同一学科尽量排在相近时间。</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="step-navigation-buttons">
          <el-button @click="activeTab = 'tasks'">上一步：任课安排</el-button>
          <el-button type="primary" @click="activeTab = 'solve'">下一步：生成课表</el-button>
        </div>
      </el-tab-pane>

      <!-- Tab 3: Solver & Snapshot -->
      <el-tab-pane name="solve" :label="(enableSportsCombined || enableWalkClass ? '4. ' : '3. ') + '生成课表'" lazy>
        <div class="solve-container">
          <div class="solve-left">
            <div class="algo-setup-card">
              <h3>生成课表</h3>
              <p class="desc">先完成排课前检查，再启动生成任务。系统会自动保存当前课表快照，生成失败或取消时不会影响已有结果。</p>

              <section class="preflight-panel" :class="{ passed: preflightReport?.can_start, blocked: preflightReport && !preflightReport.can_start }">
                <div class="preflight-panel__heading">
                  <div>
                    <h4>排课前检查</h4>
                    <p v-if="!preflightReport">检查容量、固定课、禁排、合班、场室和半课时配置。</p>
                    <p v-else-if="preflightReport.can_start">可以启动生成{{ preflightWarningCount ? `，另有 ${preflightWarningCount} 条提示` : '' }}。</p>
                    <p v-else>发现 {{ preflightErrorCount }} 项阻塞问题，修复后再生成。</p>
                  </div>
                  <el-button :loading="preflightLoading" plain type="primary" @click="runPreflightCheck">
                    <el-icon><CircleCheck /></el-icon> 运行检查
                  </el-button>
                </div>
                <div v-if="preflightReport" class="preflight-metrics">
                  <span class="metric metric--error">需修复 {{ preflightErrorCount }}</span>
                  <span class="metric metric--warning">提示 {{ preflightWarningCount }}</span>
                  <span class="metric">任课 {{ preflightReport.summary?.tasks || tasks.length }}</span>
                </div>
                <ul v-if="preflightIssues.length" class="preflight-issues">
                  <li v-for="issue in preflightIssues.slice(0, 5)" :key="`${issue.code}-${issue.message}`" :class="issue.severity">
                    {{ issue.message }}
                  </li>
                  <li v-if="preflightIssues.length > 5" class="muted">另有 {{ preflightIssues.length - 5 }} 条问题或提示。</li>
                </ul>
              </section>

              <!-- 全局优化参数 -->
              <div class="global-params-box"
                style="margin: 20px 0 25px 0; border-top: 1px solid #e2e8f0; padding-top: 20px;">
                <h4
                  style="margin: 0 0 15px 0; color: #1e293b; font-size: 14px; font-weight: 700; display: flex; align-items: center; gap: 8px;">
                  <el-icon color="#3b82f6">
                    <Setting />
                  </el-icon>
                  全局排课偏好设置 (自动保存)
                </h4>

                <el-form :model="globalParams" label-position="top" size="default">
                  <el-form-item label="生成策略" required>
                    <el-radio-group v-model="globalParams.intensity" @change="saveGlobalParameters"
                      :disabled="workspace?.status === 'running'">
                      <el-radio-button label="low">快速试排</el-radio-button>
                      <el-radio-button label="medium">推荐生成</el-radio-button>
                      <el-radio-button label="high">细致优化</el-radio-button>
                    </el-radio-group>
                    <div style="font-size: 12px; color: #64748b; margin-top: 4px; line-height: 1.4;">
                      {{ getGenerationStrategyHelp(globalParams.intensity) }}
                    </div>
                  </el-form-item>

                  <el-collapse class="solve-advanced-collapse">
                    <el-collapse-item name="advanced-solver">
                      <template #title>
                        <span style="font-size: 13px; color: #64748b;">高级设置（通常无需修改）</span>
                      </template>

                      <el-form-item label="生成引擎">
                        <el-radio-group v-model="selectedSolver" :disabled="workspace?.status === 'running'">
                          <el-radio label="cpsat" border>常用模式</el-radio>
                          <el-radio label="classic" border>兼容模式</el-radio>
                        </el-radio-group>
                      </el-form-item>

                      <div style="font-size: 12px; color: #64748b; line-height: 1.5;">
                        当前策略对应的最长尝试时间：快速试排约 15 秒，推荐生成约 45 秒，细致优化约 120 秒。兼容模式用于常用模式无法生成时备用。
                      </div>
                    </el-collapse-item>
                  </el-collapse>
                </el-form>
              </div>

              <div class="action-zone">
                <el-button type="primary" size="large" class="run-solver-btn" :disabled="preflightReport && !preflightReport.can_start" :loading="scheduleRunStatus === 'running' || scheduleRunStatus === 'queued'"
                  @click="triggerAutoScheduling">
                  <el-icon>
                    <Cpu />
                  </el-icon> {{ results.length ? '重新生成课表' : '开始生成课表' }}
                </el-button>
                <p class="action-zone__hint">启动前会自动创建当前课表的保护快照。</p>
              </div>

              <!-- Progress bar -->
              <div v-if="scheduleRunStatus === 'running' || scheduleRunStatus === 'queued' || scheduleProgress > 0" class="solve-progress-box">
                <div class="progress-labels">
                  <span>当前进度</span>
                  <span>{{ scheduleProgress }}%</span>
                </div>
                <el-progress :percentage="scheduleProgress"
                  :status="scheduleRunStatus === 'failed' || scheduleRunStatus === 'timed_out' ? 'exception' : (scheduleRunStatus === 'success' ? 'success' : '')"
                  :stroke-width="12" />
                <p class="status-msg">{{ scheduleRunLog || '正在准备生成课表...' }}</p>
                <div v-if="scheduleRunStatus === 'running' || scheduleRunStatus === 'queued'" style="margin-top: 15px; display: flex; justify-content: flex-end;">
                  <el-button type="danger" size="small" plain @click="handleResetStatus">
                    重置/强制中止状态
                  </el-button>
                </div>
              </div>
            </div>

            <!-- Backups / Mirror Snapshot Section -->
            <div class="backups-card" style="margin-top: 30px;">
              <div class="card-title-row">
                <h3>保存当前课表</h3>
                <el-button type="success" size="small" plain @click="openBackupDialog">
                  <el-icon>
                    <Camera />
                  </el-icon> 保存一个版本
                </el-button>
              </div>
              <p class="desc">调整课表前可以先保存一份，后面想回到这个版本时会更方便。</p>

              <el-table :data="backups" border style="width: 100%; margin-top: 15px;">
                <el-table-column prop="name" label="版本名称" />
                <el-table-column prop="created_at" label="保存时间" width="200">
                  <template #default="{ row }">
                    {{ formatDateTime(row.created_at) }}
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="160" align="center">
                  <template #default="{ row }">
                    <el-button-group>
                      <el-button size="small" type="primary" plain @click="restoreBackup(row.id)">
                        恢复到这里
                      </el-button>
                      <el-button size="small" type="danger" :icon="Delete" @click="deleteBackup(row.id)" />
                    </el-button-group>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>

          <div class="solve-right">
            <h3>生成任务</h3>
            <div class="status-card" :class="scheduleRunStatus">
              <el-icon class="status-icon">
                <CircleCheck v-if="scheduleRunStatus === 'success'" />
                <Warning v-else-if="scheduleRunStatus === 'failed' || scheduleRunStatus === 'timed_out'" />
                <Loading v-else-if="scheduleRunStatus === 'running' || scheduleRunStatus === 'queued'" />
                <Notebook v-else />
              </el-icon>
              <div class="status-info">
                <h4>{{ scheduleStatusText }}</h4>
                <p v-if="scheduleRunStatus === 'success'">课表已经生成好了，可以进入课表校对、手动调整或导出。</p>
                <p v-else-if="scheduleRunStatus === 'failed' || scheduleRunStatus === 'timed_out'" class="status-error-detail">这次没有生成成功：{{ activeJob?.error_message || workspace?.error_message ||
                  '请先查看排课前检查和规则设置。' }}
                </p>
                <p v-else-if="scheduleRunStatus === 'running' || scheduleRunStatus === 'queued'">
                  {{ scheduleRunLog || '系统正在准备生成课表。' }}
                  <el-button type="danger" size="small" style="margin-top: 10px; display: block;" @click="handleResetStatus">
                    取消本次生成
                  </el-button>
                </p>
                <p v-else>先运行排课前检查，确认没有阻塞项后再开始生成。</p>
              </div>
            </div>

            <div class="solve-side-summary">
              <div><span>当前课表</span><strong>{{ results.length ? '可用' : '未生成' }}</strong></div>
              <div><span>保护版本</span><strong>{{ backups.length }} 个</strong></div>
              <div><span>手动调整</span><strong>{{ results.filter(item => item.is_manual_override).length }} 节</strong></div>
            </div>
          </div>
        </div>

        <div class="step-navigation-buttons">
          <el-button @click="activeTab = 'rules'">上一步：补充要求</el-button>
          <el-button type="primary" :disabled="results.length === 0"
            @click="activeTab = 'timetable'">下一步：查看课表</el-button>
        </div>
      </el-tab-pane>

      <!-- Tab 4: Timetable Full view & Manual Adjustment -->
      <el-tab-pane name="timetable" :label="(enableSportsCombined || enableWalkClass ? '5. ' : '4. ') + '查看与调整默认课表'" lazy>
        <div class="timetable-workspace">
          <!-- Timetable action bar -->
          <div class="timetable-bar">
            <div class="bar-left">
              <el-radio-group v-model="viewMode" size="default" @change="handleViewModeChange">
                <el-radio-button label="class">按班级看</el-radio-button>
                <el-radio-button label="teacher">按教师看</el-radio-button>
                <el-radio-button label="room">按教室看</el-radio-button>
              </el-radio-group>

              <el-select v-if="viewMode === 'class'" v-model="selectedClass" placeholder="请选择班级"
                style="width: 180px; margin-left: 15px;" @change="loadGridData">
                <el-option v-for="c in classOptions" :key="c" :label="c" :value="c" />
              </el-select>

              <el-select v-if="viewMode === 'teacher'" v-model="selectedTeacherId" placeholder="搜索/选择教师" filterable
                :filter-method="filterTeachersPinyin" style="width: 180px; margin-left: 15px;" @change="loadGridData">
                <el-option v-for="t in filteredTeacherOptions" :key="t.id" :label="t.name" :value="t.id" />
              </el-select>

              <el-select v-if="viewMode === 'room'" v-model="selectedRoomId" placeholder="请选择教室"
                style="width: 180px; margin-left: 15px;" @change="loadGridData">
                <el-option v-for="r in roomOptions" :key="r.id" :label="r.name" :value="r.id" />
              </el-select>
            </div>

            <div class="bar-right">
              <el-button type="warning" plain @click="runConflictCheck">
                <el-icon>
                  <Warning />
                </el-icon> 检查当前冲突
              </el-button>
              <el-button type="primary" @click="exportTimetable">
                <el-icon>
                  <Download />
                </el-icon> 导出 Excel 课表
              </el-button>
              <el-button type="success" @click="exportTimetableWord">
                <el-icon>
                  <Download />
                </el-icon> 导出 Word 课表
              </el-button>
            </div>
          </div>


          <div class="completion-strip" :class="{ warning: viewCompletion.remaining > 0 }">
            <span>当前视图课时：已排 {{ viewCompletion.scheduled }} / 应排 {{ viewCompletion.required }}</span>
            <span v-if="viewCompletion.remaining > 0">还有 {{ viewCompletion.remaining }} 节未排入课表。</span>
            <span v-else>教学计划课时已排完。</span>
            <span v-if="viewMode === 'class' && viewCompletion.capacity > viewCompletion.required">
              日课表容量 {{ viewCompletion.capacity }} 节，空白格属于可用余量。
            </span>
          </div>

          <!-- Active Card Tuning Guide & Diagnosis Banner -->
          <div v-if="selectedActiveCard && !draggedCourseCard" class="tuning-diagnostic-banner"
            style="margin-bottom: 15px; padding: 15px 20px; border-radius: 10px; display: flex; align-items: center; justify-content: space-between; transition: all 0.3s ease; box-shadow: 0 4px 15px rgba(0,0,0,0.06);"
            :class="getTuningBannerClass()">
            <div style="display: flex; align-items: center; gap: 12px; flex: 1;">
              <span class="tuning-pulse" style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: #3b82f6;"></span>
              <div>
                <div style="font-weight: 700; font-size: 14px; margin-bottom: 2px;">
                  正在调动：<span style="color: #1d4ed8; text-decoration: underline;">
                    {{ selectedActiveCard.subject_name }}
                  </span> 
                  <span style="font-weight: normal; color: #475569; font-size: 13px; margin-left: 8px;">
                    ({{ selectedActiveCard.teacher_name || '未分配教师' }} - {{ `${selectedActiveCard.grade}${selectedActiveCard.class_name}` }})
                  </span>
                </div>
                <div style="font-size: 13px; color: #475569;">
                  <span v-if="hoveredSlot">
                    时段诊断：<strong>周{{ getDayName(hoveredSlot.day) }}第{{ hoveredSlot.period }}节</strong> —— 
                    <span :style="getDiagnosticMessageStyle()">{{ getDiagnosticMessage() }}</span>
                  </span>
                  <span v-else>
                    💡 提示：按住拖拽或在表格格子中点击选择目标时段；悬停在格子上可获取详细冲突诊断。
                  </span>
                </div>
              </div>
            </div>
            <div style="display: flex; gap: 10px; align-items: center;">
              <el-button v-if="!selectedActiveCard.is_from_pool" size="small" type="primary" plain @click="openSubstituteDialog(selectedActiveCard)">推荐代课</el-button>
              <el-button size="small" type="info" plain @click="cancelTuning">取消选择</el-button>
            </div>
          </div>

          <div class="timetable-layout">
            <!-- Left Grid area -->
            <div class="grid-section" v-loading="isTuning" element-loading-text="调课中..." element-loading-background="rgba(255,255,255,0.6)">
              <div class="grid-table-container">
                <table class="grid-table">
                  <thead>
                    <tr>
                      <th class="period-th">课节</th>
                      <th v-for="d in daysPerWeek" :key="d" class="day-th">
                        {{ getDayName(d) }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="period in totalDailyPeriods" :key="period">
                      <!-- Part division line -->
                      <tr v-if="period === viewPeriodsMorning + 1" class="division-row">
                        <td :colspan="daysPerWeek + 1">下午课程</td>
                      </tr>
                      <tr v-if="period === viewPeriodsMorning + viewPeriodsAfternoon + 1" class="division-row">
                        <td :colspan="daysPerWeek + 1">晚上课程</td>
                      </tr>

                      <tr>
                        <td class="period-cell">
                          <span class="p-num">{{ period }}</span>
                          <span class="p-name">{{ getPeriodName(period) }}</span>
                        </td>
                        <td v-for="d in daysPerWeek" :key="d" class="grid-cell" :data-day="d" :data-period="period" :class="[
                          getSlotConflictClass(d, period),
                          {
                            'selected-target': selectedActiveCard && activeSlotMatches(d, period),
                            'slot-highlight': isSlotInteractive(d, period),
                            'drag-target': dragOverSlot && dragOverSlot.day === d && dragOverSlot.period === period,
                            'disabled-slot': !isSlotAvailable(d, period)
                          }
                        ]" @click="handleCellClick(d, period)" @dragover.prevent
                          @dragenter.prevent="handleCellDragEnter(d, period)"
                          @drop.prevent="handleCellDrop(d, period)"
                          @mouseenter="hoveredSlot = { day: d, period: period }" @mouseleave="hoveredSlot = null">
                          <span v-if="getTimetableCellFreeLabel(d, period)" class="disabled-slot-label" style="background-color: #fef08a; color: #854d0e;">{{ getTimetableCellFreeLabel(d, period) }}</span>
                          <span v-else-if="!isSlotAvailable(d, period)" class="disabled-slot-label">不排</span>
                           <!-- Card stack in slot -->
                          <div class="slot-cards-container">
                              <div v-for="card in getSlotCards(d, period)" :key="card.id" class="course-card" draggable="true"
                                :title="getCourseCardTitle(card)"
                                :style="getCourseCardStyle(card)" :class="{
                                  'half-period': card.is_half_period,
                                  'manual': card.is_manual_override,
                                  'dragging': draggedCourseCard && draggedCourseCard.id === card.id
                                }" @mousedown.left.stop="startCoursePointerDrag(card, $event)"
                                @click.stop="handleCourseCardClick(card)" @dragstart.stop="onCourseDragStart(card, $event)"
                                @dragend="onDragEnd">
                                <div class="card-title">
                                  {{ card.subject_name }}
                                  <el-tag v-if="card.is_combined" size="small" type="success" effect="dark" style="float: right; transform: scale(0.85); transform-origin: right center; padding: 0 4px; height: 18px; line-height: 16px;">合</el-tag>
                                </div>
                                <div class="card-details">
                                  <span v-if="viewMode !== 'teacher'">{{ card.teacher_name }}</span>
                                  <span v-else>{{ card.grade }}{{ card.class_name }}</span>
                                  <span class="room" v-if="card.room_name">| {{ card.room_name }}</span>
                                </div>
                                <div class="card-badges">
                                  <el-tag v-if="card.is_half_period" size="small"
                                    :type="getHalfPeriodTagType(card.half_period_type)">
                                    {{ getHalfPeriodText(card.half_period_type) }}
                                  </el-tag>
                                  <span v-if="card.is_manual_override" class="manual-badge" title="手动调课">手</span>
                                </div>
                                <el-button class="card-remove-btn" type="danger" link :icon="Close"
                                  @mousedown.stop
                                  @click.stop="removeCardFromGrid(card)" />
                              </div>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Right Sidebar Panel: Staging area, Holding pool & Comparison mini timetables -->
            <div class="sidebar-section">
              <!-- 暂存区 Staging Area -->
              <div class="sidebar-card staging-zone" data-staging-drop="true"
                :class="{ 'staging-drag-over': isStagingDragOver }"
                @dragover.prevent="onStagingDragOver"
                @dragenter.prevent="onStagingDragEnter"
                @dragleave.prevent="onStagingDragLeave"
                @drop.prevent="onStagingDrop">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <h3 style="margin: 0;">📦 暂存区</h3>
                  <el-button v-if="stagingCards.length > 0" type="danger" link size="small" @click="clearAllStaging">清空</el-button>
                </div>
                <p class="desc">将课表中的课程拖到此处暂存，再拖回课表完成调整。</p>

                <div class="staging-pool">
                  <div v-for="(sc, idx) in stagingCards" :key="idx" class="staging-card"
                    draggable="true"
                    :class="{ 'selected': selectedActiveCard && selectedActiveCard.task_id === sc.task_id && selectedActiveCard.is_from_pool }"
                    @mousedown.left.stop="startStagingPointerDrag(sc, $event)"
                    @click="handleStagingCardClick(sc)"
                    @dragstart.stop="onStagingCardDragStart(sc, $event)"
                    @dragend="onDragEnd">
                    <div class="staging-card-main">
                      <div class="staging-card-subject">{{ sc.subject_name }}</div>
                      <div class="staging-card-info">
                        {{ sc.teacher_name }} · {{ sc.grade }}{{ sc.class_name }}
                      </div>
                    </div>
                    <el-button type="danger" link :icon="Close" size="small"
                      @mousedown.stop
                      @click.stop="removeStagingCard(idx)" style="flex-shrink: 0;" />
                  </div>

                  <!-- Empty state / drop hint -->
                  <div v-if="stagingCards.length === 0" class="staging-empty">
                    <div class="staging-empty-icon">📥</div>
                    <div>拖拽课程卡片到此处暂存</div>
                  </div>
                </div>
              </div>

              <!-- Holding Pool -->
              <div class="sidebar-card" style="margin-top: 12px;">
                <h3>待排课节</h3>
                <p class="desc">选择下方课节，然后点击或拖拽到左侧课表的空白格排入。</p>

                <div class="holding-pool" style="max-height: 220px; overflow-y: auto;">
                  <div v-for="h in remainingWorkloads" :key="h.task_id" class="holding-card"
                    :draggable="h.remaining > 0"
                    :class="{ 'selected': selectedActiveCard && selectedActiveCard.task_id === h.task_id && selectedActiveCard.is_from_pool }"
                    @click="selectHoldingTask(h)" @dragstart.stop="onHoldingDragStart(h, $event)" @dragend="onDragEnd">
                    <div class="h-title">{{ h.subject_name }}</div>
                    <div class="h-info">
                      <span>{{ h.teacher_name }} ({{ h.grade }}{{ h.class_name }})</span>
                    </div>
                    <div class="h-progress">
                      <span class="prog-text">已排 {{ h.scheduled }} / {{ h.weekly_periods }}</span>
                      <el-tag :type="h.remaining > 0 ? 'warning' : 'success'" size="small">
                        未排 {{ h.remaining }} 节
                      </el-tag>
                    </div>
                  </div>

                  <div v-if="remainingWorkloads.length === 0" class="pool-empty">
                    当前查看的对象无多余待排课节。
                  </div>
                </div>
              </div>

              <!-- Mini Schedules Panel -->
              <div class="sidebar-card" style="margin-top: 15px;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                  <h3 style="margin: 0; font-size: 14px; font-weight: bold;">对照小课表</h3>
                  <el-checkbox v-model="autoAssociateMini" size="small">自动关联</el-checkbox>
                </div>
                <p class="desc">展示关联的班级、教师或学科的小课表，辅助观察冲突。</p>

                <!-- Search & Add Pin -->
                <div style="display: flex; gap: 6px; margin-bottom: 10px;">
                  <el-select v-model="miniSelectVal" filterable size="small" placeholder="添加对照老师/班级/学科"
                    style="width: 100%;" @change="handleAddMiniPin">
                    <el-option-group label="教师">
                      <el-option v-for="t in teacherList" :key="'t_' + t.id" :label="'教师: ' + t.name"
                        :value="'teacher:' + t.id" />
                    </el-option-group>
                    <el-option-group label="班级">
                      <el-option v-for="c in classOptions" :key="'c_' + c" :label="'班级: ' + c" :value="'class:' + c" />
                    </el-option-group>
                    <el-option-group label="学科">
                      <el-option v-for="s in uniqueSubjects" :key="'s_' + s" :label="'学科: ' + s"
                        :value="'subject:' + s" />
                    </el-option-group>
                  </el-select>
                </div>

                <div class="mini-schedules-container"
                  style="display: flex; flex-direction: column; gap: 12px; max-height: 250px; overflow-y: auto; padding-right: 2px; border-bottom: 1px dashed #e2e8f0; padding-bottom: 12px; margin-bottom: 12px;">
                  <div v-for="mini in activeMiniSchedules" :key="mini.key"
                    style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px; background: #ffffff; position: relative;">
                    <!-- Mini Schedule Header -->
                    <div
                      style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
                      <span style="font-size: 12px; font-weight: bold; color: #1e293b;">
                        {{ mini.type === 'teacher' ? '👤 教师: ' : (mini.type === 'class' ? '🏫 班级: ' : '📚 学科: ') }}{{
                        mini.name }}
                      </span>
                      <el-button type="danger" link :icon="Close" size="small" style="padding: 2px;"
                        @click="removeMiniPin(mini.key)" />
                    </div>

                    <!-- Compact Grid -->
                    <div class="mini-grid-wrapper">
                      <div :style="{
                        display: 'grid',
                        gridTemplateColumns: `16px repeat(${daysPerWeek}, 1fr)`,
                        gap: '2px'
                      }">
                        <!-- Header Column corner -->
                        <div style="height: 14px;"></div>
                        <!-- Day Headers -->
                        <div v-for="d in daysPerWeek" :key="d"
                          style="font-size: 8px; color: #64748b; text-align: center; line-height: 14px; font-weight: bold;">
                          {{ getDayShortName(d) }}
                        </div>

                        <!-- Grid Rows -->
                        <template v-for="p in totalDailyPeriods" :key="p">
                          <!-- Period indicator -->
                          <div
                            style="font-size: 8px; color: #94a3b8; text-align: center; line-height: 18px; font-weight: bold; background: #f8fafc; border-radius: 2px;">
                            {{ p }}
                          </div>
                          <!-- Cells for days -->
                          <div v-for="d in daysPerWeek" :key="d" :style="getMiniCellStyle(d, p, mini)"
                            :title="getMiniCellTooltip(d, p, mini)"
                            style="display: flex; align-items: center; justify-content: center; user-select: none;">
                            <span style="transform: scale(0.85); display: inline-block;">
                              {{ getMiniCellContent(d, p, mini) }}
                            </span>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>

                  <div v-if="activeMiniSchedules.length === 0"
                    style="color: #94a3b8; font-size: 11px; text-align: center; padding: 15px 0;">
                    无对照小课表。选中课程或手动搜索添加。
                  </div>
                </div>

                <!-- 学科课表缩略 (简写) -->
                <div>
                  <h3
                    style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold; display: flex; align-items: center; gap: 6px;">
                    <el-icon color="#8b5cf6">
                      <Collection />
                    </el-icon>
                    学科缩略图 (简写)
                  </h3>
                  <p class="desc">展示各学科在全校/全级班级中的课时分布简图。</p>

                  <div class="subject-minis-container"
                    style="display: flex; flex-direction: column; gap: 10px; max-height: 380px; overflow-y: auto; padding-right: 2px;">
                    <div v-for="subName in uniqueSubjects" :key="subName"
                      style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px; background: #ffffff;">
                      <!-- Subject Mini Header -->
                      <div
                        style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
                        <span style="font-size: 11px; font-weight: bold; color: #1e293b;">
                          📚 {{ subName }}
                        </span>
                      </div>

                      <!-- Compact Grid -->
                      <div class="mini-grid-wrapper">
                        <div :style="{
                          display: 'grid',
                          gridTemplateColumns: `16px repeat(${daysPerWeek}, 1fr)`,
                          gap: '2px'
                        }">
                          <!-- Header Column corner -->
                          <div style="height: 14px;"></div>
                          <!-- Day Headers -->
                          <div v-for="d in daysPerWeek" :key="d"
                            style="font-size: 8px; color: #64748b; text-align: center; line-height: 14px; font-weight: bold;">
                            {{ getDayShortName(d) }}
                          </div>

                          <!-- Grid Rows -->
                          <template v-for="p in totalDailyPeriods" :key="p">
                            <!-- Period indicator -->
                            <div
                              style="font-size: 8px; color: #94a3b8; text-align: center; line-height: 18px; font-weight: bold; background: #f8fafc; border-radius: 2px;">
                              {{ p }}
                            </div>
                            <!-- Cells for days -->
                            <div v-for="d in daysPerWeek" :key="d"
                              :style="getMiniCellStyle(d, p, { type: 'subject', name: subName })"
                              :title="getMiniCellTooltip(d, p, { type: 'subject', name: subName })"
                              style="display: flex; align-items: center; justify-content: center; user-select: none;">
                              <span style="transform: scale(0.85); display: inline-block;">
                                {{ getMiniCellContent(d, p, { type: 'subject', name: subName }) }}
                              </span>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>

                    <div v-if="uniqueSubjects.length === 0"
                      style="color: #94a3b8; font-size: 11px; text-align: center; padding: 15px 0;">
                      无可用学科数据。
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-tab-pane>

      <!-- Tab 5: Week-specific Adjustments -->
      <el-tab-pane name="week-adjust" :label="(enableSportsCombined || enableWalkClass ? '6. ' : '5. ') + '周次调课'" lazy>
        <div class="timetable-workspace">
          <!-- Timetable action bar -->
          <div class="timetable-bar">
            <div class="bar-left">
              <el-radio-group v-model="viewMode" size="default" @change="handleViewModeChange">
                <el-radio-button label="class">按班级看</el-radio-button>
                <el-radio-button label="teacher">按教师看</el-radio-button>
                <el-radio-button label="room">按教室看</el-radio-button>
              </el-radio-group>

              <el-select v-if="viewMode === 'class'" v-model="selectedClass" placeholder="请选择班级"
                style="width: 180px; margin-left: 15px;" @change="loadGridData">
                <el-option v-for="c in classOptions" :key="c" :label="c" :value="c" />
              </el-select>

              <el-select v-if="viewMode === 'teacher'" v-model="selectedTeacherId" placeholder="搜索/选择教师" filterable
                :filter-method="filterTeachersPinyin" style="width: 180px; margin-left: 15px;" @change="loadGridData">
                <el-option v-for="t in filteredTeacherOptions" :key="t.id" :label="t.name" :value="t.id" />
              </el-select>

              <el-select v-if="viewMode === 'room'" v-model="selectedRoomId" placeholder="请选择教室"
                style="width: 180px; margin-left: 15px;" @change="loadGridData">
                <el-option v-for="r in roomOptions" :key="r.id" :label="r.name" :value="r.id" />
              </el-select>

              <span style="margin-left: 20px; font-weight: bold; color: #475569; font-size: 14px;">选择周次：</span>
              <el-select v-model="selectedWeek" placeholder="第几周" style="width: 110px;" @change="handleWeekChange">
                <el-option v-for="w in totalWeeks" :key="w" :label="`第 ${w} 周`" :value="w" />
              </el-select>
            </div>

            <div class="bar-right">
              <el-button type="info" plain @click="openOverrideListDialog">
                <el-icon>
                  <List />
                </el-icon> 调课记录
              </el-button>
              <el-button type="success" @click="exportOverrideNotice(null)">
                <el-icon>
                  <Printer />
                </el-icon> 导出调课单
              </el-button>
            </div>
          </div>

          <div class="completion-strip" :class="{ warning: viewCompletion.remaining > 0 }">
            <span>第 {{ selectedWeek }} 周当前视图课时：已排 {{ viewCompletion.scheduled }} / 应排 {{ viewCompletion.required }}</span>
          </div>

          <!-- Active Card Tuning Guide & Diagnosis Banner -->
          <div v-if="selectedActiveCard" class="tuning-diagnostic-banner"
            style="margin-bottom: 15px; padding: 15px 20px; border-radius: 10px; display: flex; align-items: center; justify-content: space-between; transition: all 0.3s ease; box-shadow: 0 4px 15px rgba(0,0,0,0.06);"
            :class="getTuningBannerClass()">
            <div style="display: flex; align-items: center; gap: 12px; flex: 1;">
              <span class="tuning-pulse" style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: #3b82f6;"></span>
              <div>
                <div style="font-weight: 700; font-size: 14px; margin-bottom: 2px;">
                  正在调课：<span style="color: #1d4ed8; text-decoration: underline;">
                    {{ selectedActiveCard.subject_name }}
                  </span> 
                  <span style="font-weight: normal; color: #475569; font-size: 13px; margin-left: 8px;">
                    ({{ selectedActiveCard.teacher_name || '未分配教师' }} - {{ `${selectedActiveCard.grade}${selectedActiveCard.class_name}` }})
                  </span>
                </div>
                <div style="font-size: 13px; color: #475569;">
                  💡 提示：已选中此课程卡片，请点击右侧的“📅 调课”按钮对本周的这一节课进行停课、换课、或代课。
                </div>
              </div>
            </div>
            <div style="display: flex; gap: 10px; align-items: center;">
              <el-button size="small" type="primary" :icon="Calendar" @click="openWeekOverrideDialog(selectedActiveCard)">📅 调课</el-button>
              <el-button v-if="!selectedActiveCard.is_from_pool" size="small" type="success" plain @click="openSubstituteDialog(selectedActiveCard)">推荐代课</el-button>
              <el-button size="small" type="info" plain @click="cancelTuning">取消选择</el-button>
            </div>
          </div>

          <div class="timetable-layout" style="grid-template-columns: 1fr;">
            <!-- Left Grid area -->
            <div class="grid-section" v-loading="isTuning" element-loading-text="加载中..." element-loading-background="rgba(255,255,255,0.6)">
              <div class="grid-table-container">
                <table class="grid-table">
                  <thead>
                    <tr>
                      <th class="period-th">课节</th>
                      <th v-for="d in daysPerWeek" :key="d" class="day-th">
                        {{ getDayName(d) }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="period in totalDailyPeriods" :key="period">
                      <!-- Part division line -->
                      <tr v-if="period === viewPeriodsMorning + 1" class="division-row">
                        <td :colspan="daysPerWeek + 1">下午课程</td>
                      </tr>
                      <tr v-if="period === viewPeriodsMorning + viewPeriodsAfternoon + 1" class="division-row">
                        <td :colspan="daysPerWeek + 1">晚上课程</td>
                      </tr>

                      <tr>
                        <td class="period-cell">
                          <span class="p-num">{{ period }}</span>
                          <span class="p-name">{{ getPeriodName(period) }}</span>
                        </td>
                        <td v-for="d in daysPerWeek" :key="d" class="grid-cell" :data-day="d" :data-period="period" :class="[
                          getSlotConflictClass(d, period),
                          {
                            'selected-target': selectedActiveCard && activeSlotMatches(d, period),
                            'slot-highlight': isSlotInteractive(d, period),
                            'drag-target': dragOverSlot && dragOverSlot.day === d && dragOverSlot.period === period,
                            'disabled-slot': !isSlotAvailable(d, period)
                          }
                        ]" @click="handleCellClick(d, period)" @dragover.prevent
                          @dragenter.prevent="handleCellDragEnter(d, period)"
                          @drop.prevent="handleCellDrop(d, period)"
                          @mouseenter="hoveredSlot = { day: d, period: period }" @mouseleave="hoveredSlot = null">
                          <span v-if="getTimetableCellFreeLabel(d, period)" class="disabled-slot-label" style="background-color: #fef08a; color: #854d0e;">{{ getTimetableCellFreeLabel(d, period) }}</span>
                          <span v-else-if="!isSlotAvailable(d, period)" class="disabled-slot-label">不排</span>
                           <!-- Card stack in slot -->
                          <div class="slot-cards-container">
                              <div v-for="card in getSlotCards(d, period)" :key="card.id" class="course-card" :class="{ 'is-overridden': card.is_overridden }" draggable="true"
                                :title="getCourseCardTitle(card)"
                                :style="getCourseCardStyle(card)"
                                @mousedown.left.stop="startCoursePointerDrag(card, $event)"
                                @click.stop="handleCourseCardClick(card)" @dragstart.stop="onCourseDragStart(card, $event)"
                                @dragend="onDragEnd">
                                <div class="card-title">
                                  {{ card.subject_name }}
                                  <el-tag v-if="card.is_combined" size="small" type="success" effect="dark" style="float: right; transform: scale(0.85); transform-origin: right center; padding: 0 4px; height: 18px; line-height: 16px;">合</el-tag>
                                </div>
                                <div class="card-details">
                                  <span v-if="viewMode !== 'teacher'">{{ card.teacher_name }}</span>
                                  <span v-else>{{ card.grade }}{{ card.class_name }}</span>
                                  <span class="room" v-if="card.room_name">| {{ card.room_name }}</span>
                                </div>
                                <div class="card-badges">
                                  <el-tag v-if="card.is_half_period" size="small"
                                    :type="getHalfPeriodTagType(card.half_period_type)">
                                    {{ getHalfPeriodText(card.half_period_type) }}
                                  </el-tag>
                                  <span v-if="card.is_manual_override" class="manual-badge" title="手动调课">手</span>
                                  <!-- 调课标记 -->
                                  <el-tooltip v-if="card.is_overridden" :content="getOverrideTooltip(card)" placement="top">
                                    <span class="override-badge" :class="'override-' + card.override_type">
                                      {{ card.override_type === 'replace' ? '换' : card.override_type === 'substitute' ? '代' : '停' }}
                                    </span>
                                  </el-tooltip>
                                </div>
                              </div>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- Task manual creation / edit dialog -->
    <el-dialog v-model="taskDialogVisible" :title="isEditTask ? '修改教学计划课节' : '添加教学任课计划'" width="500px">
      <el-form :model="taskForm" ref="taskFormRef" label-position="top">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="年级" prop="grade" required>
              <el-input v-model="taskForm.grade" placeholder="如：高一" :disabled="taskForm.is_combined" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="班级" prop="class_name" required>
              <el-input v-model="taskForm.class_name" placeholder="如：1班" :disabled="taskForm.is_combined" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="科目" prop="subject_id" required>
          <div style="display: flex; gap: 8px; width: 100%;">
            <el-select v-model="taskForm.subject_id" filterable placeholder="选择科目" style="flex: 1;" :disabled="taskForm.is_combined">
              <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
            </el-select>
            <el-button type="primary" plain @click="openSubjectManager" :disabled="taskForm.is_combined">管理科目</el-button>
          </div>
        </el-form-item>

        <el-form-item label="任课教师" prop="teacher_id" required>
          <el-select v-model="taskForm.teacher_id" filterable :filter-method="filterTeachersPinyinForm"
            placeholder="搜索/选择老师(支持拼音首字母)" style="width: 100%" :disabled="taskForm.is_combined">
            <el-option v-for="t in filteredTeachersForm" :key="t.id" :label="t.name" :value="t.id" />
          </el-select>
          <div style="margin-top: 8px;">
            <el-button type="success" plain size="small" @click="openTeacherManager" :disabled="taskForm.is_combined">管理教师</el-button>
          </div>
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="周课时数" prop="weekly_periods" required>
              <el-input-number v-model="taskForm.weekly_periods" :min="0.5" :max="15" :step="0.5" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="连堂节数" prop="consecutive_periods">
              <el-input-number v-model="taskForm.consecutive_periods" :min="1" :max="3" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="专用教室/室外场室(选填)" prop="room_id">
          <el-select v-model="taskForm.room_id" placeholder="默认使用本班级教室" clearable style="width: 100%">
            <el-option v-for="r in roomOptions" :key="r.id" :label="r.name" :value="r.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="合班性质（选填）">
          <el-row :gutter="10">
            <el-col :span="8">
              <el-checkbox v-model="taskForm.is_combined" :disabled="isEditTask && taskForm.is_combined">设为合班</el-checkbox>
            </el-col>
            <el-col :span="16">
              <el-input v-model="taskForm.combined_group_id" placeholder="合班编码（同组的需相同）"
                :disabled="!taskForm.is_combined || (isEditTask && taskForm.is_combined)" />
            </el-col>
          </el-row>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="taskDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitTaskForm">保存</el-button>
      </template>
    </el-dialog>

    <!-- Constraint manual creation dialog -->
    <el-dialog v-model="constraintDialogVisible" title="添加补充要求"
      :width="constraintForm.constraint_type === 'subject_preset_slots' ? '800px' : '550px'">
      <el-form :model="constraintForm" label-position="top">
        <el-form-item label="要求类型" required>
          <el-select v-model="constraintForm.constraint_type" placeholder="选择一种要求" style="width: 100%"
            @change="handleConstraintTypeChange">
            <el-option v-for="t in constraintTypesList" :key="t.value" :label="t.label" :value="t.value" />
          </el-select>
        </el-form-item>

        <!-- Dynamic configuration widgets by constraint type -->
        <template v-if="constraintForm.constraint_type === 'fixed_slot'">
          <el-form-item label="选择哪门课" required>
            <el-select v-model="constraintForm.config.task_key" placeholder="选择班级、学科和老师" style="width: 100%">
              <el-option v-for="t in tasks" :key="t.id"
                :label="`${t.grade}${t.class_name} - ${t.subject_name} (${t.teacher_name})`"
                :value="`${t.grade}|${t.class_name}|${t.subject_id}`" />
            </el-select>
          </el-form-item>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="星期" required>
                <el-input-number v-model="constraintForm.config.day" :min="1" :max="daysPerWeek" style="width: 100%" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="第几节课" required>
                <el-input-number v-model="constraintForm.config.period" :min="1" :max="totalDailyPeriods"
                  style="width: 100%" />
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'forbidden_slot'">
          <el-form-item label="对谁生效" required>
            <el-radio-group v-model="constraintForm.config.target_type">
              <el-radio label="class">班级</el-radio>
              <el-radio label="teacher">教师</el-radio>
              <el-radio label="subject">科目</el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="选择对象" required>
            <el-select v-if="constraintForm.config.target_type === 'teacher'" v-model="constraintForm.config.target_id"
              placeholder="选择教师" filterable :filter-method="filterTeachersPinyinForm" style="width: 100%">
              <el-option v-for="t in filteredTeachersForm" :key="t.id" :label="t.name" :value="t.id" />
            </el-select>
            <el-select v-else-if="constraintForm.config.target_type === 'subject'"
              v-model="constraintForm.config.target_id" placeholder="选择科目" style="width: 100%">
              <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
            </el-select>
            <el-select v-else v-model="constraintForm.config.target_id" placeholder="选择班级" style="width: 100%">
              <el-option v-for="c in classOptions" :key="c" :label="c" :value="c" />
            </el-select>
          </el-form-item>

          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="星期" required>
                <el-input-number v-model="constraintForm.config.day" :min="1" :max="daysPerWeek" style="width: 100%" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="第几节课" required>
                <el-input-number v-model="constraintForm.config.period" :min="1" :max="totalDailyPeriods"
                  style="width: 100%" />
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'subject_mutex'">
          <el-form-item label="不要排在同一天的学科" required>
            <el-select v-model="constraintForm.config.subject_ids" multiple placeholder="至少选择两门学科" style="width: 100%">
              <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
            </el-select>
          </el-form-item>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'subject_consecutive_limit'">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="前一门课" required>
                <el-select v-model="constraintForm.config.subject_id_1" placeholder="选择科目" style="width: 100%">
                  <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="后一门课" required>
                <el-select v-model="constraintForm.config.subject_id_2" placeholder="选择科目" style="width: 100%">
                  <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'time_slot_limit'">
          <el-form-item label="限制对象类型" required>
            <el-radio-group v-model="constraintForm.config.target_type">
              <el-radio label="class_subject">班级科目</el-radio>
              <el-radio label="teacher">教师</el-radio>
            </el-radio-group>
          </el-form-item>

          <template v-if="constraintForm.config.target_type === 'teacher'">
            <el-form-item label="选择教师" required>
              <el-select v-model="constraintForm.config.teacher_id" placeholder="选择教师" filterable
                :filter-method="filterTeachersPinyinForm" style="width: 100%">
                <el-option v-for="t in filteredTeachersForm" :key="t.id" :label="t.name" :value="t.id" />
              </el-select>
            </el-form-item>
          </template>
          <template v-else>
            <el-row :gutter="15">
              <el-col :span="8">
                <el-form-item label="选择年级 (可选)">
                  <el-select v-model="constraintForm.config.grade" placeholder="全部年级" clearable style="width: 100%"
                    @change="handlePresetGradeChange">
                    <el-option v-for="g in gradesConfig" :key="g.name" :label="g.name" :value="g.name" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="选择班级 (可选)">
                  <el-select v-model="constraintForm.config.class_name" placeholder="全部班级" clearable
                    style="width: 100%">
                    <el-option v-for="c in presetClassOptions" :key="c" :label="c" :value="c" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="选择科目" required>
                  <el-select v-model="constraintForm.config.subject_id" placeholder="选择科目" style="width: 100%">
                    <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>
          </template>

          <el-form-item label="该时段内最多安排几节课" required>
            <el-input-number v-model="constraintForm.config.max_periods" :min="1" :max="35" style="width: 100%" />
            <div style="font-size: 12px; color: #64748b; margin-top: 4px;">
              限制在下方选中的时段范围内，该对象最多被排入的课节总数。
            </div>
          </el-form-item>

          <!-- Interactive grid -->
          <div class="preset-slots-grid-wrapper" style="margin-bottom: 20px;">
            <div style="margin-bottom: 8px; font-weight: 600; font-size: 13px; color: #475569;">
              点击网格以选择限制时间范围（支持多选）：
            </div>
            <table
              style="width: 100%; border-collapse: collapse; text-align: center; font-size: 12px; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
              <thead>
                <tr style="background: #f1f5f9; border-bottom: 1px solid #e2e8f0;">
                  <th
                    style="padding: 10px; font-weight: 600; color: #475569; border-right: 1px solid #e2e8f0; width: 100px;">
                    节次</th>
                  <th v-for="d in daysPerWeek" :key="d"
                    style="padding: 10px; font-weight: 600; color: #475569; border-right: 1px solid #e2e8f0;">
                    {{ getDayName(d) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in totalPeriodsCount" :key="p" style="border-bottom: 1px solid #e2e8f0;">
                  <td
                    style="padding: 10px; font-weight: 600; color: #475569; background: #f8fafc; border-right: 1px solid #e2e8f0;">
                    第{{ p }}节
                  </td>
                  <td v-for="d in daysPerWeek" :key="d"
                    style="padding: 8px 4px; border-right: 1px solid #e2e8f0; cursor: pointer; transition: all 0.2s; min-height: 35px; user-select: none;"
                    :style="getPresetSlotStyle(d, p)" @click="clickPresetSlot(d, p)">
                    <div style="font-weight: bold; font-size: 11px;">
                      {{ getPresetSlotText(d, p) }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'daily_limit'">
          <el-form-item label="限制对象类型" required>
            <el-radio-group v-model="constraintForm.config.target_type">
              <el-radio label="class_subject">班级科目</el-radio>
              <el-radio label="teacher">教师</el-radio>
            </el-radio-group>
          </el-form-item>

          <template v-if="constraintForm.config.target_type === 'teacher'">
            <el-form-item label="选择教师" required>
              <el-select v-model="constraintForm.config.teacher_id" placeholder="选择教师" filterable
                :filter-method="filterTeachersPinyinForm" style="width: 100%">
                <el-option v-for="t in filteredTeachersForm" :key="t.id" :label="t.name" :value="t.id" />
              </el-select>
            </el-form-item>
          </template>
          <template v-else>
            <el-row :gutter="15">
              <el-col :span="8">
                <el-form-item label="选择年级 (可选)">
                  <el-select v-model="constraintForm.config.grade" placeholder="全部年级" clearable style="width: 100%"
                    @change="handlePresetGradeChange">
                    <el-option v-for="g in gradesConfig" :key="g.name" :label="g.name" :value="g.name" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="选择班级 (可选)">
                  <el-select v-model="constraintForm.config.class_name" placeholder="全部班级" clearable
                    style="width: 100%">
                    <el-option v-for="c in presetClassOptions" :key="c" :label="c" :value="c" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="选择科目" required>
                  <el-select v-model="constraintForm.config.subject_id" placeholder="选择科目" style="width: 100%">
                    <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>
          </template>

          <el-form-item label="每天最多课时数" required>
            <el-input-number v-model="constraintForm.config.max_periods" :min="1" :max="8" style="width: 100%" />
            <div style="font-size: 12px; color: #64748b; margin-top: 4px;">
              限制该对象在任何单天内被排入的最大课节数。
            </div>
          </el-form-item>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'subject_adjacency_limit'">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="学科 A" required>
                <el-select v-model="constraintForm.config.subject_id_1" placeholder="选择科目" style="width: 100%">
                  <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="学科 B" required>
                <el-select v-model="constraintForm.config.subject_id_2" placeholder="选择科目" style="width: 100%">
                  <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="限制方式" required>
            <el-select v-model="constraintForm.config.direction" placeholder="选择限制方式" style="width: 100%">
              <el-option label="学科 A 不能排在学科 B 的紧邻前面" value="before" />
              <el-option label="学科 A 不能排在学科 B 的紧邻后面" value="after" />
              <el-option label="学科 A 和学科 B 不能排在相邻位置" value="both" />
            </el-select>
          </el-form-item>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'teacher_max_daily_periods'">
          <el-form-item label="选择教师" required>
            <el-select v-model="constraintForm.config.teacher_id" placeholder="选择教师" filterable
              :filter-method="filterTeachersPinyinForm" style="width: 100%">
              <el-option v-for="t in filteredTeachersForm" :key="t.id" :label="t.name" :value="t.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="一天最多上几节课" required>
            <el-input-number v-model="constraintForm.config.max_periods" :min="1" :max="totalDailyPeriods"
              style="width: 100%" />
          </el-form-item>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'teacher_mutex'">
          <el-form-item label="互斥教师组" required>
            <el-select v-model="constraintForm.config.teacher_ids" multiple placeholder="选择互斥教师（不可同一时段上课）"
              style="width: 100%">
              <el-option v-for="t in teacherList" :key="t.id" :label="t.name" :value="t.id" />
            </el-select>
          </el-form-item>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'grade_progress_sync'">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="年级" required>
                <el-input v-model="constraintForm.config.grade" placeholder="如：高一" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="科目" required>
                <el-select v-model="constraintForm.config.subject_id" placeholder="选择科目" style="width: 100%">
                  <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'subject_period_preference'">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="限制科目" required>
                <el-select v-model="constraintForm.config.subject_id" placeholder="选择科目" style="width: 100%">
                  <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="偏好时间段" required>
                <el-select v-model="constraintForm.config.preference" placeholder="选择偏好时间" style="width: 100%">
                  <el-option label="尽量排在上午" value="morning" />
                  <el-option label="尽量排在下午" value="afternoon" />
                  <el-option label="尽量排在晚上" value="evening" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'subject_simultaneous_limit'">
          <el-row :gutter="15">
            <el-col :span="8">
              <el-form-item label="限制科目" required>
                <el-select v-model="constraintForm.config.subject_id" placeholder="选择科目" style="width: 100%">
                  <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="限制范围" required>
                <el-select v-model="constraintForm.config.limit_scope" placeholder="选择范围" style="width: 100%">
                  <el-option label="全校范围内" value="school" />
                  <el-option label="各年级内分别限制" value="grade" />
                  <el-option label="同校区范围内" value="campus" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="同时上课班级上限" required>
                <el-input-number v-model="constraintForm.config.max_classes" :min="1" :max="100" style="width: 100%" />
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'subject_venue_binding'">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="限制科目" required>
                <el-select v-model="constraintForm.config.subject_id" placeholder="选择科目" style="width: 100%">
                  <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="绑定场地类型" required>
                <el-select v-model="constraintForm.config.venue_type" placeholder="选择或输入场地类型" allow-create filterable
                  style="width: 100%">
                  <el-option v-for="t in venueTypes" :key="t" :label="t" :value="t" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'class_venue_requirement'">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="选择班级课程" required>
                <el-select v-model="constraintForm.config.task_key" placeholder="选择班级、学科和老师" style="width: 100%">
                  <el-option v-for="t in tasks" :key="`${t.grade}|${t.class_name}|${t.subject_id}`"
                    :label="`${t.grade}${t.class_name} - ${t.subject_name} (${t.teacher_name || '无教师'})`"
                    :value="`${t.grade}|${t.class_name}|${t.subject_id}`" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="绑定场地类型" required>
                <el-select v-model="constraintForm.config.venue_type" placeholder="选择或输入场地类型" allow-create filterable
                  style="width: 100%">
                  <el-option v-for="t in venueTypes" :key="t" :label="t" :value="t" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'consecutive_settings'">
          <el-row :gutter="15">
            <el-col :span="8">
              <el-form-item label="设置科目" required>
                <el-select v-model="constraintForm.config.subject_id" placeholder="选择科目" style="width: 100%">
                  <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="年级筛选 (选填)">
                <el-select v-model="constraintForm.config.grade" placeholder="不限年级" clearable style="width: 100%">
                  <el-option v-for="g in gradesConfig" :key="g.name" :label="g.name" :value="g.name" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="连堂组数/周" required>
                <el-input-number v-model="constraintForm.config.consecutive_count" :min="1" :max="5"
                  style="width: 100%" />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="连堂块大小" required>
                <el-select v-model="constraintForm.config.block_size" placeholder="选择连堂节数" style="width: 100%">
                  <el-option label="2 节连堂" :value="2" />
                  <el-option label="3 节连堂" :value="3" />
                  <el-option label="4 节连堂" :value="4" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="偏好开始节次 (多选)">
                <el-select v-model="constraintForm.config.preferred_slots" multiple placeholder="不限"
                  style="width: 100%">
                  <el-option label="第 1 节" :value="1" />
                  <el-option label="第 2 节" :value="2" />
                  <el-option label="第 3 节" :value="3" />
                  <el-option label="第 4 节" :value="4" />
                  <el-option label="第 5 节" :value="5" />
                  <el-option label="第 6 节" :value="6" />
                  <el-option label="第 7 节" :value="7" />
                  <el-option label="第 8 节" :value="8" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'subject_preset_slots'">
          <el-row :gutter="15">
            <el-col :span="6">
              <el-form-item label="目标学科" required>
                <el-select v-model="constraintForm.config.subject_id" placeholder="选择科目" style="width: 100%">
                  <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item label="限制范围" required>
                <el-select v-model="constraintForm.config.scope" placeholder="选择范围" style="width: 100%"
                  @change="handlePresetScopeChange">
                  <el-option label="全校相同科目" value="school" />
                  <el-option label="某班型相同科目" value="class_type" />
                  <el-option label="某年级相同科目" value="grade" />
                  <el-option label="特定班级科目" value="class" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6" v-if="constraintForm.config.scope === 'grade' || constraintForm.config.scope === 'class'">
              <el-form-item label="选择年级" required>
                <el-select v-model="constraintForm.config.grade" placeholder="选择年级" style="width: 100%"
                  @change="handlePresetGradeChange">
                  <el-option v-for="g in gradesConfig" :key="g.name" :label="g.name" :value="g.name" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6" v-if="constraintForm.config.scope === 'class'">
              <el-form-item label="选择班级" required>
                <el-select v-model="constraintForm.config.class_name" placeholder="选择班级" style="width: 100%">
                  <el-option v-for="c in presetClassOptions" :key="c" :label="c" :value="c" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6" v-if="constraintForm.config.scope === 'class_type'">
              <el-form-item label="班级类型" required>
                <el-select v-model="constraintForm.config.class_type" placeholder="选择班级类型" style="width: 100%">
                  <el-option label="普通班" value="普通班" />
                  <el-option label="艺术班" value="艺术班" />
                  <el-option label="体育班" value="体育班" />
                  <el-option label="实验班" value="实验班" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- Brush Selection Palette -->
          <div
            style="margin-bottom: 15px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; background: #f8fafc; padding: 12px; border-radius: 8px; border: 1px solid #e2e8f0;">
            <span style="font-weight: 600; font-size: 14px; color: #334155;">涂鸦刷子类型：</span>
            <el-radio-group v-model="activePresetBrush" size="small">
              <el-radio-button label="forbidden">🔴 禁止排 (硬约束)</el-radio-button>
              <el-radio-button label="mandatory">🟢 必须排 (硬约束)</el-radio-button>
              <el-radio-button label="preferred">🔵 尽量排 (软约束)</el-radio-button>
              <el-radio-button label="dispreferred">🟡 尽量不排 (软约束)</el-radio-button>
              <el-radio-button label="normal">⚪ 清除要求(橡皮擦)</el-radio-button>
            </el-radio-group>
            <div style="font-size: 12px; color: #64748b; margin-top: 4px; width: 100%;">
              选择刷子类型后，在下方课表单元格上点击即可应用该预设。
            </div>
          </div>

          <!-- Interactive grid -->
          <div class="preset-slots-grid-wrapper" style="margin-bottom: 20px;">
            <table
              style="width: 100%; border-collapse: collapse; text-align: center; font-size: 12px; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
              <thead>
                <tr style="background: #f1f5f9; border-bottom: 1px solid #e2e8f0;">
                  <th
                    style="padding: 10px; font-weight: 600; color: #475569; border-right: 1px solid #e2e8f0; width: 100px;">
                    节次</th>
                  <th v-for="d in daysPerWeek" :key="d"
                    style="padding: 10px; font-weight: 600; color: #475569; border-right: 1px solid #e2e8f0;">
                    {{ getDayName(d) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in totalPeriodsCount" :key="p" style="border-bottom: 1px solid #e2e8f0;">
                  <td
                    style="padding: 10px; font-weight: 600; color: #475569; background: #f8fafc; border-right: 1px solid #e2e8f0;">
                    第{{ p }}节
                  </td>
                  <td v-for="d in daysPerWeek" :key="d"
                    style="padding: 8px 4px; border-right: 1px solid #e2e8f0; cursor: pointer; transition: all 0.2s; min-height: 35px; user-select: none;"
                    :style="getPresetSlotStyle(d, p)" @click="clickPresetSlot(d, p)" @dblclick="clearPresetSlot(d, p)">
                    <div style="font-weight: bold; font-size: 11px;">
                      {{ getPresetSlotText(d, p) }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'teacher_preset_slots'">
          <el-row :gutter="15">
            <el-col :span="24">
              <el-form-item label="目标教师 (可多选)" required>
                <el-select v-model="constraintForm.config.teacher_ids" multiple filterable placeholder="选择要设置的教师，支持多选"
                  style="width: 100%">
                  <el-option v-for="t in teacherList" :key="t.id" :label="t.name" :value="t.id">
                    <span style="float: left">{{ t.name }}</span>
                    <span style="float: right; color: #8492a6; font-size: 13px">{{ getTeacherShortName(t.name) }}</span>
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- Brush Selection Palette -->
          <div
            style="margin-bottom: 15px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; background: #f8fafc; padding: 12px; border-radius: 8px; border: 1px solid #e2e8f0;">
            <span style="font-weight: 600; font-size: 14px; color: #334155;">涂鸦刷子类型：</span>
            <el-radio-group v-model="activePresetBrush" size="small">
              <el-radio-button label="forbidden">🔴 禁止排 (硬约束)</el-radio-button>
              <el-radio-button label="mandatory">🟢 必须排 (硬约束)</el-radio-button>
              <el-radio-button label="preferred">🔵 尽量排 (软约束)</el-radio-button>
              <el-radio-button label="dispreferred">🟡 尽量不排 (软约束)</el-radio-button>
              <el-radio-button label="normal">⚪ 清除要求(橡皮擦)</el-radio-button>
            </el-radio-group>
            <div style="font-size: 12px; color: #64748b; margin-top: 4px; width: 100%;">
              选择刷子类型后，在下方课表单元格上点击即可为选择的教师应用该预设。
            </div>
          </div>

          <!-- Interactive grid -->
          <div class="preset-slots-grid-wrapper" style="margin-bottom: 20px;">
            <table
              style="width: 100%; border-collapse: collapse; text-align: center; font-size: 12px; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
              <thead>
                <tr style="background: #f1f5f9; border-bottom: 1px solid #e2e8f0;">
                  <th
                    style="padding: 10px; font-weight: 600; color: #475569; border-right: 1px solid #e2e8f0; width: 100px;">
                    节次</th>
                  <th v-for="d in daysPerWeek" :key="d"
                    style="padding: 10px; font-weight: 600; color: #475569; border-right: 1px solid #e2e8f0;">
                    {{ getDayName(d) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in totalPeriodsCount" :key="p" style="border-bottom: 1px solid #e2e8f0;">
                  <td
                    style="padding: 10px; font-weight: 600; color: #475569; background: #f8fafc; border-right: 1px solid #e2e8f0;">
                    第{{ p }}节
                  </td>
                  <td v-for="d in daysPerWeek" :key="d"
                    style="padding: 8px 4px; border-right: 1px solid #e2e8f0; cursor: pointer; transition: all 0.2s; min-height: 35px; user-select: none;"
                    :style="getPresetSlotStyle(d, p)" @click="clickPresetSlot(d, p)" @dblclick="clearPresetSlot(d, p)">
                    <div style="font-weight: bold; font-size: 11px;">
                      {{ getPresetSlotText(d, p) }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'subject_consecutive_preference'">
          <el-form-item label="选择科目" required>
            <el-select v-model="constraintForm.config.subject_id" placeholder="选择科目" style="width: 100%">
              <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="连堂偏好" required>
            <el-radio-group v-model="constraintForm.config.preference_type">
              <el-radio label="consecutive">尽量连堂</el-radio>
              <el-radio label="split">尽量分散（不连堂）</el-radio>
            </el-radio-group>
          </el-form-item>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'teacher_schedule_concentration'">
          <el-form-item label="选择教师范围" required>
            <el-select v-model="constraintForm.config.teacher_id" placeholder="选择教师范围" filterable :filter-method="filterTeachersPinyinForm" style="width: 100%">
              <el-option label="全体教师" value="all" />
              <el-option v-for="t in filteredTeachersForm" :key="t.id" :label="t.name" :value="t.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="排程集中度" required>
            <el-radio-group v-model="constraintForm.config.concentration">
              <el-radio label="concentrated">尽量集中（减少空堂，利于备课）</el-radio>
              <el-radio label="dispersed">尽量分散（防止一天排课过多，利于休息）</el-radio>
            </el-radio-group>
          </el-form-item>
        </template>

        <template v-else-if="constraintForm.constraint_type === 'teacher_noon_break_protection'">
          <el-form-item label="选择教师范围" required>
            <el-select v-model="constraintForm.config.teacher_id" placeholder="选择教师范围" filterable :filter-method="filterTeachersPinyinForm" style="width: 100%">
              <el-option label="全体教师" value="all" />
              <el-option v-for="t in filteredTeachersForm" :key="t.id" :label="t.name" :value="t.id" />
            </el-select>
          </el-form-item>
          <div style="font-size: 13px; color: #64748b; margin-bottom: 12px; line-height: 1.5;">
            💡 教师午休时间保护将尽量避免为同一位教师安排跨中午连堂（如上午最后一节课与下午第一节课连续上课）。
          </div>
        </template>

        <el-form-item label="强制程度">
          <el-radio-group v-model="constraintForm.is_hard">
            <el-radio :label="true">强约束 (必须满足，无解时报错)</el-radio>
            <el-radio :label="false">软约束 (尽量满足，有得分权重)</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="权重系数 (软约束有效)" v-if="!constraintForm.is_hard">
          <el-slider v-model="constraintForm.weight" :min="1" :max="10" show-input />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="constraintDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitConstraintForm">保存并添加</el-button>
      </template>
    </el-dialog>

    <!-- Bulk forbidden slots dialog -->
    <el-dialog v-model="bulkForbiddenDialogVisible" title="批量设置禁排时段" width="600px" destroy-on-close>
      <el-form :model="bulkForbiddenForm" label-position="top">
        <el-alert type="info" :closable="false" show-icon style="margin-bottom: 16px;"
          title="一次性为多个星期、多个课节生成禁排规则，适合教研、年级会议等固定时段。" />

        <el-form-item label="对谁生效" required>
          <el-radio-group v-model="bulkForbiddenForm.target_type">
            <el-radio label="subject">科目</el-radio>
            <el-radio label="teacher">教师</el-radio>
            <el-radio label="class">班级</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="选择对象" required>
          <el-select v-if="bulkForbiddenForm.target_type === 'subject'" v-model="bulkForbiddenForm.target_id"
            placeholder="选择科目" filterable style="width: 100%;">
            <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.id" />
          </el-select>
          <el-select v-else-if="bulkForbiddenForm.target_type === 'teacher'" v-model="bulkForbiddenForm.target_id"
            placeholder="选择教师" filterable :filter-method="filterTeachersPinyinForm" style="width: 100%;">
            <el-option v-for="t in teacherList" :key="t.id" :label="t.name" :value="t.id" />
          </el-select>
          <el-select v-else v-model="bulkForbiddenForm.target_id" placeholder="选择班级" filterable style="width: 100%;">
            <el-option v-for="c in classOptions" :key="c" :label="c" :value="c" />
          </el-select>
        </el-form-item>

        <el-form-item label="星期" required>
          <el-checkbox-group v-model="bulkForbiddenForm.days">
            <el-checkbox v-for="d in daysPerWeek" :key="d" :label="d">{{ getDayName(d) }}</el-checkbox>
          </el-checkbox-group>
        </el-form-item>

        <el-form-item label="时段" required>
          <el-radio-group v-model="bulkForbiddenForm.session">
            <el-radio-button label="morning">上午</el-radio-button>
            <el-radio-button label="afternoon">下午</el-radio-button>
            <el-radio-button label="evening">晚上</el-radio-button>
            <el-radio-button label="custom">自定义</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item v-if="bulkForbiddenForm.session === 'custom'" label="课节范围" required>
          <div style="display: flex; align-items: center; gap: 8px;">
            <el-input-number v-model="bulkForbiddenForm.start_period" :min="1" :max="totalDailyPeriods" />
            <span>至</span>
            <el-input-number v-model="bulkForbiddenForm.end_period" :min="1" :max="totalDailyPeriods" />
            <span style="font-size: 12px; color: #64748b;">节</span>
          </div>
        </el-form-item>

        <div style="font-size: 13px; color: #64748b; background: #f8fafc; padding: 10px 12px; border-radius: 6px;">
          将生成 {{ bulkForbiddenSlotPreview.length }} 条禁排规则（{{ bulkForbiddenSlotPreviewText }}）。
        </div>
      </el-form>
      <template #footer>
        <el-button @click="bulkForbiddenDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="bulkForbiddenSubmitting" @click="submitBulkForbiddenDialog">确定批量设置</el-button>
      </template>
    </el-dialog>

    <!-- Create Backup Dialog -->
    <el-dialog v-model="backupDialogVisible" title="创建排课快照版本镜像" width="400px">
      <el-form label-position="top">
        <el-form-item label="快照版本名称" required>
          <el-input v-model="backupName" placeholder="例如：手调微调第一版/排课初稿" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="backupDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitCreateBackup">确认拍照保存</el-button>
      </template>
    </el-dialog>

    <!-- Excel import dialog -->
    <el-dialog v-model="importDialogVisible" title="Excel 批量导入任课计划" width="450px">
      <div class="excel-import-area">
        <el-upload drag action="" :auto-upload="false" :on-change="handleExcelChange" :file-list="fileList" :limit="1">
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">
            将 Excel 文件拖拽到此处，或<em>点击上传</em>
          </div>
        </el-upload>
        <div class="tip">
          请先下载模板，再把内容按模板整理后导入。表里的学科或教师如果系统里还没有，会自动补上。
        </div>
      </div>
      <template #footer>
        <el-button @click="importDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="importing" @click="submitExcelImport">确定导入</el-button>
      </template>
    </el-dialog>

    <!-- Paste Import Dialog -->
    <el-dialog v-model="pasteImportDialogVisible" title="剪贴板快速导入任课计划" width="700px">
      <div style="margin-bottom: 12px; font-size: 13px; color: #606266; line-height: 1.5;">
        可以直接从 Excel 或 WPS 复制多行内容到这里。建议列顺序为 <strong>年级、班级、科目、教师、周课时</strong>，其余内容可不填。
      </div>
      <el-input v-model="pastedText" type="textarea" :rows="10" placeholder="在这里粘贴从 Excel 复制的数据..."
        @input="parsePastedText" />
      <div v-if="parsedTasks.length > 0" style="margin-top: 15px;">
        <div style="font-weight: bold; margin-bottom: 8px; font-size: 14px;">导入预览（共 {{ parsedTasks.length }} 条）</div>
        <el-table :data="parsedTasks" height="200" stripe size="small" style="width: 100%;">
          <el-table-column prop="grade" label="年级" width="80" />
          <el-table-column prop="class_name" label="班级" width="80" />
          <el-table-column prop="subject_name" label="科目" width="100" />
          <el-table-column prop="teacher_name" label="任课教师" width="100" />
          <el-table-column prop="weekly_periods" label="周课时" width="80" />
          <el-table-column prop="consecutive_periods" label="连堂" width="80" />
          <el-table-column prop="room_name" label="专用教室" />
          <el-table-column prop="is_combined_desc" label="是否合班" width="80" />
          <el-table-column prop="combined_group_id" label="合班编码" width="100" />
        </el-table>
      </div>
      <template #footer>
        <el-button @click="pasteImportDialogVisible = false">取消</el-button>
        <el-button type="primary" :disabled="parsedTasks.length === 0" :loading="pasteImporting"
          @click="submitPasteImport">确认导入</el-button>
      </template>
    </el-dialog>

    <!-- Online Table Edit Dialog -->
    <el-dialog v-model="gridEditDialogVisible" title="表格填写任课安排" width="90%" style="max-width: 1200px;"
      :before-close="handleGridEditClose">
      <div style="margin-bottom: 15px; display: flex; justify-content: space-between; align-items: center;">
        <div style="font-size: 13px; color: #606266;">
          可以直接在表格里改内容或新增行。学科、教师和教室如果还没有，保存时会自动补上。
        </div>
        <div style="display: flex; gap: 10px;">
          <el-button type="success" plain @click="addGridRow">
            <el-icon>
              <Plus />
            </el-icon> 添加一行
          </el-button>
          <el-button type="danger" plain @click="clearGridRows">
            <el-icon>
              <Delete />
            </el-icon> 清空当前内容
          </el-button>
        </div>
      </div>

      <el-table :data="gridRows" border stripe style="width: 100%;" max-height="450px">
        <el-table-column label="序号" type="index" width="60" align="center" />

        <el-table-column label="年级*" min-width="120">
          <template #default="{ row }">
            <el-input v-model="row.grade" placeholder="如：高一" size="small" />
          </template>
        </el-table-column>

        <el-table-column label="班级*" min-width="120">
          <template #default="{ row }">
            <el-input v-model="row.class_name" placeholder="如：1班" size="small" />
          </template>
        </el-table-column>

        <el-table-column label="科目*" min-width="140">
          <template #default="{ row }">
            <el-select v-model="row.subject_name" filterable allow-create default-first-option placeholder="选择或输入科目"
              size="small" style="width: 100%">
              <el-option v-for="s in subjectList" :key="s.id" :label="s.name" :value="s.name" />
            </el-select>
          </template>
        </el-table-column>

        <el-table-column label="任课教师*" min-width="150">
          <template #default="{ row }">
            <el-select v-model="row.teacher_name" filterable allow-create default-first-option placeholder="选择或输入教师"
              size="small" style="width: 100%">
              <el-option v-for="t in teacherList" :key="t.id" :label="t.name" :value="t.name" />
            </el-select>
          </template>
        </el-table-column>

        <el-table-column label="周课时*" width="120">
          <template #default="{ row }">
            <el-input-number v-model="row.weekly_periods" :min="0.5" :max="15" :step="0.5" size="small"
              style="width: 100%" />
          </template>
        </el-table-column>

        <el-table-column label="连堂" width="100">
          <template #default="{ row }">
            <el-input-number v-model="row.consecutive_periods" :min="1" :max="3" size="small" style="width: 100%" />
          </template>
        </el-table-column>

        <el-table-column label="专用教室" min-width="140">
          <template #default="{ row }">
            <el-select v-model="row.room_name" filterable allow-create default-first-option clearable
              placeholder="默认本班教室" size="small" style="width: 100%">
              <el-option v-for="r in roomOptions" :key="r.id" :label="r.name" :value="r.name" />
            </el-select>
          </template>
        </el-table-column>

        <el-table-column label="是否合班" width="90" align="center">
          <template #default="{ row }">
            <el-checkbox v-model="row.is_combined" />
          </template>
        </el-table-column>

        <el-table-column label="合班编码" min-width="130">
          <template #default="{ row }">
            <el-input v-model="row.combined_group_id" :disabled="!row.is_combined" placeholder="合班组相同编码" size="small" />
          </template>
        </el-table-column>

        <el-table-column label="操作" width="80" align="center" fixed="right">
          <template #default="{ $index }">
            <el-button type="danger" link size="small" @click="removeGridRow($index)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <template #footer>
        <el-button @click="gridEditDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="gridSaving" @click="submitGridEdit">保存全部任课安排</el-button>
      </template>
    </el-dialog>

    <!-- Conflict warning drawer -->
    <el-drawer v-model="conflictDrawerVisible" title="课表检查结果" size="450px">
      <div class="conflict-drawer-content">
        <el-alert v-if="conflicts.length === 0" title="当前没有发现教师、班级或教室冲突。" type="success" show-icon :closable="false" />
        <template v-else>
          <div class="conflict-tip-top">发现 {{ conflicts.length }} 处需要处理的问题：</div>

          <div v-for="(c, idx) in conflicts" :key="idx" class="conflict-item-card">
            <div class="conflict-title">
              <el-tag :type="c.type === 'teacher' ? 'danger' : 'warning'" size="small">
                {{ c.type === 'teacher' ? '教师冲突' : (c.type === 'class' ? '班级冲突' : '场地冲突') }}
              </el-tag>
              <span class="day-p">周{{ c.day_of_week }} 第{{ c.period_index }}节 ({{ c.week_type === 'odd' ? '单周' :
                (c.week_type === 'even' ? '双周' : '单双周') }})</span>
            </div>
            <div class="conflict-desc">{{ c.description }}</div>
          </div>
        </template>
      </div>
    </el-drawer>

    <!-- Subject Management Dialog -->
    <el-dialog v-model="teacherManagerVisible" title="教师设置" width="780px">
      <div style="margin-bottom: 15px;">
        <el-form :inline="true" :model="newTeacherForm" @submit.prevent="createScheduleTeacher">
          <el-form-item label="教师姓名" required style="margin-bottom: 10px;">
            <el-input v-model="newTeacherForm.name" placeholder="如：张三" style="width: 140px;" />
          </el-form-item>
          <el-form-item label="工号" style="margin-bottom: 0;">
            <el-input v-model="newTeacherForm.employee_id" placeholder="可选" style="width: 120px;" />
          </el-form-item>
          <el-form-item label="任教学科" style="margin-bottom: 0;">
            <el-select v-model="newTeacherForm.subject_ids" multiple collapse-tags placeholder="选择学科"
              style="width: 180px;">
              <el-option v-for="sub in globalSubjects" :key="sub.id" :label="sub.name" :value="sub.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="任教年级" style="margin-bottom: 0;">
            <el-select v-model="newTeacherForm.grades" multiple collapse-tags placeholder="选择年级" style="width: 180px;">
              <el-option v-for="grade in gradeOptions" :key="grade" :label="grade" :value="grade" />
            </el-select>
          </el-form-item>
          <el-form-item style="margin-bottom: 0;">
            <el-button-group>
              <el-button type="primary" @click="createScheduleTeacher">添加教师</el-button>
              <el-button type="success" plain :icon="Upload" @click="showTeacherBatchImport">批量导入</el-button>
            </el-button-group>
          </el-form-item>
        </el-form>
      </div>

      <el-table :data="teacherList" border size="small" style="width: 100%" max-height="360">
        <el-table-column prop="name" label="教师姓名" min-width="120" />
        <el-table-column prop="employee_id" label="工号" width="120">
          <template #default="{ row }">
            <span>{{ row.employee_id || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="任教学科" min-width="180">
          <template #default="{ row }">
            <span>{{(row.subjects || []).map(item => item.name).join('、') || '-'}}</span>
          </template>
        </el-table-column>
        <el-table-column label="任教年级" min-width="160">
          <template #default="{ row }">
            <span>{{ (row.grades || []).join('、') || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="140" align="center">
          <template #default="{ row }">
            <el-button-group>
              <el-button size="small" type="primary" plain :icon="Edit"
                @click="openTeacherDetailDialog(row)">编辑</el-button>
              <el-button size="small" type="danger" plain :icon="Delete" @click="deleteTeacher(row.id)">删除</el-button>
            </el-button-group>
          </template>
        </el-table-column>
      </el-table>

      <template #footer>
        <el-button @click="teacherManagerVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- Detailed Teacher Settings Dialog -->
    <el-dialog v-model="teacherDetailDialogVisible" title="教师信息编辑" width="540px" append-to-body destroy-on-close>
      <el-form :model="teacherDetailForm" label-width="100px" size="default" style="padding: 10px 20px 0 10px;">
        <el-form-item label="教师姓名" required>
          <el-input v-model="teacherDetailForm.name" placeholder="例如：张三" />
        </el-form-item>
        <el-form-item label="工号">
          <el-input v-model="teacherDetailForm.employee_id" placeholder="可选，例如：T001" />
        </el-form-item>
        <el-form-item label="任教学科">
          <el-select v-model="teacherDetailForm.subject_ids" multiple placeholder="选择教师所教学科" style="width: 100%;">
            <el-option v-for="sub in globalSubjects" :key="sub.id" :label="sub.name" :value="sub.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="任教年级">
          <el-select v-model="teacherDetailForm.grades" multiple placeholder="选择教师所教年级" style="width: 100%;">
            <el-option v-for="grade in gradeOptions" :key="grade" :label="grade" :value="grade" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="teacherDetailDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveTeacherDetail">保存</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="subjectManagerVisible" title="科目/学科库管理 (独立排课科目)" width="680px">
      <div
        style="margin-bottom: 15px; background: #f8fafc; padding: 12px; border-radius: 8px; border: 1px solid #f1f5f9;">
        <el-form :inline="true" :model="newSubjectForm" @submit.prevent="createSubject"
          style="margin-bottom: 0; display: flex; align-items: center; gap: 10px;">
          <el-form-item label="快速新增科目" required style="margin-bottom: 0; margin-right: 0;">
            <el-input v-model="newSubjectForm.name" placeholder="如：心理健康" style="width: 200px;" />
          </el-form-item>
          <el-form-item style="margin-bottom: 0; margin-right: 0;">
            <el-button type="primary" @click="createSubject">添加</el-button>
          </el-form-item>
          <span style="font-size: 12px; color: #64748b; margin-left: auto;">添加后点击“编辑”可配置连堂、限制、颜色等详情。</span>
        </el-form>
      </div>

      <el-table :data="subjectList" border size="small" style="width: 100%" max-height="380">
        <el-table-column prop="name" label="科目名称">
          <template #default="{ row }">
            <div style="display: flex; align-items: center; gap: 8px;">
              <span v-if="row.color"
                :style="{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: row.color, border: '1px solid #cbd5e1' }"></span>
              <span v-else
                style="display: inline-block; width: 12px; height: 12px; borderRadius: '50%', backgroundColor: '#e2e8f0', border: '1px solid #cbd5e1'"></span>
              <span style="font-weight: 600; color: #334155;">{{ row.name }}</span>
              <el-tag v-if="row.short_name" size="small" type="info" style="font-family: monospace;">{{ row.short_name
                }}</el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="subject_type" label="所属类型" width="120" align="center">
          <template #default="{ row }">
            <el-tag size="small" :type="row.subject_type === '文化课' ? 'primary' : 'success'">{{ row.subject_type || '文化课'
              }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="基础规则" min-width="160">
          <template #default="{ row }">
            <div style="display: flex; flex-wrap: wrap; gap: 4px;">
              <el-tag v-if="row.requires_teacher !== false" size="small" type="info" effect="plain">需教师</el-tag>
              <el-tag v-if="row.requires_room" size="small" type="warning" effect="plain">需场地</el-tag>
              <el-tag v-if="row.allows_consecutive !== false" size="small" type="success" effect="plain">连堂</el-tag>
              <el-tag v-if="row.allows_multiple_daily" size="small" type="danger" effect="plain">一天多次</el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="140" align="center">
          <template #default="{ row }">
            <el-button-group>
              <el-button size="small" type="primary" plain :icon="Edit"
                @click="openSubjectDetailDialog(row)">编辑</el-button>
              <el-button size="small" type="danger" plain :icon="Delete" @click="deleteSubject(row.id)">删除</el-button>
            </el-button-group>
          </template>
        </el-table-column>
      </el-table>

      <template #footer>
        <el-button @click="subjectManagerVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- Detailed Subject Settings Dialog -->
    <el-dialog v-model="subjectDetailDialogVisible" title="学科高级配置与规则" width="580px" append-to-body destroy-on-close>
      <el-form :model="subjectDetailForm" label-width="140px" size="default" style="padding: 10px 20px 0 10px;">
        <el-form-item label="学科名称" required>
          <el-input v-model="subjectDetailForm.name" placeholder="例如：语文" />
        </el-form-item>

        <el-form-item label="简称/缩写">
          <el-input v-model="subjectDetailForm.short_name" placeholder="例如：语 / CHI" />
        </el-form-item>

        <el-form-item label="代表颜色">
          <div style="display: flex; gap: 10px; align-items: center;">
            <el-color-picker v-model="subjectDetailForm.color"
              :predefine="['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280']" />
            <el-input v-model="subjectDetailForm.color" placeholder="#4f46e5" style="width: 120px;" />
          </div>
        </el-form-item>

        <el-form-item label="所属类型">
          <el-select v-model="subjectDetailForm.subject_type" style="width: 100%;">
            <el-option label="文化课" value="文化课" />
            <el-option label="体育艺术" value="体育艺术" />
            <el-option label="实验课" value="实验课" />
            <el-option label="校本课" value="校本课" />
            <el-option label="自习" value="自习" />
            <el-option label="班会" value="班会" />
            <el-option label="活动课" value="活动课" />
          </el-select>
        </el-form-item>

        <div
          style="margin-top: 15px; margin-bottom: 15px; border-bottom: 1px solid #f1f5f9; padding-bottom: 8px; font-weight: 700; color: #475569; font-size: 13px;">
          基本排课规则与限制
        </div>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="需要任课教师" label-width="120px">
              <el-switch v-model="subjectDetailForm.requires_teacher" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="需要固定场地" label-width="120px">
              <el-switch v-model="subjectDetailForm.requires_room" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="允许安排连堂课" label-width="120px">
              <el-switch v-model="subjectDetailForm.allows_consecutive" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="一天可安排多次" label-width="120px">
              <el-switch v-model="subjectDetailForm.allows_multiple_daily" />
            </el-form-item>
          </el-col>
        </el-row>

        <div
          style="margin-top: 15px; margin-bottom: 15px; border-bottom: 1px solid #f1f5f9; padding-bottom: 8px; font-weight: 700; color: #475569; font-size: 13px;">
          允许上课时段限制
        </div>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="上午可排" label-width="90px">
              <el-switch v-model="subjectDetailForm.allows_morning" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="下午可排" label-width="90px">
              <el-switch v-model="subjectDetailForm.allows_afternoon" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="最后一节可排" label-width="100px">
              <el-switch v-model="subjectDetailForm.allows_last_period" />
            </el-form-item>
          </el-col>
        </el-row>

        <div
          style="margin-top: 15px; margin-bottom: 15px; border-bottom: 1px solid #f1f5f9; padding-bottom: 8px; font-weight: 700; color: #475569; font-size: 13px;">
          排课算法与统计参数
        </div>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="计入教师工作量" label-width="110px">
              <el-switch v-model="subjectDetailForm.include_in_workload" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="启用同科限制" label-width="110px">
              <el-switch v-model="subjectDetailForm.subject_limit_rules" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="考试科目平衡" label-width="110px">
              <el-switch v-model="subjectDetailForm.exam_balance" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <el-button @click="subjectDetailDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="savingSubjectDetail" @click="saveSubjectDetail">保存修改</el-button>
      </template>
    </el-dialog>


    <el-dialog v-model="freeLabelDialogVisible" title="修改免任课名称" width="420px">
      <el-form label-position="top">
        <el-form-item label="说明文字">
          <el-input v-model="freeLabelEditText" maxlength="30" show-word-limit placeholder="例如：年级讲座、班会、社团活动" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="freeLabelDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="freeLabelSaving" @click="saveFreeLabelEdit">保存</el-button>
      </template>
    </el-dialog>

    <!-- Public Period Edit Dialog -->
    <el-dialog v-model="publicPeriodDialogVisible" :title="publicPeriodForm.id ? '编辑公共时段' : '新增公共时段'" width="550px"
      destroy-on-close>
      <el-form :model="publicPeriodForm" label-width="120px" size="default">
        <el-form-item label="时段名称" required>
          <el-input v-model="publicPeriodForm.name" placeholder="例如：早读、课间操、午休" />
        </el-form-item>

        <el-form-item label="时段类型">
          <el-radio-group v-model="publicPeriodForm.type">
            <el-radio label="display">展示用</el-radio>
            <el-radio label="activity">固定活动</el-radio>
            <el-radio label="forbidden">禁排时段</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="时段位置" required>
          <div style="display: flex; gap: 10px; align-items: center; width: 100%;">
            <el-select v-model="publicPeriodForm.position" style="width: 180px;">
              <el-option label="第一节前" value="before_period_1" />
              <el-option label="第几节后" value="after_period" />
              <el-option label="上午结束后" value="morning_end" />
              <el-option label="下午结束后" value="afternoon_end" />
              <el-option label="晚上结束后" value="evening_end" />
            </el-select>
            <el-input-number v-if="publicPeriodForm.position === 'after_period'"
              v-model="publicPeriodForm.position_value" :min="1" :max="12" style="width: 120px;" />
          </div>
        </el-form-item>

        <el-form-item label="适用范围">
          <div style="display: flex; flex-direction: column; gap: 8px; width: 100%;">
            <el-radio-group v-model="publicPeriodForm.scope">
              <el-radio label="all">全校</el-radio>
              <el-radio label="grade">特定年级</el-radio>
            </el-radio-group>

            <el-select v-if="publicPeriodForm.scope === 'grade'" v-model="publicPeriodForm.scope_grade"
              placeholder="选择年级" style="width: 200px;">
              <el-option v-for="grade in gradesConfig" :key="grade.name" :label="grade.name" :value="grade.name" />
            </el-select>
          </div>
        </el-form-item>

        <el-form-item label="时间设定">
          <div style="display: flex; gap: 10px; align-items: center;">
            <el-input v-model="publicPeriodForm.start" placeholder="10:00" style="width: 100px;" />
            <span>至</span>
            <el-input v-model="publicPeriodForm.end" placeholder="10:30" style="width: 100px;" />
          </div>
        </el-form-item>

        <el-form-item label="显示在课表">
          <el-switch v-model="publicPeriodForm.show_in_schedule" />
        </el-form-item>

        <el-form-item label="是否占用教师">
          <el-switch v-model="publicPeriodForm.occupies_teacher" />
        </el-form-item>

        <el-form-item label="是否占用场地">
          <el-switch v-model="publicPeriodForm.occupies_room" />
        </el-form-item>

        <el-form-item label="影响自动排课">
          <el-switch v-model="publicPeriodForm.affects_scheduling" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="publicPeriodDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="savePublicPeriodForm">确定</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- Venue dialog -->
    <el-dialog v-model="venueDialogVisible" :title="venueForm.id ? '编辑专用场室' : '添加专用场室'" width="450px">
      <el-form :model="venueForm" label-position="top">
        <el-form-item label="场室名称" required>
          <el-input v-model="venueForm.name" placeholder="例如：机房一、化学实验室" />
        </el-form-item>
        <el-form-item label="场室类型 / 标签" required>
          <el-select v-model="venueForm.type" placeholder="选择或直接输入类型标签" allow-create filterable style="width: 100%">
            <el-option v-for="t in venueTypes" :key="t" :label="t" :value="t" />
          </el-select>
          <div style="font-size: 11px; color: #94a3b8; margin-top: 4px;">注：相同类型的场室会共享其容纳上限。例如，有两个类型为“机房”且容量为 1
            的场室，则同一节最多排 2
            个需要机房的班级。</div>
        </el-form-item>
        <el-form-item label="容纳班级上限" required>
          <el-input-number v-model="venueForm.capacity" :min="1" :max="10" style="width: 100%" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="venueDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveVenueForm">确定</el-button>
      </template>
    </el-dialog>

    <!-- Venue Forbidden Slots Dialog -->
    <el-dialog v-model="venueForbiddenDialogVisible" title="设置场室禁排时段" width="700px">
      <div v-if="activeVenueForForbidden" style="margin-bottom: 15px;">
        <span style="font-weight: bold; font-size: 15px; color: #1e293b;">
          当前场室：{{ activeVenueForForbidden.name }} ({{ activeVenueForForbidden.type }})
        </span>
        <p style="font-size: 12px; color: #64748b; margin-top: 4px; margin-bottom: 0;">
          点击网格方块切换该时段是否禁止排课。红色格子表示禁排。
        </p>
      </div>

      <div class="excel-table-wrapper" style="margin-top: 10px;">
        <table class="excel-table" style="text-align: center;">
          <thead>
            <tr>
              <th style="width: 100px;">节次</th>
              <th v-for="d in 7" :key="d">{{ getDayName(d) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in 8" :key="p">
              <td><strong>第 {{ p }} 节</strong></td>
              <td v-for="d in 7" :key="d" :style="{
                background: isVenueSlotForbidden(activeVenueForForbidden, d, p) ? '#fecaca' : '#ffffff',
                cursor: 'pointer',
                padding: '12px',
                border: '1px solid #cbd5e1',
                transition: 'all 0.2s'
              }" @click="toggleVenueSlotForbidden(activeVenueForForbidden, d, p)">
                <span :style="{
                  color: isVenueSlotForbidden(activeVenueForForbidden, d, p) ? '#dc2626' : '#94a3b8',
                  fontSize: '11px',
                  fontWeight: isVenueSlotForbidden(activeVenueForForbidden, d, p) ? 'bold' : 'normal'
                }">
                  {{ isVenueSlotForbidden(activeVenueForForbidden, d, p) ? '禁排' : '可用' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <template #footer>
        <el-button @click="venueForbiddenDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveVenueForbiddenSlots">确定</el-button>
      </template>
    </el-dialog>

    <!-- Batch Import Teachers Dialog -->
    <el-dialog v-model="teacherBatchImportVisible" title="批量导入可用教师" width="850px">
      <el-tabs v-model="teacherImportTab" class="import-tabs">
        <el-tab-pane label="Excel 导入" name="excel">
          <div class="excel-import-section" style="padding: 10px 0;">
            <div class="import-instructions"
              style="background-color: #f8fafc; padding: 16px; border-radius: 8px; font-size: 14px; color: #475569; margin-bottom: 20px; border: 1px solid #f1f5f9;">
              <p style="margin: 0 0 8px 0; font-weight: bold; color: #1e293b;">操作指引：</p>
              <p style="margin: 4px 0;">1. 请下载标准的 Excel 模板，按照模板列名及格式填写教师信息；</p>
              <p style="margin: 4px 0;">2. <strong>姓名</strong> 和 <strong>学科</strong> 为必填项，若系统中不存在该学科，导入时将自动创建；</p>
              <p style="margin: 4px 0;">3. <strong>任教年级</strong> 若有多个请以逗号、空格或顿号分隔。</p>
              <el-button type="primary" plain size="small" :icon="Download" @click="downloadTeacherTemplate"
                style="margin-top: 12px;">
                下载 Excel 模板
              </el-button>
            </div>

            <el-upload ref="teacherUploadRef" class="upload-demo" drag action="" :auto-upload="false"
              :on-change="handleTeacherExcelChange" :on-remove="handleTeacherExcelRemove" :limit="1"
              accept=".xlsx, .xls">
              <el-icon class="el-icon--upload"
                style="font-size: 48px; color: #94a3b8; margin-bottom: 10px;"><upload-filled /></el-icon>
              <div class="el-upload__text" style="font-size: 14px; color: #64748b;">
                将 Excel 文件拖到此处，或 <em style="color: #4f46e5; font-style: normal; font-weight: 600;">点击上传</em>
              </div>
              <template #tip>
                <div class="el-upload__tip"
                  style="text-align: center; margin-top: 8px; color: #94a3b8; font-size: 12px;">
                  支持上传 .xlsx 或 .xls 格式文件
                </div>
              </template>
            </el-upload>
          </div>
        </el-tab-pane>

        <el-tab-pane label="粘贴文本导入" name="text">
          <div class="batch-import-container"
            style="display: flex; flex-direction: column; gap: 16px; padding: 10px 0;">
            <div class="import-instructions"
              style="background-color: #f8fafc; padding: 16px; border-radius: 8px; font-size: 14px; color: #475569; border: 1px solid #f1f5f9;">
              <p style="margin: 0 0 8px 0; font-weight: bold; color: #1e293b;">操作指引：</p>
              <p style="margin: 4px 0;">请粘贴教师信息，每行一位教师。格式为：<strong>姓名 任教科目 任教年级</strong></p>
              <p style="margin: 4px 0;">例如：<code
                  style="background: #e2e8f0; padding: 2px 6px; border-radius: 4px; font-family: monospace;">张三 语文
              高一,高二</code> 或者是用逗号分隔：<code
                  style="background: #e2e8f0; padding: 2px 6px; border-radius: 4px; font-family: monospace;">李四,数学,高一、高三</code>
              </p>
              <p style="margin: 4px 0; color: #94a3b8; font-size: 12px; font-family: monospace; margin-top: 8px;">
                示例：<br />
                王老师 物理 高一,高二<br />
                赵老师 英语 高三
              </p>
            </div>

            <el-input v-model="teacherImportText" type="textarea" :rows="6" placeholder="在此粘贴教师信息，格式：姓名 学科 年级（用空格或逗号分隔）"
              @input="parseTeacherImportText" />

            <div v-show="parsedImportTeachers.length > 0" class="preview-section" style="margin-top: 10px;">
              <h4 style="margin: 0 0 8px 0; color: #1e293b; font-size: 14px; font-weight: 700;">解析预览 (共 {{
                parsedImportTeachers.length }} 位教师)</h4>
              <el-table :data="parsedImportTeachers" size="small" border max-height="250" style="border-radius: 6px;">
                <el-table-column prop="name" label="姓名" width="120" />
                <el-table-column prop="subject_name" label="学科" width="150" />
                <el-table-column label="任教年级">
                  <template #default="{ row }">
                    <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                      <el-tag v-for="g in row.grades" :key="g" size="small" type="info">{{ g }}</el-tag>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>

      <template #footer>
        <el-button @click="teacherBatchImportVisible = false">取消</el-button>
        <el-button type="primary" @click="submitTeacherBatchImport" :loading="teacherImportSubmitting"
          :disabled="(teacherImportTab === 'text' && parsedImportTeachers.length === 0) || (teacherImportTab === 'excel' && !teacherFile)">
          确定导入
        </el-button>
      </template>
    </el-dialog>

    <!-- 体育与专项合班排课向导 Dialog -->
    <el-dialog v-model="sportsWizardVisible" title="体育课合班排课配置向导" width="600px" destroy-on-close>
      <div style="padding: 10px 0;">
        <el-alert
          title="合班说明"
          type="info"
          description="本向导用于配置多个行政班级在同一时间合班上体育课。系统会自动将选中的多个班级 and选中的多名体育老师进行关联，并将它们设为同一合班课组同步排定。"
          show-icon
          :closable="false"
          style="margin-bottom: 20px;"
        />
        
        <el-form :model="sportsForm" label-width="100px" size="default">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="选择年级">
                <el-select v-model="sportsForm.grade" placeholder="请选择年级" style="width: 100%;" @change="handleSportsGradeChange">
                  <el-option v-for="g in gradesConfig" :key="g.name" :label="g.name" :value="g.name" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="主学科名称">
                <el-input v-model="sportsForm.subjectName" placeholder="如：体育" style="width: 100%;" />
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="周课时">
                <el-input-number v-model="sportsForm.weeklyPeriods" :min="0.5" :max="10" :step="0.5" style="width: 100%;" />
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-form-item label="选择行政班级" required>
            <el-checkbox-group v-model="sportsForm.classes" style="width: 100%;">
              <el-checkbox v-for="cls in sportsWizardAvailableClasses" :key="cls" :label="cls">
                {{ cls }}
              </el-checkbox>
            </el-checkbox-group>
            <div style="font-size: 12px; color: #94a3b8; margin-top: 4px; line-height: 1.2;">
              勾选需要同时上体育课的多个行政班级
            </div>
          </el-form-item>

          <el-form-item label="选择体育教师" required>
            <el-select
              v-model="sportsForm.teachers"
              multiple
              filterable
              collapse-tags
              collapse-tags-tooltip
              placeholder="选择负责这些班级的体育老师（可多选）"
              style="width: 100%;"
            >
              <el-option v-for="t in teacherList" :key="t.id" :label="t.name" :value="t.id" />
            </el-select>
            <div style="font-size: 12px; color: #94a3b8; margin-top: 4px; line-height: 1.2;">
              选择参与这堂合班体育课的全部体育教师，排课引擎会将他们同时锁定。
            </div>
          </el-form-item>
        </el-form>
      </div>
      
      <template #footer>
        <el-button @click="sportsWizardVisible = false">取消</el-button>
        <el-button type="primary" @click="submitSportsWizard" :disabled="sportsForm.classes.length < 2 || sportsForm.teachers.length === 0">
          确认生成合班计划
        </el-button>
      </template>
    </el-dialog>

    <!-- 走班课配置向导 Dialog -->
    <el-dialog v-model="walkClassWizardVisible" title="走班课合班排课配置向导" width="700px" destroy-on-close>
      <div style="padding: 10px 0;">
        <el-alert
          title="走班课拼盘说明"
          type="info"
          description="本向导用于配置新高考走班模式下的「合班拼盘（Combined Group）」。在向导中，您可以选择参与走班的一组行政班级，并添加该组下同时上课的各个走班学科与任课教师。系统会自动生成各学科教学班并建立同节开课（合班）强约束。"
          show-icon
          :closable="false"
          style="margin-bottom: 20px;"
        />
        
        <el-form :model="walkClassForm" label-width="110px" size="default">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="选择年级" required>
                <el-select v-model="walkClassForm.grade" placeholder="请选择年级" style="width: 100%;" @change="handleWalkClassGradeChange">
                  <el-option v-for="g in gradesConfig" :key="g.name" :label="g.name" :value="g.name" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="周课时" required>
                <el-input-number v-model="walkClassForm.weeklyPeriods" :min="1" :max="15" :step="1" style="width: 100%;" />
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-form-item label="走班虚拟班级" required>
            <el-input v-model="walkClassForm.className" placeholder="请输入走班班级名称，例如：走班1班" style="width: 100%;" />
            <div style="font-size: 12px; color: #94a3b8; margin-top: 4px; line-height: 1.2;">
              同一时间多个老师多个学科同时上课所形成的的虚拟教学班名字（如“走班1班”）。
            </div>
          </el-form-item>
          
          <el-form-item label="参与行政班级" required>
            <el-checkbox-group v-model="walkClassForm.classes" style="width: 100%;">
              <el-checkbox v-for="cls in walkClassWizardAvailableClasses" :key="cls" :label="cls">
                {{ cls }}
              </el-checkbox>
            </el-checkbox-group>
            <div style="font-size: 12px; color: #94a3b8; margin-top: 4px; line-height: 1.2;">
              勾选需要集中在此走班专属时段内进行自由走班的所有行政班级
            </div>
          </el-form-item>

          <div style="font-weight: bold; color: #334155; margin-bottom: 12px; font-size: 14px; margin-top: 20px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px;">
            配置走班拼盘科目与教师
          </div>

          <el-form-item label="选择走班科目" required>
            <el-select
              v-model="walkClassForm.subjects"
              multiple
              filterable
              collapse-tags
              collapse-tags-tooltip
              placeholder="选择参与走班的所有学科（可多选）"
              style="width: 100%;"
            >
              <el-option v-for="sub in subjectList" :key="sub.id" :label="sub.name" :value="sub.id" />
            </el-select>
            <div style="font-size: 12px; color: #94a3b8; margin-top: 4px; line-height: 1.2;">
              选择在该走班时段中同时开设的全部学科（如：物理、化学、地理等）
            </div>
          </el-form-item>

          <el-form-item label="选择授课教师" required>
            <el-select
              v-model="walkClassForm.teachers"
              multiple
              filterable
              collapse-tags
              collapse-tags-tooltip
              placeholder="选择参与这些走班科目的授课教师（可多选）"
              style="width: 100%;"
            >
              <el-option v-for="t in teacherList" :key="t.id" :label="t.name" :value="t.id" />
            </el-select>
            <div style="font-size: 12px; color: #94a3b8; margin-top: 4px; line-height: 1.2;">
              选择在这些走班学科下任教的所有教师，系统会将他们在此走班时段锁定上课
            </div>
          </el-form-item>
        </el-form>
      </div>
      
      <template #footer>
        <el-button @click="walkClassWizardVisible = false">取消</el-button>
        <el-button type="primary" @click="submitWalkClassWizard" :disabled="walkClassForm.classes.length < 2 || walkClassForm.subjects.length === 0 || walkClassForm.teachers.length === 0">
          确认生成走班计划
        </el-button>
      </template>
    </el-dialog>

    <!-- 代课推荐 Dialog -->
    <el-dialog v-model="substituteDialogVisible" title="智能代课教师推荐" width="650px">
      <div v-if="substituteLoading" style="text-align: center; padding: 30px;">
        <el-icon class="is-loading" :size="30"><Loading /></el-icon>
        <p style="margin-top: 15px;">正在匹配空闲代课教师...</p>
      </div>
      <div v-else>
        <div style="margin-bottom: 20px; padding: 12px; background: #f8fafc; border-radius: 8px;">
          <strong>原排课信息：</strong>
          <p style="margin: 5px 0 0 0; color: #64748b;">
            时间：周{{ getDayName(substituteContext.day) }}第{{ substituteContext.period }}节<br/>
            班级：{{ substituteContext.grade }}{{ substituteContext.className }}<br/>
            科目：{{ substituteContext.subjectName }}<br/>
            原任课：{{ substituteContext.originalTeacherName }}
          </p>
        </div>
        
        <el-table :data="substituteCandidates" border stripe style="width: 100%" height="300">
          <el-table-column prop="name" label="推荐教师" />
          <el-table-column prop="employee_id" label="工号" width="120" />
          <el-table-column prop="current_weekly_periods" label="当前周课时" width="120">
            <template #default="{ row }">
              <el-tag :type="row.current_weekly_periods > 15 ? 'warning' : 'success'">
                {{ row.current_weekly_periods }} 节
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120" align="center">
            <template #default="{ row }">
              <el-button size="small" type="primary" plain @click="copySubstituteTeacher(row.name)">复制并联系</el-button>
            </template>
          </el-table-column>
        </el-table>
        <p v-if="substituteCandidates.length === 0" style="color: #ef4444; text-align: center; padding: 20px;">
          未找到在该时段空闲且教授该科目的教师。
        </p>
      </div>
    </el-dialog>

    <!-- =========== 调课对话框 =========== -->
    <el-dialog
      v-model="weekOverrideDialogVisible"
      title="📅 添加调课记录"
      width="520px"
      destroy-on-close
    >
      <div v-if="overrideTargetCard" style="margin-bottom: 12px; padding: 12px 16px; background: #f0f7ff; border-radius: 8px; border: 1px solid #bfdbfe;">
        <div style="font-weight: 700; font-size: 14px; color: #1d4ed8; margin-bottom: 4px;">
          {{ overrideTargetCard.subject_name }}
        </div>
        <div style="font-size: 13px; color: #475569;">
          {{ overrideTargetCard.teacher_name }} · {{ overrideTargetCard.grade }}{{ overrideTargetCard.class_name }}
          · {{ getDayName(overrideTargetCard.day_of_week) }} 第{{ overrideTargetCard.period_index }}节
        </div>
      </div>
      <el-form :model="overrideForm" label-width="90px" label-position="left">
        <el-form-item label="影响周次" required>
          <el-select v-model="overrideForm.week_number" placeholder="请选择第几周" style="width: 180px;">
            <el-option
              v-for="w in totalWeeks"
              :key="w"
              :label="`第 ${w} 周`"
              :value="w"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="调课类型" required>
          <el-radio-group v-model="overrideForm.override_type">
            <el-radio-button label="cancel">🚫 停课</el-radio-button>
            <el-radio-button label="replace">🔄 换课（改时间）</el-radio-button>
            <el-radio-button label="substitute">👤 代课（换教师）</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="overrideForm.override_type === 'replace'" label="新上课时间" required>
          <div style="display: flex; gap: 8px; align-items: center;">
            <el-select v-model="overrideForm.new_day_of_week" placeholder="星期" style="width: 100px;">
              <el-option v-for="d in daysPerWeek" :key="d" :label="getDayName(d)" :value="d" />
            </el-select>
            <span style="color: #64748b;">第</span>
            <el-select v-model="overrideForm.new_period_index" placeholder="节次" style="width: 90px;">
              <el-option v-for="p in totalDailyPeriods" :key="p" :label="`${p}节`" :value="p" />
            </el-select>
            <span style="color: #64748b;">节</span>
          </div>
        </el-form-item>
        <el-form-item v-if="overrideForm.override_type === 'substitute' || (overrideForm.override_type === 'replace' && overrideForm.changeTeacher)" label="代课/换课教师">
          <el-select
            v-model="overrideForm.new_teacher_id"
            placeholder="选择教师（可不填）"
            filterable
            clearable
            style="width: 200px;"
          >
            <el-option v-for="t in teacherList" :key="t.id" :label="t.name" :value="t.id" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="overrideForm.override_type === 'replace'" label="同时换教师">
          <el-switch v-model="overrideForm.changeTeacher" />
        </el-form-item>
        <el-form-item label="备注说明">
          <el-input
            v-model="overrideForm.remark"
            type="textarea"
            :rows="2"
            placeholder="例如：因校运会停课、张三请假"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="weekOverrideDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="overrideSaving" @click="saveWeekOverride">确认保存</el-button>
      </template>
    </el-dialog>

    <!-- =========== 调课记录列表对话框 =========== -->
    <el-dialog
      v-model="overrideListDialogVisible"
      :title="`第 ${selectedWeek} 周 调课记录`"
      width="780px"
    >
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
        <span style="font-size: 13px; color: #64748b;">共 {{ weekOverrides.length }} 条调课记录</span>
        <el-button type="success" size="small" @click="exportOverrideNotice(null)">
          <el-icon><Printer /></el-icon> 导出调课单（全部）
        </el-button>
      </div>
      <el-table :data="weekOverrides" border stripe size="small" style="width: 100%;">
        <el-table-column label="类型" width="70">
          <template #default="{ row }">
            <el-tag
              :type="row.override_type === 'cancel' ? 'danger' : row.override_type === 'replace' ? 'primary' : 'success'"
              size="small"
            >
              {{ row.override_type === 'cancel' ? '停课' : row.override_type === 'replace' ? '换课' : '代课' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="教师" prop="original_teacher_name" width="80" />
        <el-table-column label="班级" width="90">
          <template #default="{ row }">{{ row.grade }}{{ row.class_name }}</template>
        </el-table-column>
        <el-table-column label="科目" prop="subject_name" width="80" />
        <el-table-column label="原时间" width="110">
          <template #default="{ row }">
            {{ getDayName(row.original_day_of_week) }} 第{{ row.original_period_index }}节
          </template>
        </el-table-column>
        <el-table-column label="调整说明" min-width="160">
          <template #default="{ row }">
            <span v-if="row.override_type === 'cancel'" style="color: #ef4444;">停课（取消本节）</span>
            <span v-else-if="row.override_type === 'replace'" style="color: #3b82f6;">
              调至 {{ getDayName(row.new_day_of_week) }} 第{{ row.new_period_index }}节
              <span v-if="row.new_teacher_name">（{{ row.new_teacher_name }}）</span>
            </span>
            <span v-else-if="row.override_type === 'substitute'" style="color: #10b981;">
              代课：{{ row.new_teacher_name || '—' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="备注" prop="remark" min-width="100" show-overflow-tooltip />
        <el-table-column label="操作" width="120" fixed="right">
          <template #default="{ row }">
            <el-button
              type="success"
              link
              size="small"
              @click="exportOverrideNotice(row.original_teacher_id || null, row.original_teacher_name)"
            >打印</el-button>
            <el-button type="danger" link size="small" @click="deleteOverride(row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="weekOverrides.length === 0" style="text-align: center; color: #94a3b8; padding: 30px 0;">
        本周暂无调课记录。点击课表上的课程卡片选中后，可点击"📅 调课"添加。
      </div>
    </el-dialog>

    <!-- Excel/Word 导出选项弹窗 -->
    <el-dialog v-model="exportExcelDialogVisible" :title="exportFormat === 'excel' ? '导出 Excel 课程表' : '导出 Word 课程表'" width="520px" destroy-on-close>
      <div style="margin-bottom: 18px; padding-left: 4px;">
        <span style="font-size: 14px; color: #475569; font-weight: 500;">选择您需要导出的表格内容/页面：</span>
      </div>
      <el-checkbox-group v-model="exportSelectedExcelTypes" style="display: flex; flex-direction: column; gap: 10px; margin-bottom: 24px; padding: 16px; border-radius: 10px; background: #f8fafc; border: 1px solid #e2e8f0;">
        <div style="display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; border-radius: 8px; background: #ffffff; border: 1px solid #f1f5f9; transition: all 0.2s ease;">
          <el-checkbox label="class" size="large" style="margin-right: 0; display: flex; align-items: center; width: 100%;">
            <div style="display: flex; flex-direction: column; margin-left: 6px; text-align: left;">
              <span style="font-weight: 600; color: #0f172a; font-size: 14px; line-height: 1.5;">班级课程表</span>
              <span style="font-size: 12px; color: #64748b; margin-top: 2px;">导出每个行政班的课表，单页顺序排列</span>
            </div>
          </el-checkbox>
        </div>
        <div style="display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; border-radius: 8px; background: #ffffff; border: 1px solid #f1f5f9; transition: all 0.2s ease;">
          <el-checkbox label="teacher" size="large" style="margin-right: 0; display: flex; align-items: center; width: 100%;">
            <div style="display: flex; flex-direction: column; margin-left: 6px; text-align: left;">
              <span style="font-weight: 600; color: #0f172a; font-size: 14px; line-height: 1.5;">教师课程表</span>
              <span style="font-size: 12px; color: #64748b; margin-top: 2px;">导出每位任课教师的个人课表，单页顺序排列</span>
            </div>
          </el-checkbox>
        </div>
        <div style="display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; border-radius: 8px; background: #ffffff; border: 1px solid #f1f5f9; transition: all 0.2s ease;">
          <el-checkbox label="master" size="large" style="margin-right: 0; display: flex; align-items: center; width: 100%;">
            <div style="display: flex; flex-direction: column; margin-left: 6px; text-align: left;">
              <span style="font-weight: 600; color: #0f172a; font-size: 14px; line-height: 1.5;">总课程表</span>
              <span style="font-size: 12px; color: #64748b; margin-top: 2px;">全校班级总课表，行展示班级，列展示每天节次</span>
            </div>
          </el-checkbox>
        </div>
        <div style="display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; border-radius: 8px; background: #ffffff; border: 1px solid #f1f5f9; transition: all 0.2s ease;">
          <el-checkbox label="workload" size="large" style="margin-right: 0; display: flex; align-items: center; width: 100%;">
            <div style="display: flex; flex-direction: column; margin-left: 6px; text-align: left;">
              <span style="font-weight: 600; color: #0f172a; font-size: 14px; line-height: 1.5;">总任课表</span>
              <span style="font-size: 12px; color: #64748b; margin-top: 2px;">全校教师任课总表，展示班级在各学科下的任课教师及课时</span>
            </div>
          </el-checkbox>
        </div>
        <div style="display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; border-radius: 8px; background: #ffffff; border: 1px solid #f1f5f9; transition: all 0.2s ease;">
          <el-checkbox label="room" size="large" style="margin-right: 0; display: flex; align-items: center; width: 100%;">
            <div style="display: flex; flex-direction: column; margin-left: 6px; text-align: left;">
              <span style="font-weight: 600; color: #0f172a; font-size: 14px; line-height: 1.5;">场室课程表</span>
              <span style="font-size: 12px; color: #64748b; margin-top: 2px;">导出功能室、体育馆等专用场室的使用排程表</span>
            </div>
          </el-checkbox>
        </div>
      </el-checkbox-group>
      <template #footer>
        <div style="display: flex; justify-content: flex-end; gap: 12px;">
          <el-button @click="exportExcelDialogVisible = false">取消</el-button>
          <el-button type="primary" :disabled="exportSelectedExcelTypes.length === 0" @click="confirmExportExcel" :loading="exportLoading">确认导出</el-button>
        </div>
      </template>
    </el-dialog>

    <div v-if="pointerDragState.moved && pointerDragState.card" class="pointer-drag-card"
      :style="{ left: `${pointerDragState.x + 14}px`, top: `${pointerDragState.y + 14}px` }">
      <div class="pointer-drag-title">{{ pointerDragState.card.subject_name }}</div>
      <div class="pointer-drag-meta">
        {{ pointerDragState.card.teacher_name || '未分配教师' }} · {{ pointerDragState.card.grade }}{{ pointerDragState.card.class_name }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  ArrowLeft, Cpu, Grid, Download, Upload, Plus, Delete, Edit, Camera,
  CircleCheck, Warning, Loading, Close, Notebook, UploadFilled, Collection,
  Bell, Notification, OfficeBuilding, Calendar, List, Printer
} from '@element-plus/icons-vue'
import { courseScheduleApi, teacherApi, examApi } from '@/api'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useCourseScheduleJob } from '@/composables/useCourseScheduleJob'

const route = useRoute()
const router = useRouter()
const workspaceId = Number(route.params.workspaceId)

// State variables
const pageLoading = ref(false)
const activeTab = ref('combined')
const workspace = ref(null)

const selectedSolver = ref('cpsat')

const tasks = ref([])
const constraints = ref([])
const backups = ref([])
const results = ref([])

// ===== Week override 调课相关状态 =====
const selectedWeek = ref(null)           // 当前选择的周次（null = 全局默认）
const totalWeeks = ref(25)               // 学期总周数，默认 25
const weekOverrides = ref([])            // 当前周的调课记录列表
const weekOverrideDialogVisible = ref(false)
const overrideListDialogVisible = ref(false)
const overrideTargetCard = ref(null)     // 触发调课的课程卡片
const overrideSaving = ref(false)
const overrideForm = ref({
  week_number: null,
  override_type: 'cancel',               // 'cancel' | 'replace' | 'substitute'
  new_day_of_week: null,
  new_period_index: null,
  new_teacher_id: null,
  changeTeacher: false,                  // replace 时是否同时换教师
  remark: ''
})
const conflicts = ref([])
const isSandboxActive = ref(false)
const sandboxBackupId = ref(null)

// --- Missing variables from recovery ---
const freeLabelDialogVisible = ref(false)
const freeLabelEditText = ref('')
const freeLabelEditingContext = ref(null)
const freeLabelSaving = ref(false)
const gradeFreeCustomTexts = ref({})
const gradeFreePresetTexts = ref({})
const gradeScheduleClassTargets = ref({})
const gradeScheduleScopes = ref({})
const gridCards = ref([])
const newClassInputs = ref({})
const publicPeriodDialogVisible = ref(false)
const publicPeriodForm = ref({
  id: '',
  name: '',
  type: 'display', // display, activity, forbidden
  position: 'before_period_1', // before_period_1, after_period, morning_end, afternoon_end, evening_end
  position_value: 1,
  scope: 'all', // all, grade
  scope_grade: '',
  show_in_schedule: true,
  occupies_teacher: false,
  occupies_room: false,
  affects_scheduling: false,
  start: '',
  end: ''
})
const publicPeriods = ref([])
const savingProgress = ref(false)
const scheduleAutoSaving = ref({})
const preflightReport = ref(null)
const preflightLoading = ref(false)
const { activeJob, startPolling: startJobPolling, stopPolling: stopJobPolling } = useCourseScheduleJob(
  courseScheduleApi,
  workspaceId,
  async (job) => {
    const ws = await courseScheduleApi.getWorkspace(workspaceId)
    workspace.value = { ...workspace.value, ...ws }
    await refreshResults()
    if (job.status === 'success') {
      ElMessage.success('课表已经生成完成')
    } else if (job.status !== 'cancelled') {
      ElMessage.error(job.error_message || '这次没有生成成功，请检查预诊断和规则设置。')
    }
  }
)
const scheduleRunStatus = computed(() => activeJob.value?.status || workspace.value?.status || 'idle')
const scheduleProgress = computed(() => {
  const progress = activeJob.value?.progress ?? workspace.value?.progress ?? 0
  return Math.min(100, Math.max(0, Number(progress) || 0))
})
const scheduleRunLog = computed(() => activeJob.value?.log_message || workspace.value?.log_message || '')
const preflightIssues = computed(() => preflightReport.value?.issues || [])
const preflightErrorCount = computed(() => preflightIssues.value.filter(item => item.severity === 'error').length)
const preflightWarningCount = computed(() => preflightIssues.value.filter(item => item.severity !== 'error').length)
const scheduleStatusText = computed(() => ({
  queued: '排课任务排队中',
  running: '正在生成课表',
  success: '课表已生成',
  failed: '生成未完成',
  timed_out: '生成超时',
  cancelled: '生成已取消',
  idle: '等待开始生成',
}[scheduleRunStatus.value] || '等待开始生成'))
const venueDialogVisible = ref(false)
const venueForbiddenDialogVisible = ref(false)
const venueForm = ref({
  id: '',
  name: '',
  type: '',
  capacity: 1,
  forbidden_slots: []
})
const venues = ref([])
const enableSportsCombined = computed(() => {
  return workspace.value?.config?.enable_sports_combined !== false
})

const enableWalkClass = computed(() => {
  return workspace.value?.config?.enable_walk_class !== false
})


const venueTypes = computed(() => {
  const types = new Set()
  venues.value.forEach(v => {
    if (v.type) types.add(v.type)
  })
  return Array.from(types)
})

const remainingWorkloads = computed(() => {
  let targetTasks = []
  if (viewMode.value === 'class' && selectedClass.value) {
    targetTasks = tasks.value.filter(t => {
      if (`${t.grade}${t.class_name}` === selectedClass.value) return true
      if (t.linked_classes && Array.isArray(t.linked_classes)) {
        return t.linked_classes.some(lc => `${lc.grade || t.grade}${lc.class_name || lc.name}` === selectedClass.value)
      }
      return false
    })
  } else if (viewMode.value === 'teacher' && selectedTeacherId.value) {
    targetTasks = tasks.value.filter(t => {
      if (t.teacher_id === selectedTeacherId.value) return true
      if (t.linked_classes && Array.isArray(t.linked_classes)) {
        return t.linked_classes.some(lc => {
          const ids = String(lc.teacher_ids || '').split(',').map(id => Number(id)).filter(Boolean)
          return ids.includes(selectedTeacherId.value)
        })
      }
      if (t.teacher_ids) {
        return String(t.teacher_ids).split(',').map(id => Number(id)).filter(Boolean).includes(selectedTeacherId.value)
      }
      return false
    })
  } else if (viewMode.value === 'room' && selectedRoomId.value) {
    targetTasks = tasks.value.filter(t => t.room_id === selectedRoomId.value)
  }
  const stats = []
  targetTasks.forEach(t => {
    const matches = results.value.filter(r => r.task_id === t.id)
    const uniqueSlots = {}
    matches.forEach(m => {
      const key = `${m.day_of_week}-${m.period_index}`
      if (!uniqueSlots[key]) {
        uniqueSlots[key] = m.is_half_period ? 0.5 : 1
      }
    })
    let scheduledCount = 0
    Object.values(uniqueSlots).forEach(val => {
      scheduledCount += val
    })
    const remaining = Number(t.weekly_periods) - scheduledCount
    let displayClassName = t.class_name
    if (t.linked_classes && Array.isArray(t.linked_classes)) {
      displayClassName = t.linked_classes.map(lc => lc.class_name || lc.name).join(',')
    }
    stats.push({
      task_id: t.id,
      subject_name: t.subject_name,
      teacher_name: t.teacher_name,
      grade: t.grade,
      class_name: displayClassName,
      weekly_periods: t.weekly_periods,
      scheduled: scheduledCount,
      remaining: remaining > 0 ? remaining : 0
    })
  })
  return stats
})

const viewCompletion = computed(() => {
  const workloads = remainingWorkloads.value
  const required = workloads.reduce((sum, item) => sum + Number(item.weekly_periods || 0), 0)
  const scheduled = workloads.reduce((sum, item) => sum + Number(item.scheduled || 0), 0)
  const remaining = Math.max(0, required - scheduled)
  const capacity = viewMode.value === 'class' ? Array.from({ length: daysPerWeek.value }, (_, idx) => getDayTotalPeriods(idx + 1)).reduce((sum, value) => sum + value, 0) : 0
  return {
    required: formatLessonCount(required),
    scheduled: formatLessonCount(scheduled),
    remaining: formatLessonCount(remaining),
    capacity
  }
})

const classCapacityStats = computed(() => {
  const stats = []
  flatClasses.value.forEach(cls => {
    const grade = gradesConfig.value.find(g => g.name === cls.grade)
    if (!grade || !grade.schedule_cfg) return
    let totalPeriods = 0
    const subjects = getGradeSubjects(grade)
    
    // 1. Regular subjects (with double-counting prevention for sports/combined tasks)
    subjects.forEach(sub => {
      // Check if there is a combined task in tasks.value covering this class for this subject
      const combinedTask = tasks.value.find(t => {
        if (!t.is_combined) return false
        const gid = t.combined_group_id || ''
        if (gid.startsWith('PE_COMBINED_') && !enableSportsCombined.value) return false
        if (gid.startsWith('WALK_COMBINED_') && !enableWalkClass.value) return false
        const isSameSubject = (t.subject_id === sub.id) || (t.subject_name === sub.name)
        if (!isSameSubject) return false
        
        const isLinked = t.grade === cls.grade && (
          t.class_name === cls.class_name || 
          (t.linked_classes && Array.isArray(t.linked_classes) && t.linked_classes.some(lc => (lc.class_name || lc.name) === cls.class_name && (lc.grade || t.grade) === cls.grade))
        )
        return isLinked
      })
      
      if (combinedTask) {
        // Use the combined task's periods
        totalPeriods += parseFloat(combinedTask.weekly_periods) || 0
      } else {
        // Use the grid value (override to 0 if subject is disabled due to walk-class)
        const val = isSubjectDisabledForClass(sub, cls) ? 0 : (parseFloat(periodsGrid.value?.[sub.id]?.[cls.key]) || 0)
        totalPeriods += val
      }
    })
    
    // 2. Add other combined tasks (like walk-classes) whose subjects are not in the regular grid subjects
    tasks.value.forEach(t => {
      if (!t.is_combined) return
      const gid = t.combined_group_id || ''
      if (gid.startsWith('PE_COMBINED_') && !enableSportsCombined.value) return
      if (gid.startsWith('WALK_COMBINED_') && !enableWalkClass.value) return
      
      // If the subject is already handled in the regular grid, skip it to prevent double counting
      const isHandled = subjects.some(sub => (t.subject_id === sub.id) || (t.subject_name === sub.name))
      if (isHandled) return
      
      // Check if this combined task covers our class cls
      const isLinked = t.grade === cls.grade && (
        t.class_name === cls.class_name || 
        (t.linked_classes && Array.isArray(t.linked_classes) && t.linked_classes.some(lc => (lc.class_name || lc.name) === cls.class_name && (lc.grade || t.grade) === cls.grade))
      )
      
      if (isLinked) {
        totalPeriods += parseFloat(t.weekly_periods) || 0
      }
    })

    let capacity = 0
    const days = grade.schedule_cfg.days_per_week || 5
    const counts = grade.schedule_cfg.day_period_counts || {}
    for (let day = 1; day <= days; day++) {
      const dayCount = counts[String(day)] || counts[day] || {
        morning: grade.schedule_cfg.morning_count || 0,
        afternoon: grade.schedule_cfg.afternoon_count || 0,
        evening: grade.schedule_cfg.evening_count || 0
      }
      const dayTotal = Number(dayCount.morning || 0) + Number(dayCount.afternoon || 0) + Number(dayCount.evening || 0)
      capacity += dayTotal
      for (let p = 1; p <= dayTotal; p++) {
        let isForbidden = false
        if (grade.schedule_cfg.forbidden_periods?.some(fp => fp.day === day && fp.period_index === p)) {
          isForbidden = true
        }
        if (grade.schedule_cfg.class_forbidden_periods?.[cls.class_name]?.some(fp => fp.day === day && fp.period_index === p)) {
          isForbidden = true
        }
        if (isForbidden) {
          capacity--
        }
      }
    }
    let occupiedFixed = 0
    constraints.value.forEach(c => {
      if (c.constraint_type === 'fixed_slot') {
        const taskKey = c.config?.task_key || ''
        const parts = taskKey.split('|')
        if (parts.length >= 2 && parts[0] === cls.grade && parts[1] === cls.class_name) {
          occupiedFixed++
        }
      }
    })
    const excess = totalPeriods > capacity ? totalPeriods - capacity : 0
    const missing = capacity > totalPeriods ? capacity - totalPeriods : 0
    const remaining = Math.max(0, capacity - totalPeriods)
    stats.push({
      grade: cls.grade,
      class_name: cls.class_name,
      label: cls.label,
      key: cls.key,
      totalPeriods,
      capacity,
      occupiedFixed,
      remaining,
      excess,
      missing,
      hasError: excess > 0
    })
  })
  return stats
})

const consecutiveValidationAlerts = computed(() => {
  const alerts = []
  const consecRules = constraints.value.filter(c => c.constraint_type === 'consecutive_settings')
  consecRules.forEach(rule => {
    const config = rule.config || {}
    const subId = config.subject_id
    const ruleGrade = config.grade
    const consecCount = Number(config.consecutive_count || 1)
    const blockSize = Number(config.block_size || 2)
    const requiredPeriods = consecCount * blockSize
    const subjectName = subjectList.value.find(s => s.id === subId)?.name || `科目 ID:${subId}`
    flatClasses.value.forEach(cls => {
      if (ruleGrade && cls.grade !== ruleGrade) return
      const sub = subjectList.value.find(s => s.id === subId)
      const isSubDisabled = sub ? isSubjectDisabledForClass(sub, cls) : false
      const weeklyPeriods = isSubDisabled ? 0 : (parseFloat(periodsGrid.value?.[subId]?.[cls.key]) || 0)
      if (weeklyPeriods > 0) {
        if (requiredPeriods > weeklyPeriods) {
          alerts.push({
            type: 'error',
            message: `【冲突】${cls.grade}${cls.class_name} 的《${subjectName}》周课时仅 ${weeklyPeriods} 节，但设置了 ${consecCount} 组 ${blockSize} 节连堂（共需 ${requiredPeriods} 节），课时不足以安排连堂！`
          })
        } else {
          const remainingSingle = weeklyPeriods - requiredPeriods
          alerts.push({
            type: 'info',
            message: `【诊断】${cls.grade}${cls.class_name} 的《${subjectName}》周课时 ${weeklyPeriods} 节：将排 ${consecCount} 组 ${blockSize} 节连堂，剩余 ${remainingSingle} 节单堂课。`
          })
        }
      }
    })
  })
  return alerts
})

const groupedCapacityStats = computed(() => {
  const groups = {}
  classCapacityStats.value.forEach(stat => {
    if (!groups[stat.grade]) {
      groups[stat.grade] = []
    }
    groups[stat.grade].push(stat)
  })
  return Object.keys(groups).sort().map(grade => ({
    grade,
    classes: groups[grade].sort((a, b) => {
      const numA = parseInt(a.class_name) || 0
      const numB = parseInt(b.class_name) || 0
      return numA - numB
    })
  }))
})

const hasCapacityError = computed(() => {
  return classCapacityStats.value.some(stat => stat.excess > 0)
})

const hasConsecutiveError = computed(() => {
  return consecutiveValidationAlerts.value.some(alert => alert.type === 'error')
})



// Sports Specialty Combined Class Wizard State
const sportsWizardVisible = ref(false)
const editingGroupId = ref(null)
const sportsForm = ref({
  grade: '',
  subjectName: '体育',
  weeklyPeriods: 2,
  consecutivePeriods: 1,
  classes: [],
  teachers: []
})

// Walk-Class Setup Wizard State
const walkClassWizardVisible = ref(false)
const walkClassForm = ref({
  grade: '',
  className: '',
  classes: [],
  weeklyPeriods: 3,
  subjects: [],
  teachers: []
})

const walkClassWizardAvailableClasses = computed(() => {
  const grade = gradesConfig.value.find(g => g.name === walkClassForm.value.grade)
  if (!grade) return []
  return (grade.classes || []).map(c => typeof c === 'string' ? c : c.name)
})

const sportsWizardAvailableClasses = computed(() => {
  const grade = gradesConfig.value.find(g => g.name === sportsForm.value.grade)
  if (!grade) return []
  return (grade.classes || []).map(c => typeof c === 'string' ? c : c.name)
})

const sportsCombinedGroups = computed(() => {
  const groupsMap = {}
  const addGroupTeacher = (group, teacher) => {
    if (!teacher?.id) return
    if (!group.teachers.some(item => Number(item.id) === Number(teacher.id))) {
      group.teachers.push({
        id: Number(teacher.id),
        name: teacher.name || getTeacherName(teacher.id) || '未知教师'
      })
    }
  }

  tasks.value.forEach(t => {
    if (t.is_combined && t.combined_group_id && !t.combined_group_id.startsWith('WALK_')) {
      const groupId = t.combined_group_id
      if (!groupsMap[groupId]) {
        groupsMap[groupId] = {
          groupId: groupId,
          grade: t.grade,
          subjectName: t.subject_name,
          weeklyPeriods: t.weekly_periods,
          consecutivePeriods: t.consecutive_periods,
          classes: [],
          teachers: []
        }
      }
      // Collect classes from linked_classes
      if (t.linked_classes && Array.isArray(t.linked_classes)) {
        t.linked_classes.forEach(lc => {
          const cName = lc.class_name || lc.name
          if (cName && !groupsMap[groupId].classes.includes(cName)) {
            groupsMap[groupId].classes.push(cName)
          }

          const teacherIds = String(lc.teacher_ids || '').split(',').map(id => Number(id)).filter(Boolean)
          const teacherNames = String(lc.teacher_names || '').split(',').map(name => name.trim())
          teacherIds.forEach((id, index) => {
            addGroupTeacher(groupsMap[groupId], {
              id,
              name: teacherNames[index] || getTeacherName(id)
            })
          })
        })
      } else if (t.class_name && !groupsMap[groupId].classes.includes(t.class_name)) {
        groupsMap[groupId].classes.push(t.class_name)
      }
      
      // Collect teachers
      addGroupTeacher(groupsMap[groupId], {
        id: t.teacher_id,
        name: t.teacher_name || '未知教师'
      })
    }
  })
  
  return Object.values(groupsMap)
})

const walkClassCombinedGroups = computed(() => {
  const groupsMap = {}
  const addGroupTeacher = (group, teacher) => {
    if (!teacher?.id) return
    if (!group.teachers.some(item => Number(item.id) === Number(teacher.id))) {
      group.teachers.push({
        id: Number(teacher.id),
        name: teacher.name || getTeacherName(teacher.id) || '未知教师'
      })
    }
  }

  tasks.value.forEach(t => {
    if (t.is_combined && t.combined_group_id && t.combined_group_id.startsWith('WALK_')) {
      const groupId = t.combined_group_id
      if (!groupsMap[groupId]) {
        groupsMap[groupId] = {
          groupId: groupId,
          grade: t.grade,
          className: t.class_name,
          subjects: [],
          weeklyPeriods: t.weekly_periods,
          classes: [],
          teachers: []
        }
      }
      if (t.subject_name) {
        if (t.subject_name.startsWith('走班(') && t.subject_name.endsWith(')')) {
          const inner = t.subject_name.substring(3, t.subject_name.length - 1)
          inner.split(',').forEach(s => {
            const cleanS = s.trim()
            if (cleanS && !groupsMap[groupId].subjects.includes(cleanS)) {
              groupsMap[groupId].subjects.push(cleanS)
            }
          })
        } else if (!groupsMap[groupId].subjects.includes(t.subject_name)) {
          groupsMap[groupId].subjects.push(t.subject_name)
        }
      }
      
      // Collect classes from linked_classes
      if (t.linked_classes && Array.isArray(t.linked_classes)) {
        t.linked_classes.forEach(lc => {
          const cName = lc.class_name || lc.name
          if (cName && !groupsMap[groupId].classes.includes(cName)) {
            groupsMap[groupId].classes.push(cName)
          }

          const teacherIds = String(lc.teacher_ids || '').split(',').map(id => Number(id)).filter(Boolean)
          const teacherNames = String(lc.teacher_names || '').split(',').map(name => name.trim())
          teacherIds.forEach((id, index) => {
            addGroupTeacher(groupsMap[groupId], {
              id,
              name: teacherNames[index] || getTeacherName(id)
            })
          })
        })
      } else if (t.class_name && !groupsMap[groupId].classes.includes(t.class_name)) {
        groupsMap[groupId].classes.push(t.class_name)
      }
      
      // Collect teachers
      addGroupTeacher(groupsMap[groupId], {
        id: t.teacher_id,
        name: t.teacher_name || '未知教师'
      })
    }
  })
  
  return Object.values(groupsMap)
})

const editSportsGroup = (group) => {
  editingGroupId.value = group.groupId
  sportsForm.value = {
    grade: group.grade,
    subjectName: group.subjectName,
    weeklyPeriods: group.weeklyPeriods,
    consecutivePeriods: group.consecutivePeriods,
    classes: [...group.classes],
    teachers: group.teachers.map(t => t.id)
  }
  sportsWizardVisible.value = true
}

const deleteSportsGroup = (group) => {
  ElMessageBox.confirm(
    `确定要删除该体育合班计划吗？删除将同时删除对应的排课任务和排课结果。`,
    '提示',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  ).then(async () => {
    try {
      pageLoading.value = true
      const toDelete = tasks.value.filter(t => t.combined_group_id === group.groupId)
      for (const t of toDelete) {
        await courseScheduleApi.deleteTask(workspaceId, t.id)
      }
      ElMessage.success('删除成功')
      await refreshTasks()
      await refreshResults()
    } catch (err) {
      console.error(err)
      ElMessage.error(`删除失败: ${err.message || err}`)
    } finally {
      pageLoading.value = false
    }
  }).catch(() => {})
}

const handleSportsGradeChange = () => {
  sportsForm.value.classes = []
  sportsForm.value.teachers = []
}

const handleWalkClassGradeChange = () => {
  walkClassForm.value.classes = []
  walkClassForm.value.teachers = []
}

const openSportsWizard = () => {
  sportsForm.value = {
    grade: gradesConfig.value.length > 0 ? gradesConfig.value[0].name : '',
    subjectName: '体育',
    weeklyPeriods: 2,
    consecutivePeriods: 1,
    classes: [],
    teachers: []
  }
  sportsWizardVisible.value = true
}

const openWalkClassWizard = () => {
  const nextIndex = walkClassCombinedGroups.value.length + 1
  walkClassForm.value = {
    grade: gradesConfig.value.length > 0 ? gradesConfig.value[0].name : '',
    className: `走班${nextIndex}班`,
    classes: [],
    weeklyPeriods: 3,
    subjects: [],
    teachers: []
  }
  walkClassWizardVisible.value = true
}

const submitSportsWizard = async () => {
  try {
    const grade = sportsForm.value.grade
    const subName = sportsForm.value.subjectName || '体育'
    const weeklyPeriods = sportsForm.value.weeklyPeriods || 2
    const consecutivePeriods = 1
    
    if (!grade || sportsForm.value.classes.length < 2) {
      ElMessage.warning('请选择年级并至少勾选两个行政班级')
      return
    }
    
    if (!sportsForm.value.teachers || sportsForm.value.teachers.length === 0) {
      ElMessage.warning('请选择至少一位任教体育老师')
      return
    }
    
    // Generate a unique combined_group_id
    const groupId = editingGroupId.value || `PE_COMBINED_${grade}_${Date.now()}`
    
    pageLoading.value = true
    
    // 1. Get or create subject "体育"
    let subId = null
    const existingSub = rawSubjectList.value.find(s => s.name === subName)
    if (existingSub) {
      subId = existingSub.id
    } else {
      await courseScheduleApi.createSubject({
        name: subName,
        subject_type: '体育艺术',
        requires_room: true
      })
      await refreshSubjectResources()
      const freshlyCreated = rawSubjectList.value.find(s => s.name === subName)
      if (freshlyCreated) {
        subId = freshlyCreated.id
      }
    }
    
    if (!subId) {
      throw new Error(`创建或获取科目 ${subName} 失败`)
    }
    
    // 2. Clear existing PE tasks for these classes or group to avoid duplicates
    let toDelete = []
    if (editingGroupId.value) {
      toDelete = tasks.value.filter(t => t.combined_group_id === editingGroupId.value)
    } else {
      const classesSet = new Set(sportsForm.value.classes)
      toDelete = tasks.value.filter(t => 
        t.grade === grade && 
        classesSet.has(t.class_name) && 
        t.subject_name === subName
      )
    }
    for (const t of toDelete) {
      await courseScheduleApi.deleteTask(workspaceId, t.id)
    }
    
    const teacherIds = sportsForm.value.teachers.map(id => Number(id)).filter(Boolean)
    const teacherNames = teacherIds.map(id => getTeacherName(id)).filter(Boolean)

    // Also format linked_classes to be stored. A PE combined class is one shared
    // lesson unit: multiple classes take class together with multiple teachers.
    const linkedClasses = sportsForm.value.classes.map(c => ({
      grade: grade,
      class_name: c,
      teacher_ids: teacherIds.join(','),
      teacher_names: teacherNames.join(',')
    }))
    
    // 3. Create one shared task for the whole PE combined class group.
    await courseScheduleApi.createTask(workspaceId, {
      grade: grade,
      class_name: sportsForm.value.classes[0],
      subject_id: subId,
      teacher_id: teacherIds[0],
      room_id: null,
      weekly_periods: weeklyPeriods,
      consecutive_periods: consecutivePeriods,
      is_combined: true,
      combined_group_id: groupId,
      linked_classes: linkedClasses
    })
    
    ElMessage.success('体育合班教学计划生成成功！')
    sportsWizardVisible.value = false
    editingGroupId.value = null
    await refreshTasks()
    await refreshResults()
  } catch (err) {
    console.error(err)
    ElMessage.error(`生成体育合班计划失败: ${err.message || err}`)
  } finally {
    pageLoading.value = false
  }
}

const submitWalkClassWizard = async () => {
  try {
    const grade = walkClassForm.value.grade
    const className = walkClassForm.value.className || '走班班级'
    const weeklyPeriods = walkClassForm.value.weeklyPeriods || 3
    const classes = walkClassForm.value.classes
    const subjects = walkClassForm.value.subjects || []
    const teachers = walkClassForm.value.teachers || []
    
    if (!grade || classes.length < 2) {
      ElMessage.warning('请选择年级并至少勾选两个行政班级')
      return
    }
    
    if (!className.trim()) {
      ElMessage.warning('请输入走班虚拟班级名称')
      return
    }
    
    if (subjects.length === 0) {
      ElMessage.warning('请选择至少一个走班科目')
      return
    }
    
    if (teachers.length === 0) {
      ElMessage.warning('请选择至少一位任课教师')
      return
    }
    
    pageLoading.value = true
    
    // 1. Get or create subject with combined name
    const subNames = subjects.map(sid => {
      return subjectList.value.find(s => s.id === sid)?.name || ''
    }).filter(Boolean)
    const subName = `走班(${subNames.join(',')})`
    
    let subId = null
    const existingSub = rawSubjectList.value.find(s => s.name === subName)
    if (existingSub) {
      subId = existingSub.id
    } else {
      await courseScheduleApi.createSubject({
        name: subName,
        subject_type: '文化课',
        requires_room: false
      })
      await refreshSubjectResources()
      const freshlyCreated = rawSubjectList.value.find(s => s.name === subName)
      if (freshlyCreated) {
        subId = freshlyCreated.id
      }
    }
    
    if (!subId) {
      throw new Error(`创建或获取科目 ${subName} 失败`)
    }
    
    // Generate a unique combined_group_id starting with WALK_
    const groupId = `WALK_COMBINED_${grade}_${Date.now()}`
    
    const teacherIds = teachers.map(id => Number(id)).filter(Boolean)
    const teacherNames = teacherIds.map(id => getTeacherName(id)).filter(Boolean)
    
    // Format linked_classes
    const linkedClasses = classes.map(c => ({
      grade: grade,
      class_name: c,
      teacher_ids: teacherIds.join(','),
      teacher_names: teacherNames.join(',')
    }))
    
    // 2. Create one single task representing this walk-class session (like PE)
    await courseScheduleApi.createTask(workspaceId, {
      grade: grade,
      class_name: className.trim(),
      subject_id: subId,
      teacher_id: teacherIds[0],
      room_id: null,
      weekly_periods: weeklyPeriods,
      consecutive_periods: 1,
      is_combined: true,
      combined_group_id: groupId,
      linked_classes: linkedClasses
    })
    
    ElMessage.success('走班课拼盘及合班计划已成功生成！')
    walkClassWizardVisible.value = false
    await refreshTasks()
    await refreshResults()
  } catch (err) {
    console.error(err)
    ElMessage.error(`生成走班计划失败: ${err.message || err}`)
  } finally {
    pageLoading.value = false
  }
}

const deleteWalkClassGroup = (group) => {
  ElMessageBox.confirm(
    `确定要删除该走班课合班计划吗？删除将同时删除对应的排课任务和排课结果。`,
    '提示',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  ).then(async () => {
    try {
      pageLoading.value = true
      const toDelete = tasks.value.filter(t => t.combined_group_id === group.groupId)
      for (const t of toDelete) {
        await courseScheduleApi.deleteTask(workspaceId, t.id)
      }
      ElMessage.success('删除成功')
      await refreshTasks()
      await refreshResults()
    } catch (err) {
      console.error(err)
      ElMessage.error(`删除失败: ${err.message || err}`)
    } finally {
      pageLoading.value = false
    }
  }).catch(() => {})
}

const getCombinedTasksForCard = (card) => {
  if (!card.combined_group_id) return []
  return results.value.filter(r => 
    r.combined_group_id === card.combined_group_id && 
    r.day_of_week === card.day_of_week && 
    r.period_index === card.period_index
  )
}

const getCourseCardTitle = (card) => {
  if (!card.is_combined) {
    return `${card.subject_name || ''} ${card.teacher_name || ''} ${card.grade || ''}${card.class_name || ''}`.trim()
  }
  const combined = getCombinedTasksForCard(card)
  if (!combined.length) {
    return `${card.subject_name || ''} ${card.teacher_name || ''}`.trim()
  }
  return combined.map((item) => {
    const className = `${item.grade || ''}${item.class_name || ''}`.trim()
    return `${className}：${item.subject_name || ''}（${item.teacher_name || '未分配'}）`
  }).join('\n')
}

const subjectFallbackPalette = [
  { base: '#ef4444', bg: '#fee2e2', border: '#fca5a5' },
  { base: '#2563eb', bg: '#dbeafe', border: '#93c5fd' },
  { base: '#16a34a', bg: '#dcfce7', border: '#86efac' },
  { base: '#d97706', bg: '#fef3c7', border: '#fcd34d' },
  { base: '#0891b2', bg: '#ecfeff', border: '#67e8f9' },
  { base: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd' },
  { base: '#db2777', bg: '#fce7f3', border: '#f9a8d4' },
  { base: '#65a30d', bg: '#f7fee7', border: '#bef264' },
  { base: '#ea580c', bg: '#ffedd5', border: '#fed7aa' },
  { base: '#475569', bg: '#f1f5f9', border: '#cbd5e1' },
  { base: '#0d9488', bg: '#ccfbf1', border: '#5eead4' },
  { base: '#9333ea', bg: '#f3e8ff', border: '#d8b4fe' }
]

const getReadableSubjectPalette = (card) => {
  const sub = subjectList.value.find(s => s.id === card.subject_id || s.name === card.subject_name)
  if (sub?.color) {
    return {
      base: sub.color,
      bg: `${sub.color}18`,
      border: `${sub.color}66`
    }
  }

  const key = String(card.subject_id || card.subject_name || '')
  let hash = 0
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0
  }
  return subjectFallbackPalette[hash % subjectFallbackPalette.length]
}

const getCourseCardStyle = (card) => {
  const palette = getReadableSubjectPalette(card)
  const baseColor = palette.base
  const bgColor = palette.bg
  const borderColor = palette.border
  
  const styleObj = {
    backgroundColor: bgColor,
    borderLeft: `4px solid ${baseColor}`,
    borderTop: `1px solid ${borderColor}`,
    borderBottom: `1px solid ${borderColor}`,
    borderRight: `1px solid ${borderColor}`
  }
  
  if (selectedActiveCard.value && selectedActiveCard.value.id === card.id) {
    styleObj.boxShadow = `0 0 0 2px ${baseColor}`
    styleObj.transform = 'scale(0.97)'
  }
  
  return styleObj
}

const teacherList = ref([])
const rawSubjectList = ref([])
const subjectList = computed(() => {
  return rawSubjectList.value.filter(sub => {
    if (sub.name && sub.name.includes('走班')) {
      return tasks.value && tasks.value.some(t => t.subject_id === sub.id || t.subject_name === sub.name)
    }
    return true
  })
})
const globalSubjects = ref([])
const roomOptions = ref([])

const uniqueSubjects = computed(() => {
  if (subjectList.value && subjectList.value.length > 0) {
    return subjectList.value.map(s => s.name)
  }
  const set = new Set()
  tasks.value.forEach(t => {
    if (t.subject_name) set.add(t.subject_name)
  })
  return Array.from(set)
})

// Search
const searchTaskQuery = ref('')

// Wizard Setup View States
const entryViewMode = ref('wizard') // Default to wizard view
const wizardStep = ref(0)
const gradesConfig = ref([])
const teachersGrid = ref({})
const copiedTeacherCell = ref(null)
const tempTeacherId = ref(null)

const getTeacherName = (tid) => {
  const t = teacherList.value.find(x => x.id === tid)
  return t ? t.name : '未知'
}

const getRemainingPeriods = (subId, classKey) => {
  const total = periodsGrid.value?.[subId]?.[classKey] || 0
  const cell = teachersGrid.value?.[subId]?.[classKey]
  const assignments = Array.isArray(cell)
    ? cell
    : (cell && (typeof cell === 'number' || typeof cell === 'string')
      ? [{ teacher_id: Number(cell), periods: total }]
      : [])
  const assigned = assignments.reduce((sum, t) => sum + (t.periods || 0), 0)
  return total - assigned
}

const onTeacherCellClick = (subId, classKey) => {
  tempTeacherId.value = null
  if (!teachersGrid.value[subId]) {
    teachersGrid.value[subId] = {}
  }
  const cell = teachersGrid.value[subId][classKey]
  if (!Array.isArray(cell)) {
    const total = periodsGrid.value?.[subId]?.[classKey] || 0
    teachersGrid.value[subId][classKey] = cell && (typeof cell === 'number' || typeof cell === 'string')
      ? [{ teacher_id: Number(cell), periods: total }]
      : []
  }
}

const syncCombinedTeachers = (subId, classKey) => {
  if (!tasks.value || tasks.value.length === 0) return;
  const parts = classKey.split(":");
  if (parts.length !== 2) return;
  const grade = parts[0];
  const className = parts[1];

  const combinedTask = tasks.value.find((t) => {
    return (
      t.subject_id === subId &&
      t.is_combined &&
      Array.isArray(t.linked_classes) &&
      t.linked_classes.some(
        (lc) =>
          lc.grade === grade &&
          (lc.class_name === className || lc.name === className)
      )
    );
  });

  if (combinedTask && Array.isArray(combinedTask.linked_classes)) {
    const sourceCell = teachersGrid.value?.[subId]?.[classKey];
    combinedTask.linked_classes.forEach((lc) => {
      const g = lc.grade;
      const c = lc.class_name || lc.name;
      if (g && c) {
        const targetKey = `${g}:${c}`;
        if (targetKey !== classKey) {
          if (!teachersGrid.value[subId]) {
            teachersGrid.value[subId] = {};
          }
          teachersGrid.value[subId][targetKey] = sourceCell
            ? JSON.parse(JSON.stringify(sourceCell))
            : [];
        }
      }
    });
  }
};

const addTeacherToCell = (subId, classKey) => {
  if (!tempTeacherId.value) return
  if (!teachersGrid.value[subId]) {
    teachersGrid.value[subId] = {}
  }
  const cell = teachersGrid.value[subId][classKey]
  if (!Array.isArray(cell)) {
    const total = periodsGrid.value?.[subId]?.[classKey] || 0
    teachersGrid.value[subId][classKey] = cell && (typeof cell === 'number' || typeof cell === 'string')
      ? [{ teacher_id: Number(cell), periods: total }]
      : []
  }

  const existing = teachersGrid.value[subId][classKey].find(x => x.teacher_id === tempTeacherId.value)
  if (existing) {
    tempTeacherId.value = null
    return
  }

  const remaining = getRemainingPeriods(subId, classKey)
  teachersGrid.value[subId][classKey].push({
    teacher_id: tempTeacherId.value,
    periods: remaining > 0 ? remaining : 0
  })
  tempTeacherId.value = null
  syncCombinedTeachers(subId, classKey)
}

const removeTeacherFromCell = (subId, classKey, idx) => {
  if (teachersGrid.value[subId]) {
    const cell = teachersGrid.value[subId][classKey]
    if (!Array.isArray(cell)) {
      const total = periodsGrid.value?.[subId]?.[classKey] || 0
      teachersGrid.value[subId][classKey] = cell && (typeof cell === 'number' || typeof cell === 'string')
        ? [{ teacher_id: Number(cell), periods: total }]
        : []
    }
    if (teachersGrid.value[subId][classKey][idx]) {
      teachersGrid.value[subId][classKey].splice(idx, 1)
    }
    syncCombinedTeachers(subId, classKey)
  }
}

const copyTeacherCell = (subId, classKey) => {
  const cell = teachersGrid.value[subId]?.[classKey]
  if (Array.isArray(cell)) {
    copiedTeacherCell.value = JSON.parse(JSON.stringify(cell))
    ElMessage.success('已复制教师分配设置')
  } else {
    ElMessage.warning('该单元格为空，无法复制')
  }
}

const pasteTeacherCell = (subId, classKey) => {
  if (copiedTeacherCell.value) {
    if (!teachersGrid.value[subId]) {
      teachersGrid.value[subId] = {}
    }
    teachersGrid.value[subId][classKey] = JSON.parse(JSON.stringify(copiedTeacherCell.value))
    syncCombinedTeachers(subId, classKey)
    ElMessage.success('已粘贴')
  }
}

const periodsGrid = ref({})
const teacherAssignViewMode = ref('grid')
const unassignedFilter = ref('')
const draggedBlock = ref(null)

const unassignedBlocks = computed(() => {
  const blocks = []
  groupedGradeTables.value.forEach(grade => {
    grade.classEntries.forEach(cls => {
      grade.subjects.forEach(sub => {
        if (isClassPeriodZero(sub.id, cls.key)) return
        const remaining = getRemainingPeriods(sub.id, cls.key)
        if (remaining > 0) {
          blocks.push({
            subId: sub.id,
            classKey: cls.key,
            subjectName: sub.name,
            className: cls.class_name,
            remainingPeriods: remaining
          })
        }
      })
    })
  })
  return blocks
})

const filteredUnassignedBlocks = computed(() => {
  const q = unassignedFilter.value.toLowerCase()
  if (!q) return unassignedBlocks.value
  return unassignedBlocks.value.filter(b => b.className.toLowerCase().includes(q) || b.subjectName.toLowerCase().includes(q))
})

const getTeacherAssignedBlocks = (teacherId) => {
  const blocks = []
  groupedGradeTables.value.forEach(grade => {
    grade.classEntries.forEach(cls => {
      grade.subjects.forEach(sub => {
        const cell = teachersGrid.value?.[sub.id]?.[cls.key]
        if (Array.isArray(cell)) {
          cell.forEach((t, idx) => {
            if (t.teacher_id === teacherId) {
              blocks.push({
                subId: sub.id,
                classKey: cls.key,
                subjectName: sub.name,
                className: cls.class_name,
                periods: t.periods,
                assignIdx: idx
              })
            }
          })
        } else if (cell && (typeof cell === 'number' || typeof cell === 'string')) {
          if (Number(cell) === teacherId) {
            const total = periodsGrid.value?.[sub.id]?.[cls.key] || 0
            blocks.push({
              subId: sub.id,
              classKey: cls.key,
              subjectName: sub.name,
              className: cls.class_name,
              periods: total,
              assignIdx: 0
            })
          }
        }
      })
    })
  })
  return blocks
}

const getTeacherTotalPeriods = (teacherId) => {
  return getTeacherAssignedBlocks(teacherId).reduce((sum, b) => sum + b.periods, 0)
}

const onDragStart = (e, block) => {
  draggedBlock.value = block
  e.dataTransfer.effectAllowed = 'move'
}

const onDropToTeacher = (e, teacherId) => {
  if (!draggedBlock.value) return
  const { subId, classKey, remainingPeriods } = draggedBlock.value

  if (!teachersGrid.value[subId]) {
    teachersGrid.value[subId] = {}
  }
  const cell = teachersGrid.value[subId][classKey]
  if (!Array.isArray(cell)) {
    const total = periodsGrid.value?.[subId]?.[classKey] || 0
    teachersGrid.value[subId][classKey] = cell && (typeof cell === 'number' || typeof cell === 'string')
      ? [{ teacher_id: Number(cell), periods: total }]
      : []
  }

  const existing = teachersGrid.value[subId][classKey].find(t => t.teacher_id === teacherId)
  if (existing) {
    existing.periods += remainingPeriods
  } else {
    teachersGrid.value[subId][classKey].push({
      teacher_id: teacherId,
      periods: remainingPeriods
    })
  }

  syncCombinedTeachers(subId, classKey)
  draggedBlock.value = null
}

const editAssignedBlock = (block) => {
  ElMessageBox.prompt('请输入分配节数:', '修改课时', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    inputValue: block.periods,
    inputPattern: /^\d+$/,
    inputErrorMessage: '请输入有效数字'
  }).then(({ value }) => {
    const val = parseInt(value, 10)
    const totalPeriods = periodsGrid.value?.[block.subId]?.[block.classKey] || 0
    if (!teachersGrid.value[block.subId]) {
      teachersGrid.value[block.subId] = {}
    }
    const cell = teachersGrid.value[block.subId][block.classKey] || []
    const others = cell.reduce((sum, t, idx) => sum + (idx === block.assignIdx ? 0 : t.periods), 0)

    if (others + val > totalPeriods) {
      ElMessage.warning(`超出总课时限制 (${totalPeriods}节)，无法修改`)
    } else {
      if (!teachersGrid.value[block.subId][block.classKey]) {
        teachersGrid.value[block.subId][block.classKey] = []
      }
      teachersGrid.value[block.subId][block.classKey][block.assignIdx].periods = val
      syncCombinedTeachers(block.subId, block.classKey)
    }
  }).catch(() => { })
}

const gradePeriodEditMode = ref({})

const downloadPeriodsTemplate = async () => {
  try {
    const res = await courseScheduleApi.exportPeriodsTemplate(workspaceId)
    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `周课时模板_${workspaceId}.xlsx`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    ElMessage.error('下载模板失败')
  }
}

const saveGridChangesSilently = async () => {
  try {
    syncPeriodsToTeachersGrid();
    const nextConfig = {
      ...(workspace.value?.config || {}),
      grades_config: gradesConfig.value,
      periods_grid: periodsGrid.value,
      teachers_grid: teachersGrid.value
    }
    const updated = await courseScheduleApi.updateWorkspace(workspaceId, { config: nextConfig })
    workspace.value = updated
    const payload = {
      grades_config: gradesConfig.value,
      periods_grid: periodsGrid.value,
      teachers_grid: teachersGrid.value
    }
    await courseScheduleApi.setupSync(workspaceId, payload)
    await refreshTasks()
    ElMessage.success('导入更改已自动保存')
  } catch (error) {
    console.error('自动保存导入更改失败:', error)
    ElMessage.error(error.response?.data?.detail || '导入成功，但自动保存至服务器失败，请手动保存进度')
  }
}

const handlePeriodsExcelUpload = async (file) => {
  if (!file.raw) return
  const formData = new FormData()
  formData.append('file', file.raw)
  try {
    const res = await courseScheduleApi.importPeriodsGrid(workspaceId, formData)
    if (res && res.success) {
      const parsedGrid = res.data
      let importCount = 0

      // Parse periodsGrid from parsedGrid
      for (const [subId, classesMap] of Object.entries(parsedGrid)) {
        const numericSubId = Number(subId)
        if (!periodsGrid.value[numericSubId]) {
          periodsGrid.value[numericSubId] = {}
        }
        for (const [classKey, periods] of Object.entries(classesMap)) {
          periodsGrid.value[numericSubId][classKey] = periods
          importCount++
        }
      }

      ElMessage.success(`成功导入 ${importCount} 个班级科目的周课时数`)
      await saveGridChangesSilently()
    }
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || '导入失败，请检查文件格式')
  }
}

const downloadTeacherGridTemplate = async () => {
  try {
    const res = await courseScheduleApi.exportTeacherGridTemplate(workspaceId)
    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `任课表模板_${workspaceId}.xlsx`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    ElMessage.error('下载模板失败')
  }
}

const handleTeacherGridExcelUpload = async (file) => {
  if (!file.raw) return
  const formData = new FormData()
  formData.append('file', file.raw)
  try {
    const res = await courseScheduleApi.importTeacherGrid(workspaceId, formData)
    if (res && res.success) {
      const parsedGrid = res.data
      let importCount = 0
      // Merge into teachersGrid
      for (const [subId, classesMap] of Object.entries(parsedGrid)) {
        if (!teachersGrid.value[subId]) {
          teachersGrid.value[subId] = {}
        }
        for (const [classKey, assignments] of Object.entries(classesMap)) {
          // Verify that this class is expected to have periods
          if (!isClassPeriodZero(subId, classKey)) {
            // Check periods and assign correctly
            const totalPeriods = periodsGrid.value?.[subId]?.[classKey] || 0
            const finalAssignments = []
            let assignedSum = 0

            assignments.forEach(a => {
              let p = a.periods
              if (p === null || p === undefined) {
                // default to remaining periods
                p = Math.max(0, totalPeriods - assignedSum)
              }
              finalAssignments.push({ teacher_id: a.teacher_id, periods: p })
              assignedSum += p
            })

            teachersGrid.value[subId][classKey] = finalAssignments
            importCount++
          }
        }
      }
      if (importCount === 0) {
        ElMessage.error(res.message || '导入成功 0 条，请检查学科名称和教师姓名是否与系统一致')
        return
      }
      ElMessage.success(res.message || `成功导入 ${importCount} 个班级科目的任课安排`)
      await saveGridChangesSilently()
    }
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || '导入失败，请检查文件格式')
  }
}

const savingWizard = ref(false)
const savingGrades = ref(false)

const allSubjectIds = computed(() => subjectList.value.map(sub => sub.id))
const gradeOptions = computed(() => gradesConfig.value.map(g => g.name).filter(Boolean))

const flatClasses = computed(() => {
  const list = []
  gradesConfig.value.forEach(g => {
    if (!g.name) return
    g.classes.forEach(c => {
      const name = typeof c === 'string' ? c : (c?.name || '')
      list.push({
        grade: g.name,
        class_name: name,
        key: `${g.name}:${name}`,
        label: `${g.name}${name}`
      })
    })
  })
  return list
})

const getDefaultDayPeriodCounts = (cfg = {}) => {
  const days = Number(cfg.days_per_week || workspace.value?.config?.days_per_week || 5)
  const morning = Number(cfg.morning_count ?? cfg.periods_morning ?? workspace.value?.config?.periods_morning ?? 4)
  const afternoon = Number(cfg.afternoon_count ?? cfg.periods_afternoon ?? workspace.value?.config?.periods_afternoon ?? 4)
  const evening = Number(cfg.evening_count ?? cfg.periods_evening ?? workspace.value?.config?.periods_evening ?? 0)
  const existing = cfg.day_period_counts || {}
  const result = {}
  for (let day = 1; day <= days; day++) {
    const current = existing[String(day)] || existing[day] || {}
    result[String(day)] = {
      morning: Number(current.morning ?? morning),
      afternoon: Number(current.afternoon ?? afternoon),
      evening: Number(current.evening ?? evening)
    }
  }
  return result
}

const getGradeSubjects = (grade) => {
  const selectedIds = Array.isArray(grade?.subject_ids) ? grade.subject_ids : []
  const activeIds = selectedIds.length > 0 ? selectedIds : allSubjectIds.value
  return subjectList.value.filter(sub => activeIds.includes(sub.id))
}

const subjectById = computed(() => {
  const map = new Map()
  subjectList.value.forEach(subject => map.set(subject.id, subject))
  return map
})

// Index combined/walk-class tasks once. The teacher grid calls
// isSubjectDisabledForClass for every rendered cell, so filtering tasks there
// made switching to this step increasingly expensive as data grew.
const combinedTasksByClass = computed(() => {
  const map = new Map()
  const addTask = (classKey, task) => {
    if (!classKey) return
    if (!map.has(classKey)) map.set(classKey, [])
    map.get(classKey).push(task)
  }

  tasks.value.forEach(task => {
    if (!task?.is_combined) return
    const groupId = task.combined_group_id || ''
    if (!groupId.startsWith('WALK_COMBINED_') && !groupId.startsWith('PE_COMBINED_')) return

    const primaryGrade = task.grade || ''
    if (task.class_name) addTask(`${primaryGrade}:${task.class_name}`, task)
    if (Array.isArray(task.linked_classes)) {
      task.linked_classes.forEach(linked => {
        const grade = linked.grade || primaryGrade
        const className = linked.class_name || linked.name
        if (className) addTask(`${grade}:${className}`, task)
      })
    }
  })
  return map
})

const isSubjectDisabledForClass = (sub, cls) => {
  if (!tasks.value || tasks.value.length === 0) return false

  const classTasks = combinedTasksByClass.value.get(cls.key) || []

  // 1. Check Walk Class Combined (if enabled)
  if (enableWalkClass.value) {
    for (const t of classTasks) {
      if (!(t.combined_group_id || '').startsWith('WALK_COMBINED_')) continue
      const sName = t.subject_name || ''
      const match = sName.match(/^走班\((.+)\)$/)
      if (match) {
        const walkSubjects = match[1].split(',').map(s => s.trim())
        if (walkSubjects.some(ws => {
          const wsNorm = ws.toLowerCase()
          const subNameNorm = sub.name.toLowerCase()
          const subShortNorm = (sub.short_name || '').toLowerCase()

          return wsNorm.includes(subNameNorm) || subNameNorm.includes(wsNorm) || (
            subShortNorm && (wsNorm.includes(subShortNorm) || subShortNorm.includes(wsNorm))
          ) || (
            (subNameNorm === '物理' && (wsNorm.includes('物') || wsNorm.includes('物理'))) ||
            (subNameNorm === '化学' && (wsNorm.includes('化') || wsNorm.includes('化学'))) ||
            (subNameNorm === '生物' && (wsNorm.includes('生') || wsNorm.includes('生物')))
          )
        })) {
          return true
        }
      }
    }
  }

  // 2. Check Sports/PE Combined (if enabled)
  if (enableSportsCombined.value) {
    const peTasks = classTasks.filter(t => (t.combined_group_id || '').startsWith('PE_COMBINED_'))
    if (peTasks.length > 0) {
      const subName = (sub.name || '').toLowerCase()
      const isSportsSub = subName.includes("体育") || 
                          subName.includes("专项") || 
                          subName.includes("运动") || 
                          subName.includes("活动") || 
                          ["篮球", "足球", "排球", "羽毛球", "乒乓球", "网球", "游泳", "武术", "健美操", "田径", "跳绳"].some(kw => subName.includes(kw))
      
      if (isSportsSub) {
        return true
      }
      
      if (peTasks.some(t => t.subject_id === sub.id || t.subject_name === sub.name)) {
        return true
      }
    }
  }

  return false
}

const getSubjectDisabledTooltip = (sub, cls) => {
  if (!isSubjectDisabledForClass(sub, cls)) return ''
  
  if (enableSportsCombined.value && tasks.value) {
    const hasPeTask = tasks.value.some(t => {
      if (!t.is_combined) return false
      const gid = t.combined_group_id || ''
      if (!gid.startsWith('PE_COMBINED_')) return false
      return t.grade === cls.grade && (
        t.class_name === cls.class_name ||
        (t.linked_classes && Array.isArray(t.linked_classes) && t.linked_classes.some(lc => (lc.class_name || lc.name) === cls.class_name))
      )
    })
    if (hasPeTask) {
      const subName = (sub.name || '').toLowerCase()
      const isSportsSub = subName.includes("体育") || 
                          subName.includes("专项") || 
                          subName.includes("运动") || 
                          subName.includes("活动") || 
                          ["篮球", "足球", "排球", "羽毛球", "乒乓球", "网球", "游泳", "武术", "健美操", "田径", "跳绳"].some(kw => subName.includes(kw))
      if (isSportsSub) {
        return `《${sub.name}》已在体育合班中安排，该行政班无需设置单班课时`
      }
    }
  }
  return `《${sub.name}》已在走班拼盘中安排，该行政班无需设置单班课时`
}

const groupedGradeTables = computed(() => {
  return gradesConfig.value
    .filter(grade => grade?.name && Array.isArray(grade.classes) && grade.classes.length > 0)
    .map(grade => {
      const allSubs = getGradeSubjects(grade)
      const filteredSubs = allSubs.filter(sub => {
        const classes = grade.classes || []
        const allDisabled = classes.every(c => {
          const cName = typeof c === 'string' ? c : c.name
          const cls = { grade: grade.name, class_name: cName, key: `${grade.name}:${cName}` }
          return isSubjectDisabledForClass(sub, cls)
        })
        return !allDisabled
      })
      
      return {
        ...grade,
        gradeName: grade.name,
        subjects: filteredSubs,
        classEntries: grade.classes.map(c => {
          const name = typeof c === 'string' ? c : (c?.name || '')
          return {
            class_name: name,
            key: `${grade.name}:${name}`,
            label: `${grade.name}${name}`
          }
        })
      }
    })
})

// Form & dialogs
const exportExcelDialogVisible = ref(false)
const exportSelectedExcelTypes = ref(['class', 'teacher', 'master', 'workload', 'room'])
const exportLoading = ref(false)
const exportFormat = ref('excel')

const taskDialogVisible = ref(false)
const isEditTask = ref(false)
const taskFormRef = ref(null)
const taskForm = ref({
  id: null,
  grade: '',
  class_name: '',
  subject_id: '',
  teacher_id: '',
  weekly_periods: 4,
  consecutive_periods: 1,
  room_id: null,
  is_combined: false,
  combined_group_id: ''
})

const constraintDialogVisible = ref(false)
const constraintForm = ref({
  constraint_type: 'fixed_slot',
  config: {},
  is_hard: true,
  weight: 5
})

const bulkForbiddenDialogVisible = ref(false)
const bulkForbiddenSubmitting = ref(false)
const bulkForbiddenForm = ref({
  target_type: 'subject',
  target_id: '',
  days: [4],
  session: 'afternoon',
  start_period: 1,
  end_period: 1
})

const globalParams = ref({
  intensity: 'medium',
  consecutive_preference: 'none',
  teacher_half_day_preference: 'none',
  avoid_lunch_consecutive: false
})

watch(() => workspace.value, (newVal) => {
  if (newVal?.config) {
    globalParams.value.intensity = newVal.config.intensity || 'medium'
    globalParams.value.consecutive_preference = newVal.config.consecutive_preference || 'none'
    globalParams.value.teacher_half_day_preference = newVal.config.teacher_half_day_preference || 'none'
    globalParams.value.avoid_lunch_consecutive = newVal.config.avoid_lunch_consecutive || false

    const sports = newVal.config.enable_sports_combined !== false
    const walk = newVal.config.enable_walk_class !== false
    if (!sports && !walk && activeTab.value === 'combined') {
      activeTab.value = 'tasks'
    }
  }
}, { immediate: true, deep: true })

watch([isSandboxActive, sandboxBackupId], () => {
  const localSandboxKey = `aidean_sandbox_${workspaceId}`
  localStorage.setItem(localSandboxKey, JSON.stringify({
    active: isSandboxActive.value,
    backupId: sandboxBackupId.value
  }))
})

const getGenerationStrategyHelp = (intensity) => {
  if (intensity === 'low') {
    return '适合先快速看看课表大致能不能排出来，等待时间最短。'
  }
  if (intensity === 'high') {
    return '适合规则较多、想让系统多尝试一会儿时使用，等待时间会更长。'
  }
  return '推荐大多数日常排课使用，兼顾生成质量和等待时间。'
}

const saveGlobalParameters = async () => {
  try {
    const nextConfig = {
      ...(workspace.value?.config || {}),
      intensity: globalParams.value.intensity,
      consecutive_preference: globalParams.value.consecutive_preference,
      teacher_half_day_preference: globalParams.value.teacher_half_day_preference,
      avoid_lunch_consecutive: globalParams.value.avoid_lunch_consecutive
    }
    const updated = await courseScheduleApi.updateWorkspace(workspaceId, { config: nextConfig })
    workspace.value = updated
    ElMessage.success('全局排课偏好已自动保存')
  } catch (error) {
    console.error(error)
    ElMessage.error('全局排课参数保存失败')
  }
}

const activePresetBrush = ref('forbidden')
const presetClassOptions = ref([])

const totalPeriodsCount = computed(() => {
  return periodsMorning.value + periodsAfternoon.value + periodsEvening.value
})

const handlePresetGradeChange = (val) => {
  constraintForm.value.config.class_name = ''
  const gradeCfg = gradesConfig.value.find(g => g.name === val)
  if (gradeCfg) {
    presetClassOptions.value = (gradeCfg.classes || []).map(c => typeof c === 'object' ? c.name : c)
  } else {
    presetClassOptions.value = []
  }
}

const handlePresetScopeChange = () => {
  constraintForm.value.config.grade = ''
  constraintForm.value.config.class_name = ''
  constraintForm.value.config.class_type = '普通班'
  presetClassOptions.value = []
}

const bulkForbiddenSlotPreview = computed(() => {
  const form = bulkForbiddenForm.value
  const slots = []
  const days = [...(form.days || [])].sort((a, b) => a - b)

  days.forEach(day => {
    const dayConfig = getDayPeriodConfig(day)
    let start = Number(form.start_period) || 1
    let end = Number(form.end_period) || 1
    if (form.session === 'morning') {
      start = 1
      end = dayConfig.morning
    } else if (form.session === 'afternoon') {
      start = dayConfig.morning + 1
      end = dayConfig.morning + dayConfig.afternoon
    } else if (form.session === 'evening') {
      start = dayConfig.morning + dayConfig.afternoon + 1
      end = start + dayConfig.evening - 1
    }
    if (end < start) return
    for (let period = start; period <= end; period += 1) {
      slots.push({ day, period })
    }
  })
  return slots
})

const bulkForbiddenSlotPreviewText = computed(() => {
  const form = bulkForbiddenForm.value
  const days = [...(form.days || [])].sort((a, b) => a - b).map(getDayName).join('、') || '未选择星期'
  if (form.session === 'custom') {
    return `${days}第${form.start_period}-${form.end_period}节`
  }
  return `${days}${form.session === 'morning' ? '上午' : form.session === 'afternoon' ? '下午' : '晚上'}`
})

const openBulkForbiddenDialog = () => {
  bulkForbiddenForm.value = {
    target_type: 'subject',
    target_id: subjectList.value.find(s => s.name === '语文')?.id || '',
    days: [4],
    session: 'afternoon',
    start_period: 1,
    end_period: totalDailyPeriods.value
  }
  bulkForbiddenDialogVisible.value = true
}

const submitBulkForbiddenDialog = async () => {
  const form = bulkForbiddenForm.value
  if (form.target_id === '' || form.target_id === null || form.target_id === undefined) {
    ElMessage.warning('请选择禁排对象')
    return
  }
  if (!form.days || form.days.length === 0) {
    ElMessage.warning('至少选择一个星期')
    return
  }
  const slots = bulkForbiddenSlotPreview.value
  if (slots.length === 0) {
    ElMessage.warning('当前时段没有可设置的课节')
    return
  }

  const existingKeys = new Set(
    constraints.value
      .filter(c => c.constraint_type === 'forbidden_slot')
      .map(c => `${c.config?.target_type}:${c.config?.target_id}:${c.config?.day}:${c.config?.period}`)
  )
  const pendingSlots = slots.filter(slot => {
    const targetId = form.target_type === 'class' ? form.target_id : Number(form.target_id)
    return !existingKeys.has(`${form.target_type}:${targetId}:${slot.day}:${slot.period}`)
  })

  if (pendingSlots.length === 0) {
    ElMessage.info('所选时段已经设置过禁排规则，无需重复添加')
    bulkForbiddenDialogVisible.value = false
    return
  }

  bulkForbiddenSubmitting.value = true
  try {
    const targetId = form.target_type === 'class' ? form.target_id : Number(form.target_id)
    await Promise.all(pendingSlots.map(slot => courseScheduleApi.createConstraint(workspaceId, {
      constraint_type: 'forbidden_slot',
      is_hard: true,
      weight: 1,
      config: {
        target_type: form.target_type,
        target_id: targetId,
        day: slot.day,
        period: slot.period
      }
    })))
    await refreshConstraints()
    bulkForbiddenDialogVisible.value = false
    ElMessage.success(`已批量设置 ${pendingSlots.length} 个禁排时段`)
  } catch (error) {
    console.error(error)
    ElMessage.error(error.response?.data?.detail || '批量设置失败，请稍后重试')
  } finally {
    bulkForbiddenSubmitting.value = false
  }
}

const clickPresetSlot = (d, p) => {
  const key = `${d}:${p}`
  if (!constraintForm.value.config.slots) {
    constraintForm.value.config.slots = {}
  }
  if (constraintForm.value.constraint_type === 'time_slot_limit') {
    if (constraintForm.value.config.slots[key] === 'selected') {
      delete constraintForm.value.config.slots[key]
    } else {
      constraintForm.value.config.slots[key] = 'selected'
    }
    return
  }
  if (activePresetBrush.value === 'normal') {
    delete constraintForm.value.config.slots[key]
  } else {
    constraintForm.value.config.slots[key] = activePresetBrush.value
  }
}

const clearPresetSlot = (d, p) => {
  const key = `${d}:${p}`
  if (constraintForm.value.config.slots) {
    delete constraintForm.value.config.slots[key]
  }
}

const getPresetSlotStyle = (d, p) => {
  const key = `${d}:${p}`
  const state = constraintForm.value.config?.slots?.[key]
  if (state === 'selected') {
    return { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #3b82f6', fontWeight: 'bold' }
  } else if (state === 'forbidden') {
    return { background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5' }
  } else if (state === 'mandatory') {
    return { background: '#dcfce7', color: '#16a34a', border: '1px solid #86efac' }
  } else if (state === 'preferred') {
    return { background: '#dbeafe', color: '#2563eb', border: '1px solid #93c5fd' }
  } else if (state === 'dispreferred') {
    return { background: '#ffedd5', color: '#ea580c', border: '1px solid #fed7aa' }
  }
  return { background: '#ffffff', color: '#94a3b8', border: '1px dashed #e2e8f0' }
}

const getPresetSlotText = (d, p) => {
  const key = `${d}:${p}`
  const state = constraintForm.value.config?.slots?.[key]
  if (state === 'selected') return '🔵 限制时段'
  if (state === 'forbidden') return '🔴 禁排'
  if (state === 'mandatory') return '🟢 必排'
  if (state === 'preferred') return '🔵 尽量排'
  if (state === 'dispreferred') return '🟡 尽量不'
  return ''
}

const backupDialogVisible = ref(false)
const backupName = ref('')
const conflictDrawerVisible = ref(false)

const importDialogVisible = ref(false)
const importing = ref(false)
const fileList = ref([])
const uploadFile = ref(null)

const pasteImportDialogVisible = ref(false)
const pasteImporting = ref(false)
const pastedText = ref('')
const parsedTasks = ref([])

const gridEditDialogVisible = ref(false)
const gridSaving = ref(false)
const gridRows = ref([])

// Timetable view state
const viewMode = ref('class')
const selectedClass = ref('')
const selectedTeacherId = ref('')
const selectedRoomId = ref('')

// Active selecting card for swap/place
const selectedActiveCard = ref(null)
const dragOverSlot = ref(null)
const draggedCourseCard = ref(null)
const dragEnterCounter = ref(0)
const dropHandled = ref(false)
const isTuning = ref(false)
const hoveredSlot = ref(null)
const pointerDragState = ref({
  active: false,
  source: null,
  card: null,
  startX: 0,
  startY: 0,
  x: 0,
  y: 0,
  moved: false
})
const suppressNextCardClick = ref(false)

// Staging area (暂存区)
const stagingCards = ref([])
const isStagingDragOver = ref(false)
const stagingEnterCounter = ref(0)

// Associated mini schedules variables
const autoAssociateMini = ref(true)
const miniSelectVal = ref('')
const pinnedMinis = ref([])

const activeMiniSchedules = computed(() => {
  const list = []
  const addedKeys = new Set()

  const addEntity = (item) => {
    if (!addedKeys.has(item.key)) {
      list.push(item)
      addedKeys.add(item.key)
    }
  }

  // 1. Pinned minis first
  pinnedMinis.value.forEach(p => addEntity(p))

  // 2. Auto association if enabled
  if (autoAssociateMini.value) {
    let hasDynamicItems = false

    // 2.1 Dragged card or selected card association
    if (selectedActiveCard.value) {
      hasDynamicItems = true
      if (selectedActiveCard.value.teacher_id) {
        addEntity({
          key: `teacher_${selectedActiveCard.value.teacher_id}`,
          type: 'teacher',
          id: selectedActiveCard.value.teacher_id,
          name: selectedActiveCard.value.teacher_name
        })
      }
      const classKey = `${selectedActiveCard.value.grade}${selectedActiveCard.value.class_name}`
      addEntity({
        key: `class_${classKey}`,
        type: 'class',
        grade: selectedActiveCard.value.grade,
        class_name: selectedActiveCard.value.class_name,
        name: classKey
      })
    }

    // 2.2 Hovered slot association
    if (hoveredSlot.value) {
      const cards = getSlotCards(hoveredSlot.value.day, hoveredSlot.value.period)
      if (cards.length > 0) {
        hasDynamicItems = true
        cards.forEach(card => {
          if (card.teacher_id) {
            addEntity({
              key: `teacher_${card.teacher_id}`,
              type: 'teacher',
              id: card.teacher_id,
              name: card.teacher_name
            })
          }
          if (card.grade && card.class_name) {
            const classKey = `${card.grade}${card.class_name}`
            addEntity({
              key: `class_${classKey}`,
              type: 'class',
              grade: card.grade,
              class_name: card.class_name,
              name: classKey
            })
          }
        })
      }
    }

    // 2.3 Fallback to main view filter association ONLY if no dynamic drag/hover selection is active
    if (!hasDynamicItems) {
      if (viewMode.value === 'class' && selectedClass.value) {
        const classTasks = tasks.value.filter(t => {
          if (`${t.grade}${t.class_name}` === selectedClass.value) return true
          if (t.linked_classes && Array.isArray(t.linked_classes)) {
            return t.linked_classes.some(lc => `${lc.grade || t.grade}${lc.class_name || lc.name}` === selectedClass.value)
          }
          return false
        })
        classTasks.forEach(ct => {
          if (ct.teacher_id) {
            addEntity({
              key: `teacher_${ct.teacher_id}`,
              type: 'teacher',
              id: ct.teacher_id,
              name: ct.teacher_name
            })
          }
          if (ct.linked_classes && Array.isArray(ct.linked_classes)) {
            ct.linked_classes.forEach(lc => {
              const ids = String(lc.teacher_ids || '').split(',').map(id => Number(id)).filter(Boolean)
              const names = String(lc.teacher_names || '').split(',').map(n => n.trim())
              ids.forEach((tid, idx) => {
                addEntity({
                  key: `teacher_${tid}`,
                  type: 'teacher',
                  id: tid,
                  name: names[idx] || getTeacherName(tid)
                })
              })
            })
          }
        })
      } else if (viewMode.value === 'teacher' && selectedTeacherId.value) {
        const teacherTasks = tasks.value.filter(t => {
          if (t.teacher_id === selectedTeacherId.value) return true
          if (t.linked_classes && Array.isArray(t.linked_classes)) {
            return t.linked_classes.some(lc => {
              const ids = String(lc.teacher_ids || '').split(',').map(id => Number(id)).filter(Boolean)
              return ids.includes(selectedTeacherId.value)
            })
          }
          if (t.teacher_ids) {
            return String(t.teacher_ids).split(',').map(id => Number(id)).filter(Boolean).includes(selectedTeacherId.value)
          }
          return false
        })
        teacherTasks.forEach(tt => {
          if (tt.grade && tt.class_name) {
            const classKey = `${tt.grade}${tt.class_name}`
            addEntity({
              key: `class_${classKey}`,
              type: 'class',
              grade: tt.grade,
              class_name: tt.class_name,
              name: classKey
            })
          }
          if (tt.linked_classes && Array.isArray(tt.linked_classes)) {
            tt.linked_classes.forEach(lc => {
              const g = lc.grade || tt.grade
              const cName = lc.class_name || lc.name
              if (g && cName) {
                const classKey = `${g}${cName}`
                addEntity({
                  key: `class_${classKey}`,
                  type: 'class',
                  grade: g,
                  class_name: cName,
                  name: classKey
                })
              }
            })
          }
        })
      }
    }
  }

  return list
})

const handleAddMiniPin = (val) => {
  if (!val) return
  const [type, idOrName] = val.split(':')
  const key = `${type}_${idOrName}`

  if (type === 'teacher') {
    const teacherId = Number(idOrName)
    const t = teacherList.value.find(item => item.id === teacherId)
    if (t && !pinnedMinis.value.some(p => p.key === key)) {
      pinnedMinis.value.push({
        key,
        type,
        id: teacherId,
        name: t.name
      })
    }
  } else if (type === 'class') {
    const classKey = idOrName
    const task = tasks.value.find(item => `${item.grade}${item.class_name}` === classKey)
    const grade = task ? task.grade : '高一'
    const className = task ? task.class_name : classKey.replace('高一', '')
    if (!pinnedMinis.value.some(p => p.key === key)) {
      pinnedMinis.value.push({
        key,
        type,
        grade,
        class_name: className,
        name: classKey
      })
    }
  } else if (type === 'subject') {
    const subjectName = idOrName
    if (!pinnedMinis.value.some(p => p.key === key)) {
      pinnedMinis.value.push({
        key,
        type,
        name: subjectName
      })
    }
  }

  miniSelectVal.value = ''
}

const removeMiniPin = (key) => {
  pinnedMinis.value = pinnedMinis.value.filter(p => p.key !== key)
}

const getEntityResults = (day, period, info) => {
  if (info.type === 'teacher') {
    return results.value.filter(r => {
      const matchTeacher = r.teacher_id === info.id || 
                           (r.teacher_ids && String(r.teacher_ids).split(',').map(Number).includes(info.id));
      return matchTeacher && r.day_of_week === day && r.period_index === period;
    });
  } else if (info.type === 'class') {
    return results.value.filter(r => r.grade === info.grade && r.class_name === info.class_name && r.day_of_week === day && r.period_index === period)
  } else if (info.type === 'subject') {
    return results.value.filter(r => r.subject_name === info.name && r.day_of_week === day && r.period_index === period)
  }
  return []
}

const checkEntityForbidden = (day, period, info) => {
  const isForbiddenBySlot = constraints.value.some(c => {
    if (c.constraint_type === 'forbidden_slot' && c.is_hard) {
      const cfg = c.config || {}
      if (Number(cfg.day) === day && Number(cfg.period) === period) {
        if (info.type === 'teacher' && cfg.target_type === 'teacher' && Number(cfg.target_id) === info.id) {
          return true
        }
        if (info.type === 'class' && cfg.target_type === 'class') {
          const classId = cfg.target_id
          const currentClassKey = `${info.grade}${info.class_name}`
          if (typeof classId === 'string') {
            if (classId.includes('-')) {
              const [g, c_name] = classId.split('-')
              return info.grade === g && info.class_name === c_name
            }
            return classId === currentClassKey || classId === `${info.grade}-${info.class_name}`
          }
        }
      }
    }
    return false
  })

  if (isForbiddenBySlot) return true

  if (info.type === 'teacher') {
    const isForbiddenByPreset = constraints.value.some(c => {
      if (c.constraint_type === 'teacher_preset_slots' && c.is_hard) {
        const cfg = c.config || {}
        const teacherIds = cfg.teacher_ids || []
        if (teacherIds.map(Number).includes(info.id)) {
          const slots = cfg.slots || {}
          const slotState = slots[`${day}_${period}`]
          return slotState === 'forbidden'
        }
      }
      return false
    })
    if (isForbiddenByPreset) return true
  }

  return false
}

const getMiniCellStyle = (d, p, mini) => {
  const cards = getEntityResults(d, p, mini)
  const isForbidden = checkEntityForbidden(d, p, mini)

  // Dynamic highlight from hovered or selected active card
  let isHighlighted = false
  if (hoveredSlot.value && hoveredSlot.value.day === d && hoveredSlot.value.period === p) {
    isHighlighted = true
  } else if (selectedActiveCard.value && selectedActiveCard.value.day_of_week === d && selectedActiveCard.value.period_index === p) {
    isHighlighted = true
  }

  const baseStyle = {
    borderRadius: '3px',
    height: '18px',
    fontSize: '9px',
    transition: 'all 0.15s ease'
  }

  if (isHighlighted) {
    baseStyle.boxShadow = '0 0 0 2px #f59e0b inset'
  }

  if (cards.length > 0) {
    if (cards.length > 1 && mini.type !== 'subject') {
      return {
        ...baseStyle,
        backgroundColor: '#ef4444',
        border: '1px solid #ef4444',
        color: '#ffffff',
        cursor: 'default'
      }
    }
    const card = cards[0]
    const sub = subjectList.value.find(s => s.id === card.subject_id || s.name === card.subject_name)
    const color = sub?.color || '#3b82f6'
    return {
      ...baseStyle,
      backgroundColor: color,
      border: `1px solid ${color}`,
      color: '#ffffff',
      cursor: 'default'
    }
  }

  if (isForbidden) {
    return {
      ...baseStyle,
      backgroundColor: '#e2e8f0',
      border: '1px solid #cbd5e1',
      color: '#94a3b8',
      cursor: 'not-allowed'
    }
  }

  return {
    ...baseStyle,
    backgroundColor: '#f8fafc',
    border: '1px dashed #cbd5e1',
    cursor: 'pointer'
  }
}

const getMiniCellContent = (d, p, mini) => {
  const cards = getEntityResults(d, p, mini)
  const isForbidden = checkEntityForbidden(d, p, mini)

  if (cards.length > 0) {
    if (mini.type === 'subject') {
      if (cards.length > 1) {
        return String(cards.length)
      }
      const card = cards[0]
      return card.class_name ? card.class_name.replace('班', '') : ''
    }
    if (cards.length > 1) return '重'
    const card = cards[0]
    if (mini.type === 'teacher') {
      return card.class_name ? card.class_name.replace('班', '') : ''
    } else {
      return card.subject_name ? card.subject_name.charAt(0) : ''
    }
  }

  if (isForbidden) return '✖'
  return ''
}

const getMiniCellTooltip = (d, p, mini) => {
  const dayName = getDayName(d)
  const periodName = getPeriodName(p)
  const prefix = `${dayName}第${p}节(${periodName}): `

  const cards = getEntityResults(d, p, mini)
  const isForbidden = checkEntityForbidden(d, p, mini)

  if (cards.length > 0) {
    if (mini.type === 'subject') {
      return prefix + `【${mini.name}】已排班级: ` + cards.map(c => `${c.grade}${c.class_name} (${c.teacher_name})`).join(' / ')
    }
    return prefix + cards.map(c => `【${c.grade}${c.class_name}】${c.subject_name} (${c.teacher_name})`).join(' / ')
  }
  if (isForbidden) return prefix + '禁排时段'
  return prefix + '空闲'
}

// Pin Yin autocomplete fields
const filteredTeachersForm = ref([])
const filteredTeacherOptions = ref([])

// Workspace configuration properties
const daysPerWeek = computed(() => workspace.value?.config?.days_per_week || 5)
const periodsMorning = computed(() => workspace.value?.config?.periods_morning || 4)
const periodsAfternoon = computed(() => workspace.value?.config?.periods_afternoon || 4)
const periodsEvening = computed(() => workspace.value?.config?.periods_evening || 0)
const getDayPeriodConfig = (day) => {
  const cfg = workspace.value?.config || {}
  const dayCfg = cfg.day_period_counts?.[String(day)] || {}
  return {
    morning: Number(dayCfg.morning ?? periodsMorning.value ?? 0),
    afternoon: Number(dayCfg.afternoon ?? periodsAfternoon.value ?? 0),
    evening: Number(dayCfg.evening ?? periodsEvening.value ?? 0)
  }
}

const getDayTotalPeriods = (day) => {
  if (viewMode.value === 'class' && selectedClass.value) {
    const gName = gradesConfig.value.find(g => selectedClass.value.startsWith(g.name))?.name
    const grade = gradesConfig.value.find(g => g.name === gName)
    if (grade && grade.schedule_cfg) {
      const dayCfg = grade.schedule_cfg.day_period_counts?.[String(day)] || {}
      return Number(dayCfg.morning ?? grade.schedule_cfg.morning_count ?? periodsMorning.value) + 
             Number(dayCfg.afternoon ?? grade.schedule_cfg.afternoon_count ?? periodsAfternoon.value) + 
             Number(dayCfg.evening ?? grade.schedule_cfg.evening_count ?? periodsEvening.value)
    }
  }
  const cfg = getDayPeriodConfig(day)
  return cfg.morning + cfg.afternoon + cfg.evening
}

const isSlotAvailable = (day, period) => {
  return Number(period) >= 1 && Number(period) <= getDayTotalPeriods(day)
}

const totalDailyPeriods = computed(() => {
  if (!workspace.value?.config) return 8
  const dayTotals = Array.from({ length: daysPerWeek.value }, (_, idx) => getDayTotalPeriods(idx + 1))
  return Math.max(...dayTotals)
})

const viewPeriodsMorning = computed(() => {
  if (viewMode.value === 'class' && selectedClass.value) {
    const gName = gradesConfig.value.find(g => selectedClass.value.startsWith(g.name))?.name
    const grade = gradesConfig.value.find(g => g.name === gName)
    if (grade && grade.schedule_cfg) {
       let maxM = grade.schedule_cfg.morning_count ?? periodsMorning.value
       for (let d = 1; d <= daysPerWeek.value; d++) {
         const m = Number(grade.schedule_cfg.day_period_counts?.[String(d)]?.morning ?? maxM)
         if (m > maxM) maxM = m
       }
       return maxM
    }
  }
  return periodsMorning.value
})

const viewPeriodsAfternoon = computed(() => {
  if (viewMode.value === 'class' && selectedClass.value) {
    const gName = gradesConfig.value.find(g => selectedClass.value.startsWith(g.name))?.name
    const grade = gradesConfig.value.find(g => g.name === gName)
    if (grade && grade.schedule_cfg) {
       let maxA = grade.schedule_cfg.afternoon_count ?? periodsAfternoon.value
       for (let d = 1; d <= daysPerWeek.value; d++) {
         const a = Number(grade.schedule_cfg.day_period_counts?.[String(d)]?.afternoon ?? maxA)
         if (a > maxA) maxA = a
       }
       return maxA
    }
  }
  return periodsAfternoon.value
})

const getTimetableCellFreeLabel = (day, period) => {
  if (viewMode.value === 'class' && selectedClass.value) {
    const gName = gradesConfig.value.find(g => selectedClass.value.startsWith(g.name))?.name
    const grade = gradesConfig.value.find(g => g.name === gName)
    if (grade && grade.schedule_cfg) {
      const cName = selectedClass.value.substring(gName.length)
      let list = grade.schedule_cfg.class_forbidden_periods?.[cName] || []
      let item = list.find(fp => fp.day === day && fp.period_index === period)
      if (item && item.label) return item.label
      
      list = grade.schedule_cfg.forbidden_periods || []
      item = list.find(fp => fp.day === day && fp.period_index === period)
      if (item && item.label) return item.label
    }
  }
  return ''
}

// Options
const classOptions = computed(() => {
  const classes = new Set()
  tasks.value.forEach(t => {
    // Add primary class
    if (t.grade && t.class_name) {
      classes.add(`${t.grade}${t.class_name}`)
    }
    // Add linked classes if present
    if (t.linked_classes && Array.isArray(t.linked_classes)) {
      t.linked_classes.forEach(lc => {
        const g = lc.grade || lc.class_name_grade
        const c = lc.class_name || lc.name
        if (g && c) {
          classes.add(`${g}${c}`)
        }
      })
    }
  })
  return Array.from(classes).sort()
})

const filteredTasks = computed(() => {
  if (!searchTaskQuery.value) return tasks.value
  const q = searchTaskQuery.value.trim().toLowerCase()
  return tasks.value.filter(t => {
    return t.grade.toLowerCase().includes(q) ||
      t.class_name.toLowerCase().includes(q) ||
      t.subject_name.toLowerCase().includes(q) ||
      t.teacher_name.toLowerCase().includes(q)
  })
})

const exportTimetable = () => {
  exportFormat.value = 'excel'
  exportExcelDialogVisible.value = true
}

const exportTimetableWord = () => {
  exportFormat.value = 'word'
  exportExcelDialogVisible.value = true
}

const confirmExportExcel = async () => {
  if (exportSelectedExcelTypes.value.length === 0) {
    ElMessage.warning('请至少选择一个要导出的表格内容')
    return
  }
  exportLoading.value = true
  try {
    const typesStr = exportSelectedExcelTypes.value.join(',')
    let data, filename
    if (exportFormat.value === 'excel') {
      data = await courseScheduleApi.exportTimetable(workspaceId, typesStr)
      filename = `课表导出_${workspace.value.name}.xlsx`
    } else {
      data = await courseScheduleApi.exportTimetableWord(workspaceId, typesStr)
      filename = `课表导出_${workspace.value.name}.docx`
    }
    const url = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    exportExcelDialogVisible.value = false
  } catch (error) {
    console.error('Export failed:', error)
    ElMessage.error(exportFormat.value === 'excel' ? '导出失败' : '导出 Word 失败')
  } finally {
    exportLoading.value = false
  }
}

onMounted(() => {
  window.addEventListener('mousemove', handlePointerDragMove)
  window.addEventListener('mouseup', handlePointerDragUp)
  const localSandboxKey = `aidean_sandbox_${workspaceId}`
  const localSandboxState = localStorage.getItem(localSandboxKey)
  if (localSandboxState) {
    try {
      const parsed = JSON.parse(localSandboxState)
      isSandboxActive.value = parsed.active || false
      sandboxBackupId.value = parsed.backupId || null
    } catch (e) {
      console.error(e)
    }
  }
  loadWorkspaceData()
  loadGlobalResources()
})

onBeforeUnmount(() => {
  window.removeEventListener('mousemove', handlePointerDragMove)
  window.removeEventListener('mouseup', handlePointerDragUp)
})

// Load workspace detail
const loadWorkspaceData = async () => {
  pageLoading.value = true
  try {
    const ws = await courseScheduleApi.getWorkspace(workspaceId)
    workspace.value = ws
    initTotalWeeks()  // 初始化学期总周数

    if (ws.config && ws.config.engine) {
      selectedSolver.value = ws.config.engine === 'classic' ? 'classic' : 'cpsat'
    }

    // Resume the newest persisted job after a page reload.
    if (ws.status === 'running') {
      const jobs = await courseScheduleApi.getJobs(workspaceId)
      const runningJob = jobs.find(job => ['queued', 'running'].includes(job.status))
      if (runningJob) startJobPolling(runningJob)
    }

    await refreshTasks()
    await refreshConstraints()
    await refreshBackups()
    await refreshResults()

    // Select default class
    if (classOptions.value.length > 0 && !selectedClass.value) {
      selectedClass.value = classOptions.value[0]
    }

    checkAndInitWizard()
  } catch (error) {
    console.error('Failed to load workspace data', error)
  } finally {
    pageLoading.value = false
  }
}

const isSlotInteractive = (day, period) => {
  if (!selectedActiveCard.value) return false
  if (day && period && !isSlotAvailable(day, period)) return false
  return true
}

const activeSlotMatches = (day, period) => {
  if (!selectedActiveCard.value) return false
  return selectedActiveCard.value.day_of_week === day && selectedActiveCard.value.period_index === period
}

const resetPointerDragState = () => {
  pointerDragState.value = {
    active: false,
    source: null,
    card: null,
    startX: 0,
    startY: 0,
    x: 0,
    y: 0,
    moved: false
  }
}

const startCoursePointerDrag = (card, event) => {
  if (event.target?.closest?.('.card-remove-btn')) return
  pointerDragState.value = {
    active: true,
    source: 'grid',
    card,
    startX: event.clientX,
    startY: event.clientY,
    x: event.clientX,
    y: event.clientY,
    moved: false
  }
}

const startStagingPointerDrag = (sc, event) => {
  if (event.target?.closest?.('button')) return
  const card = {
    id: null,
    task_id: sc.task_id,
    subject_name: sc.subject_name,
    teacher_name: sc.teacher_name,
    grade: sc.grade,
    class_name: sc.class_name,
    is_half_period: sc.is_half_period || false,
    half_period_type: sc.half_period_type || 'all',
    is_from_pool: true,
    is_combined: sc.is_combined || false,
    combined_group_id: sc.combined_group_id || null,
    _from_staging: true
  }
  pointerDragState.value = {
    active: true,
    source: 'staging',
    card,
    startX: event.clientX,
    startY: event.clientY,
    x: event.clientX,
    y: event.clientY,
    moved: false
  }
}

const handlePointerDragMove = (event) => {
  const state = pointerDragState.value
  if (!state.active || !state.card) return

  const dx = Math.abs(event.clientX - state.startX)
  const dy = Math.abs(event.clientY - state.startY)
  if (!state.moved && dx + dy < 6) return

  if (!state.moved) {
    selectedActiveCard.value = {
      ...state.card,
      is_from_pool: state.source === 'staging'
    }
    draggedCourseCard.value = state.card
    document.body.style.cursor = 'grabbing'
  }

  state.moved = true
  state.x = event.clientX
  state.y = event.clientY
  pointerDragState.value = { ...state }

  const cell = document.elementFromPoint(event.clientX, event.clientY)?.closest?.('.grid-cell')
  if (cell?.dataset?.day && cell?.dataset?.period) {
    setDragOverSlot(Number(cell.dataset.day), Number(cell.dataset.period))
  } else {
    dragOverSlot.value = null
  }

  isStagingDragOver.value = state.source === 'grid' && !!document
    .elementFromPoint(event.clientX, event.clientY)
    ?.closest?.('[data-staging-drop="true"]')
}

const handlePointerDragUp = async (event) => {
  const state = pointerDragState.value
  if (!state.active) return

  resetPointerDragState()
  if (!state.moved || !state.card) {
    return
  }

  const target = document.elementFromPoint(event.clientX, event.clientY)
  const stagingTarget = target?.closest?.('[data-staging-drop="true"]')
  const cell = target?.closest?.('.grid-cell')

  isStagingDragOver.value = false
  document.body.style.cursor = ''

  if (state.source === 'grid' && stagingTarget) {
    await moveGridCardToStaging(state.card)
  } else if (cell?.dataset?.day && cell?.dataset?.period) {
    await placeActiveCard(Number(cell.dataset.day), Number(cell.dataset.period))
  }

  suppressNextCardClick.value = true
  window.setTimeout(() => {
    suppressNextCardClick.value = false
  }, 0)
  dragOverSlot.value = null
  draggedCourseCard.value = null
}

const handleCourseCardClick = (card) => {
  if (suppressNextCardClick.value) return
  selectCard(card)
}

const selectCard = (card) => {
  if (selectedActiveCard.value && selectedActiveCard.value.id === card.id) {
    // Toggle select
    selectedActiveCard.value = null
  } else {
    // Select the card to be adjusted/swapped
    selectedActiveCard.value = {
      ...card,
      is_from_pool: false
    }
    if (activeTab.value !== 'week-adjust') {
      ElMessage.info(`已选中课时【${card.subject_name}】，点击或拖到目标格子即可移动；目标格已有同班课程时会自动对调。`)
    } else {
      ElMessage.info(`已选中第 ${selectedWeek.value} 周课时【${card.subject_name}】，请点击下方诊断栏的“📅 调课”按钮进行调整。`)
    }
  }
}

const selectHoldingTask = (holdTask) => {
  if (holdTask.remaining <= 0) {
    ElMessage.warning('该科目周课时数已全部排完！')
    return
  }

  if (selectedActiveCard.value && selectedActiveCard.value.task_id === holdTask.task_id && selectedActiveCard.value.is_from_pool) {
    selectedActiveCard.value = null
  } else {
    selectedActiveCard.value = buildHoldingActiveCard(holdTask)
    ElMessage.info(`已选中待排科目【${holdTask.subject_name}】，点击左侧课表任意单元格排入该课程。`)
  }
}

const buildHoldingActiveCard = (holdTask) => {
  const t = tasks.value.find(x => x.id === holdTask.task_id)
  return {
    id: null,
    task_id: holdTask.task_id,
    subject_name: holdTask.subject_name,
    teacher_name: holdTask.teacher_name,
    grade: holdTask.grade,
    class_name: holdTask.class_name,
    is_half_period: holdTask.weekly_periods % 1 !== 0,
    half_period_type: (holdTask.weekly_periods % 1 !== 0) ? 'odd' : 'all',
    is_from_pool: true,
    is_combined: t ? t.is_combined : false,
    combined_group_id: t ? t.combined_group_id : null
  }
}

const handleCellDragEnter = (day, period) => {
  if (!selectedActiveCard.value && !draggedCourseCard.value) return
  if (!isSlotAvailable(day, period)) return
  if (!dragOverSlot.value || dragOverSlot.value.day !== day || dragOverSlot.value.period !== period) {
    dragOverSlot.value = { day, period }
  }
}

const getDragPayload = (event) => {
  if (!event?.dataTransfer) return null
  const raw = event.dataTransfer.getData('application/json') || ''
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch (err) {
    console.warn('无法解析拖拽数据:', err)
    return null
  }
}

const setDragPayload = (event, type, card) => {
  if (!event?.dataTransfer || !card) return
  event.dataTransfer.setData('application/json', JSON.stringify({ type, card }))
}

const setCourseDragImage = (event, card) => {
  if (!event?.dataTransfer || !event.dataTransfer.setDragImage || !card) return

  const ghost = document.createElement('div')
  ghost.className = 'course-drag-ghost'
  ghost.innerHTML = `
    <div class="ghost-title">${card.subject_name || ''}</div>
    <div class="ghost-meta">${card.teacher_name || '未分配教师'} · ${card.grade || ''}${card.class_name || ''}</div>
  `
  document.body.appendChild(ghost)
  event.dataTransfer.setDragImage(ghost, 18, 18)
  window.setTimeout(() => {
    if (ghost.parentNode) {
      ghost.parentNode.removeChild(ghost)
    }
  }, 0)
}

const onCourseDragStart = (card, event) => {
  dropHandled.value = false
  dragEnterCounter.value = 0
  selectedActiveCard.value = {
    ...card,
    is_from_pool: false
  }
  draggedCourseCard.value = card
  if (event?.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', `course:${card.id}`)
    setDragPayload(event, 'course', card)
    setCourseDragImage(event, card)
  }
  // Set body cursor to grabbing during drag
  document.body.style.cursor = 'grabbing'
}

const onHoldingDragStart = (holdTask, event) => {
  if (holdTask.remaining <= 0) return
  dropHandled.value = false
  dragEnterCounter.value = 0
  selectedActiveCard.value = buildHoldingActiveCard(holdTask)
  draggedCourseCard.value = selectedActiveCard.value
  if (event?.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', `holding:${holdTask.task_id}`)
    setDragPayload(event, 'holding', selectedActiveCard.value)
    setCourseDragImage(event, selectedActiveCard.value)
  }
  document.body.style.cursor = 'grabbing'
}

const onDragEnd = () => {
  document.body.style.cursor = ''
  dragEnterCounter.value = 0
  dragOverSlot.value = null
  isStagingDragOver.value = false
  stagingEnterCounter.value = 0
  resetPointerDragState()
  // Only clean up if drop wasn't already handled (prevents race condition)
  if (!dropHandled.value) {
    // Drag ended without a valid drop — keep selectedActiveCard for click-based placement
    draggedCourseCard.value = null
  }
}

const handleCellDrop = async (day, period) => {
  dropHandled.value = true
  document.body.style.cursor = ''
  dragOverSlot.value = null
  dragEnterCounter.value = 0
  resetPointerDragState()
  const droppedCard = draggedCourseCard.value
  draggedCourseCard.value = null
  await placeActiveCard(day, period)
}

const handleCellClick = async (day, period) => {
  await placeActiveCard(day, period)
}

// ========== Staging Area Handlers ==========
const onStagingDragOver = (event) => {
  // Only show staging drop zone when dragging from the grid (not from pool/staging)
  if (draggedCourseCard.value && !selectedActiveCard.value?.is_from_pool) {
    event.dataTransfer.dropEffect = 'move'
  }
}

const onStagingDragEnter = () => {
  stagingEnterCounter.value++
  if (draggedCourseCard.value && !selectedActiveCard.value?.is_from_pool) {
    isStagingDragOver.value = true
    dragOverSlot.value = null
  }
}

const onStagingDragLeave = () => {
  stagingEnterCounter.value--
  if (stagingEnterCounter.value <= 0) {
    stagingEnterCounter.value = 0
    isStagingDragOver.value = false
  }
}

const moveGridCardToStaging = async (card) => {
  if (!card) return
  // Record the card info to staging before removing from grid
  const stagingEntry = {
    task_id: card.task_id,
    subject_name: card.subject_name,
    teacher_name: card.teacher_name,
    grade: card.grade,
    class_name: card.class_name,
    is_half_period: card.is_half_period,
    half_period_type: card.half_period_type,
    is_combined: card.is_combined,
    combined_group_id: card.combined_group_id,
    weekly_periods: undefined // will be set from task
  }

  // Get weekly_periods from task
  const taskObj = tasks.value.find(t => t.id === card.task_id)
  if (taskObj) {
    stagingEntry.weekly_periods = taskObj.weekly_periods
  }

  // Remove card from grid via API
  const adjs = []
  if (card.is_combined && card.combined_group_id) {
    const groupResults = Array.from(new Map(results.value.filter(r =>
      r.combined_group_id === card.combined_group_id &&
      r.day_of_week === card.day_of_week &&
      r.period_index === card.period_index
    ).map(r => [r.id, r])).values())
    groupResults.forEach(r => {
      adjs.push({
        result_id: r.id,
        task_id: r.task_id,
        day_of_week: 0,
        period_index: 0,
        old_day: r.day_of_week,
        old_period: r.period_index
      })
    })
  } else {
    adjs.push({
      result_id: card.id,
      task_id: card.task_id,
      day_of_week: 0,
      period_index: 0,
      old_day: card.day_of_week,
      old_period: card.period_index
    })
  }

  selectedActiveCard.value = null
  draggedCourseCard.value = null

  const previousResults = JSON.parse(JSON.stringify(results.value))
  setTimeout(() => {
    applyOptimisticAdjustments(adjs)
    stagingCards.value.push(stagingEntry)
  }, 50)

  try {
    isTuning.value = true
    await courseScheduleApi.adjustResults(workspaceId, adjs)
    ElMessage.success('已移入暂存区')
    await refreshResults()
  } catch (e) {
    console.error(e)
    ElMessage.error(e.response?.data?.detail || e.message || '移入暂存区失败')
    results.value = previousResults
    loadGridData()
    const idx = stagingCards.value.indexOf(stagingEntry)
    if (idx >= 0) stagingCards.value.splice(idx, 1)
  } finally {
    isTuning.value = false
  }
}

const onStagingDrop = async (event) => {
  isStagingDragOver.value = false
  stagingEnterCounter.value = 0
  dropHandled.value = true
  document.body.style.cursor = ''
  resetPointerDragState()

  const dragPayload = getDragPayload(event)
  const isGridCourseDrag = dragPayload?.type === 'course' || (draggedCourseCard.value && !selectedActiveCard.value?.is_from_pool)
  const card = draggedCourseCard.value || (isGridCourseDrag ? dragPayload?.card : null)
  if (!card || !isGridCourseDrag) {
    draggedCourseCard.value = null
    return
  }

  await moveGridCardToStaging(card)
}

const selectStagingCard = (sc) => {
  if (selectedActiveCard.value && selectedActiveCard.value.task_id === sc.task_id && selectedActiveCard.value.is_from_pool) {
    selectedActiveCard.value = null
  } else {
    const t = tasks.value.find(x => x.id === sc.task_id)
    selectedActiveCard.value = {
      id: null,
      task_id: sc.task_id,
      subject_name: sc.subject_name,
      teacher_name: sc.teacher_name,
      grade: sc.grade,
      class_name: sc.class_name,
      is_half_period: sc.is_half_period || false,
      half_period_type: sc.half_period_type || 'all',
      is_from_pool: true,
      is_combined: sc.is_combined || false,
      combined_group_id: sc.combined_group_id || null,
      _from_staging: true
    }
    ElMessage.info(`已选中暂存课程【${sc.subject_name}】，点击或拖拽到课表目标格排入。`)
  }
}

const handleStagingCardClick = (sc) => {
  if (suppressNextCardClick.value) return
  selectStagingCard(sc)
}

const onStagingCardDragStart = (sc, event) => {
  dropHandled.value = false
  dragEnterCounter.value = 0
  const t = tasks.value.find(x => x.id === sc.task_id)
  selectedActiveCard.value = {
    id: null,
    task_id: sc.task_id,
    subject_name: sc.subject_name,
    teacher_name: sc.teacher_name,
    grade: sc.grade,
    class_name: sc.class_name,
    is_half_period: sc.is_half_period || false,
    half_period_type: sc.half_period_type || 'all',
    is_from_pool: true,
    is_combined: sc.is_combined || false,
    combined_group_id: sc.combined_group_id || null,
    _from_staging: true
  }
  draggedCourseCard.value = selectedActiveCard.value
  if (event?.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', `staging:${sc.task_id}`)
    setDragPayload(event, 'staging', selectedActiveCard.value)
    setCourseDragImage(event, selectedActiveCard.value)
  }
  document.body.style.cursor = 'grabbing'
}

const removeStagingCard = (idx) => {
  stagingCards.value.splice(idx, 1)
}

const clearAllStaging = () => {
  stagingCards.value = []
  ElMessage.info('暂存区已清空')
}

const getSubjectType = (subjectId) => {
  const sub = subjectList.value.find(s => s.id === subjectId)
  return sub ? sub.subject_type : '文化课'
}

const checkSlotConflict = (day, period) => {
  if (!selectedActiveCard.value) return { hasConflict: false, message: '', type: 'none' }
  
  const active = selectedActiveCard.value
  const isCombined = active.is_combined && active.combined_group_id
  
  // 1. Is this slot available/open in the school calendar for this grade?
  if (!isSlotAvailable(day, period)) {
    return {
      hasConflict: true,
      message: '该时段未对年级开放排课（属于学校非教学时段）',
      type: 'hard'
    }
  }
  
  // Collect all tasks and classes that will move to (day, period)
  let movingTasks = []
  if (active.is_from_pool) {
    if (isCombined) {
      movingTasks = tasks.value.filter(t => t.combined_group_id === active.combined_group_id)
    } else {
      const t = tasks.value.find(x => x.id === active.task_id)
      if (t) movingTasks.push(t)
    }
  } else {
    // Cell to cell move
    if (isCombined) {
      // Find all results in this group at old position
      const groupRes = results.value.filter(r => 
        r.combined_group_id === active.combined_group_id &&
        r.day_of_week === active.day_of_week &&
        r.period_index === active.period_index
      )
      groupRes.forEach(r => {
        const t = tasks.value.find(x => x.id === r.task_id)
        if (t) movingTasks.push(t)
      })
    } else {
      const t = tasks.value.find(x => x.id === active.task_id)
      if (t) movingTasks.push(t)
    }
  }
  
  if (movingTasks.length === 0) {
    return { hasConflict: false, message: '', type: 'none' }
  }

  // 2. Check forbidden_slot constraints for moving tasks
  for (const t of movingTasks) {
    for (const c of constraints.value) {
      if (c.constraint_type === 'forbidden_slot' && c.is_hard) {
        const cfg = c.config || {}
        if (cfg.day === day && cfg.period === period) {
          const tType = cfg.target_type
          const tId = cfg.target_id
          
          if (tType === 'teacher') {
            const matchTeacher = t.teacher_id === Number(tId) ||
              (t.linked_classes && Array.isArray(t.linked_classes) && t.linked_classes.some(lc => {
                const ids = String(lc.teacher_ids || '').split(',').map(id => Number(id)).filter(Boolean);
                return ids.includes(Number(tId));
              }));
            if (matchTeacher) {
              return {
                hasConflict: true,
                message: `教师【${getTeacherName(Number(tId)) || '教师'}】在此时段为硬性禁排/请假时段`,
                type: 'hard'
              }
            }
          }
          if (tType === 'subject' && t.subject_id === Number(tId)) {
            return {
              hasConflict: true,
              message: `科目【${t.subject_name}】在此时段被设置禁排`,
              type: 'hard'
            }
          }
          const subType = getSubjectType(t.subject_id)
          if (tType === 'subject_type' && subType === String(tId)) {
            const cGrade = cfg.grade
            if (!cGrade || t.grade === String(cGrade)) {
              return {
                hasConflict: true,
                message: `该时段为走班锁定/禁排时段，限制常规【${tId}】排入`,
                type: 'hard'
              }
            }
          }
          if (tType === 'class') {
            const classKey = `${t.grade}${t.class_name}`
            const classKeyDash = `${t.grade}-${t.class_name}`
            if (String(tId) === classKey || String(tId) === classKeyDash) {
              return {
                hasConflict: true,
                message: `班级【${classKey}】在此时段被设置禁排`,
                type: 'hard'
              }
            }
          }
        }
      }
    }
  }

  // 3. Check teacher conflicts
  for (const t of movingTasks) {
    const tTeacherIds = [];
    if (t.teacher_id) tTeacherIds.push(Number(t.teacher_id));
    if (t.linked_classes && Array.isArray(t.linked_classes)) {
      t.linked_classes.forEach(lc => {
        const ids = String(lc.teacher_ids || '').split(',').map(id => Number(id)).filter(Boolean);
        ids.forEach(id => {
          if (!tTeacherIds.includes(id)) tTeacherIds.push(id);
        });
      });
    }
    
    if (tTeacherIds.length === 0) continue;
    
    // Find who is teaching at (day, period) in the workspace
    const otherTeacherResults = results.value.filter(r => {
      if (r.day_of_week !== day || r.period_index !== period) return false;
      const rTeacherIds = [];
      if (r.teacher_id) rTeacherIds.push(Number(r.teacher_id));
      if (r.teacher_ids) {
        String(r.teacher_ids).split(',').map(id => Number(id)).filter(Boolean).forEach(id => {
          if (!rTeacherIds.includes(id)) rTeacherIds.push(id);
        });
      }
      // Check intersection
      return tTeacherIds.some(id => rTeacherIds.includes(id));
    });
    
    for (const r of otherTeacherResults) {
      // Exclude itself or tasks in the same combined group
      const isSameTask = r.task_id === t.id
      const isSameCombined = isCombined && r.combined_group_id === active.combined_group_id
      
      // If we are swapping with the target course in the same class, it is not a conflict (that course will move away)
      const isSwappedOut = movingTasks.some(mt => mt.grade === r.grade && mt.class_name === r.class_name)
      
      if (!isSameTask && !isSameCombined && !isSwappedOut) {
        const rTeacherIds = [];
        if (r.teacher_id) rTeacherIds.push(Number(r.teacher_id));
        if (r.teacher_ids) {
          String(r.teacher_ids).split(',').map(id => Number(id)).filter(Boolean).forEach(id => {
            if (!rTeacherIds.includes(id)) rTeacherIds.push(id);
          });
        }
        const intersectingId = tTeacherIds.find(id => rTeacherIds.includes(id));
        const tName = intersectingId ? getTeacherName(intersectingId) : (t.teacher_name || '教师');
        
        return {
          hasConflict: true,
          message: `教师【${tName}】在此时段有冲突：正在【${r.grade}${r.class_name}】上【${r.subject_name}】`,
          type: 'hard'
        }
      }
    }
  }

  // 4. Check room conflicts
  for (const t of movingTasks) {
    const roomName = t.room_name
    if (!roomName) continue
    
    // Find who is using the room at (day, period)
    const otherRoomResults = results.value.filter(r => 
      r.day_of_week === day && 
      r.period_index === period && 
      r.room_name === roomName
    )
    
    for (const r of otherRoomResults) {
      const isSameTask = r.task_id === t.id
      const isSameCombined = isCombined && r.combined_group_id === active.combined_group_id
      const isSwappedOut = movingTasks.some(mt => mt.grade === r.grade && mt.class_name === r.class_name)
      
      if (!isSameTask && !isSameCombined && !isSwappedOut) {
        return {
          hasConflict: true,
          message: `场地【${roomName}】在此时段有冲突：已被【${r.grade}${r.class_name}】的【${r.subject_name}】占用`,
          type: 'hard'
        }
      }
    }
  }

  // 5. Evaluate swap or place status (success case)
  const swapTargets = getVisibleSlotSwapTargets(
    day,
    period,
    movingTasks.map(t => Number(t.id)),
    active.combined_group_id
  )
  
  if (swapTargets.length > 0) {
    const desc = swapTargets.map(st => `【${st.grade}${st.class_name} - ${st.subject_name}】`).join('、')
    return {
      hasConflict: false,
      message: `✅ 排入后将与当前的 ${desc} 进行对调`,
      type: 'swap'
    }
  }

  return {
    hasConflict: false,
    message: '✅ 此格子空闲，可直接排入该课程',
    type: 'free'
  }
}

const getSlotConflictClass = (day, period) => {
  if (!selectedActiveCard.value) return ''
  const check = checkSlotConflict(day, period)
  if (check.hasConflict) {
    return 'conflict-blocked'
  } else if (check.type === 'swap') {
    return 'conflict-swap'
  } else if (check.type === 'free') {
    return 'conflict-safe'
  }
  return ''
}

const cancelTuning = () => {
  selectedActiveCard.value = null
}

const getTuningBannerClass = () => {
  if (!hoveredSlot.value) return 'banner-neutral'
  const check = checkSlotConflict(hoveredSlot.value.day, hoveredSlot.value.period)
  if (check.hasConflict) return 'banner-danger'
  if (check.type === 'swap') return 'banner-warning'
  return 'banner-success'
}

const getDiagnosticMessage = () => {
  if (!hoveredSlot.value) return ''
  const check = checkSlotConflict(hoveredSlot.value.day, hoveredSlot.value.period)
  return check.message
}

const getDiagnosticMessageStyle = () => {
  if (!hoveredSlot.value) return {}
  const check = checkSlotConflict(hoveredSlot.value.day, hoveredSlot.value.period)
  if (check.hasConflict) return { color: '#dc2626', fontWeight: 'bold' }
  if (check.type === 'swap') return { color: '#d97706', fontWeight: 'bold' }
  return { color: '#16a34a', fontWeight: 'bold' }
}

const getClassCardAtSlot = (grade, className, day, period) => {
  return results.value.find(r => 
    r.grade === grade && 
    r.class_name === className && 
    r.day_of_week === day && 
    r.period_index === period
  )
}

const getVisibleSlotSwapTargets = (day, period, excludeTaskIds = [], combinedGroupId = null) => {
  const excluded = new Set((excludeTaskIds || []).map(id => Number(id)))
  const expanded = []

  getSlotCards(day, period).forEach((card) => {
    const sourceCards = Array.isArray(card.original_cards) && card.original_cards.length > 0
      ? card.original_cards
      : [card]
    sourceCards.forEach((item) => {
      if (!item) return
      const taskId = Number(item.task_id)
      if (excluded.has(taskId)) return
      if (combinedGroupId && item.combined_group_id === combinedGroupId) return
      if (!expanded.some(existing => Number(existing.task_id) === taskId)) {
        expanded.push(item)
      }
    })
  })
  return expanded
}

const applyOptimisticAdjustments = (adjs) => {
  adjs.forEach(adj => {
    const taskId = Number(adj.task_id)
    if (adj.day_of_week === 0 && adj.period_index === 0) {
      // Delete operation
      const idx = results.value.findIndex(r =>
        (adj.result_id != null ? String(r.id) === String(adj.result_id) : Number(r.task_id) === taskId) &&
        Number(r.day_of_week) === Number(adj.old_day) && 
        Number(r.period_index) === Number(adj.old_period)
      )
      if (idx >= 0) {
        results.value.splice(idx, 1)
      }
    } else {
      // Move or Swap operation
      let record
      if (adj.result_id != null) {
        record = results.value.find(r => String(r.id) === String(adj.result_id))
      } else if (adj.old_day !== undefined && adj.old_period !== undefined) {
        record = results.value.find(r => 
          Number(r.task_id) === taskId && 
          Number(r.day_of_week) === Number(adj.old_day) && 
          Number(r.period_index) === Number(adj.old_period)
        )
      }
      
      if (record) {
        record.day_of_week = adj.day_of_week
        record.period_index = adj.period_index
        record.is_manual_override = true
      } else {
        // Create new record (placement from pool/staging)
        const t = tasks.value.find(x => Number(x.id) === taskId)
        if (t) {
          results.value.push({
            id: 'temp_' + Date.now() + '_' + Math.random(),
            task_id: t.id,
            subject_name: t.subject_name,
            teacher_name: t.teacher_name,
            grade: t.grade,
            class_name: t.class_name,
            room_name: t.room_name,
            day_of_week: adj.day_of_week,
            period_index: adj.period_index,
            is_half_period: adj.is_half_period || false,
            half_period_type: adj.half_period_type || 'all',
            is_manual_override: true,
            is_combined: t.is_combined || false,
            combined_group_id: t.combined_group_id || null
          })
        }
      }
    }
  })
  loadGridData()
}

const placeActiveCardWeek = async (day, period) => {
  if (!selectedActiveCard.value) return
  const active = selectedActiveCard.value
  const week = selectedWeek.value
  
  if (!week) {
    ElMessage.warning('未选择当前周次，无法调课')
    return
  }

  if (active.day_of_week === day && active.period_index === period) {
    selectedActiveCard.value = null
    draggedCourseCard.value = null
    return
  }

  const previousResults = JSON.parse(JSON.stringify(results.value))
  
  try {
    isTuning.value = true
    
    // Find target cards in grid for target slot
    const targetCards = results.value.filter(r => {
      if (r.day_of_week !== day || r.period_index !== period) return false
      if (viewMode.value === 'class' && (r.grade === active.grade && r.class_name === active.class_name)) return true
      if (viewMode.value === 'teacher' && r.teacher_id === active.teacher_id) return true
      if (viewMode.value === 'room' && r.room_name === active.room_name) return true
      return false
    })

    // Optimistic UI update
    results.value = results.value.map(r => {
      if (r.id === active.id) {
        return { ...r, day_of_week: day, period_index: period, is_overridden: true, override_type: 'replace' }
      }
      if (targetCards.some(tc => tc.id === r.id)) {
        return { ...r, day_of_week: active.day_of_week, period_index: active.period_index, is_overridden: true, override_type: 'replace' }
      }
      return r
    })
    loadGridData()

    // Delete existing overrides for active card
    if (active.is_overridden && active.override_id) {
      await courseScheduleApi.deleteWeekOverride(workspaceId, active.override_id)
    }
    
    // Delete existing overrides for target cards
    for (const tc of targetCards) {
      if (tc.is_overridden && tc.override_id) {
        await courseScheduleApi.deleteWeekOverride(workspaceId, tc.override_id)
      }
    }

    // Create replace override for active card
    const payloadActive = {
      week_number: week,
      original_result_id: active.id,
      override_type: 'replace',
      new_day_of_week: day,
      new_period_index: period,
      new_teacher_id: null,
      remark: '拖拽调课'
    }
    await courseScheduleApi.createWeekOverride(workspaceId, payloadActive)
    
    // Create replace override for target cards (swap)
    for (const tc of targetCards) {
      const payloadTarget = {
        week_number: week,
        original_result_id: tc.id,
        override_type: 'replace',
        new_day_of_week: active.day_of_week,
        new_period_index: active.period_index,
        new_teacher_id: null,
        remark: '拖拽调课对调'
      }
      await courseScheduleApi.createWeekOverride(workspaceId, payloadTarget)
    }

    ElMessage.success('调课对调成功！')
    await refreshResults()
    loadGridData()
  } catch (e) {
    console.error(e)
    ElMessage.error(e.response?.data?.detail || e.message || '调课冲突，更新失败！')
    results.value = previousResults
    loadGridData()
  } finally {
    isTuning.value = false
    selectedActiveCard.value = null
    draggedCourseCard.value = null
    dropHandled.value = false
  }
}

const placeActiveCard = async (day, period) => {
  if (!selectedActiveCard.value) return
  if (!isSlotAvailable(day, period)) {
    ElMessage.warning(`${getDayName(day)}第${period}节当前没有开放，请先调整此年级的上课时间。`)
    return
  }

  if (activeTab.value === 'week-adjust') {
    await placeActiveCardWeek(day, period)
    return
  }

  // Pre-validate conflicts in client-side
  const conflict = checkSlotConflict(day, period)
  if (conflict.hasConflict) {
    ElMessage.warning(`无法排入：${conflict.message}`)
    return
  }

  const active = selectedActiveCard.value
  if (!active.is_from_pool && active.day_of_week === day && active.period_index === period) {
    selectedActiveCard.value = null
    draggedCourseCard.value = null
    return
  }
  const adjs = []

  // Check if active card is a combined group
  const isCombined = active.is_combined && active.combined_group_id
  
  if (active.is_from_pool) {
    // ----------------------------------------------------
    // CASE 1: Dragging/clicking from holding pool to cell
    // ----------------------------------------------------
    if (isCombined) {
      // Find all tasks in the same combined group from the tasks list
      const groupTasks = tasks.value.filter(t => t.combined_group_id === active.combined_group_id)
      const targetCards = getVisibleSlotSwapTargets(
        day,
        period,
        groupTasks.map(t => Number(t.id)),
        active.combined_group_id
      )

      targetCards.forEach((targetCard) => {
        adjs.push({
          result_id: targetCard.id,
          task_id: targetCard.task_id,
          day_of_week: 0,
          period_index: 0,
          old_day: day,
          old_period: period
        })
      })
      
      for (const t of groupTasks) {
        // Place the combined task from pool to (day, period)
        adjs.push({
          task_id: t.id,
          day_of_week: day,
          period_index: period,
          is_half_period: false,
          half_period_type: 'all'
        })
      }
    } else {
      // Single task placement from pool
      const taskObj = tasks.value.find(t => t.id === active.task_id)
      if (taskObj) {
        const targetCard = getVisibleSlotSwapTargets(day, period, [taskObj.id], active.combined_group_id)[0]
        if (targetCard) {
          adjs.push({
            result_id: targetCard.id,
            task_id: targetCard.task_id,
            day_of_week: 0,
            period_index: 0,
            old_day: day,
            old_period: period
          })
        }
        adjs.push({
          task_id: active.task_id,
          day_of_week: day,
          period_index: period,
          is_half_period: active.is_half_period,
          half_period_type: active.half_period_type
        })
      }
    }
  } else {
    // ----------------------------------------------------
    // CASE 2: Dragging/clicking from cell to cell
    // ----------------------------------------------------
    const oldDay = active.day_of_week
    const oldPeriod = active.period_index

    if (isCombined) {
      // Find all result records currently in this combined group at (oldDay, oldPeriod)
      const groupResults = Array.from(new Map(results.value.filter(r => 
        r.combined_group_id === active.combined_group_id &&
        r.day_of_week === oldDay &&
        r.period_index === oldPeriod
      ).map(r => [r.id, r])).values())
      const targetCards = getVisibleSlotSwapTargets(
        day,
        period,
        groupResults.map(r => Number(r.task_id)),
        active.combined_group_id
      )
      
      for (const r of groupResults) {
        // 1. Move this combined task to the target (day, period)
        adjs.push({
          result_id: r.id,
          task_id: r.task_id,
          day_of_week: day,
          period_index: period,
          is_half_period: r.is_half_period,
          half_period_type: r.half_period_type,
          old_day: oldDay,
          old_period: oldPeriod
        })
      }
      targetCards.forEach((targetCard) => {
        adjs.push({
          result_id: targetCard.id,
          task_id: targetCard.task_id,
          day_of_week: oldDay,
          period_index: oldPeriod,
          is_half_period: targetCard.is_half_period,
          half_period_type: targetCard.half_period_type,
          old_day: day,
          old_period: period
        })
      })
    } else {
      // Single task move or swap
      adjs.push({
        result_id: active.id,
        task_id: active.task_id,
        day_of_week: day,
        period_index: period,
        is_half_period: active.is_half_period,
        half_period_type: active.half_period_type,
        old_day: oldDay,
        old_period: oldPeriod
      })
      
      const targetCard = getVisibleSlotSwapTargets(day, period, [active.task_id], active.combined_group_id)[0]
      if (targetCard) {
        adjs.push({
          result_id: targetCard.id,
          task_id: targetCard.task_id,
          day_of_week: oldDay,
          period_index: oldPeriod,
          is_half_period: targetCard.is_half_period,
          half_period_type: targetCard.half_period_type,
          old_day: day,
          old_period: period
        })
      }
    }
  }

  if (adjs.length === 0) return

  const previousResults = JSON.parse(JSON.stringify(results.value))
  setTimeout(() => {
    applyOptimisticAdjustments(adjs)
  }, 50)

  selectedActiveCard.value = null
  draggedCourseCard.value = null
  dropHandled.value = false

  try {
    isTuning.value = true
    const wasFromStaging = active._from_staging
    const stagingTaskId = active.task_id
    await courseScheduleApi.adjustResults(workspaceId, adjs)
    ElMessage.success('调整课表成功！')
    // Remove from staging if it came from there
    if (wasFromStaging && stagingTaskId) {
      const idx = stagingCards.value.findIndex(sc => sc.task_id === stagingTaskId)
      if (idx >= 0) stagingCards.value.splice(idx, 1)
    }
    await refreshResults()
    loadGridData()
  } catch (e) {
    console.error(e)
    ElMessage.error(e.response?.data?.detail || e.message || '调整冲突，更新失败！')
    results.value = previousResults
    loadGridData()
  } finally {
    isTuning.value = false
    draggedCourseCard.value = null
    dropHandled.value = false
  }
}

const removeCardFromGrid = async (card) => {
  const adjs = []
  if (card.is_combined && card.combined_group_id) {
    const groupResults = Array.from(new Map(results.value.filter(r => 
      r.combined_group_id === card.combined_group_id &&
      r.day_of_week === card.day_of_week &&
      r.period_index === card.period_index
    ).map(r => [r.id, r])).values())
    groupResults.forEach(r => {
      adjs.push({
        result_id: r.id,
        task_id: r.task_id,
        day_of_week: 0,
        period_index: 0,
        old_day: r.day_of_week,
        old_period: r.period_index
      })
    })
  } else {
    adjs.push({
      result_id: card.id,
      task_id: card.task_id,
      day_of_week: 0,
      period_index: 0,
      old_day: card.day_of_week,
      old_period: card.period_index
    })
  }

  const taskObj = tasks.value.find(t => t.id === card.task_id)
  const stagingEntry = {
    task_id: card.task_id,
    subject_name: card.subject_name,
    teacher_name: card.teacher_name,
    grade: card.grade,
    class_name: card.class_name,
    is_half_period: card.is_half_period,
    half_period_type: card.half_period_type,
    is_combined: card.is_combined,
    combined_group_id: card.combined_group_id,
    weekly_periods: taskObj?.weekly_periods
  }

  selectedActiveCard.value = null
  draggedCourseCard.value = null

  const previousResults = JSON.parse(JSON.stringify(results.value))
  applyOptimisticAdjustments(adjs)
  stagingCards.value.push(stagingEntry)

  try {
    isTuning.value = true
    await courseScheduleApi.adjustResults(workspaceId, adjs)
    ElMessage.success('已移入暂存区')
    await refreshResults()
  } catch (e) {
    console.error(e)
    ElMessage.error(e.response?.data?.detail || e.message || '移除课程失败')
    results.value = previousResults
    loadGridData()
    const idx = stagingCards.value.indexOf(stagingEntry)
    if (idx >= 0) stagingCards.value.splice(idx, 1)
  } finally {
    isTuning.value = false
  }
}

// Conflict checking drawer
const runConflictCheck = async () => {
  try {
    const res = await courseScheduleApi.checkConflicts(workspaceId)
    conflicts.value = res.conflicts || []
    conflictDrawerVisible.value = true
  } catch (e) {
    console.error(e)
  }
}
// Helpers
const getStatusType = (status) => {
  switch (status) {
    case 'idle': return 'info'
    case 'running': return 'primary'
    case 'success': return 'success'
    case 'failed': return 'danger'
    default: return 'info'
  }
}

const getStatusText = (status) => {
  switch (status) {
    case 'idle': return '未编排'
    case 'running': return '正在生成'
    case 'success': return '已生成'
    case 'failed': return '生成失败'
    default: return status
  }
}

const getConstraintTagType = (type) => {
  switch (type) {
    case 'fixed_slot': return 'success'
    case 'forbidden_slot': return 'danger'
    case 'subject_mutex': return 'warning'
    case 'teacher_max_daily_periods': return 'primary'
    case 'subject_period_preference': return 'warning'
    case 'subject_simultaneous_limit': return 'danger'
    case 'subject_venue_binding': return 'primary'
    case 'class_venue_requirement': return 'success'
    case 'consecutive_settings': return 'info'
    case 'subject_preset_slots': return 'success'
    case 'time_slot_limit': return 'primary'
    case 'daily_limit': return 'warning'
    case 'subject_adjacency_limit': return 'danger'
    default: return 'info'
  }
}
const constraintTypesList = [
  { value: 'fixed_slot', label: '固定上课时间' },
  { value: 'forbidden_slot', label: '这时不排课' },
  { value: 'subject_preset_slots', label: '课程预设' },
  { value: 'teacher_preset_slots', label: '教师预设' },
  { value: 'subject_mutex', label: '科目同天互斥' },
  { value: 'subject_consecutive_limit', label: '科目禁止相邻' },
  { value: 'subject_adjacency_limit', label: '科目禁止相邻(支持前后)' },
  { value: 'teacher_max_daily_periods', label: '老师一天最多上几节' },
  { value: 'teacher_mutex', label: '老师不能同一时间上课' },
  { value: 'grade_progress_sync', label: '同年级进度尽量一致' },
  { value: 'subject_period_preference', label: '某门课尽量放在固定时段' },
  { value: 'subject_simultaneous_limit', label: '同科同时上课班级上限' },
  { value: 'subject_venue_binding', label: '学科绑定专用场地' },
  { value: 'class_venue_requirement', label: '特需班级排定专用场地' },
  { value: 'consecutive_settings', label: '学科连堂高级设置' },
  { value: 'time_slot_limit', label: '时段上限限制' },
  { value: 'daily_limit', label: '各天上限限制' },
  { value: 'subject_consecutive_preference', label: '同日同班同科连堂偏好' },
  { value: 'teacher_schedule_concentration', label: '教师排程集中度偏好' },
  { value: 'teacher_noon_break_protection', label: '教师午休时间保护' }
];

const getConstraintTypeText = (type) => {
  switch (type) {
    case 'fixed_slot': return '固定上课时间'
    case 'forbidden_slot': return '这时不排课'
    case 'subject_mutex': return '科目同天互斥'
    case 'subject_consecutive_limit': return '科目禁止相邻'
    case 'teacher_max_daily_periods': return '老师一天最多上几节'
    case 'teacher_mutex': return '老师不能同一时间上课'
    case 'grade_progress_sync': return '同年级进度尽量一致'
    case 'subject_period_preference': return '某门课尽量放在固定时段'
    case 'subject_simultaneous_limit': return '同科同时上课班级上限'
    case 'subject_venue_binding': return '学科绑定专用场地'
    case 'class_venue_requirement': return '特需班级排定专用场地'
    case 'consecutive_settings': return '学科连堂高级设置'
    case 'time_slot_limit': return '时段上限限制'
    case 'daily_limit': return '各天上限限制'
    case 'subject_adjacency_limit': return '科目禁止相邻(支持前后)'
    case 'subject_preset_slots': return '课程预设'
    case 'teacher_preset_slots': return '教师预设'
    case 'subject_consecutive_preference': return '同日同班同科连堂偏好'
    case 'teacher_schedule_concentration': return '教师排程集中度偏好'
    case 'teacher_noon_break_protection': return '教师午休时间保护'
    default: return type
  }
}

const getDayName = (day) => {
  const map = { 1: '星期一', 2: '星期二', 3: '星期三', 4: '星期四', 5: '星期五', 6: '星期六', 7: '星期日' }
  return map[day] || `周${day}`
}

const getTeacherShortName = (name) => {
  if (!name) return ''
  return name.length > 1 ? name.charAt(name.length - 1) : name
}

const getDayShortName = (day) => {
  const names = { 1: '一', 2: '二', 3: '三', 4: '四', 5: '五', 6: '六', 7: '日' }
  return names[day] || day
}

const getPeriodName = (p) => {
  if (workspace.value?.config?.period_names?.[String(p)]) {
    return workspace.value.config.period_names[String(p)]
  }
  return `第 ${p} 节`
}

const getHalfPeriodText = (type) => {
  if (type === 'upper') return '上半节'
  if (type === 'lower') return '下半节'
  if (type === 'odd') return '单周'
  if (type === 'even') return '双周'
  return '半节'
}

const getHalfPeriodTagType = (type) => {
  if (type === 'odd' || type === 'upper') return 'primary'
  if (type === 'even' || type === 'lower') return 'success'
  return 'info'
}

const formatDateTime = (val) => {
  if (!val) return ''
  return new Date(val).toLocaleString()
}

const goBack = () => {
  router.push('/course-schedule')
}

const handleTabChange = async (name) => {
  selectedActiveCard.value = null
  if (name === 'timetable') {
    selectedWeek.value = null
    await refreshResults()
  } else if (name === 'week-adjust') {
    if (selectedWeek.value === null) {
      selectedWeek.value = 1
    }
    await refreshResults()
  }
}

const subjectManagerVisible = ref(false)
const newSubjectForm = ref({ name: '' })
const subjectDetailDialogVisible = ref(false)
const savingSubjectDetail = ref(false)
const subjectDetailForm = ref({
  id: null,
  name: '',
  short_name: '',
  color: '',
  subject_type: '文化课',
  requires_teacher: true,
  requires_room: false,
  allows_consecutive: true,
  allows_multiple_daily: false,
  allows_morning: true,
  allows_afternoon: true,
  allows_last_period: true,
  include_in_workload: true,
  subject_limit_rules: true,
  exam_balance: true
})
const teacherManagerVisible = ref(false)
const newTeacherForm = ref({
  name: '',
  employee_id: '',
  subject_ids: [],
  grades: []
})

const teacherDetailDialogVisible = ref(false)
const teacherDetailForm = ref({
  id: null,
  name: '',
  employee_id: '',
  subject_ids: [],
  grades: []
})

const openTeacherDetailDialog = (teacher) => {
  teacherDetailForm.value = {
    id: teacher.id,
    name: teacher.name || '',
    employee_id: teacher.employee_id || '',
    subject_ids: (teacher.subjects || []).map(item => item.id),
    grades: teacher.grades || []
  }
  teacherDetailDialogVisible.value = true
}

const saveTeacherDetail = async () => {
  const name = teacherDetailForm.value.name.trim()
  if (!name) {
    ElMessage.warning('请输入教师姓名')
    return
  }
  try {
    await teacherApi.update(teacherDetailForm.value.id, {
      name,
      employee_id: teacherDetailForm.value.employee_id?.trim() || undefined,
      subject_ids: teacherDetailForm.value.subject_ids || [],
      grades: teacherDetailForm.value.grades || []
    })
    await refreshTeacherResources()
    ElMessage.success('教师信息已更新')
    teacherDetailDialogVisible.value = false
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || '保存失败')
  }
}

const deleteTeacher = async (id) => {
  try {
    await ElMessageBox.confirm('确定要删除该教师吗？删除后相关的任课安排可能会失效。', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await teacherApi.delete(id)
    await refreshTeacherResources()
    ElMessage.success('教师已删除')
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.response?.data?.detail || '删除失败')
    }
  }
}


const openSubjectManager = () => {
  newSubjectForm.value.name = ''
  subjectManagerVisible.value = true
}

const openTeacherManager = () => {
  newTeacherForm.value = {
    name: '',
    employee_id: '',
    subject_ids: [],
    grades: gradeOptions.value.length === 1 ? [gradeOptions.value[0]] : []
  }
  teacherManagerVisible.value = true
}

const createSubject = async () => {
  const name = newSubjectForm.value.name.trim()
  if (!name) {
    ElMessage.warning('请输入科目名称')
    return
  }
  try {
    await courseScheduleApi.createSubject({ name })
    await refreshSubjectResources()
    const created = rawSubjectList.value.find(item => item.name === name)
    if (created) {
      gradesConfig.value.forEach(grade => {
        if (!Array.isArray(grade.subject_ids)) grade.subject_ids = []
        if (!grade.subject_ids.includes(created.id)) {
          grade.subject_ids.push(created.id)
        }
      })
    }
    ElMessage.success('添加科目成功')
    newSubjectForm.value.name = ''
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || '添加科目失败')
  }
}

const openSubjectDetailDialog = (row) => {
  subjectDetailForm.value = {
    id: row.id,
    name: row.name || '',
    short_name: row.short_name || '',
    color: row.color || '',
    subject_type: row.subject_type || '文化课',
    requires_teacher: row.requires_teacher !== false,
    requires_room: !!row.requires_room,
    allows_consecutive: row.allows_consecutive !== false,
    allows_multiple_daily: !!row.allows_multiple_daily,
    allows_morning: row.allows_morning !== false,
    allows_afternoon: row.allows_afternoon !== false,
    allows_last_period: row.allows_last_period !== false,
    include_in_workload: row.include_in_workload !== false,
    subject_limit_rules: row.subject_limit_rules !== false,
    exam_balance: row.exam_balance !== false
  }
  subjectDetailDialogVisible.value = true
}

const saveSubjectDetail = async () => {
  const name = subjectDetailForm.value.name.trim()
  if (!name) {
    ElMessage.warning('请输入科目名称')
    return
  }
  savingSubjectDetail.value = true
  try {
    await courseScheduleApi.updateSubject(subjectDetailForm.value.id, {
      name: name,
      short_name: subjectDetailForm.value.short_name || null,
      color: subjectDetailForm.value.color || null,
      subject_type: subjectDetailForm.value.subject_type,
      requires_teacher: subjectDetailForm.value.requires_teacher,
      requires_room: subjectDetailForm.value.requires_room,
      allows_consecutive: subjectDetailForm.value.allows_consecutive,
      allows_multiple_daily: subjectDetailForm.value.allows_multiple_daily,
      allows_morning: subjectDetailForm.value.allows_morning,
      allows_afternoon: subjectDetailForm.value.allows_afternoon,
      allows_last_period: subjectDetailForm.value.allows_last_period,
      include_in_workload: subjectDetailForm.value.include_in_workload,
      subject_limit_rules: subjectDetailForm.value.subject_limit_rules,
      exam_balance: subjectDetailForm.value.exam_balance
    })
    ElMessage.success('更新科目详情成功')
    subjectDetailDialogVisible.value = false
    await refreshSubjectResources()
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || '保存科目详情失败')
  } finally {
    savingSubjectDetail.value = false
  }
}

const deleteSubject = async (id) => {
  try {
    await ElMessageBox.confirm('确定要删除该科目吗？这可能会影响使用此科目的现有任课计划！', '删除确认', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await courseScheduleApi.deleteSubject(id)
    ElMessage.success('删除科目成功')
    gradesConfig.value.forEach(grade => {
      grade.subject_ids = (grade.subject_ids || []).filter(subjectId => subjectId !== id)
    })
    await refreshSubjectResources()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.response?.data?.detail || '删除科目失败')
    }
  }
}

const createScheduleTeacher = async () => {
  const name = newTeacherForm.value.name.trim()
  if (!name) {
    ElMessage.warning('请输入教师姓名')
    return
  }
  try {
    await teacherApi.create({
      name,
      short_name: null,
      employee_id: newTeacherForm.value.employee_id?.trim() || undefined,
      max_hours: null,
      subject_ids: newTeacherForm.value.subject_ids || [],
      grades: newTeacherForm.value.grades || []
    })
    await refreshTeacherResources()
    ElMessage.success('教师已添加')
    newTeacherForm.value = {
      name: '',
      employee_id: '',
      subject_ids: [],
      grades: []
    }
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || '添加教师失败')
  }
}

// ======================== Teacher Batch Import ========================
const teacherBatchImportVisible = ref(false)
const teacherImportTab = ref('excel')
const teacherUploadRef = ref(null)
const teacherFile = ref(null)
const teacherImportText = ref('')
const teacherImportSubmitting = ref(false)
const parsedImportTeachers = ref([])

const showTeacherBatchImport = () => {
  teacherImportTab.value = 'excel'
  teacherFile.value = null
  if (teacherUploadRef.value) {
    teacherUploadRef.value.clearFiles()
  }
  teacherImportText.value = ''
  parsedImportTeachers.value = []
  teacherBatchImportVisible.value = true
}

const handleTeacherExcelChange = (uploadFile) => {
  teacherFile.value = uploadFile.raw
}

const handleTeacherExcelRemove = () => {
  teacherFile.value = null
}

const downloadTeacherTemplate = async () => {
  try {
    const blob = await teacherApi.downloadTemplate()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = '排课可用教师导入模板.xlsx'
    link.click()
    window.URL.revokeObjectURL(url)
  } catch (e) {
    console.error(e)
    ElMessage.error('下载模板失败')
  }
}

const parseTeacherImportText = () => {
  if (!teacherImportText.value.trim()) {
    parsedImportTeachers.value = []
    return
  }

  const lines = teacherImportText.value.split('\n')
  const result = []

  lines.forEach(line => {
    const rawLine = line.trim()
    if (!rawLine) return

    // Smartly detect field separator
    let parts = []
    if (rawLine.includes('\t')) {
      parts = rawLine.split('\t')
    } else {
      const englishCommas = (rawLine.match(/,/g) || []).length
      const chineseCommas = (rawLine.match(/，/g) || []).length
      const totalCommas = englishCommas + chineseCommas

      const englishSemicolons = (rawLine.match(/;/g) || []).length
      const chineseSemicolons = (rawLine.match(/；/g) || []).length
      const totalSemicolons = englishSemicolons + chineseSemicolons

      const enumerationCommas = (rawLine.match(/、/g) || []).length
      const verticalBars = (rawLine.match(/\|/g) || []).length

      if (totalCommas >= 2) {
        parts = rawLine.split(/[,，]/)
      } else if (totalSemicolons >= 2) {
        parts = rawLine.split(/[;；]/)
      } else if (enumerationCommas >= 2) {
        parts = rawLine.split(/[、]/)
      } else if (verticalBars >= 2) {
        parts = rawLine.split(/[|]/)
      } else {
        parts = rawLine.split(/\s+/)
      }
    }

    // Clean up parts
    parts = parts.map(p => p.trim()).filter(p => p.length > 0)

    if (parts.length >= 2) {
      const name = parts[0]
      const subject_name = parts[1]

      const gradeStartIndex = 2
      const remainingParts = parts.slice(gradeStartIndex)

      const grades = []
      remainingParts.forEach(part => {
        const subGrades = part.split(/[\s,，/、|]+/).map(g => g.trim()).filter(g => g.length > 0)
        grades.push(...subGrades)
      })

      const uniqueGrades = Array.from(new Set(grades))

      if (name && subject_name) {
        result.push({
          name,
          subject_name,
          grades: uniqueGrades
        })
      }
    }
  })

  parsedImportTeachers.value = result
}

const submitTeacherBatchImport = async () => {
  if (teacherImportTab.value === 'text') {
    if (parsedImportTeachers.value.length === 0) return

    teacherImportSubmitting.value = true
    try {
      const res = await teacherApi.batchImport({ teachers: parsedImportTeachers.value })
      ElMessage.success(res.message || '导入成功')
      teacherBatchImportVisible.value = false
      await refreshTeacherResources()
      await refreshSubjectResources()
      await refreshGlobalSubjects()
    } catch (e) {
      console.error(e)
    } finally {
      teacherImportSubmitting.value = false
    }
  } else if (teacherImportTab.value === 'excel') {
    if (!teacherFile.value) {
      ElMessage.warning('请先选择要上传的 Excel 文件')
      return
    }

    teacherImportSubmitting.value = true
    try {
      const res = await teacherApi.batchImportExcel(teacherFile.value)
      ElMessage.success(res.message || '导入成功')
      teacherBatchImportVisible.value = false
      await refreshTeacherResources()
      await refreshSubjectResources()
      await refreshGlobalSubjects()
    } catch (e) {
      console.error(e)
    } finally {
      teacherImportSubmitting.value = false
    }
  }
}

// Substitute Recommendation Logic
const substituteDialogVisible = ref(false)
const substituteLoading = ref(false)
const substituteCandidates = ref([])
const substituteContext = ref({
  day: null,
  period: null,
  taskId: null,
  subjectId: null,
  subjectName: '',
  grade: '',
  className: '',
  originalTeacherId: null,
  originalTeacherName: '',
  resultId: null
})

const openSubstituteDialog = async (card) => {
  if (card.is_from_pool) return
  substituteContext.value = {
    day: card.day,
    period: card.period,
    taskId: card.task_id,
    subjectId: card.subject_id,
    subjectName: card.subject_name,
    grade: card.grade,
    className: card.class_name,
    originalTeacherId: card.teacher_id,
    originalTeacherName: card.teacher_name || '未分配',
    resultId: card.id
  }
  
  substituteDialogVisible.value = true
  substituteLoading.value = true
  try {
    const res = await courseScheduleApi.recommendSubstitutes(workspaceId, {
      day_of_week: card.day,
      period_index: card.period,
      subject_id: card.subject_id,
      original_teacher_id: card.teacher_id || 0
    })
    substituteCandidates.value = res.recommendations || []
    if (res.message) {
      ElMessage.success(res.message)
    }
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || '获取代课推荐失败')
    substituteCandidates.value = []
  } finally {
    substituteLoading.value = false
  }
}

const copySubstituteTeacher = (name) => {
  navigator.clipboard.writeText(`请问 ${name} 老师，周${getDayName(substituteContext.value.day)}第${substituteContext.value.period}节是否有空代一节${substituteContext.value.grade}${substituteContext.value.className}的${substituteContext.value.subjectName}课？`).then(() => {
    ElMessage.success(`已复制 ${name} 老师的代课请求消息，请前往微信/钉钉粘贴发送。`)
  }).catch(() => {
    ElMessage.warning('复制失败，请手动记录')
  })
}

// ======================== Recovered Functions ========================
// --- addClass ---
const addClass = (gradeIndex) => {
  var _newClassInputs$value;
  const text =
    (_newClassInputs$value = newClassInputs.value[gradeIndex]) === null ||
    _newClassInputs$value === void 0
      ? void 0
      : _newClassInputs$value.trim();
  if (!text) return;
  const grade = gradesConfig.value[gradeIndex];
  if (grade.classes.length > 0 && typeof grade.classes[0] === "string") {
    grade.classes = grade.classes.map((c) =>
      typeof c === "string" ? { name: c, type: "普通班", participate: true } : c
    );
  }
  const exists = grade.classes.some(
    (c) => (typeof c === "string" ? c : c.name) === text
  );
  if (!exists) {
    grade.classes.push({ name: text, type: "普通班", participate: true });
  }
  newClassInputs.value[gradeIndex] = "";
};

// --- addGrade ---
const addGrade = () => {
  gradesConfig.value.push({
    name: "",
    campus: "主校区",
    phase: "高中",
    classes: [
      { name: "1班", type: "普通班", participate: true },
      { name: "2班", type: "普通班", participate: true },
      { name: "3班", type: "普通班", participate: true },
    ],
    subject_ids: [...allSubjectIds.value],
  });
};

// --- addGridRow ---
const addGridRow = () => {
  gridRows.value.push({
    grade: "",
    class_name: "",
    subject_name: "",
    teacher_name: "",
    weekly_periods: 4,
    consecutive_periods: 1,
    room_name: "",
    is_combined: false,
    combined_group_id: "",
  });
};

// --- addNewClassEntry ---
const addNewClassEntry = (gradeIndex) => {
  const grade = gradesConfig.value[gradeIndex];
  if (grade.classes.length > 0 && typeof grade.classes[0] === "string") {
    grade.classes = grade.classes.map((c) =>
      typeof c === "string" ? { name: c, type: "普通班", participate: true } : c
    );
  }
  grade.classes.push({ name: "", type: "普通班", participate: true });
};

// --- applyDefaultCountsToAllDaysForGrade ---
const applyDefaultCountsToAllDaysForGrade = (grade) => {
  if (!(grade !== null && grade !== void 0 && grade.schedule_cfg)) return;
  const counts = {};
  for (let day = 1; day <= grade.schedule_cfg.days_per_week; day++) {
    counts[String(day)] = {
      morning: Number(grade.schedule_cfg.morning_count || 0),
      afternoon: Number(grade.schedule_cfg.afternoon_count || 0),
      evening: Number(grade.schedule_cfg.evening_count || 0),
    };
  }
  grade.schedule_cfg.day_period_counts = counts;
  saveGradeScheduleSettings(grade, true);
};

// --- areClassesDifferentForSlot ---
const areClassesDifferentForSlot = (grade, day, row) => {
  const academicIndex =
    row === null || row === void 0 ? void 0 : row.academic_index;
  if (!academicIndex) return false; // Get all active classes
  const activeClassNames = (grade.classes || [])
    .filter((c) => {
      return typeof c === "string" ? true : c.participate !== false;
    })
    .map((c) => {
      return typeof c === "string" ? c : c.name || "";
    })
    .filter(Boolean);
  if (activeClassNames.length <= 1) return false; // Find forbidden period for each class
  const classStates = activeClassNames.map((className) => {
    var _grade$schedule_cfg5;
    const list =
      (grade === null ||
      grade === void 0 ||
      (_grade$schedule_cfg5 = grade.schedule_cfg) === null ||
      _grade$schedule_cfg5 === void 0 ||
      (_grade$schedule_cfg5 = _grade$schedule_cfg5.class_forbidden_periods) ===
        null ||
      _grade$schedule_cfg5 === void 0
        ? void 0
        : _grade$schedule_cfg5[className]) || [];
    const fp = list.find(
      (item) => item.day === day && item.period_index === academicIndex
    );
    return {
      forbidden: !!fp,
      label: (fp === null || fp === void 0 ? void 0 : fp.label) || "",
    };
  }); // Check if they are all identical
  const first = classStates[0];
  const allIdentical = classStates.every((state) => {
    return state.forbidden === first.forbidden && state.label === first.label;
  });
  return !allIdentical;
};

// --- autoGenerateClasses ---
const autoGenerateClasses = (gradeIndex, count) => {
  const grade = gradesConfig.value[gradeIndex];
  if (grade.classes.length > 0 && typeof grade.classes[0] === "string") {
    grade.classes = grade.classes.map((c) =>
      typeof c === "string" ? { name: c, type: "普通班", participate: true } : c
    );
  }
  for (let i = 1; i <= count; i++) {
    const className = `${i}班`;
    const exists = grade.classes.some(
      (c) => (typeof c === "string" ? c : c.name) === className
    );
    if (!exists) {
      grade.classes.push({
        name: className,
        type: "普通班",
        participate: true,
      });
    }
  }
};

// --- autoSaveGradeSchedule ---
const autoSaveGradeSchedule = async (grade) => {
  const key = grade.name || `grade-${gradesConfig.value.indexOf(grade)}`;
  scheduleAutoSaving.value[key] = true;
  try {
    var _workspace$value15;
    const nextConfig = {
      ...(((_workspace$value15 = workspace.value) === null ||
      _workspace$value15 === void 0
        ? void 0
        : _workspace$value15.config) || {}),
      grades_config: gradesConfig.value,
    };
    const updated = await courseScheduleApi.updateWorkspace(workspaceId, {
      config: nextConfig,
    });
    workspace.value = updated;
  } catch (error) {
    var _error$response4;
    console.error(error);
    ElMessage.error(
      ((_error$response4 = error.response) === null ||
      _error$response4 === void 0 ||
      (_error$response4 = _error$response4.data) === null ||
      _error$response4 === void 0
        ? void 0
        : _error$response4.detail) || "自动保存失败，请稍后再试"
    );
  } finally {
    scheduleAutoSaving.value[key] = false;
  }
};

// --- checkAndInitWizard ---
const checkAndInitWizard = () => {
  if (
    tasks.value &&
    tasks.value.length >= 0 &&
    subjectList.value &&
    subjectList.value.length > 0
  ) {
    initWizardData();
  }
};

// --- clearAllTasks ---
const clearAllTasks = () => {
  ElMessageBox.confirm(
    "确定清空当前排课里的全部任课安排吗？已经生成的课表也会受影响。",
    "清空确认",
    { confirmButtonText: "确认清空", cancelButtonText: "取消", type: "warning" }
  ).then(async () => {
    await courseScheduleApi.clearAllTasks(workspaceId);
    ElMessage.success("任课安排已清空");
    await refreshTasks();
    await refreshResults();
  });
};

// --- clearGridRows ---
const clearGridRows = () => {
  gridRows.value = [];
};

// --- deleteBackup ---
const deleteBackup = async (backupId) => {
  await courseScheduleApi.deleteBackup(workspaceId, backupId);
  ElMessage.success("这个保存版本已删除");
  await refreshBackups();
};

// --- deleteConstraint ---
const deleteConstraint = async (id) => {
  await courseScheduleApi.deleteConstraint(workspaceId, id);
  ElMessage.success("补充要求已删除");
  await refreshConstraints();
};

// --- deletePublicPeriod ---
const deletePublicPeriod = async (item) => {
  try {
    await ElMessageBox.confirm(
      `确定删除公共时段“${item.name}”吗？`,
      "删除提示",
      { confirmButtonText: "确定", cancelButtonText: "取消", type: "warning" }
    );
    publicPeriods.value = publicPeriods.value.filter((p) => p.id !== item.id);
    await savePublicPeriodsToWorkspace();
    ElMessage.success("删除成功");
  } catch (err) {
    if (err !== "cancel") {
      console.error(err);
    }
  }
};

// --- deleteTask ---
const deleteTask = (taskId) => {
  ElMessageBox.confirm(
    "确定删除这条任课安排吗？如果已经生成过课表，对应课表内容也会一起移除。",
    "删除确认",
    { confirmButtonText: "删除", cancelButtonText: "取消", type: "warning" }
  ).then(async () => {
    await courseScheduleApi.deleteTask(workspaceId, taskId);
    ElMessage.success("删除成功");
    await refreshTasks();
    await refreshResults();
  });
};

// --- deleteVenue ---
const deleteVenue = async (row) => {
  try {
    await ElMessageBox.confirm(`确定删除场室“${row.name}”吗？`, "删除提示", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning",
    });
    venues.value = venues.value.filter((v) => v.id !== row.id);
    await saveVenuesToWorkspace();
  } catch (err) {
    if (err !== "cancel") console.error(err);
  }
};

// --- detectGradesConfigFromTasks ---
const detectGradesConfigFromTasks = () => {
  const gradesMap = {};
  tasks.value.forEach((t) => {
    if (!gradesMap[t.grade]) {
      gradesMap[t.grade] = new Set();
    }
    gradesMap[t.grade].add(t.class_name);
  });
  const config = [];
  Object.keys(gradesMap)
    .sort()
    .forEach((grade) => {
      config.push({
        name: grade,
        classes: Array.from(gradesMap[grade]).sort((a, b) => {
          const numA = parseInt(a) || 0;
          const numB = parseInt(b) || 0;
          return numA - numB;
        }),
        subject_ids: tasks.value
          .filter((task) => task.grade === grade)
          .map((task) => task.subject_id)
          .filter((value, index, arr) => arr.indexOf(value) === index),
        schedule_cfg: null,
      });
    });
  return config;
};

// --- downloadTemplate ---
const downloadTemplate = async () => {
  try {
    const data = await courseScheduleApi.downloadTemplate();
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "teaching_tasks_template.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error(error);
  }
};

// --- ensurePeriodTimes ---
const ensurePeriodTimes = (cfg) => {
  if (!cfg.period_times) {
    cfg.period_times = {};
  }
  const defaultTimes = {
    early_study: { start: "07:30", end: "08:00" },
    morning_1: { start: "08:10", end: "08:55" },
    morning_2: { start: "09:05", end: "09:50" },
    morning_3: { start: "10:10", end: "10:55" },
    morning_4: { start: "11:05", end: "11:50" },
    noon_study: { start: "12:30", end: "13:30" },
    afternoon_1: { start: "14:00", end: "14:45" },
    afternoon_2: { start: "14:55", end: "15:40" },
    afternoon_3: { start: "15:50", end: "16:35" },
    afternoon_4: { start: "16:45", end: "17:30" },
    evening_1: { start: "18:30", end: "19:15" },
    evening_2: { start: "19:25", end: "20:10" },
  }; // Backfill missing ones
  Object.keys(defaultTimes).forEach((k) => {
    if (!cfg.period_times[k]) {
      cfg.period_times[k] = { ...defaultTimes[k] };
    }
  }); // Backfill dynamically added academic periods
  for (let i = 1; i <= cfg.morning_count; i++) {
    const k = `morning_${i}`;
    if (!cfg.period_times[k]) {
      cfg.period_times[k] = { start: "08:00", end: "08:45" };
    }
  }
  for (let i = 1; i <= cfg.afternoon_count; i++) {
    const k = `afternoon_${i}`;
    if (!cfg.period_times[k]) {
      cfg.period_times[k] = { start: "14:00", end: "14:45" };
    }
  }
  for (let i = 1; i <= cfg.evening_count; i++) {
    const k = `evening_${i}`;
    if (!cfg.period_times[k]) {
      cfg.period_times[k] = { start: "18:30", end: "19:15" };
    }
  }
};

// --- filterTeachersPinyin ---
const filterTeachersPinyin = (query) => {
  if (!query) {
    filteredTeacherOptions.value = teacherList.value;
    return;
  }
  const q = query.toLowerCase().trim();
  filteredTeacherOptions.value = teacherList.value.filter((t) => {
    const pinyin = getInitials(t.name);
    return t.name.toLowerCase().includes(q) || pinyin.includes(q);
  });
};

// --- filterTeachersPinyinForm ---
const filterTeachersPinyinForm = (query) => {
  if (!query) {
    filteredTeachersForm.value = teacherList.value;
    return;
  }
  const q = query.toLowerCase().trim();
  filteredTeachersForm.value = teacherList.value.filter((t) => {
    const pinyin = getInitials(t.name);
    return t.name.toLowerCase().includes(q) || pinyin.includes(q);
  });
};

// --- formatConstraintDesc ---
const formatConstraintDesc = (row) => {
  const c = row.config || {};
  const getSubName = (id) => {
    var _subjectList$value$fi2;
    return (
      ((_subjectList$value$fi2 = subjectList.value.find((s) => s.id === id)) ===
        null || _subjectList$value$fi2 === void 0
        ? void 0
        : _subjectList$value$fi2.name) || id
    );
  };
  const getTeacherName = (id) => {
    var _teacherList$value$fi;
    return (
      ((_teacherList$value$fi = teacherList.value.find((t) => t.id === id)) ===
        null || _teacherList$value$fi === void 0
        ? void 0
        : _teacherList$value$fi.name) || id
    );
  };
  switch (row.constraint_type) {
    case "fixed_slot":
      return `${c.grade}${c.class_name} 的【${getSubName(
        c.subject_id
      )}】固定放在周${c.day}第${c.period}节。`;
    case "forbidden_slot": {
      const typeText =
        c.target_type === "teacher"
          ? "教师"
          : c.target_type === "subject"
          ? "科目"
          : "班级";
      let nameText = c.target_id;
      if (c.target_type === "teacher")
        nameText = getTeacherName(Number(c.target_id));
      if (c.target_type === "subject")
        nameText = getSubName(Number(c.target_id));
      return `${typeText}【${nameText}】不排在周${c.day}第${c.period}节。`;
    }
    case "subject_mutex":
      return `学科【${(c.subject_ids || [])
        .map(getSubName)
        .join("、")}】不要排在同一天。`;
    case "subject_consecutive_limit":
      return `学科【${getSubName(c.subject_id_1)}】和【${getSubName(
        c.subject_id_2
      )}】不要前后连着上。`;
    case "teacher_max_daily_periods":
      return `教师【${getTeacherName(c.teacher_id)}】一天最多上 ${
        c.max_periods
      } 节课。`;
    case "teacher_mutex":
      return `教师【${(c.teacher_ids || [])
        .map(getTeacherName)
        .join("、")}】不能安排在同一时间上课。`;
    case "grade_progress_sync":
      return `年级【${c.grade}】的【${getSubName(
        c.subject_id
      )}】尽量排在同一天。`;
    case "subject_period_preference": {
      const prefText =
        c.preference === "morning"
          ? "上午"
          : c.preference === "afternoon"
          ? "下午"
          : "晚上";
      return `学科【${getSubName(c.subject_id)}】尽量安排在【${prefText}】。`;
    }
    case "subject_simultaneous_limit": {
      const scopeMap = { school: "全校", grade: "各年级", campus: "同校区" };
      return `同科限制：学科【${getSubName(c.subject_id)}】在【${
        scopeMap[c.limit_scope] || "全校"
      }】范围内，同一时间最多允许 ${c.max_classes} 个班级同时上课。`;
    }
    case "subject_venue_binding":
      return `场地绑定：学科【${getSubName(c.subject_id)}】必须在【${
        c.venue_type
      }】类型的场地上课。`;
    case "class_venue_requirement":
      return `专属场地：班级课程【${c.grade}${c.class_name} - ${getSubName(
        c.subject_id
      )}】必须在【${c.venue_type}】类型的场地上课。`;
    case "consecutive_settings": {
      const prefText =
        c.preferred_slots && c.preferred_slots.length > 0
          ? `，偏好课节：第 ${c.preferred_slots.join("、")} 节`
          : "";
      return `连堂设置：学科【${getSubName(c.subject_id)}】每周排 ${
        c.consecutive_count
      } 组 ${c.block_size} 节连堂${prefText}。`;
    }
    case "subject_preset_slots": {
      const scopeMap = {
        school: "全校相同科目",
        grade: "年级相同科目",
        class_type: "班型相同科目",
        class: "特定班级科目",
      };
      let targetText = scopeMap[c.scope] || c.scope;
      if (c.scope === "grade") targetText += `（${c.grade}）`;
      if (c.scope === "class") targetText += `（${c.grade}${c.class_name}）`;
      if (c.scope === "class_type") targetText += `（${c.class_type}）`;
      const slots = c.slots || {};
      const stateCount = {
        forbidden: 0,
        mandatory: 0,
        preferred: 0,
        dispreferred: 0,
      };
      Object.values(slots).forEach((s) => {
        if (stateCount[s] !== undefined) stateCount[s]++;
      });
      const descParts = [];
      if (stateCount.forbidden > 0)
        descParts.push(`禁排 ${stateCount.forbidden} 个时段`);
      if (stateCount.mandatory > 0)
        descParts.push(`必排 ${stateCount.mandatory} 个时段`);
      if (stateCount.preferred > 0)
        descParts.push(`尽量排 ${stateCount.preferred} 个时段`);
      if (stateCount.dispreferred > 0)
        descParts.push(`尽量不排 ${stateCount.dispreferred} 个时段`);
      return `课程预设：针对【${targetText}】的【${getSubName(
        c.subject_id
      )}】，${descParts.join("，") || "未设置具体偏好"}`;
    }
    case "teacher_preset_slots": {
      const teacherNames = (c.teacher_ids || [])
        .map((id) => {
          var _teacherList$value$fi2;
          return (
            ((_teacherList$value$fi2 = teacherList.value.find(
              (t) => t.id === id
            )) === null || _teacherList$value$fi2 === void 0
              ? void 0
              : _teacherList$value$fi2.name) || id
          );
        })
        .join("、");
      const slots = c.slots || {};
      const stateCount = {
        forbidden: 0,
        mandatory: 0,
        preferred: 0,
        dispreferred: 0,
      };
      Object.values(slots).forEach((s) => {
        if (stateCount[s] !== undefined) stateCount[s]++;
      });
      const descParts = [];
      if (stateCount.forbidden > 0)
        descParts.push(`禁排 ${stateCount.forbidden} 个时段`);
      if (stateCount.mandatory > 0)
        descParts.push(`必排 ${stateCount.mandatory} 个时段`);
      if (stateCount.preferred > 0)
        descParts.push(`尽量排 ${stateCount.preferred} 个时段`);
      if (stateCount.dispreferred > 0)
        descParts.push(`尽量不排 ${stateCount.dispreferred} 个时段`);
      return `教师预设：针对【${teacherNames || "未选择教师"}】，${
        descParts.join("，") || "未设置具体偏好"
      }`;
    }
    case "time_slot_limit": {
      const typeText =
        c.target_type === "teacher"
          ? `教师【${getTeacherName(c.teacher_id)}】`
          : `班级课程【${c.grade || "全校"}${c.class_name || ""} - ${getSubName(
              c.subject_id
            )}】`;
      const slotCount = Object.keys(c.slots || {}).length;
      return `时段限制：针对${typeText}，在选定的 ${slotCount} 个时段内，最多允许排 ${c.max_periods} 节课。`;
    }
    case "daily_limit": {
      const typeText =
        c.target_type === "teacher"
          ? `教师【${getTeacherName(c.teacher_id)}】`
          : `班级课程【${c.grade || "全校"}${c.class_name || ""} - ${getSubName(
              c.subject_id
            )}】`;
      return `各天限制：针对${typeText}，每天最多允许排 ${c.max_periods} 节课。`;
    }
    case "subject_adjacency_limit": {
      const dirText =
        c.direction === "before"
          ? "前面"
          : c.direction === "after"
          ? "后面"
          : "前后相邻位置";
      return `禁止相邻：学科【${getSubName(
        c.subject_id_1
      )}】禁止排在学科【${getSubName(c.subject_id_2)}】的【${dirText}】。`;
    }
    case "subject_consecutive_preference": {
      const prefType = c.preference_type === "consecutive" ? "尽量连堂" : "尽量分散（不连堂）";
      return `连堂偏好：学科【${getSubName(c.subject_id)}】在同一个班级上课时【${prefType}】。`;
    }
    case "teacher_schedule_concentration": {
      const concText = c.concentration === "concentrated" ? "尽量集中（减少空堂）" : "尽量分散（防止疲劳）";
      return `教师排程集中度：【${c.teacher_id === 'all' ? '全体教师' : getTeacherName(c.teacher_id)}】的课程【${concText}】。`;
    }
    case "teacher_noon_break_protection": {
      return `教师午休保护：【${c.teacher_id === 'all' ? '全体教师' : getTeacherName(c.teacher_id)}】尽量避免跨中午连堂（如上午最后一节和下午第一节连续上课）。`;
    }
    default:
      return JSON.stringify(c);
  }
};

// --- formatLessonCount ---
const formatLessonCount = (value) => {
  const n = Number(value || 0);
  return Number.isInteger(n) ? n : Number(n.toFixed(1));
};

// --- getAcademicPeriodCountBySection ---
const getAcademicPeriodCountBySection = (grade, day) => {
  var _grade$schedule_cfg$d6;
  if (!(grade !== null && grade !== void 0 && grade.schedule_cfg))
    return { morning: 0, afternoon: 0, evening: 0 };
  return (
    ((_grade$schedule_cfg$d6 = grade.schedule_cfg.day_period_counts) === null ||
    _grade$schedule_cfg$d6 === void 0
      ? void 0
      : _grade$schedule_cfg$d6[String(day)]) || {
      morning: Number(grade.schedule_cfg.morning_count || 0),
      afternoon: Number(grade.schedule_cfg.afternoon_count || 0),
      evening: Number(grade.schedule_cfg.evening_count || 0),
    }
  );
};

// --- getClassFreeLabel ---
const getClassFreeLabel = (grade, gradeIndex, day, row) => {
  var _grade$schedule_cfg4;
  const academicIndex =
    row === null || row === void 0 ? void 0 : row.academic_index;
  const className = gradeScheduleClassTargets.value[gradeIndex];
  const item = (
    (grade === null ||
    grade === void 0 ||
    (_grade$schedule_cfg4 = grade.schedule_cfg) === null ||
    _grade$schedule_cfg4 === void 0 ||
    (_grade$schedule_cfg4 = _grade$schedule_cfg4.class_forbidden_periods) ===
      null ||
    _grade$schedule_cfg4 === void 0
      ? void 0
      : _grade$schedule_cfg4[className]) || []
  ).find((fp) => fp.day === day && fp.period_index === academicIndex);
  return (item === null || item === void 0 ? void 0 : item.label) || "";
};

// --- getClassesDifferentTooltip ---
const getClassesDifferentTooltip = (grade, day, row) => {
  const academicIndex =
    row === null || row === void 0 ? void 0 : row.academic_index;
  if (!academicIndex) return "";
  const activeClassNames = (grade.classes || [])
    .filter((c) => {
      return typeof c === "string" ? true : c.participate !== false;
    })
    .map((c) => {
      return typeof c === "string" ? c : c.name || "";
    })
    .filter(Boolean);
  const details = activeClassNames.map((className) => {
    var _grade$schedule_cfg6;
    const list =
      (grade === null ||
      grade === void 0 ||
      (_grade$schedule_cfg6 = grade.schedule_cfg) === null ||
      _grade$schedule_cfg6 === void 0 ||
      (_grade$schedule_cfg6 = _grade$schedule_cfg6.class_forbidden_periods) ===
        null ||
      _grade$schedule_cfg6 === void 0
        ? void 0
        : _grade$schedule_cfg6[className]) || [];
    const fp = list.find(
      (item) => item.day === day && item.period_index === academicIndex
    );
    const status = fp ? fp.label || "免排课" : "正常上课";
    return `${className}: ${status}`;
  });
  return details.join(" | ");
};

// --- getCurrentFreeLabel ---
const getCurrentFreeLabel = (gradeIndex) => {
  const preset = gradeFreePresetTexts.value[gradeIndex];
  if (preset && preset !== "__custom__") return preset;
  return (gradeFreeCustomTexts.value[gradeIndex] || "").trim();
};

// --- getDayChineseName ---
const getDayChineseName = (d) => {
  const names = {
    1: "星期一",
    2: "星期二",
    3: "星期三",
    4: "星期四",
    5: "星期五",
    6: "星期六",
    7: "星期日",
  };
  return names[d] || `周${d}`;
};

// --- getGradeFreeLabel ---
const getGradeFreeLabel = (grade, day, row) => {
  var _grade$schedule_cfg3;
  const academicIndex =
    row === null || row === void 0 ? void 0 : row.academic_index;
  const item = (
    (grade === null ||
    grade === void 0 ||
    (_grade$schedule_cfg3 = grade.schedule_cfg) === null ||
    _grade$schedule_cfg3 === void 0
      ? void 0
      : _grade$schedule_cfg3.forbidden_periods) || []
  ).find((fp) => fp.day === day && fp.period_index === academicIndex);
  return (item === null || item === void 0 ? void 0 : item.label) || "";
};

// --- getGradePeriodValue ---
const getGradePeriodValue = (subjectId, classEntries) => {
  var _classEntries$, _periodsGrid$value$su, _periodsGrid$value9;
  const firstKey =
    classEntries === null ||
    classEntries === void 0 ||
    (_classEntries$ = classEntries[0]) === null ||
    _classEntries$ === void 0
      ? void 0
      : _classEntries$.key;
  if (!firstKey) return "";
  return (_periodsGrid$value$su =
    (_periodsGrid$value9 = periodsGrid.value) === null ||
    _periodsGrid$value9 === void 0 ||
    (_periodsGrid$value9 = _periodsGrid$value9[subjectId]) === null ||
    _periodsGrid$value9 === void 0
      ? void 0
      : _periodsGrid$value9[firstKey]) !== null &&
    _periodsGrid$value$su !== void 0
    ? _periodsGrid$value$su
    : 0;
};

// --- getGradeScheduleSummary ---
const getGradeScheduleSummary = (grade) => {
  const cfg = grade === null || grade === void 0 ? void 0 : grade.schedule_cfg;
  if (!cfg) return "未设置";
  const dayCounts = cfg.day_period_counts || {};
  const dayTexts = Object.keys(dayCounts)
    .sort((a, b) => Number(a) - Number(b))
    .map((day) => {
      const item = dayCounts[day];
      return `${getDayChineseName(Number(day)).replace("星期", "周")}${
        item.morning
      }/${item.afternoon}/${item.evening}`;
    });
  const summaryText = dayTexts.join("；");
  const hasClassDiff = Object.values(cfg.class_forbidden_periods || {}).some(
    (list) => Array.isArray(list) && list.length > 0
  );
  return hasClassDiff
    ? `默认 ${cfg.morning_count}-${cfg.afternoon_count}-${cfg.evening_count}，${summaryText}，免任课：各班不一样`
    : `默认 ${cfg.morning_count}-${cfg.afternoon_count}-${cfg.evening_count}，${summaryText}`;
};

// --- getGradeScopeUniformLabel ---
const getGradeScopeUniformLabel = (grade, day, row) => {
  var _grade$schedule_cfg9;
  const academicIndex =
    row === null || row === void 0 ? void 0 : row.academic_index;
  if (!academicIndex) return "";
  const activeClassNames = (grade.classes || [])
    .filter((c) => {
      return typeof c === "string" ? true : c.participate !== false;
    })
    .map((c) => {
      return typeof c === "string" ? c : c.name || "";
    })
    .filter(Boolean);
  if (activeClassNames.length === 0) return "";
  const list =
    (grade === null ||
    grade === void 0 ||
    (_grade$schedule_cfg9 = grade.schedule_cfg) === null ||
    _grade$schedule_cfg9 === void 0 ||
    (_grade$schedule_cfg9 = _grade$schedule_cfg9.class_forbidden_periods) ===
      null ||
    _grade$schedule_cfg9 === void 0
      ? void 0
      : _grade$schedule_cfg9[activeClassNames[0]]) || [];
  const fp = list.find(
    (item) => item.day === day && item.period_index === academicIndex
  );
  return (fp === null || fp === void 0 ? void 0 : fp.label) || "";
};

// --- getGridRows ---
const getGridRows = (cfg) => {
  const rows = [];
  let academicIndex = 1;
  if (cfg.has_early_study) {
    rows.push({
      key: "early_study",
      type: "early_study",
      name: "早自习",
      is_academic: false,
      academic_index: null,
    });
  }
  for (let i = 1; i <= cfg.morning_count; i++) {
    rows.push({
      key: `morning_${i}`,
      type: "morning",
      name: `第${i}节`,
      is_academic: true,
      academic_index: academicIndex++,
      section_index: i,
    });
  }
  if (cfg.has_noon_study) {
    rows.push({
      key: "noon_study",
      type: "noon_study",
      name: "午自习",
      is_academic: false,
      academic_index: null,
    });
  }
  for (let i = 1; i <= cfg.afternoon_count; i++) {
    const num = cfg.morning_count + i;
    rows.push({
      key: `afternoon_${i}`,
      type: "afternoon",
      name: `第${num}节`,
      is_academic: true,
      academic_index: academicIndex++,
      section_index: i,
    });
  }
  for (let i = 1; i <= cfg.evening_count; i++) {
    rows.push({
      key: `evening_${i}`,
      type: "evening",
      name: `晚${i}`,
      is_academic: true,
      academic_index: academicIndex++,
      section_index: i,
    });
  }
  return rows;
};

const PINYIN_INITIALS = {
  '语': 'y', '数': 's', '英': 'y', '物': 'w', '化': 'h', '生': 's', '地': 'd', '历': 'l', '政': 'z',
  '体': 't', '美': 'm', '音': 'y', '张': 'z', '王': 'w', '李': 'l', '赵': 'z', '陈': 'c', '刘': 'l',
  '杨': 'y', '黄': 'h', '吴': 'w', '徐': 'x', '孙': 's', '胡': 'h', '朱': 'z', '高': 'g', '林': 'l',
  '何': 'h', '郭': 'g', '马': 'm', '罗': 'l', '梁': 'l', '宋': 's', '郑': 'z', '韩': 'h', '唐': 't',
  '冯': 'f', '于': 'y', '董': 'd', '萧': 'x', '程': 'c', '柴': 'c', '袁': 'y', '邓': 'd', '许': 'x',
  '傅': 'f', '沈': 's', '曾': 'z', '彭': 'p', '吕': 'l', '苏': 's', '卢': 'l', '蒋': 'j', '蔡': 'c',
  '贾': 'j', '丁': 'd', '魏': 'w', '薛': 'x', '叶': 'y', '阎': 'y', '余': 'y', '潘': 'p', '杜': 'd',
  '戴': 'd'
}

// --- getInitials ---
const getInitials = (name) => {
  let initials = "";
  for (let i = 0; i < name.length; i++) {
    const char = name[i];
    initials += PINYIN_INITIALS[char] || char.toLowerCase()[0] || "";
  }
  return initials;
};

// --- getPublicPeriodPositionLabel ---
const getPublicPeriodPositionLabel = (item) => {
  const map = {
    before_period_1: "第一节前",
    after_period: `第 ${item.position_value || 1} 节后`,
    morning_end: "上午结束后",
    afternoon_end: "下午结束后",
    evening_end: "晚上结束后",
  };
  return map[item.position] || item.position;
};

// --- getPublicPeriodScopeLabel ---
const getPublicPeriodScopeLabel = (item) => {
  if (item.scope === "all") return "全校";
  if (item.scope === "grade") return `年级: ${item.scope_grade || "未选择"}`;
  return item.scope;
};

// --- getPublicPeriodTypeLabel ---
const getPublicPeriodTypeLabel = (type) => {
  const map = {
    display: "展示用",
    activity: "固定活动",
    forbidden: "禁排时段",
  };
  return map[type] || type;
};

// --- getPublicPeriodsForGrade ---
const getPublicPeriodsForGrade = (grade) => {
  return publicPeriods.value.filter((p) => {
    if (p.scope === "all") return true;
    if (p.scope === "grade") return p.scope_grade === grade.name;
    return false;
  });
};

// --- getScheduleRowsForGrade ---
const getScheduleRowsForGrade = (grade) => {
  if (!(grade !== null && grade !== void 0 && grade.schedule_cfg)) return [];
  return getGridRows(grade.schedule_cfg);
};

// --- getScheduleRowsWithPublicPeriods ---
const getScheduleRowsWithPublicPeriods = (grade) => {
  const academicRows = getScheduleRowsForGrade(grade);
  const result = []; // Get public periods for this grade
  const applicablePublicPeriods = getPublicPeriodsForGrade(grade).filter(
    (p) => p.show_in_schedule
  ); // 1. Position: before_period_1
  applicablePublicPeriods
    .filter((p) => p.position === "before_period_1")
    .forEach((p) => {
      result.push({
        ...p,
        is_public: true,
        key: `public_${p.id}`,
        row_type: "public",
      });
    }); // 2. Process academic rows
  academicRows.forEach((row) => {
    result.push(row); // Check if there are public periods after this academic period
    applicablePublicPeriods
      .filter(
        (p) =>
          p.position === "after_period" &&
          p.position_value === row.academic_index
      )
      .forEach((p) => {
        result.push({
          ...p,
          is_public: true,
          key: `public_${p.id}`,
          row_type: "public",
        });
      }); // Check for morning_end (last morning academic row)
    const isLastMorning =
      row.type === "morning" &&
      !academicRows.some(
        (r, idx) => r.type === "morning" && idx > academicRows.indexOf(row)
      );
    if (isLastMorning) {
      applicablePublicPeriods
        .filter((p) => p.position === "morning_end")
        .forEach((p) => {
          result.push({
            ...p,
            is_public: true,
            key: `public_${p.id}`,
            row_type: "public",
          });
        });
    } // Check for afternoon_end (last afternoon academic row)
    const isLastAfternoon =
      row.type === "afternoon" &&
      !academicRows.some(
        (r, idx) => r.type === "afternoon" && idx > academicRows.indexOf(row)
      );
    if (isLastAfternoon) {
      applicablePublicPeriods
        .filter((p) => p.position === "afternoon_end")
        .forEach((p) => {
          result.push({
            ...p,
            is_public: true,
            key: `public_${p.id}`,
            row_type: "public",
          });
        });
    } // Check for evening_end (last evening academic row)
    const isLastEvening =
      row.type === "evening" &&
      !academicRows.some(
        (r, idx) => r.type === "evening" && idx > academicRows.indexOf(row)
      );
    if (isLastEvening) {
      applicablePublicPeriods
        .filter((p) => p.position === "evening_end")
        .forEach((p) => {
          result.push({
            ...p,
            is_public: true,
            key: `public_${p.id}`,
            row_type: "public",
          });
        });
    }
  });
  return result;
};

// --- getShortFreeLabel ---
const getShortFreeLabel = (label, fallback) => {
  const text = (label || fallback || "").trim();
  if (!text) return "免任课";
  if (text.length <= 6) return text;
  return `${text.slice(0, 5)}...`;
};

// --- getSlotCards ---
const getSlotCards = (day, period) => {
  const cards = gridCards.value.filter(
    (c) => c.day_of_week === day && c.period_index === period
  );
  if (viewMode.value !== "class" || cards.length <= 1) {
    return cards;
  } // 走班/合班/体育专项 课表显示优化：在班级课表中，若同一时段有多节课，合并显示
  const isSports = cards.some(
    (c) =>
      (c.subject_name &&
        (c.subject_name.includes("体育") ||
          c.subject_name.includes("篮球") ||
          c.subject_name.includes("足球") ||
          c.subject_name.includes("排球") ||
          c.subject_name.includes("专项"))) ||
      (c.combined_group_id && c.combined_group_id.includes("体育"))
  );
  const baseCard = cards[0];
  if (isSports) {
    return [
      {
        ...baseCard,
        id: "grouped-" + cards.map((c) => c.id).join("-"),
        subject_name: "体育专项",
        teacher_name: Array.from(
          new Set(cards.map((c) => c.teacher_name).filter(Boolean))
        ).join(", "),
        room_name: Array.from(
          new Set(cards.map((c) => c.room_name).filter(Boolean))
        ).join(", "),
        original_cards: cards,
      },
    ];
  } else {
    return [
      {
        ...baseCard,
        id: "grouped-" + cards.map((c) => c.id).join("-"),
        subject_name: Array.from(
          new Set(cards.map((c) => c.subject_name))
        ).join(", "),
        teacher_name: "", // 走班课不显示教师姓名以节省空间
        room_name: "",
        original_cards: cards,
      },
    ];
  }
};


// --- goToStep ---
const ensureWizardGridsInitialized = () => {
  // Initialize only subjects selected for each grade. The previous code used
  // every global subject for every class, which made a step change do much
  // more reactive work than the grid actually needed.
  gradesConfig.value.forEach(grade => {
    if (!grade?.name || !Array.isArray(grade.classes)) return
    const gradeSubjects = getGradeSubjects(grade)
    grade.classes.forEach(classEntry => {
      const className = typeof classEntry === 'string' ? classEntry : (classEntry?.name || '')
      if (!className) return
      const classKey = `${grade.name}:${className}`
      gradeSubjects.forEach(sub => {
        if (periodsGrid.value[sub.id] === undefined) periodsGrid.value[sub.id] = {}
        if (periodsGrid.value[sub.id][classKey] === undefined) periodsGrid.value[sub.id][classKey] = 0
        if (teachersGrid.value[sub.id] === undefined) teachersGrid.value[sub.id] = {}
        if (teachersGrid.value[sub.id][classKey] === undefined) teachersGrid.value[sub.id][classKey] = []
      })
    })
  })
}

const goToStep = (stepIdx) => {
  if (stepIdx === wizardStep.value) return;
  if (stepIdx > wizardStep.value) {
    if (wizardStep.value === 2 && !validateGrades()) return;
    if (wizardStep.value <= 3 && stepIdx >= 4 && !validateGradeSchedules())
      return;
    if (stepIdx >= 4) {
      ensureWizardGridsInitialized();
    }
  }
  wizardStep.value = stepIdx;
};

// --- syncPeriodsToTeachersGrid ---
const syncPeriodsToTeachersGrid = () => {
  if (!periodsGrid.value || !teachersGrid.value) return;

  if (enableWalkClass.value || enableSportsCombined.value) {
    flatClasses.value.forEach(cls => {
      const grade = gradesConfig.value.find(g => g.name === cls.grade)
      if (!grade) return
      const subjects = getGradeSubjects(grade)
      subjects.forEach(sub => {
        if (isSubjectDisabledForClass(sub, cls)) {
          if (periodsGrid.value[sub.id]) {
            periodsGrid.value[sub.id][cls.key] = 0
          }
          if (teachersGrid.value[sub.id]) {
            teachersGrid.value[sub.id][cls.key] = []
          }
        }
      })
    })
  }

  Object.keys(periodsGrid.value).forEach((subId) => {
    const classMap = periodsGrid.value[subId];
    if (!classMap) return;
    Object.keys(classMap).forEach((classKey) => {
      const totalPeriods = parseFloat(classMap[classKey]) || 0;
      if (!teachersGrid.value[subId]) {
        teachersGrid.value[subId] = {};
      }
      if (!teachersGrid.value[subId][classKey]) {
        teachersGrid.value[subId][classKey] = [];
      }
      const teachers = teachersGrid.value[subId][classKey];
      if (Array.isArray(teachers) && teachers.length > 0) {
        let sumOthers = 0;
        for (let i = 1; i < teachers.length; i++) {
          sumOthers += parseFloat(teachers[i].periods) || 0;
        }
        teachers[0].periods = Math.max(0, totalPeriods - sumOthers);
      }
    });
  });
};

const getGradeCombinedTasks = (gradeName) => {
  if (!Array.isArray(tasks.value)) return [];
  const filtered = tasks.value.filter(t => {
    if (t.grade !== gradeName || !t.is_combined) return false;
    const gid = t.combined_group_id || '';
    if (gid.startsWith('PE_COMBINED_') && !enableSportsCombined.value) return false;
    if (gid.startsWith('WALK_COMBINED_') && !enableWalkClass.value) return false;
    return true;
  });
  
  // Stable sort to prevent list order from shifting when edited
  return [...filtered].sort((a, b) => {
    const gidA = a.combined_group_id || '';
    const gidB = b.combined_group_id || '';
    if (gidA !== gidB) {
      return gidA.localeCompare(gidB);
    }
    const classA = a.class_name || '';
    const classB = b.class_name || '';
    if (classA !== classB) {
      return classA.localeCompare(classB);
    }
    const subA = a.subject_name || '';
    const subB = b.subject_name || '';
    if (subA !== subB) {
      return subA.localeCompare(subB);
    }
    const idA = a.id !== undefined && a.id !== null ? a.id : '';
    const idB = b.id !== undefined && b.id !== null ? b.id : '';
    if (typeof idA === 'number' && typeof idB === 'number') {
      return idA - idB;
    }
    return String(idA).localeCompare(String(idB));
  });
};

// --- handleCombinedTaskPeriodChange ---
const handleCombinedTaskPeriodChange = async (row) => {
  try {
    const payload = {
      id: row.id,
      grade: row.grade,
      class_name: row.class_name,
      subject_id: row.subject_id,
      teacher_id: row.teacher_id,
      weekly_periods: row.weekly_periods,
      consecutive_periods: row.consecutive_periods || 1,
      room_id: row.room_id,
      is_combined: row.is_combined,
      combined_group_id: row.combined_group_id || "",
      linked_classes: row.linked_classes ? JSON.parse(JSON.stringify(row.linked_classes)) : null,
    };
    await courseScheduleApi.updateTask(workspaceId, row.id, payload);
    ElMessage.success(`${row.class_name} 的课时已更新为 ${row.weekly_periods} 节`);
    await refreshTasks();
    await refreshResults();
  } catch (error) {
    console.error(error);
    ElMessage.error("更新课时失败: " + (error.message || error));
  }
};

// --- handleClassPeriodChange ---
const handleClassPeriodChange = (subId, classKey, value) => {
  const val = value === "" ? 0 : parseFloat(value) || 0;
  if (periodsGrid.value[subId] === undefined) {
    periodsGrid.value[subId] = {};
  }
  periodsGrid.value[subId][classKey] = val;
};

// --- handleConstraintTypeChange ---
const handleConstraintTypeChange = () => {
  // Reset config variables
  constraintForm.value.config = {
    day: 1,
    period: 1,
    target_type: "class",
    target_id: "",
    subject_ids: [],
    subject_id_1: "",
    subject_id_2: "",
    teacher_id: "",
    max_periods: 4,
    teacher_ids: [],
    grade: "",
    subject_id: "",
    task_key: "",
    preference: "morning", // New types config
    limit_scope: "school",
    max_classes: 1,
    venue_type: "",
    consecutive_count: 1,
    block_size: 2,
    preferred_slots: [], // subject_preset_slots config
    scope: "school",
    class_name: "",
    class_type: "普通班",
    slots: {},
  };
  if (constraintForm.value.constraint_type === "subject_preset_slots") {
    activePresetBrush.value = "forbidden";
  } else if (constraintForm.value.constraint_type === "teacher_preset_slots") {
    activePresetBrush.value = "forbidden";
  } else if (constraintForm.value.constraint_type === "time_slot_limit") {
    constraintForm.value.config.target_type = "class_subject";
    constraintForm.value.config.slots = {};
  } else if (constraintForm.value.constraint_type === "daily_limit") {
    constraintForm.value.config.target_type = "class_subject";
  } else if (constraintForm.value.constraint_type === "subject_consecutive_preference") {
    constraintForm.value.config.subject_id = "";
    constraintForm.value.config.preference_type = "consecutive";
  } else if (constraintForm.value.constraint_type === "teacher_schedule_concentration") {
    constraintForm.value.config.teacher_id = "all";
    constraintForm.value.config.concentration = "concentrated";
  } else if (constraintForm.value.constraint_type === "teacher_noon_break_protection") {
    constraintForm.value.config.teacher_id = "all";
    constraintForm.value.config.enabled = true;
  }
};

// --- handleExcelChange ---
const handleExcelChange = (file) => {
  uploadFile.value = file.raw;
};

// --- handleFreePresetChange ---
const handleFreePresetChange = (gradeIndex) => {
  const preset = gradeFreePresetTexts.value[gradeIndex];
  if (preset && preset !== "__custom__") {
    gradeFreeCustomTexts.value[gradeIndex] = preset;
  } else if (
    preset === "__custom__" &&
    !gradeFreeCustomTexts.value[gradeIndex]
  ) {
    gradeFreeCustomTexts.value[gradeIndex] = "";
  }
};

// --- handleGradePeriodCountChange ---
const handleGradePeriodCountChange = (grade) => {
  if (!(grade !== null && grade !== void 0 && grade.schedule_cfg)) return;
  grade.schedule_cfg.day_period_counts = getDefaultDayPeriodCounts(
    grade.schedule_cfg
  );
  ensurePeriodTimes(grade.schedule_cfg);
  saveGradeScheduleSettings(grade);
};

// --- handleGridEditClose ---
const handleGridEditClose = (done) => {
  ElMessageBox.confirm("未保存的修改将会丢失，确定关闭吗？", "提示", {
    confirmButtonText: "确定",
    cancelButtonText: "取消",
    type: "warning",
  })
    .then(() => {
      done();
    })
    .catch(() => {});
};

// --- handlePeriodChange ---
const handlePeriodChange = (subId, allClassEntries, value) => {
  const val = value === "" ? 0 : parseFloat(value) || 0;
  if (periodsGrid.value[subId] === undefined) {
    periodsGrid.value[subId] = {};
  } // Sync periods for all classes in this grade
  allClassEntries.forEach((cls) => {
    periodsGrid.value[subId][cls.key] = val;
  });
};

// --- handlePublicPeriodTimeChange ---
const handlePublicPeriodTimeChange = async (row) => {
  // Update in publicPeriods list
  const cleanId = row.key.replace("public_", "");
  const match = publicPeriods.value.find((p) => p.id === cleanId);
  if (match) {
    match.start = row.start;
    match.end = row.end;
    await savePublicPeriodsToWorkspace();
  }
};

// --- handleSandboxChange ---
const handleSandboxChange = () => {
  if (!isSandboxActive.value) {
    // Turning it ON
    return new Promise((resolve) => {
      ElMessageBox.confirm(
        "确认进入沙盒推演模式吗？系统将自动创建当前课表的备份镜像。此后在此工作区的修改都将作为沙盒草稿保存。您可以随时发布覆盖或一键放弃回滚。",
        "进入沙盒推演",
        {
          confirmButtonText: "进入沙盒",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(async () => {
          pageLoading.value = true;
          try {
            const name = `进入沙盒推演备份_${new Date().toLocaleString()}`;
            const backup = await courseScheduleApi.createBackup(workspaceId, {
              name,
            });
            sandboxBackupId.value = backup.id;
            await refreshBackups();
            ElMessage.success("已成功进入沙盒推演模式，课表备份已就绪！");
            resolve(true);
          } catch (e) {
            ElMessage.error("进入沙盒模式失败，备份创建失败");
            resolve(false);
          } finally {
            pageLoading.value = false;
          }
        })
        .catch(() => {
          resolve(false);
        });
    });
  } else {
    // Turning it OFF: prevent direct switch toggle, force them to use the banner buttons
    ElMessage.info(
      "要退出沙盒模式，请使用课表顶部的“发布合并”或“放弃修改”按钮。"
    );
    return false;
  }
};

// --- handleViewModeChange ---
const handleViewModeChange = () => {
  if (viewMode.value === "class") {
    if (classOptions.value.length > 0)
      selectedClass.value = classOptions.value[0];
  } else if (viewMode.value === "teacher") {
    filteredTeacherOptions.value = teacherList.value;
    if (teacherList.value.length > 0)
      selectedTeacherId.value = teacherList.value[0].id;
  } else if (viewMode.value === "room") {
    if (roomOptions.value.length > 0)
      selectedRoomId.value = roomOptions.value[0].id;
  }
  loadGridData();
};

// --- initGradeScheduleConfig ---
const initGradeScheduleConfig = (grade) => {
  if (!grade.schedule_cfg) {
    var _workspace$value11,
      _workspace$value$conf,
      _workspace$value12,
      _workspace$value$conf2,
      _workspace$value13,
      _workspace$value$conf3,
      _workspace$value14;
    const days =
      ((_workspace$value11 = workspace.value) === null ||
      _workspace$value11 === void 0 ||
      (_workspace$value11 = _workspace$value11.config) === null ||
      _workspace$value11 === void 0
        ? void 0
        : _workspace$value11.days_per_week) || 5;
    grade.schedule_cfg = {
      days_per_week: days,
      morning_count:
        (_workspace$value$conf =
          (_workspace$value12 = workspace.value) === null ||
          _workspace$value12 === void 0 ||
          (_workspace$value12 = _workspace$value12.config) === null ||
          _workspace$value12 === void 0
            ? void 0
            : _workspace$value12.periods_morning) !== null &&
        _workspace$value$conf !== void 0
          ? _workspace$value$conf
          : 4,
      afternoon_count:
        (_workspace$value$conf2 =
          (_workspace$value13 = workspace.value) === null ||
          _workspace$value13 === void 0 ||
          (_workspace$value13 = _workspace$value13.config) === null ||
          _workspace$value13 === void 0
            ? void 0
            : _workspace$value13.periods_afternoon) !== null &&
        _workspace$value$conf2 !== void 0
          ? _workspace$value$conf2
          : 4,
      evening_count:
        (_workspace$value$conf3 =
          (_workspace$value14 = workspace.value) === null ||
          _workspace$value14 === void 0 ||
          (_workspace$value14 = _workspace$value14.config) === null ||
          _workspace$value14 === void 0
            ? void 0
            : _workspace$value14.periods_evening) !== null &&
        _workspace$value$conf3 !== void 0
          ? _workspace$value$conf3
          : 0,
      has_early_study: true,
      has_noon_study: true,
      day_period_counts: {},
      forbidden_periods: [],
      class_forbidden_periods: {},
    };
  }
  grade.schedule_cfg.day_period_counts = getDefaultDayPeriodCounts(
    grade.schedule_cfg
  );
  if (!grade.schedule_cfg.class_forbidden_periods) {
    grade.schedule_cfg.class_forbidden_periods = {};
  }
  grade.schedule_cfg.forbidden_periods = (
    grade.schedule_cfg.forbidden_periods || []
  ).map((item) => normalizeFreePeriodEntry(item));
  Object.keys(grade.schedule_cfg.class_forbidden_periods).forEach(
    (className) => {
      grade.schedule_cfg.class_forbidden_periods[className] = (
        grade.schedule_cfg.class_forbidden_periods[className] || []
      ).map((item) => normalizeFreePeriodEntry(item));
    }
  );
};

// --- initWizardData ---
const initWizardData = () => {
  var _workspace$value16,
    _workspace$value17,
    _workspace$value18,
    _workspace$value19,
    _workspace$value20; // Initialize public periods
  publicPeriods.value = JSON.parse(
    JSON.stringify(
      ((_workspace$value16 = workspace.value) === null ||
      _workspace$value16 === void 0 ||
      (_workspace$value16 = _workspace$value16.config) === null ||
      _workspace$value16 === void 0
        ? void 0
        : _workspace$value16.public_periods) || []
    )
  ); // Initialize venues
  venues.value = JSON.parse(
    JSON.stringify(
      ((_workspace$value17 = workspace.value) === null ||
      _workspace$value17 === void 0 ||
      (_workspace$value17 = _workspace$value17.config) === null ||
      _workspace$value17 === void 0
        ? void 0
        : _workspace$value17.venues) || []
    )
  ); // 1. Initialize gradesConfig
  if (
    (_workspace$value18 = workspace.value) !== null &&
    _workspace$value18 !== void 0 &&
    (_workspace$value18 = _workspace$value18.config) !== null &&
    _workspace$value18 !== void 0 &&
    _workspace$value18.grades_config &&
    workspace.value.config.grades_config.length > 0
  ) {
    gradesConfig.value = JSON.parse(
      JSON.stringify(workspace.value.config.grades_config)
    );
  } else {
    // Detect from tasks
    const detected = detectGradesConfigFromTasks();
    if (detected.length > 0) {
      gradesConfig.value = detected;
    } else {
      // Default initial layout
      gradesConfig.value = [
        {
          name: "高一",
          classes: ["1班", "2班", "3班"],
          subject_ids: [...allSubjectIds.value],
        },
      ];
    }
  }
  gradesConfig.value = gradesConfig.value.map((grade) => ({
    ...grade,
    subject_ids:
      Array.isArray(grade.subject_ids) && grade.subject_ids.length > 0
        ? grade.subject_ids
        : [...allSubjectIds.value],
    schedule_cfg: grade.schedule_cfg || null,
  }));
  gradesConfig.value.forEach((grade) => {
    initGradeScheduleConfig(grade);
    ensurePeriodTimes(grade.schedule_cfg);
  });
  gradesConfig.value.forEach((grade, index) => {
    if (!gradeScheduleScopes.value[index]) {
      gradeScheduleScopes.value[index] = "grade";
    }
    if (
      !gradeScheduleClassTargets.value[index] &&
      Array.isArray(grade.classes) &&
      grade.classes.length > 0
    ) {
      const firstClass = grade.classes[0];
      gradeScheduleClassTargets.value[index] =
        typeof firstClass === "string"
          ? firstClass
          : (firstClass === null || firstClass === void 0
              ? void 0
              : firstClass.name) || "";
    }
  }); // 2. Initialize grids
  const pg = {};
  const tg = {};
  subjectList.value.forEach((sub) => {
    pg[sub.id] = {};
    tg[sub.id] = {};
  }); // Try to load from workspace config first (saved progress)
  const savedPeriods =
    (_workspace$value19 = workspace.value) === null ||
    _workspace$value19 === void 0 ||
    (_workspace$value19 = _workspace$value19.config) === null ||
    _workspace$value19 === void 0
      ? void 0
      : _workspace$value19.periods_grid;
  const savedTeachers =
    (_workspace$value20 = workspace.value) === null ||
    _workspace$value20 === void 0 ||
    (_workspace$value20 = _workspace$value20.config) === null ||
    _workspace$value20 === void 0
      ? void 0
      : _workspace$value20.teachers_grid; // Prioritize pre-filling from existing database tasks if we have tasks
  if (savedPeriods) {
    Object.keys(savedPeriods).forEach((subId) => {
      if (pg[subId] !== undefined) {
        pg[subId] = { ...savedPeriods[subId] };
      }
    });
    if (savedTeachers) {
      Object.keys(savedTeachers).forEach((subId) => {
        if (tg[subId] !== undefined) {
          tg[subId] = JSON.parse(JSON.stringify(savedTeachers[subId] || {}));
        }
      });
    }
  } else if (tasks.value && tasks.value.length > 0) {
    tasks.value.forEach((t) => {
      const targetClassKeys = [];
      if (t.is_combined && Array.isArray(t.linked_classes) && t.linked_classes.length > 0) {
        t.linked_classes.forEach((lc) => {
          const g = lc.grade;
          const c = lc.class_name || lc.name;
          if (g && c) {
            targetClassKeys.push(`${g}:${c}`);
          }
        });
      }
      if (targetClassKeys.length === 0) {
        targetClassKeys.push(`${t.grade}:${t.class_name}`);
      }

      targetClassKeys.forEach((classKey) => {
        if (pg[t.subject_id]) {
          pg[t.subject_id][classKey] = t.weekly_periods;
        }
        if (tg[t.subject_id]) {
          if (!Array.isArray(tg[t.subject_id][classKey])) {
            tg[t.subject_id][classKey] = [];
          }
          const exists = tg[t.subject_id][classKey].some(
            (item) => item.teacher_id === t.teacher_id && item.periods === t.weekly_periods
          );
          if (!exists) {
            tg[t.subject_id][classKey].push({
              teacher_id: t.teacher_id,
              periods: t.weekly_periods,
            });
          }
        }
      });
    });
  }
  periodsGrid.value = pg;
  teachersGrid.value = tg; // Detect and set class period edit mode (unified or individual)
  gradesConfig.value.forEach((grade) => {
    if (!grade.name) return;
    let isDifferent = false;
    const classKeys = (grade.classes || []).map((c) => {
      const name =
        typeof c === "string"
          ? c
          : (c === null || c === void 0 ? void 0 : c.name) || "";
      return `${grade.name}:${name}`;
    });
    if (classKeys.length > 1) {
      const subjects = getGradeSubjects(grade);
      for (const sub of subjects) {
        var _pg$sub$id$classKeys$, _pg$sub$id;
        const firstVal =
          (_pg$sub$id$classKeys$ =
            (_pg$sub$id = pg[sub.id]) === null || _pg$sub$id === void 0
              ? void 0
              : _pg$sub$id[classKeys[0]]) !== null &&
          _pg$sub$id$classKeys$ !== void 0
            ? _pg$sub$id$classKeys$
            : 0;
        for (let i = 1; i < classKeys.length; i++) {
          var _pg$sub$id$classKeys$2, _pg$sub$id2;
          const val =
            (_pg$sub$id$classKeys$2 =
              (_pg$sub$id2 = pg[sub.id]) === null || _pg$sub$id2 === void 0
                ? void 0
                : _pg$sub$id2[classKeys[i]]) !== null &&
            _pg$sub$id$classKeys$2 !== void 0
              ? _pg$sub$id$classKeys$2
              : 0;
          if (val !== firstVal) {
            isDifferent = true;
            break;
          }
        }
        if (isDifferent) break;
      }
    }
    gradePeriodEditMode.value[grade.name] = isDifferent
      ? "individual"
      : "unified";
  });
};

// --- isClassCellForbidden ---
const isClassCellForbidden = (grade, day, row) => {
  var _grade$schedule_cfg2;
  const academicIndex =
    row === null || row === void 0 ? void 0 : row.academic_index;
  if (!academicIndex) return false;
  const className =
    gradeScheduleClassTargets.value[gradesConfig.value.indexOf(grade)];
  if (!className) return false;
  const list =
    (grade === null ||
    grade === void 0 ||
    (_grade$schedule_cfg2 = grade.schedule_cfg) === null ||
    _grade$schedule_cfg2 === void 0 ||
    (_grade$schedule_cfg2 = _grade$schedule_cfg2.class_forbidden_periods) ===
      null ||
    _grade$schedule_cfg2 === void 0
      ? void 0
      : _grade$schedule_cfg2[className]) || [];
  return list.some((fp) => fp.day === day && fp.period_index === academicIndex);
};

// --- isClassPeriodZero ---
const isClassPeriodZero = (subId, classKey) => {
  const sub = subjectById.value.get(subId)
  const parts = (classKey || '').split(':')
  const cls = parts.length >= 2 ? { grade: parts[0], class_name: parts[1], key: classKey } : null
  
  if (sub && cls && isSubjectDisabledForClass(sub, cls)) {
    return true
  }

  var _periodsGrid$value0;
  const val =
    (_periodsGrid$value0 = periodsGrid.value) === null ||
    _periodsGrid$value0 === void 0 ||
    (_periodsGrid$value0 = _periodsGrid$value0[subId]) === null ||
    _periodsGrid$value0 === void 0
      ? void 0
      : _periodsGrid$value0[classKey];
  return val === undefined || val === null || Number(val) === 0;
};

// --- isGradeCellForbidden ---
const isGradeCellForbidden = (grade, day, row) => {
  var _grade$schedule_cfg;
  const academicIndex =
    row === null || row === void 0 ? void 0 : row.academic_index;
  if (!academicIndex) return true;
  if (
    !(
      grade !== null &&
      grade !== void 0 &&
      (_grade$schedule_cfg = grade.schedule_cfg) !== null &&
      _grade$schedule_cfg !== void 0 &&
      _grade$schedule_cfg.forbidden_periods
    )
  )
    return false;
  return grade.schedule_cfg.forbidden_periods.some(
    (fp) => fp.day === day && fp.period_index === academicIndex
  );
};

// --- isGradeScopeDifferent ---
const isGradeScopeDifferent = (grade, gradeIndex, day, row) => {
  if (gradeScheduleScopes.value[gradeIndex] !== "grade") return false;
  return areClassesDifferentForSlot(grade, day, row);
};

// --- isGradeScopeUniformClassForbidden ---
const isGradeScopeUniformClassForbidden = (grade, gradeIndex, day, row) => {
  if (gradeScheduleScopes.value[gradeIndex] !== "grade") return false;
  const academicIndex =
    row === null || row === void 0 ? void 0 : row.academic_index;
  if (!academicIndex) return false;
  const activeClassNames = (grade.classes || [])
    .filter((c) => {
      return typeof c === "string" ? true : c.participate !== false;
    })
    .map((c) => {
      return typeof c === "string" ? c : c.name || "";
    })
    .filter(Boolean);
  if (activeClassNames.length === 0) return false; // Check if ALL active classes are forbidden
  const allForbidden = activeClassNames.every((className) => {
    var _grade$schedule_cfg7;
    const list =
      (grade === null ||
      grade === void 0 ||
      (_grade$schedule_cfg7 = grade.schedule_cfg) === null ||
      _grade$schedule_cfg7 === void 0 ||
      (_grade$schedule_cfg7 = _grade$schedule_cfg7.class_forbidden_periods) ===
        null ||
      _grade$schedule_cfg7 === void 0
        ? void 0
        : _grade$schedule_cfg7[className]) || [];
    return list.some(
      (item) => item.day === day && item.period_index === academicIndex
    );
  });
  if (!allForbidden) return false; // Check if they all have the same label
  const labels = activeClassNames.map((className) => {
    var _grade$schedule_cfg8;
    const list =
      (grade === null ||
      grade === void 0 ||
      (_grade$schedule_cfg8 = grade.schedule_cfg) === null ||
      _grade$schedule_cfg8 === void 0 ||
      (_grade$schedule_cfg8 = _grade$schedule_cfg8.class_forbidden_periods) ===
        null ||
      _grade$schedule_cfg8 === void 0
        ? void 0
        : _grade$schedule_cfg8[className]) || [];
    const fp = list.find(
      (item) => item.day === day && item.period_index === academicIndex
    );
    return (fp === null || fp === void 0 ? void 0 : fp.label) || "";
  });
  return labels.every((l) => l === labels[0]);
};


// --- isRowActiveForDayWithGrade ---
const isRowActiveForDayWithGrade = (grade, row, day) => {
  if (!(row !== null && row !== void 0 && row.is_academic)) return false;
  const counts = getAcademicPeriodCountBySection(grade, day);
  if (row.type === "morning")
    return row.section_index <= Number(counts.morning || 0);
  if (row.type === "afternoon")
    return row.section_index <= Number(counts.afternoon || 0);
  if (row.type === "evening")
    return row.section_index <= Number(counts.evening || 0);
  return false;
};

// --- isVenueSlotForbidden ---
const isVenueSlotForbidden = (venue, day, period) => {
  return (venue.forbidden_slots || []).some(
    (fs) => fs.day === day && fs.period === period
  );
};

// --- loadGlobalResources ---
const loadGlobalResources = async () => {
  try {
    await refreshTeacherResources();
    await refreshSubjectResources();
    await refreshGlobalSubjects();
    const rooms = await examApi.getRooms();
    roomOptions.value = rooms;
    checkAndInitWizard();
  } catch (error) {
    console.error(error);
  }
};

// --- loadGridData ---
const loadGridData = () => {
  selectedActiveCard.value = null;
  const filtered = [];
  results.value.forEach((r) => {
    // Check filter criteria
    let matched = false;
    if (viewMode.value === "class" && selectedClass.value) {
      const className = `${r.grade}${r.class_name}`;
      matched = className === selectedClass.value;
    } else if (viewMode.value === "teacher" && selectedTeacherId.value) {
      matched = r.teacher_id === selectedTeacherId.value;
      if (!matched && r.teacher_ids) {
        matched = String(r.teacher_ids).split(',').map(Number).includes(selectedTeacherId.value)
      }
    } else if (viewMode.value === "room" && selectedRoomId.value) {
      // Find room in tasks list
      const task = tasks.value.find((t) => t.id === r.task_id);
      matched = task && task.room_id === selectedRoomId.value;
    }
    if (matched) {
      filtered.push(r);
    }
  });
  gridCards.value = filtered;
};

// --- nextStep ---
const nextStep = () => {
  if (wizardStep.value === 2) {
    if (!validateGrades()) return;
  }
  if (wizardStep.value === 3) {
    if (!validateGradeSchedules()) return;
  }
  if (wizardStep.value <= 3) {
    // Ensure grids are initialized for any new class keys
    ensureWizardGridsInitialized();
  }
  wizardStep.value++;
};

// --- normalizeFreePeriodEntry ---
const normalizeFreePeriodEntry = (item) => {
  if (!item) return { day: 0, period_index: 0, label: "" };
  if (typeof item === "object") {
    return {
      day: Number(item.day),
      period_index: Number(item.period_index),
      label: item.label || item.reason || "",
    };
  }
  return { day: 0, period_index: 0, label: "" };
};

// --- openAddPublicPeriodDialog ---
const openAddPublicPeriodDialog = () => {
  var _gradesConfig$value$;
  publicPeriodForm.value = {
    id: "",
    name: "",
    type: "display",
    position: "before_period_1",
    position_value: 1,
    scope: "all",
    scope_grade:
      ((_gradesConfig$value$ = gradesConfig.value[0]) === null ||
      _gradesConfig$value$ === void 0
        ? void 0
        : _gradesConfig$value$.name) || "",
    show_in_schedule: true,
    occupies_teacher: false,
    occupies_room: false,
    affects_scheduling: false,
    start: "",
    end: "",
  };
  publicPeriodDialogVisible.value = true;
};

// --- openBackupDialog ---
const openBackupDialog = () => {
  backupName.value = "";
  backupDialogVisible.value = true;
};

// --- openConstraintDialog ---
const openConstraintDialog = (type) => {
  const targetType = typeof type === "string" ? type : "fixed_slot";
  constraintForm.value.constraint_type = targetType;
  constraintForm.value.is_hard = true;
  constraintForm.value.weight = 5;
  handleConstraintTypeChange();
  constraintDialogVisible.value = true;
};

// --- openEditPublicPeriodDialog ---
const openEditPublicPeriodDialog = (item) => {
  publicPeriodForm.value = JSON.parse(JSON.stringify(item));
  publicPeriodDialogVisible.value = true;
};

// --- openFreeLabelEditor ---
const openFreeLabelEditor = (grade, gradeIndex, day, row) => {
  const academicIndex =
    row === null || row === void 0 ? void 0 : row.academic_index;
  if (!academicIndex || !isRowActiveForDayWithGrade(grade, row, day)) return;
  const isGradeScope = isGradeCellForbidden(grade, day, row);
  const isClassScope = isClassCellForbidden(grade, day, row);
  if (!isGradeScope && !isClassScope) return;
  const className = isClassScope
    ? gradeScheduleClassTargets.value[gradeIndex]
    : null;
  freeLabelEditingContext.value = {
    grade,
    gradeIndex,
    day,
    academicIndex,
    scope: isClassScope ? "class" : "grade",
    className,
  };
  freeLabelEditText.value = isClassScope
    ? getClassFreeLabel(grade, gradeIndex, day, row)
    : getGradeFreeLabel(grade, day, row);
  freeLabelDialogVisible.value = true;
};

// --- openGridEditDialog ---
const openGridEditDialog = () => {
  if (tasks.value && tasks.value.length > 0) {
    gridRows.value = tasks.value.map((t) => ({
      grade: t.grade || "",
      class_name: t.class_name || "",
      subject_name: t.subject_name || "",
      teacher_name: t.teacher_name || "",
      weekly_periods: t.weekly_periods || 4,
      consecutive_periods: t.consecutive_periods || 1,
      room_name: t.room_name || "",
      is_combined: t.is_combined || false,
      combined_group_id: t.combined_group_id || "",
    }));
  } else {
    gridRows.value = Array.from({ length: 5 }, () => ({
      grade: "",
      class_name: "",
      subject_name: "",
      teacher_name: "",
      weekly_periods: 4,
      consecutive_periods: 1,
      room_name: "",
      is_combined: false,
      combined_group_id: "",
    }));
  }
  gridEditDialogVisible.value = true;
};

// --- openTaskDialog ---
const openTaskDialog = (row = null) => {
  filteredTeachersForm.value = teacherList.value;
  if (row) {
    isEditTask.value = true;
    taskForm.value = {
      id: row.id,
      grade: row.grade,
      class_name: row.class_name,
      subject_id: row.subject_id,
      teacher_id: row.teacher_id,
      weekly_periods: row.weekly_periods,
      consecutive_periods: row.consecutive_periods || 1,
      room_id: row.room_id,
      is_combined: row.is_combined,
      combined_group_id: row.combined_group_id || "",
      linked_classes: row.linked_classes ? JSON.parse(JSON.stringify(row.linked_classes)) : null,
    };
  } else {
    isEditTask.value = false;
    taskForm.value = {
      id: null,
      grade: "",
      class_name: "",
      subject_id: "",
      teacher_id: "",
      weekly_periods: 4,
      consecutive_periods: 1,
      room_id: null,
      is_combined: false,
      combined_group_id: "",
      linked_classes: null,
    };
  }
  taskDialogVisible.value = true;
};

// --- openVenueDialog ---
const openVenueDialog = (row = null) => {
  if (row) {
    venueForm.value = JSON.parse(JSON.stringify(row));
  } else {
    venueForm.value = {
      id: "venue_" + Date.now(),
      name: "",
      type: "",
      capacity: 1,
      forbidden_slots: [],
    };
  }
  venueDialogVisible.value = true;
};

// --- openVenueForbiddenDialog ---
const openVenueForbiddenDialog = (row) => {
  activeVenueForForbidden.value = JSON.parse(JSON.stringify(row));
  venueForbiddenDialogVisible.value = true;
};

// --- parsePastedText ---
const parsePastedText = () => {
  if (!pastedText.value) {
    parsedTasks.value = [];
    return;
  }
  const lines = pastedText.value.split("\n");
  const list = [];
  const isHeader = (cells) => {
    if (!cells || cells.length === 0) return false;
    const hStr = cells.join("");
    return (
      hStr.includes("年级") ||
      hStr.includes("班级") ||
      hStr.includes("科目") ||
      hStr.includes("教师") ||
      hStr.includes("课时")
    );
  };
  let started = false;
  for (let line of lines) {
    var _cells$,
      _cells$2,
      _cells$3,
      _cells$4,
      _cells$5,
      _cells$6,
      _cells$7,
      _cells$8,
      _cells$9;
    line = line.trim();
    if (!line) continue;
    let cells = line.split("\t");
    if (cells.length < 2) {
      cells = line.split(",");
    }
    if (cells.length < 2) {
      cells = line.split(/\s+/);
    }
    if (!started && isHeader(cells)) {
      started = true;
      continue;
    }
    started = true;
    const grade =
      ((_cells$ = cells[0]) === null || _cells$ === void 0
        ? void 0
        : _cells$.trim()) || "";
    const className =
      ((_cells$2 = cells[1]) === null || _cells$2 === void 0
        ? void 0
        : _cells$2.trim()) || "";
    const subjectName =
      ((_cells$3 = cells[2]) === null || _cells$3 === void 0
        ? void 0
        : _cells$3.trim()) || "";
    const teacherName =
      ((_cells$4 = cells[3]) === null || _cells$4 === void 0
        ? void 0
        : _cells$4.trim()) || "";
    const weeklyPeriodsStr =
      ((_cells$5 = cells[4]) === null || _cells$5 === void 0
        ? void 0
        : _cells$5.trim()) || "0";
    const consecutivePeriodsStr =
      ((_cells$6 = cells[5]) === null || _cells$6 === void 0
        ? void 0
        : _cells$6.trim()) || "1";
    const roomName =
      ((_cells$7 = cells[6]) === null || _cells$7 === void 0
        ? void 0
        : _cells$7.trim()) || "";
    const isCombinedStr =
      ((_cells$8 = cells[7]) === null || _cells$8 === void 0
        ? void 0
        : _cells$8.trim()) || "";
    const combinedGroupId =
      ((_cells$9 = cells[8]) === null || _cells$9 === void 0
        ? void 0
        : _cells$9.trim()) || "";
    if (!grade || !className || !subjectName || !teacherName) {
      continue;
    }
    const weeklyPeriods = parseFloat(weeklyPeriodsStr) || 0;
    const consecutivePeriods = parseInt(consecutivePeriodsStr) || 1;
    const isCombined = ["是", "yes", "true", "1", "合班"].includes(
      isCombinedStr
    );
    list.push({
      grade,
      class_name: className,
      subject_name: subjectName,
      teacher_name: teacherName,
      weekly_periods: weeklyPeriods,
      consecutive_periods: consecutivePeriods,
      room_name: roomName,
      is_combined: isCombined,
      is_combined_desc: isCombined ? "是" : "否",
      combined_group_id: combinedGroupId,
    });
  }
  parsedTasks.value = list;
};

// --- prevStep ---
const prevStep = () => {
  wizardStep.value--;
};

// --- publishSandbox ---
const publishSandbox = () => {
  ElMessageBox.confirm(
    "确认将当前沙盒草稿合并发布为正式课表吗？此操作将使当前所有排课调整生效。",
    "发布合并沙盒",
    {
      confirmButtonText: "发布生效",
      cancelButtonText: "继续微调",
      type: "success",
    }
  )
    .then(async () => {
      pageLoading.value = true;
      try {
        if (sandboxBackupId.value) {
          await courseScheduleApi.deleteBackup(
            workspaceId,
            sandboxBackupId.value
          );
        }
        isSandboxActive.value = false;
        sandboxBackupId.value = null;
        await refreshBackups();
        ElMessage.success("沙盒推演方案已成功合并至正式课表并发布！");
      } catch (e) {
        ElMessage.error("发布失败，请重试");
      } finally {
        pageLoading.value = false;
      }
    })
    .catch(() => {});
};

// --- refreshBackups ---
const refreshBackups = async () => {
  const data = await courseScheduleApi.getBackups(workspaceId);
  backups.value = data;
};

// --- refreshConstraints ---
const refreshConstraints = async () => {
  const data = await courseScheduleApi.getConstraints(workspaceId);
  constraints.value = data;
};

// --- refreshGlobalSubjects ---
const refreshGlobalSubjects = async () => {
  try {
    globalSubjects.value = await teacherApi.getSubjects();
  } catch (error) {
    console.error("Failed to refresh global subjects:", error);
  }
};

// --- refreshResults ---
const refreshResults = async () => {
  if (selectedWeek.value) {
    // 获取指定周次课表（含 override 合并）
    const data = await courseScheduleApi.getWeeklyResults(workspaceId, selectedWeek.value);
    results.value = data;
    await refreshWeekOverrides();
  } else {
    const data = await courseScheduleApi.getResults(workspaceId);
    results.value = data;
  }
  loadGridData();
};

// --- Week override 相关方法 ---

/** 当周次选择器变化时 */
const handleWeekChange = async (week) => {
  selectedWeek.value = week;
  await refreshResults();
};

/** 加载当前周调课记录 */
const refreshWeekOverrides = async () => {
  if (!selectedWeek.value) {
    weekOverrides.value = [];
    return;
  }
  try {
    const data = await courseScheduleApi.getWeekOverrides(workspaceId, selectedWeek.value);
    weekOverrides.value = data;
  } catch (e) {
    weekOverrides.value = [];
  }
};

/** 从 workspace config 读取总周数 */
const initTotalWeeks = () => {
  if (workspace.value && workspace.value.config) {
    const tw = workspace.value.config.total_weeks;
    if (tw && Number(tw) > 0) {
      totalWeeks.value = Number(tw);
    }
  }
};

/** 打开调课对话框 */
const openWeekOverrideDialog = (card) => {
  overrideTargetCard.value = card;
  overrideForm.value = {
    week_number: selectedWeek.value || null,
    override_type: 'cancel',
    new_day_of_week: null,
    new_period_index: null,
    new_teacher_id: null,
    changeTeacher: false,
    remark: ''
  };
  weekOverrideDialogVisible.value = true;
};

/** 保存调课记录 */
const saveWeekOverride = async () => {
  const form = overrideForm.value;
  const card = overrideTargetCard.value;
  if (!form.week_number) {
    ElMessage.warning('请选择影响的周次');
    return;
  }
  if (!card || !card.id) {
    ElMessage.error('未找到对应的排课记录，请重新选择课程');
    return;
  }
  if (form.override_type === 'replace' && (!form.new_day_of_week || !form.new_period_index)) {
    ElMessage.warning('换课时需要指定新的上课时间');
    return;
  }
  if (form.override_type === 'substitute' && !form.new_teacher_id) {
    ElMessage.warning('代课时请选择代课教师');
    return;
  }

  overrideSaving.value = true;
  try {
    const payload = {
      week_number: form.week_number,
      original_result_id: card.id,
      override_type: form.override_type,
      new_day_of_week: form.override_type === 'replace' ? form.new_day_of_week : null,
      new_period_index: form.override_type === 'replace' ? form.new_period_index : null,
      new_teacher_id: (form.override_type === 'substitute' || (form.override_type === 'replace' && form.changeTeacher))
        ? form.new_teacher_id : null,
      remark: form.remark || null,
    };
    await courseScheduleApi.createWeekOverride(workspaceId, payload);
    ElMessage.success('调课记录已保存');
    weekOverrideDialogVisible.value = false;
    // 若当前正在查看该周，刷新课表和调课列表
    if (selectedWeek.value === form.week_number) {
      await refreshResults();
    }
  } catch (e) {
    // 错误由 API 拦截器统一弹出
  } finally {
    overrideSaving.value = false;
  }
};

/** 打开调课记录列表对话框 */
const openOverrideListDialog = async () => {
  await refreshWeekOverrides();
  overrideListDialogVisible.value = true;
};

/** 删除单条调课记录 */
const deleteOverride = async (overrideId) => {
  try {
    await ElMessageBox.confirm('确定删除此调课记录？删除后课表将恢复为默认排课。', '删除确认', {
      confirmButtonText: '确定删除',
      cancelButtonText: '取消',
      type: 'warning'
    });
    await courseScheduleApi.deleteWeekOverride(workspaceId, overrideId);
    ElMessage.success('调课记录已删除');
    await refreshResults();
    if (overrideListDialogVisible.value) {
      await refreshWeekOverrides();
    }
  } catch (e) {
    if (e !== 'cancel') {
      // 错误由 API 拦截器统一处理
    }
  }
};

/** 获取调课卡片 tooltip 内容 */
const getOverrideTooltip = (card) => {
  if (!card || !card.is_overridden) return '';
  const typeMap = { cancel: '本周停课', replace: '本周换课', substitute: '本周代课' };
  return typeMap[card.override_type] || '本周已调课';
};

/** 导出调课通知单 */
const exportOverrideNotice = async (teacherId = null, teacherName = null) => {
  if (!selectedWeek.value) {
    ElMessage.warning('请先选择周次再导出调课单');
    return;
  }
  try {
    const blob = await courseScheduleApi.exportOverrideNotice(workspaceId, selectedWeek.value, teacherId);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const namePart = teacherName ? `_${teacherName}` : '';
    a.download = `第${selectedWeek.value}周调课通知单${namePart}.xlsx`;
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
    ElMessage.success('调课单导出成功');
  } catch (e) {
    ElMessage.error('导出失败，请稍后重试');
  }
};

// --- refreshSubjectResources ---
const refreshSubjectResources = async () => {
  const subjects = await courseScheduleApi.getSubjects();
  rawSubjectList.value = subjects;
};

// --- refreshTasks ---
const refreshTasks = async () => {
  const data = await courseScheduleApi.getTasks(workspaceId);
  tasks.value = data;
};

// --- refreshTeacherResources ---
const refreshTeacherResources = async () => {
  const teachers = await teacherApi.getAll();
  teacherList.value = teachers;
  filteredTeachersForm.value = teachers;
  filteredTeacherOptions.value = teachers;
};

// --- removeClassEntry ---
const removeClassEntry = (gradeIndex, classIndex) => {
  gradesConfig.value[gradeIndex].classes.splice(classIndex, 1);
};

// --- removeGrade ---
const removeGrade = (index) => {
  gradesConfig.value.splice(index, 1);
};

// --- removeGridRow ---
const removeGridRow = (index) => {
  gridRows.value.splice(index, 1);
};

// --- restoreBackup ---
const restoreBackup = (backupId) => {
  ElMessageBox.confirm(
    "确定恢复到这个版本吗？恢复后，当前还没保存成版本的调整会被覆盖。",
    "恢复确认",
    { confirmButtonText: "确认恢复", cancelButtonText: "取消", type: "warning" }
  ).then(async () => {
    await courseScheduleApi.restoreBackup(workspaceId, backupId);
    ElMessage.success("已经恢复到这个版本");
    await refreshResults();
    loadGridData();
  });
};

// --- rollbackSandbox ---
const rollbackSandbox = () => {
  ElMessageBox.confirm(
    "确定要放弃当前沙盒内的所有修改吗？系统将一键还原到您刚进入沙盒推演时的初始状态，所有在此期间的操作都将丢失。",
    "放弃修改并回滚",
    {
      confirmButtonText: "确定回滚",
      cancelButtonText: "取消",
      confirmButtonClass: "el-button--danger",
      type: "warning",
    }
  )
    .then(async () => {
      pageLoading.value = true;
      try {
        if (sandboxBackupId.value) {
          await courseScheduleApi.restoreBackup(
            workspaceId,
            sandboxBackupId.value
          );
          await courseScheduleApi.deleteBackup(
            workspaceId,
            sandboxBackupId.value
          );
        }
        isSandboxActive.value = false;
        sandboxBackupId.value = null;
        await refreshResults();
        await refreshTasks();
        await refreshBackups();
        loadGridData();
        ElMessage.success("沙盒推演已撤销，课表已还原至初始状态！");
      } catch (e) {
        ElMessage.error("回滚失败，请重试");
      } finally {
        pageLoading.value = false;
      }
    })
    .catch(() => {});
};

// --- saveFreeLabelEdit ---
const saveFreeLabelEdit = async () => {
  if (!freeLabelEditingContext.value) return;
  const ctx = freeLabelEditingContext.value;
  freeLabelSaving.value = true;
  try {
    const nextLabel = freeLabelEditText.value.trim();
    if (ctx.scope === "class") {
      var _ctx$grade$schedule_c;
      const list =
        ((_ctx$grade$schedule_c =
          ctx.grade.schedule_cfg.class_forbidden_periods) === null ||
        _ctx$grade$schedule_c === void 0
          ? void 0
          : _ctx$grade$schedule_c[ctx.className]) || [];
      const target = list.find(
        (item) =>
          item.day === ctx.day && item.period_index === ctx.academicIndex
      );
      if (target) target.label = nextLabel;
    } else {
      const list = ctx.grade.schedule_cfg.forbidden_periods || [];
      const target = list.find(
        (item) =>
          item.day === ctx.day && item.period_index === ctx.academicIndex
      );
      if (target) target.label = nextLabel;
    }
    await autoSaveGradeSchedule(ctx.grade);
    ElMessage.success("免任课名称已更新");
    freeLabelDialogVisible.value = false;
    freeLabelEditingContext.value = null;
  } finally {
    freeLabelSaving.value = false;
  }
};

// --- saveGradeScheduleSettings ---
const saveGradeScheduleSettings = async (grade, showMessage = false) => {
  if (!(grade !== null && grade !== void 0 && grade.schedule_cfg)) return;
  grade.schedule_cfg.day_period_counts = getDefaultDayPeriodCounts(
    grade.schedule_cfg
  );
  ensurePeriodTimes(grade.schedule_cfg);
  await autoSaveGradeSchedule(grade);
  if (showMessage) {
    ElMessage.success(`${grade.name || "当前年级"}的课节设置已保存`);
  }
};

// --- saveGradesOnly ---
const saveGradesOnly = async () => {
  if (!validateGrades()) return;
  savingGrades.value = true;
  try {
    var _workspace$value22;
    const nextConfig = {
      ...(((_workspace$value22 = workspace.value) === null ||
      _workspace$value22 === void 0
        ? void 0
        : _workspace$value22.config) || {}),
      grades_config: gradesConfig.value,
    };
    const updated = await courseScheduleApi.updateWorkspace(workspaceId, {
      config: nextConfig,
    });
    workspace.value = updated;
    ElMessage.success("这一步已经保存好了");
  } catch (error) {
    var _error$response6;
    console.error(error);
    ElMessage.error(
      ((_error$response6 = error.response) === null ||
      _error$response6 === void 0 ||
      (_error$response6 = _error$response6.data) === null ||
      _error$response6 === void 0
        ? void 0
        : _error$response6.detail) || "保存失败，请稍后再试"
    );
  } finally {
    savingGrades.value = false;
  }
};

// --- savePublicPeriodForm ---
const savePublicPeriodForm = async () => {
  const form = publicPeriodForm.value;
  if (!form.name || !form.name.trim()) {
    ElMessage.warning("请输入公共时段名称");
    return;
  }
  if (!form.id) {
    // Add new
    form.id = "pub_" + Date.now();
    publicPeriods.value.push(JSON.parse(JSON.stringify(form)));
  } else {
    // Edit existing
    const idx = publicPeriods.value.findIndex((p) => p.id === form.id);
    if (idx > -1) {
      publicPeriods.value[idx] = JSON.parse(JSON.stringify(form));
    }
  }
  await savePublicPeriodsToWorkspace();
  publicPeriodDialogVisible.value = false;
};

// --- savePublicPeriodsToWorkspace ---
const savePublicPeriodsToWorkspace = async () => {
  try {
    var _workspace$value25;
    const nextConfig = {
      ...(((_workspace$value25 = workspace.value) === null ||
      _workspace$value25 === void 0
        ? void 0
        : _workspace$value25.config) || {}),
      public_periods: publicPeriods.value,
    };
    const updated = await courseScheduleApi.updateWorkspace(workspaceId, {
      config: nextConfig,
    });
    workspace.value = updated;
  } catch (error) {
    var _error$response9;
    console.error(error);
    ElMessage.error(
      ((_error$response9 = error.response) === null ||
      _error$response9 === void 0 ||
      (_error$response9 = _error$response9.data) === null ||
      _error$response9 === void 0
        ? void 0
        : _error$response9.detail) || "保存公共时段配置失败"
    );
  }
};

// --- saveVenueForbiddenSlots ---
const saveVenueForbiddenSlots = async () => {
  if (activeVenueForForbidden.value) {
    const idx = venues.value.findIndex(
      (v) => v.id === activeVenueForForbidden.value.id
    );
    if (idx > -1) {
      venues.value[idx].forbidden_slots =
        activeVenueForForbidden.value.forbidden_slots;
    }
    await saveVenuesToWorkspace();
  }
  venueForbiddenDialogVisible.value = false;
};

// --- saveVenueForm ---
const saveVenueForm = async () => {
  const form = venueForm.value;
  if (!form.name || !form.name.trim()) {
    ElMessage.warning("请输入场室名称");
    return;
  }
  if (!form.type || !form.type.trim()) {
    ElMessage.warning("请选择或输入场室类型");
    return;
  }
  const idx = venues.value.findIndex((v) => v.id === form.id);
  if (idx > -1) {
    venues.value[idx] = JSON.parse(JSON.stringify(form));
  } else {
    venues.value.push(JSON.parse(JSON.stringify(form)));
  }
  await saveVenuesToWorkspace();
  venueDialogVisible.value = false;
};

// --- saveVenuesToWorkspace ---
const saveVenuesToWorkspace = async () => {
  try {
    var _workspace$value24;
    const nextConfig = {
      ...(((_workspace$value24 = workspace.value) === null ||
      _workspace$value24 === void 0
        ? void 0
        : _workspace$value24.config) || {}),
      venues: venues.value,
    };
    const updated = await courseScheduleApi.updateWorkspace(workspaceId, {
      config: nextConfig,
    });
    workspace.value = updated;
    ElMessage.success("场室配置已更新");
  } catch (error) {
    var _error$response8;
    console.error(error);
    ElMessage.error(
      ((_error$response8 = error.response) === null ||
      _error$response8 === void 0 ||
      (_error$response8 = _error$response8.data) === null ||
      _error$response8 === void 0
        ? void 0
        : _error$response8.detail) || "保存场室配置失败"
    );
  }
};

// --- saveWizardData ---
const saveWizardData = async () => {
  if (!validateGrades() || !validateGradeSchedules()) return;
  savingWizard.value = true;
  try {
    syncPeriodsToTeachersGrid();
    var _workspace$value23;
    const nextConfig = {
      ...(((_workspace$value23 = workspace.value) === null ||
      _workspace$value23 === void 0
        ? void 0
        : _workspace$value23.config) || {}),
      grades_config: gradesConfig.value,
      periods_grid: periodsGrid.value,
      teachers_grid: teachersGrid.value,
    };
    const updated = await courseScheduleApi.updateWorkspace(workspaceId, {
      config: nextConfig,
    });
    workspace.value = updated;
    const payload = {
      grades_config: gradesConfig.value,
      periods_grid: periodsGrid.value,
      teachers_grid: teachersGrid.value,
    };
    await courseScheduleApi.setupSync(workspaceId, payload);
    ElMessage.success("任课安排已经保存"); // Refresh tasks and general workspace data
    await refreshTasks();
  } catch (error) {
    var _error$response7;
    console.error(error);
    ElMessage.error(
      ((_error$response7 = error.response) === null ||
      _error$response7 === void 0 ||
      (_error$response7 = _error$response7.data) === null ||
      _error$response7 === void 0
        ? void 0
        : _error$response7.detail) || "保存失败，请稍后再试"
    );
  } finally {
    savingWizard.value = false;
  }
};

// --- saveWizardProgress ---
const saveWizardProgress = async () => {
  if (!validateGrades()) return;
  savingProgress.value = true;
  try {
    syncPeriodsToTeachersGrid();
    var _workspace$value21;
    const nextConfig = {
      ...(((_workspace$value21 = workspace.value) === null ||
      _workspace$value21 === void 0
        ? void 0
        : _workspace$value21.config) || {}),
      grades_config: gradesConfig.value,
      public_periods: publicPeriods.value,
      periods_grid: periodsGrid.value,
      teachers_grid: teachersGrid.value,
    };
    const updated = await courseScheduleApi.updateWorkspace(workspaceId, {
      config: nextConfig,
    });
    workspace.value = updated;

    const payload = {
      grades_config: gradesConfig.value,
      periods_grid: periodsGrid.value,
      teachers_grid: teachersGrid.value,
    };
    await courseScheduleApi.setupSync(workspaceId, payload);
    await refreshTasks();

    ElMessage.success("配置进度已保存成功");
  } catch (error) {
    var _error$response5;
    console.error(error);
    ElMessage.error(
      ((_error$response5 = error.response) === null ||
      _error$response5 === void 0 ||
      (_error$response5 = _error$response5.data) === null ||
      _error$response5 === void 0
        ? void 0
        : _error$response5.detail) || "保存进度失败，请稍后再试"
    );
  } finally {
    savingProgress.value = false;
  }
};

// --- addPresetConstraints ---
const addPresetConstraints = async () => {
  if (!subjectList.value || subjectList.value.length === 0) {
    ElMessage.warning('科目列表为空，请先添加科目数据');
    return;
  }
  
  let addedCount = 0;
  
  // Morning subjects
  const morningSubjects = subjectList.value.filter(s => ['语文', '数学', '外语', '英语'].includes(s.name));
  for (const s of morningSubjects) {
    try {
      await courseScheduleApi.createConstraint(workspaceId, {
        constraint_type: 'subject_period_preference',
        is_hard: false,
        weight: 5,
        config: { subject_id: s.id, preference: 'morning' }
      });
      addedCount++;
    } catch (e) { console.error(e); }
  }

  // Afternoon subjects
  const afternoonSubjects = subjectList.value.filter(s => ['体育', '音乐', '美术', '心理', '体育与健康', '综合实践', '劳动'].includes(s.name));
  for (const s of afternoonSubjects) {
    try {
      await courseScheduleApi.createConstraint(workspaceId, {
        constraint_type: 'subject_period_preference',
        is_hard: false,
        weight: 5,
        config: { subject_id: s.id, preference: 'afternoon' }
      });
      addedCount++;
    } catch (e) { console.error(e); }
  }

  if (addedCount > 0) {
    ElMessage.success(`成功添加 ${addedCount} 条软性常用时段约束`);
    await refreshConstraints();
  } else {
    ElMessage.info('未找到匹配的科目，无需添加');
  }
};

// --- submitConstraintForm ---
const submitConstraintForm = async () => {
  const f = constraintForm.value;
  const payload = {
    constraint_type: f.constraint_type,
    is_hard: f.is_hard,
    weight: f.weight,
    config: {},
  }; // Formatting config keys properly
  if (f.constraint_type === "fixed_slot") {
    if (!f.config.task_key) {
      ElMessage.warning("请先选择要设置的课程");
      return;
    }
    const [grade, class_name, subject_id] = f.config.task_key.split("|");
    payload.config = {
      grade,
      class_name,
      subject_id: Number(subject_id),
      day: f.config.day,
      period: f.config.period,
    };
  } else if (f.constraint_type === "forbidden_slot") {
    if (!f.config.target_id) {
      ElMessage.warning("请先选择对象");
      return;
    }
    payload.config = {
      target_type: f.config.target_type,
      target_id: f.config.target_id,
      day: f.config.day,
      period: f.config.period,
    };
  } else if (f.constraint_type === "subject_mutex") {
    if (!f.config.subject_ids || f.config.subject_ids.length < 2) {
      ElMessage.warning("请至少选择两门学科");
      return;
    }
    payload.config = { subject_ids: f.config.subject_ids.map(Number) };
  } else if (f.constraint_type === "subject_consecutive_limit") {
    if (!f.config.subject_id_1 || !f.config.subject_id_2) {
      ElMessage.warning("请把前后两门课都选完整");
      return;
    }
    payload.config = {
      subject_id_1: Number(f.config.subject_id_1),
      subject_id_2: Number(f.config.subject_id_2),
    };
  } else if (f.constraint_type === "teacher_max_daily_periods") {
    if (!f.config.teacher_id) {
      ElMessage.warning("请先选择老师");
      return;
    }
    payload.config = {
      teacher_id: Number(f.config.teacher_id),
      max_periods: Number(f.config.max_periods),
    };
  } else if (f.constraint_type === "teacher_mutex") {
    if (!f.config.teacher_ids || f.config.teacher_ids.length < 2) {
      ElMessage.warning("请至少选择两位老师");
      return;
    }
    payload.config = { teacher_ids: f.config.teacher_ids.map(Number) };
  } else if (f.constraint_type === "grade_progress_sync") {
    if (!f.config.grade || !f.config.subject_id) {
      ElMessage.warning("请把年级和学科选完整");
      return;
    }
    payload.config = {
      grade: f.config.grade,
      subject_id: Number(f.config.subject_id),
    };
  } else if (f.constraint_type === "subject_period_preference") {
    if (!f.config.subject_id || !f.config.preference) {
      ElMessage.warning("请先选择学科和希望安排的时间");
      return;
    }
    payload.config = {
      subject_id: Number(f.config.subject_id),
      preference: f.config.preference,
    };
  } else if (f.constraint_type === "subject_simultaneous_limit") {
    if (!f.config.subject_id) {
      ElMessage.warning("请选择学科");
      return;
    }
    payload.config = {
      subject_id: Number(f.config.subject_id),
      limit_scope: f.config.limit_scope,
      max_classes: Number(f.config.max_classes),
    };
  } else if (f.constraint_type === "subject_venue_binding") {
    if (!f.config.subject_id || !f.config.venue_type) {
      ElMessage.warning("请选择学科与场地类型");
      return;
    }
    payload.config = {
      subject_id: Number(f.config.subject_id),
      venue_type: f.config.venue_type,
    };
  } else if (f.constraint_type === "class_venue_requirement") {
    if (!f.config.task_key || !f.config.venue_type) {
      ElMessage.warning("请选择班级课程与场地类型");
      return;
    }
    const [grade, class_name, subject_id] = f.config.task_key.split("|");
    payload.config = {
      grade,
      class_name,
      subject_id: Number(subject_id),
      venue_type: f.config.venue_type,
    };
  } else if (f.constraint_type === "consecutive_settings") {
    if (!f.config.subject_id) {
      ElMessage.warning("请选择学科");
      return;
    }
    payload.config = {
      subject_id: Number(f.config.subject_id),
      grade: f.config.grade || "",
      consecutive_count: Number(f.config.consecutive_count),
      block_size: Number(f.config.block_size),
      preferred_slots: (f.config.preferred_slots || []).map(Number),
    };
  } else if (f.constraint_type === "subject_preset_slots") {
    if (!f.config.subject_id) {
      ElMessage.warning("请选择目标学科");
      return;
    }
    if (f.config.scope === "grade" && !f.config.grade) {
      ElMessage.warning("请选择对应年级");
      return;
    }
    if (
      f.config.scope === "class" &&
      (!f.config.grade || !f.config.class_name)
    ) {
      ElMessage.warning("请选择对应年级与班级");
      return;
    }
    if (f.config.scope === "class_type" && !f.config.class_type) {
      ElMessage.warning("请选择班级类型");
      return;
    }
    const slots = f.config.slots || {};
    if (Object.keys(slots).length === 0) {
      ElMessage.warning("请在小课表网格上绘制至少一个时段要求");
      return;
    }
    payload.config = {
      subject_id: Number(f.config.subject_id),
      scope: f.config.scope,
      grade: f.config.grade || "",
      class_name: f.config.class_name || "",
      class_type: f.config.class_type || "",
      slots: slots,
    };
  } else if (f.constraint_type === "teacher_preset_slots") {
    if (!f.config.teacher_ids || f.config.teacher_ids.length === 0) {
      ElMessage.warning("请选择目标教师");
      return;
    }
    const slots = f.config.slots || {};
    if (Object.keys(slots).length === 0) {
      ElMessage.warning("请在小课表网格上绘制至少一个时段要求");
      return;
    }
    payload.config = {
      teacher_ids: f.config.teacher_ids.map(Number),
      slots: slots,
    };
  } else if (f.constraint_type === "time_slot_limit") {
    const slots = f.config.slots || {};
    if (Object.keys(slots).length === 0) {
      ElMessage.warning("请在小课表网格上选择至少一个限制时段");
      return;
    }
    if (f.config.target_type === "teacher") {
      if (!f.config.teacher_id) {
        ElMessage.warning("请选择目标教师");
        return;
      }
      payload.config = {
        target_type: "teacher",
        teacher_id: Number(f.config.teacher_id),
        max_periods: Number(f.config.max_periods),
        slots: slots,
      };
    } else {
      if (!f.config.subject_id) {
        ElMessage.warning("请选择科目");
        return;
      }
      payload.config = {
        target_type: "class_subject",
        grade: f.config.grade || "",
        class_name: f.config.class_name || "",
        subject_id: Number(f.config.subject_id),
        max_periods: Number(f.config.max_periods),
        slots: slots,
      };
    }
  } else if (f.constraint_type === "daily_limit") {
    if (f.config.target_type === "teacher") {
      if (!f.config.teacher_id) {
        ElMessage.warning("请选择目标教师");
        return;
      }
      payload.config = {
        target_type: "teacher",
        teacher_id: Number(f.config.teacher_id),
        max_periods: Number(f.config.max_periods),
      };
    } else {
      if (!f.config.subject_id) {
        ElMessage.warning("请选择科目");
        return;
      }
      payload.config = {
        target_type: "class_subject",
        grade: f.config.grade || "",
        class_name: f.config.class_name || "",
        subject_id: Number(f.config.subject_id),
        max_periods: Number(f.config.max_periods),
      };
    }
  } else if (f.constraint_type === "subject_adjacency_limit") {
    if (
      !f.config.subject_id_1 ||
      !f.config.subject_id_2 ||
      !f.config.direction
    ) {
      ElMessage.warning("请把科目与限制方式填写完整");
      return;
    }
    payload.config = {
      subject_id_1: Number(f.config.subject_id_1),
      subject_id_2: Number(f.config.subject_id_2),
      direction: f.config.direction,
    };
  } else if (f.constraint_type === "subject_consecutive_preference") {
    if (!f.config.subject_id) {
      ElMessage.warning("请选择科目");
      return;
    }
    payload.config = {
      subject_id: Number(f.config.subject_id),
      preference_type: f.config.preference_type || "consecutive",
    };
    payload.is_hard = false;
  } else if (f.constraint_type === "teacher_schedule_concentration") {
    if (!f.config.teacher_id) {
      ElMessage.warning("请选择教师范围");
      return;
    }
    payload.config = {
      teacher_id: f.config.teacher_id === "all" ? "all" : Number(f.config.teacher_id),
      concentration: f.config.concentration || "concentrated",
    };
    payload.is_hard = false;
  } else if (f.constraint_type === "teacher_noon_break_protection") {
    if (!f.config.teacher_id) {
      ElMessage.warning("请选择教师范围");
      return;
    }
    payload.config = {
      teacher_id: f.config.teacher_id === "all" ? "all" : Number(f.config.teacher_id),
      enabled: true,
    };
  }
  try {
    await courseScheduleApi.createConstraint(workspaceId, payload);
    ElMessage.success("补充要求已添加");
    constraintDialogVisible.value = false;
    await refreshConstraints();
  } catch (error) {
    console.error(error);
  }
};

// --- submitCreateBackup ---
const submitCreateBackup = async () => {
  if (!backupName.value) {
    ElMessage.warning("请先填写这个版本的名称");
    return;
  }
  try {
    await courseScheduleApi.createBackup(workspaceId, {
      name: backupName.value,
    });
    ElMessage.success("当前课表已经保存为一个版本");
    backupDialogVisible.value = false;
    await refreshBackups();
  } catch (error) {
    console.error(error);
  }
};

// --- submitExcelImport ---
const submitExcelImport = async () => {
  if (!uploadFile.value) {
    ElMessage.warning("请先选择要导入的 Excel 文件");
    return;
  }
  importing.value = true;
  try {
    const res = await courseScheduleApi.importTasksExcel(
      workspaceId,
      uploadFile.value
    );
    ElMessage.success(res.message);
    importDialogVisible.value = false;
    uploadFile.value = null;
    fileList.value = [];
    await refreshTasks();
  } catch (error) {
    console.error(error);
  } finally {
    importing.value = false;
  }
};

// --- submitGridEdit ---
const submitGridEdit = async () => {
  const validRows = [];
  for (let idx = 0; idx < gridRows.value.length; idx++) {
    var _row$grade,
      _row$class_name,
      _row$subject_name,
      _row$teacher_name,
      _row$room_name,
      _row$combined_group_i;
    const row = gridRows.value[idx];
    const hasGrade = !!(
      (_row$grade = row.grade) !== null &&
      _row$grade !== void 0 &&
      _row$grade.trim()
    );
    const hasClass = !!(
      (_row$class_name = row.class_name) !== null &&
      _row$class_name !== void 0 &&
      _row$class_name.trim()
    );
    const hasSubject = !!(
      (_row$subject_name = row.subject_name) !== null &&
      _row$subject_name !== void 0 &&
      _row$subject_name.trim()
    );
    const hasTeacher = !!(
      (_row$teacher_name = row.teacher_name) !== null &&
      _row$teacher_name !== void 0 &&
      _row$teacher_name.trim()
    );
    if (!hasGrade && !hasClass && !hasSubject && !hasTeacher) {
      continue;
    }
    if (!hasGrade || !hasClass || !hasSubject || !hasTeacher) {
      ElMessage.warning(`第 ${idx + 1} 行还没填完整，请补齐后再保存`);
      return;
    }
    if (typeof row.weekly_periods !== "number" || row.weekly_periods <= 0) {
      ElMessage.warning(`第 ${idx + 1} 行的周课时要大于 0`);
      return;
    }
    validRows.push({
      grade: row.grade.trim(),
      class_name: row.class_name.trim(),
      subject_name: row.subject_name.trim(),
      teacher_name: row.teacher_name.trim(),
      weekly_periods: row.weekly_periods,
      consecutive_periods: row.consecutive_periods || 1,
      room_name:
        ((_row$room_name = row.room_name) === null || _row$room_name === void 0
          ? void 0
          : _row$room_name.trim()) || "",
      is_combined: !!row.is_combined,
      combined_group_id:
        ((_row$combined_group_i = row.combined_group_id) === null ||
        _row$combined_group_i === void 0
          ? void 0
          : _row$combined_group_i.trim()) || "",
    });
  }
  if (validRows.length === 0) {
    ElMessage.warning("表格里还没有可保存的内容");
    return;
  }
  gridSaving.value = true;
  try {
    await courseScheduleApi.clearAllTasks(workspaceId);
    const res = await courseScheduleApi.batchCreateTasks(
      workspaceId,
      validRows
    );
    ElMessage.success(res.message || "保存成功");
    gridEditDialogVisible.value = false;
    await refreshTasks();
  } catch (error) {
    console.error(error);
  } finally {
    gridSaving.value = false;
  }
};

// --- submitPasteImport ---
const submitPasteImport = async () => {
  if (parsedTasks.value.length === 0) {
    ElMessage.warning("没有识别到可导入的内容");
    return;
  }
  pasteImporting.value = true;
  try {
    const res = await courseScheduleApi.batchCreateTasks(
      workspaceId,
      parsedTasks.value
    );
    ElMessage.success(res.message || "导入成功");
    pasteImportDialogVisible.value = false;
    pastedText.value = "";
    parsedTasks.value = [];
    await refreshTasks();
  } catch (error) {
    console.error(error);
  } finally {
    pasteImporting.value = false;
  }
};

// --- submitTaskForm ---
const submitTaskForm = async () => {
  const formPayload = { ...taskForm.value };
  if (formPayload.is_combined && !formPayload.combined_group_id) {
    ElMessage.warning("如果是合班课，请把合班编号也填上");
    return;
  }
  try {
    if (isEditTask.value) {
      await courseScheduleApi.updateTask(
        workspaceId,
        formPayload.id,
        formPayload
      );
      ElMessage.success("任课安排已更新");
    } else {
      await courseScheduleApi.createTask(workspaceId, formPayload);
      ElMessage.success("已添加任课安排");
    }
    taskDialogVisible.value = false;
    await refreshTasks();
  } catch (error) {
    console.error(error);
  }
};

// --- toggleGradeCellForbidden ---
const toggleGradeCellForbidden = async (grade, gradeIndex, day, row) => {
  const academicIndex =
    row === null || row === void 0 ? void 0 : row.academic_index;
  if (!academicIndex || !isRowActiveForDayWithGrade(grade, row, day)) return;
  const scope = gradeScheduleScopes.value[gradeIndex] || "grade";
  const label = getCurrentFreeLabel(gradeIndex);
  if (scope === "class") {
    const className = gradeScheduleClassTargets.value[gradeIndex];
    if (!className) {
      ElMessage.warning("请先选择班级");
      return;
    }
    if (!grade.schedule_cfg.class_forbidden_periods) {
      grade.schedule_cfg.class_forbidden_periods = {};
    }
    if (!grade.schedule_cfg.class_forbidden_periods[className]) {
      grade.schedule_cfg.class_forbidden_periods[className] = [];
    }
    const list = grade.schedule_cfg.class_forbidden_periods[className];
    const existingIdx = list.findIndex(
      (fp) => fp.day === day && fp.period_index === academicIndex
    );
    if (existingIdx > -1) {
      list.splice(existingIdx, 1);
    } else {
      list.push({ day, period_index: academicIndex, label });
    }
  } else {
    const list = grade.schedule_cfg.forbidden_periods || [];
    const existingIdx = list.findIndex(
      (fp) => fp.day === day && fp.period_index === academicIndex
    );
    if (existingIdx > -1) {
      list.splice(existingIdx, 1);
    } else {
      list.push({ day, period_index: academicIndex, label });
    }
    grade.schedule_cfg.forbidden_periods = list;
  }
  await autoSaveGradeSchedule(grade);
};

// --- toggleVenueSlotForbidden ---
const toggleVenueSlotForbidden = (venue, day, period) => {
  if (!venue.forbidden_slots) {
    venue.forbidden_slots = [];
  }
  const idx = venue.forbidden_slots.findIndex(
    (fs) => fs.day === day && fs.period === period
  );
  if (idx > -1) {
    venue.forbidden_slots.splice(idx, 1);
  } else {
    venue.forbidden_slots.push({ day, period });
  }
};

// --- triggerAutoScheduling ---
const runPreflightCheck = async () => {
  preflightLoading.value = true
  try {
    const report = await courseScheduleApi.validateScheduling(workspaceId)
    preflightReport.value = report
    if (report.can_start) {
      ElMessage.success(report.issues.length ? `检查完成：${report.issues.length} 条提示` : '排课前检查通过')
    } else {
      ElMessage.warning(`检查完成：${report.issues.length} 项需要修复`)
    }
    return report
  } catch (error) {
    console.error(error)
    ElMessage.error(error.response?.data?.detail || '排课前检查失败，请稍后重试')
    return null
  } finally {
    preflightLoading.value = false
  }
}

const triggerAutoScheduling = async () => {
  if (hasCapacityError.value) {
    ElMessageBox.alert(
      "无法开始排课：当前有班级的课程总课时超出了可排课容量，请先调整课时或禁排时段设置后再重试。",
      "容量校验未通过",
      { type: "error", confirmButtonText: "确定" }
    );
    return;
  }
  if (hasConsecutiveError.value) {
    ElMessageBox.alert(
      "无法开始排课：当前有连堂规则设置存在课时冲突（连堂所需节数大于周总课时），请先修正规则后再重试。",
      "连堂设置冲突",
      { type: "error", confirmButtonText: "确定" }
    );
    return;
  }
  try {
    const preflight = await runPreflightCheck()
    if (!preflight) return
    if (!preflight.can_start) {
      const details = preflight.issues.map(item => `• ${item.message}`).join('\n')
      await ElMessageBox.alert(details || '请先修复排课设置。', '排课前检查未通过', {
        type: 'error',
        confirmButtonText: '返回修改',
      })
      return
    }
    if (preflight.issues.length > 0) {
      ElMessage.warning(`排课前检查发现 ${preflight.issues.length} 条提示，已继续提交任务`)
    }
    const res = await courseScheduleApi.startScheduling(
      workspaceId,
      selectedSolver.value
    );
    ElMessage.success(res.message || "已经开始生成课表");
    workspace.value.status = "running";
    workspace.value.progress = 0;
    workspace.value.log_message = '排课任务已进入队列...';
    startJobPolling({ id: res.job_id, status: 'queued' });
  } catch (error) {
    console.error(error);
  }
};

// --- handleResetStatus ---
const handleResetStatus = async () => {
  try {
    await ElMessageBox.confirm('确定要强制重置生成状态吗？这将强行中断或终止当前卡在“正在生成”的任务。', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    });
    
    if (activeJob.value && ['queued', 'running'].includes(activeJob.value.status)) {
      await courseScheduleApi.cancelJob(workspaceId, activeJob.value.id);
      activeJob.value = { ...activeJob.value, status: 'cancelled', progress: 0, log_message: '生成已取消，原课表保持不变。' }
      stopJobPolling();
      ElMessage.success('排课任务已取消，原课表保持不变');
    } else {
      await courseScheduleApi.resetWorkspaceStatus(workspaceId);
      ElMessage.success('生成状态已成功重置');
    }
    await loadWorkspaceData();
  } catch (error) {
    if (error !== 'cancel') {
      console.error(error);
    }
  }
};

// --- validateGradeSchedules ---
const validateGradeSchedules = () => {
  if (gradesConfig.value.some((g) => !g.schedule_cfg)) {
    ElMessage.warning("请先把每个年级的上课时间设置好");
    return false;
  }
  return true;
};

// --- validateGrades ---
const validateGrades = () => {
  const names = gradesConfig.value.map((g) => g.name.trim());
  if (names.some((n) => !n)) {
    ElMessage.warning("请把年级名称填写完整");
    return false;
  }
  if (new Set(names).size !== names.length) {
    ElMessage.warning("年级名称不能重复");
    return false;
  }
  if (
    gradesConfig.value.some(
      (g) => !Array.isArray(g.classes) || g.classes.length === 0
    )
  ) {
    ElMessage.warning("每个年级至少要有一个班级");
    return false;
  }
  if (
    gradesConfig.value.some(
      (g) => !Array.isArray(g.subject_ids) || g.subject_ids.length === 0
    )
  ) {
    ElMessage.warning("每个年级至少要选择一门学科");
    return false;
  }
  return true;
};


</script>

<style scoped>
/* Custom Stepper */
.custom-wizard-steps {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  background: #f8fafc;
  padding: 18px 24px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.custom-step-item {
  display: flex;
  align-items: center;
  position: relative;
  flex: 1;
  cursor: pointer;
  user-select: none;
}

.custom-step-item:last-child {
  flex: none;
}

.step-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #64748b;
  font-size: 13px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
}

.step-label {
  margin-left: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
  background: #f8fafc;
  padding-right: 12px;
}

.step-connector {
  position: absolute;
  left: 32px;
  right: 16px;
  top: 50%;
  height: 2px;
  background: #e2e8f0;
  z-index: 1;
  transform: translateY(-50%);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Active Step */
.custom-step-item.active .step-icon-wrap {
  border-color: #4f46e5;
  background: linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%);
  color: #ffffff;
  box-shadow: 0 0 12px rgba(79, 70, 229, 0.3);
}

.custom-step-item.active .step-label {
  color: #1e293b;
  font-weight: 700;
}

/* Completed Step */
.custom-step-item.completed .step-icon-wrap {
  border-color: #10b981;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
}

.custom-step-item.completed .step-label {
  color: #10b981;
}

.custom-step-item.completed .step-connector {
  background: #10b981;
}

.step-success-icon {
  font-size: 15px;
  color: #ffffff;
}

/* Grade Config Card Styles */
.grade-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.grade-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
  border-color: #cbd5e1;
}

.grade-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 12px;
  margin-bottom: 16px;
}

.wizard-base-panels {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 22px;
}

.wizard-base-panel {
  border: 1px solid #dbeafe;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
  border-radius: 14px;
  padding: 16px 18px;
}

.wizard-base-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 6px;
}

.wizard-base-desc {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  min-height: 42px;
}

.wizard-base-actions {
  margin-top: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.grade-subject-chooser {
  width: 100%;
  margin-top: 14px;
}

.grade-subject-label {
  display: inline-block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #475569;
}

.wizard-grade-grids {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.grade-grid-card {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px;
  background: #ffffff;
}

.grade-grid-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.grade-grid-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.grade-grid-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

.class-tag {
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #475569;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.class-tag:hover {
  background-color: #cbd5e1;
  color: #0f172a;
  transform: scale(1.03);
}

/* Dynamic Action Buttons */
.action-btn-gradient-primary {
  background: linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%) !important;
  border: none !important;
  color: white !important;
  font-weight: 600 !important;
  transition: all 0.2s ease !important;
}

.action-btn-gradient-primary:hover {
  background: linear-gradient(135deg, #4338ca 0%, #2563eb 100%) !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.25);
}

.action-btn-gradient-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  border: none !important;
  color: white !important;
  font-weight: 600 !important;
  transition: all 0.2s ease !important;
}

.action-btn-gradient-success:hover {
  background: linear-gradient(135deg, #0d9488 0%, #047857 100%) !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
}

/* Excel-style matrix grids */
.excel-table-wrapper {
  max-width: 100%;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  margin-top: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
}

.excel-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.excel-table th,
.excel-table td {
  border: 1px solid #e2e8f0;
  padding: 10px 14px;
  text-align: center;
  white-space: nowrap;
}

.excel-table th {
  background-color: #f8fafc;
  font-weight: 700;
  color: #334155;
  border-bottom: 2px solid #cbd5e1;
}

.excel-table th.sticky-col,
.excel-table td.sticky-col {
  position: sticky;
  left: 0;
  background-color: #f8fafc;
  z-index: 2;
  border-right: 2.5px solid #cbd5e1;
  font-weight: 700;
  color: #1e293b;
  text-align: left;
}

.excel-table td.sticky-col {
  background-color: #f1f5f9;
}

.excel-grid-input {
  width: 70px;
  padding: 6px 8px;
  border: 1.5px solid #e2e8f0;
  border-radius: 6px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  background-color: #ffffff;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.excel-grid-input:focus {
  border-color: #4f46e5;
  outline: none;
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
}

.excel-grid-input:hover:not(:focus) {
  border-color: #cbd5e1;
}

.excel-grid-input[value="0"],
.excel-grid-input:placeholder-shown {
  color: #94a3b8;
  font-weight: 400;
}

.excel-table tr:hover td {
  background-color: #f8fafc;
}

.excel-table tr:hover td.sticky-col {
  background-color: #e2e8f0;
}

/* Element Plus select styling within spreadsheet cells */
.excel-table td .el-select {
  --el-select-border-color-hover: #4f46e5;
  --el-input-border-color: #e2e8f0;
  --el-input-hover-border-color: #cbd5e1;
  --el-input-focus-border-color: #4f46e5;
}

.excel-table td .el-select .el-input__wrapper {
  box-shadow: 0 0 0 1px #e2e8f0 inset;
  border-radius: 6px;
  padding: 4px 8px;
  transition: all 0.2s ease;
}

.excel-table td .el-select .el-input__wrapper:hover {
  box-shadow: 0 0 0 1px #cbd5e1 inset;
}

.excel-table td .el-select.is-focus .el-input__wrapper {
  box-shadow: 0 0 0 1px #4f46e5 inset, 0 0 0 3px rgba(79, 70, 229, 0.15) !important;
}

/* Animations */
.animate-fade-in {
  animation: fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.workspace-detail-container {
  max-width: 1300px;
  margin: 0 auto;
  padding: 12px 0 40px 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

/* Summary Banner */
.workspace-summary-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
}

.back-link {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 6px;
}

.ws-info h2 {
  font-size: 22px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.ws-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 13px;
  color: #64748b;
}

.meta-item {
  position: relative;
}

.meta-item::before {
  content: "•";
  margin-right: 8px;
  color: #cbd5e1;
}

/* Custom Tabs */
.custom-tabs :deep(.el-tabs__item) {
  font-weight: 600;
  font-size: 14px;
  color: #64748b;
  height: 44px;
  line-height: 44px;
}

.custom-tabs :deep(.el-tabs__item.is-active) {
  color: #8b5cf6;
}

.custom-tabs :deep(.el-tabs__active-bar) {
  background-color: #8b5cf6;
}

.tab-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.step-navigation-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  padding-top: 15px;
  border-top: 1px solid #f1f5f9;
}

/* Rules Tab Container */
.rules-container {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 20px;
}

.rules-left,
.rules-right {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

.card-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.rules-left h3,
.rules-right h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.rule-hint-box {
  background: #faf5ff;
  border: 1px solid #ddd6fe;
  border-radius: 8px;
  padding: 16px;
  margin-top: 15px;
}

.rule-hint-box h4 {
  margin-top: 0;
  font-size: 14px;
  color: #6d28d9;
}

.rule-hint-box ul {
  padding-left: 20px;
  margin-bottom: 0;
}

.rule-hint-box li {
  font-size: 13px;
  color: #4c1d95;
  line-height: 1.8;
}

/* Solve Tab Container */
.solve-container {
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 20px;
}

.solve-left,
.solve-right {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
}

.solve-left h3,
.solve-right h3 {
  margin: 0;
  font-size: 17px;
  font-weight: 800;
  color: #1e293b;
}

.desc {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  margin: 10px 0 20px 0;
}

.preflight-panel {
  margin: 18px 0;
  padding: 16px;
  border: 1px solid #dbe4ee;
  border-radius: 8px;
  background: #f8fafc;
}

.preflight-panel.passed {
  border-color: #86efac;
  background: #f0fdf4;
}

.preflight-panel.blocked {
  border-color: #fecaca;
  background: #fef2f2;
}

.preflight-panel__heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}

.preflight-panel h4 {
  margin: 0;
  font-size: 14px;
  color: #0f172a;
}

.preflight-panel p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

.preflight-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 12px;
}

.metric {
  padding: 3px 8px;
  border-radius: 4px;
  background: #e2e8f0;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
}

.metric--error { background: #fee2e2; color: #b91c1c; }
.metric--warning { background: #fef3c7; color: #92400e; }

.preflight-issues {
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
}

.preflight-issues li {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.45;
  color: #475569;
}

.preflight-issues li.error { color: #b91c1c; }
.preflight-issues li.warning { color: #92400e; }
.preflight-issues li.muted { color: #64748b; }

.action-zone {
  margin: 24px 0;
  text-align: left;
}

.run-solver-btn {
  background: #8b5cf6 !important;
  border: none !important;
  font-weight: 600;
  padding: 20px 40px;
  font-size: 16px;
  border-radius: 10px;
  box-shadow: 0 10px 20px rgba(139, 92, 246, 0.3);
  color: white !important;
}

.run-solver-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(139, 92, 246, 0.4);
}

.action-zone__hint {
  margin: 9px 0 0;
  color: #64748b;
  font-size: 12px;
}

.solve-progress-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
}

.status-msg {
  font-size: 12px;
  color: #64748b;
  margin: 8px 0 0 0;
}

/* Status Cards */
.status-card {
  display: flex;
  gap: 15px;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  margin-top: 15px;
}

.status-card.success {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
}

.status-card.success .status-icon {
  color: #15803d;
}

.status-card.failed {
  background: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
}

.status-card.failed .status-icon {
  color: #b91c1c;
}

.status-card.running {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1e40af;
}

.status-card.running .status-icon {
  color: #1d4ed8;
}

.status-card.queued {
  background: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
}

.status-card.queued .status-icon {
  color: #d97706;
}

.status-card.timed_out {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #9a3412;
}

.status-card.timed_out .status-icon {
  color: #c2410c;
}

.status-icon {
  font-size: 32px;
  margin-top: 3px;
}

.status-info h4 {
  margin: 0 0 6px 0;
  font-size: 15px;
  font-weight: 700;
}

.status-info p {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
}

.solve-side-summary {
  margin-top: 14px;
  border-top: 1px solid #e2e8f0;
}

.solve-side-summary div {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 13px;
}

.solve-side-summary strong { color: #1e293b; }

.status-error-detail {
  white-space: pre-line;
  max-height: 260px;
  overflow: auto;
}

.schedule-settings-shell {
  display: grid;
  grid-template-columns: minmax(280px, 0.95fr) minmax(340px, 1.25fr);
  gap: 16px;
  align-items: start;
}

.schedule-settings-panel {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
}

.schedule-settings-panel__title {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 14px;
}

.schedule-settings-header {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.schedule-settings-header--cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
}

.schedule-count-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  min-height: 96px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
}

.schedule-count-card--action {
  justify-content: center;
}

.schedule-count-card__label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  line-height: 1.4;
}

.schedule-apply-btn {
  width: 100%;
  white-space: normal;
  line-height: 1.3;
  min-height: 40px;
}

.schedule-settings-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #e2e8f0;
}

.day-count-grid {
  display: grid;
  grid-template-columns: 88px repeat(3, minmax(84px, 1fr));
  gap: 10px 12px;
  align-items: center;
}

.day-count-grid__header {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  padding-bottom: 2px;
}

.day-count-grid__day {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

/* Timetable Grid & View Layout */
.timetable-workspace {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
}

.timetable-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  background: #f8fafc;
  padding: 12px 20px;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
}

.completion-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 16px;
  padding: 9px 12px;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  background: #f0fdf4;
  color: #166534;
  font-size: 12px;
  font-weight: 600;
}

.completion-strip.warning {
  border-color: #fde68a;
  background: #fffbeb;
  color: #92400e;
}

.timetable-layout {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 20px;
  align-items: stretch;
}

.grid-section {
  overflow-x: auto;
}

.grid-table-container {
  width: 100%;
}

.grid-table {
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  border: 1px solid #e2e8f0;
}

.grid-table th,
.grid-table td {
  border: 1px solid #e2e8f0;
  text-align: center;
}

.period-th {
  width: 90px;
  background: #f1f5f9;
  color: #475569;
  font-size: 13px;
  font-weight: 700;
  height: 40px;
}

.day-th {
  background: #f8fafc;
  color: #1e293b;
  font-size: 14px;
  font-weight: 700;
  height: 40px;
}

.period-cell {
  background: #f8fafc;
  height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.p-num {
  font-size: 16px;
  font-weight: 800;
  color: #1e293b;
}

.p-name {
  font-size: 11px;
  color: #64748b;
}

.division-row {
  background: #f1f5f9;
  height: 28px;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
}

.grid-cell {
  height: 80px;
  width: 140px;
  position: relative;
  vertical-align: top;
  padding: 4px;
  background: #ffffff;
  transition: all 0.2s ease;
  cursor: pointer;
}

.grid-cell.slot-highlight {
  background: #faf5ff;
}

.grid-cell.selected-target {
  background: #eddffd !important;
  box-shadow: inset 0 0 0 2px #a78bfa;
}

.grid-cell.drag-target {
  background: linear-gradient(135deg, #ecfeff, #e0f2fe) !important;
  box-shadow: inset 0 0 0 2.5px #06b6d4;
  transform: scale(1.02);
  transition: all 0.15s ease;
}

.grid-cell.disabled-slot {
  background: #f8fafc;
  color: #94a3b8;
  cursor: not-allowed;
}

.grid-cell.disabled-slot.slot-highlight,
.grid-cell.disabled-slot.drag-target,
.grid-cell.disabled-slot.selected-target {
  background: #f8fafc !important;
  box-shadow: none;
}

.disabled-slot-label {
  position: absolute;
  top: 6px;
  right: 8px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  pointer-events: none;
}

.slot-cards-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100%;
}

.course-card {
  background: #f3e8ff;
  border-left: 4px solid #a855f7;
  border-radius: 4px;
  padding: 6px;
  text-align: left;
  position: relative;
  transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
  min-height: 34px;
  cursor: grab;
  user-select: none;
}

.course-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.course-card:active,
.holding-card:active {
  cursor: grabbing;
  transform: scale(0.96);
}

.course-card.dragging {
  opacity: 0.55;
  transform: scale(0.98);
  box-shadow: 0 0 0 2px #7c3aed, 0 4px 12px rgba(124, 58, 237, 0.25);
}

.course-card.half-period {
  background: #ecfdf5;
  border-left-color: #10b981;
}

.course-card.half-period.selected {
  box-shadow: 0 0 0 2px #059669;
}

.course-card.selected {
  transform: scale(0.97);
  box-shadow: 0 0 0 2px #7c3aed;
}

.card-title {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
}

.card-details {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.card-badges {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
}

.manual-badge {
  background: #f59e0b;
  color: white;
  font-size: 10px;
  border-radius: 2px;
  padding: 0 2px;
  font-weight: bold;
}

/* 调课标记 badge */
.override-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  border-radius: 2px;
  padding: 0 3px;
  font-weight: bold;
  cursor: pointer;
  line-height: 14px;
  min-width: 14px;
}
.override-badge.override-cancel {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fca5a5;
}
.override-badge.override-replace {
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #93c5fd;
}
.override-badge.override-substitute {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #86efac;
}

/* 被调课的卡片高亮 */
.course-card.is-overridden {
  outline: 2px solid #f59e0b;
  outline-offset: -1px;
}

.card-remove-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.course-card:hover .card-remove-btn {
  opacity: 1;
}


.course-drag-ghost {
  position: fixed;
  top: -1000px;
  left: -1000px;
  width: 150px;
  padding: 8px 10px;
  border-left: 4px solid #2563eb;
  border-radius: 6px;
  background: #eff6ff;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.18);
  color: #0f172a;
  pointer-events: none;
  z-index: 99999;
}

.course-drag-ghost .ghost-title {
  font-size: 13px;
  font-weight: 800;
  line-height: 1.25;
}

.course-drag-ghost .ghost-meta {
  margin-top: 3px;
  font-size: 11px;
  color: #475569;
  line-height: 1.2;
}

.pointer-drag-card {
  position: fixed;
  width: 160px;
  padding: 9px 11px;
  border-left: 4px solid #2563eb;
  border-radius: 6px;
  background: #eff6ff;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.22);
  color: #0f172a;
  pointer-events: none;
  z-index: 100000;
  transform: rotate(1deg);
}

.pointer-drag-title {
  font-size: 13px;
  font-weight: 800;
  line-height: 1.25;
}

.pointer-drag-meta {
  margin-top: 3px;
  font-size: 11px;
  color: #475569;
  line-height: 1.2;
}

/* Sidebar sections */
.sidebar-section {
  display: flex;
  flex-direction: column;
}

.sidebar-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.sidebar-card h3 {
  margin: 0 0 8px 0;
  font-size: 15px;
  font-weight: 750;
  color: #1e293b;
}

.holding-pool {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 480px;
  padding-right: 4px;
}

.holding-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.holding-card:hover {
  border-color: #c084fc;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
}

.holding-card.selected {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.2);
}

.h-title {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
}

.h-info {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.h-progress {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
}

.prog-text {
  font-size: 11px;
  color: #94a3b8;
}

.pool-empty {
  text-align: center;
  padding: 40px 10px;
  color: #94a3b8;
  font-size: 12px;
}

/* Staging area styles */
.staging-zone {
  border: 2px dashed #cbd5e1;
  background: #fefce8;
  transition: all 0.25s ease;
  height: auto;
}

.staging-zone.staging-drag-over {
  border-color: #f59e0b;
  background: linear-gradient(135deg, #fffbeb, #fef3c7);
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.15), 0 4px 16px rgba(245, 158, 11, 0.1);
  transform: scale(1.01);
}

.staging-pool {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 50px;
  max-height: 200px;
  overflow-y: auto;
  padding-right: 2px;
}

.staging-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #f59e0b;
  border-radius: 6px;
  padding: 8px 10px;
  cursor: grab;
  user-select: none;
  transition: all 0.18s ease;
}

.staging-card:hover {
  border-color: #f59e0b;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.12);
  transform: translateX(2px);
}

.staging-card:active {
  cursor: grabbing;
  transform: scale(0.97);
}

.staging-card.selected {
  border-color: #f59e0b;
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.3);
  background: #fffbeb;
}

.staging-card-main {
  flex: 1;
  min-width: 0;
}

.staging-card-subject {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
}

.staging-card-info {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.staging-empty {
  text-align: center;
  padding: 20px 10px;
  color: #94a3b8;
  font-size: 12px;
  border: 1px dashed #e2e8f0;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.6);
}

.staging-empty-icon {
  font-size: 24px;
  margin-bottom: 6px;
}

/* Conflict drawer styles */
.conflict-drawer-content {
  padding: 10px 15px;
}

.conflict-tip-top {
  font-weight: 600;
  margin-bottom: 12px;
  font-size: 14px;
  color: #475569;
}

.conflict-item-card {
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
}

.conflict-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.day-p {
  font-size: 12px;
  font-weight: 700;
  color: #991b1b;
}

.conflict-desc {
  font-size: 13px;
  line-height: 1.5;
  color: #7f1d1d;
}

.excel-import-area {
  padding: 10px 0;
}

.excel-import-area .tip {
  margin-top: 15px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}

.muted-text {
  color: #94a3b8;
}

@media (max-width: 900px) {
  .schedule-settings-shell {
    grid-template-columns: 1fr;
  }

  .schedule-settings-header--cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .day-count-grid {
    grid-template-columns: 72px repeat(3, minmax(72px, 1fr));
    gap: 8px;
  }

  .wizard-base-panels {
    grid-template-columns: 1fr;
  }

  .grade-card-header,
  .grade-grid-header {
    flex-direction: column;
    align-items: stretch;
  }

  .wizard-base-actions {
    flex-direction: column;
    align-items: stretch;
  }
}

.grade-schedule-scope-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 14px 0 16px;
  flex-wrap: wrap;
}

.free-slot-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.free-slot-content--grade {
  color: #ef4444;
}

.free-slot-content--class {
  color: #f59e0b;
}

.free-slot-badge {
  max-width: 64px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Diagonal striped pattern for forbidden slot cell */
.excel-table td.forbidden-slot-cell {
  background: repeating-linear-gradient(45deg,
      #fef2f2,
      #fef2f2 10px,
      #fee2e2 10px,
      #fee2e2 20px) !important;
  border: 1.5px solid #fca5a5 !important;
  transition: all 0.2s ease;
}

.excel-table td.forbidden-slot-cell:hover {
  background: repeating-linear-gradient(45deg,
      #fee2e2,
      #fee2e2 10px,
      #fca5a5 10px,
      #fca5a5 20px) !important;
}

/* Diagonal striped pattern for self study slot cell */
.excel-table td.self-study-cell {
  background: repeating-linear-gradient(45deg,
      #f8fafc,
      #f8fafc 10px,
      #f1f5f9 10px,
      #f1f5f9 20px) !important;
  border: 1px solid #e2e8f0 !important;
}

.excel-table td.class-forbidden-slot-cell {
  background: repeating-linear-gradient(45deg,
      #fffbeb,
      #fffbeb 10px,
      #fef3c7 10px,
      #fef3c7 20px) !important;
  border: 1.5px solid #fcd34d !important;
}

.excel-table td.diff-forbidden-slot-cell {
  background: repeating-linear-gradient(45deg,
      #f5f3ff,
      #f5f3ff 10px,
      #ede9fe 10px,
      #ede9fe 20px) !important;
  border: 1.5px solid #c084fc !important;
}

.free-slot-content--diff {
  color: #8b5cf6;
}

/* Mini timetables styles */
.mini-grid-wrapper {
  margin-top: 4px;
  background: #fafafb;
  border-radius: 4px;
  padding: 6px;
  border: 1px solid #f1f5f9;
}

.mini-cell {
  transition: all 0.15s ease;
}

/* Tuning diagnostic banner styles */
.tuning-diagnostic-banner {
  transition: all 0.3s ease;
}
.tuning-diagnostic-banner.banner-neutral {
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  color: #1e293b;
}
.tuning-diagnostic-banner.banner-success {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
}
.tuning-diagnostic-banner.banner-warning {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
}
.tuning-diagnostic-banner.banner-danger {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.tuning-pulse {
  animation: pulse-animation 2s infinite;
}

@keyframes pulse-animation {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(59, 130, 246, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
  }
}

/* Grid cell highlight styles */
.grid-cell.conflict-safe {
  background-color: rgba(240, 253, 244, 0.5) !important;
  box-shadow: inset 0 0 0 1px #bbf7d0;
}
.grid-cell.conflict-swap {
  background-color: rgba(254, 243, 199, 0.55) !important;
  box-shadow: inset 0 0 0 1px #fde68a;
}
.grid-cell.conflict-blocked {
  background-color: rgba(254, 226, 226, 0.6) !important;
  box-shadow: inset 0 0 0 1px #fecaca;
  cursor: not-allowed !important;
}
.grid-cell.conflict-blocked .course-card {
  pointer-events: none;
}

@media (max-width: 860px) {
  .solve-container {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 560px) {
  .preflight-panel__heading {
    flex-direction: column;
  }
}
</style>
